import { Fragment, useCallback, useEffect, useMemo, useReducer, useRef, useState, type ReactNode } from 'react';
import type {
  Map as MapLibreMap,
  MapGeoJSONFeature,
  MapMouseEvent,
  PointLike,
  StyleSpecification,
} from 'maplibre-gl';
import 'maplibre-gl/dist/maplibre-gl.css';
import {
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  Filter,
  MapPin,
  Minus,
  MoreHorizontal,
  MousePointer2,
  PencilLine,
  Plus,
  Route,
  Ruler,
  ScanSearch,
  ScrollText,
  Trash2,
} from 'lucide-react';
import {
  AdvancedSection,
  DataSection,
  FiltersSection,
  InteractivitySection,
  MapAssetDependenciesSheet,
  MapBottomSheetSurface,
  MapDiagnosticsPane,
  MapFeatureInspectionSheet,
  MapFloatingSummaryCardSurface,
  MapHoverPopupSurface,
  type MapIconPickerSetGroup,
  MapInteractivityModalShell,
  MapLayerSelector,
  MapLegendPane,
  MapPreviewFrame,
  MapPipelineDefinitionEditor,
  MapSourcesPane,
  MapWorkbenchStyleEditorModal,
  MapPresetPane,
  MapSaveSheet,
  MapSidePanelSurface,
  MapTabbedSidePanelSurface,
  MapSurfaceDefinitionEditor,
  MapToolRail,
  MapViewerControlCluster,
  MapViewerFiltersSheetBody,
  MapViewerLegendSheetBody,
  MapWorkbenchAdvancedRuleEditorPanel,
  MapWorkspaceCommandBar,
  SourceInformationSection,
  type MapWorkbenchAdvancedRuleDefinition,
  type MapInteractivityEditorCapabilities,
  type MapInteractivityFieldOption,
  type MapInteractivityManagedItemKind,
  type LayerExperience,
  type LayerExperienceBuiltinValueDefinition,
  type LayerExperiencePipelineDefinition,
  type LayerExperienceResolverDefinition,
  type LayerExperienceSourceDefinition,
  type LayerExperienceSourceFieldDefinition,
  type LayerExperienceSurfaceDefinition,
  type MapStylingPreviewBackground,
  type MapStylingPreviewMode,
  type MapStylingPreviewSampleSet,
  type MapStyleAuthoringPaneProps,
  type MapStyleRenderStackEditorIconPickerConfig,
  type MapSourcesPaneTab,
  type MapViewerControlItem,
  type MapViewerFilterSection,
  type MapViewerFilterValue,
  type MapWorkspaceSpriteRootSelectorConfig,
} from '@workspace/silk-ui';

import { Badge } from './badge';
import { Button } from './button';
import { ColorPaletteInput } from './color-palette-input';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from './dropdown-menu';
import { Input } from './input';
import { Label } from './label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './select';
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle } from './sheet';
import { Skeleton } from './skeleton';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from './table';
import { Textarea } from './textarea';
import type { ThemeOption } from './theme-selector';
import {
  openMapExternalNavigationUrl,
  prepareMapExternalNavigationWindow,
  renderEncodedFeatureTemplate,
  resolveMapExternalNavigationUrl,
} from './map-external-navigation';
import { cn } from '../utils/cn';
import { useResolvedThemeSemanticColorPalette, type SemanticThemeColorPalette } from '../theme';
import {
  buildDrawingOverlayFeatureCollection,
  buildInteractiveDrawingGeometry,
  canCompleteDrawingDraft,
  completeDrawingDraft,
  computeMapGeometryBounds,
  DEFAULT_VISIBLE_MAP_TOOL_IDS,
  getDrawingToolGeometryKind,
  isDrawingToolId,
  isGeometryCompatibleWithTool,
  MAP_DRAW_POLYGON_CLOSE_TOLERANCE_PX,
  MAP_DRAW_VERTEX_HIT_TARGET_PX,
  MAP_BUILT_IN_TOOL_DEFINITIONS,
  updateCoordinateAtIndex,
  updateDrawnGeometryVertex,
  type MapBuiltInToolDefinition,
  type MapBuiltInToolId,
  type MapDrawnGeometry,
} from '../utils/map-tools';
import {
  getMapSourceGeometryKinds,
  isMapSourceGeometryAuthoringReady,
  readMapSourceVectorLayers,
  type MapSourceGeometryKind,
  type MapSourceVectorLayerRecord,
} from '../utils/map-source-geometry';
import { derivePreviewViewFromStyleBounds } from '../utils/map-preview-view';
import { normalizeMapStyleSourcesForRequest } from '../utils/map-style-sources';
import {
  loadLayerRuleBuilderState,
  type LayerRuleDefinition,
  type LayerRuleBuilderState,
} from './map-layer-rules';
import {
  normalizeMapLibreLayoutOverrides,
  normalizeMapLibrePaintOverrides,
} from './map-workbench/styleValueNormalization';
import {
  DEFAULT_ADVANCED_RULE_COLOR,
  normalizeAdvancedRuleColor,
} from './map-layer-rules/colors';
import {
  applyStylingPreviewIsolationPlan,
  buildStylingPreviewCanvasProps,
  buildStylingPreviewIsolationPlan,
  type StylingPreviewCanvasSampleFeature,
} from './map-style-workbench/stylingPreviewCanvasController';
import {
  buildAdvancedRuleEditorPanelProps,
  convertAdvancedRuleLogicToStyleRuleLogic,
  ensureAdvancedRuleGroup,
} from './map-workbench/advancedRuleEditorController';
import {
  buildMapWorkbenchRenderStackController,
  normalizeMapWorkbenchRenderStackTemplateId,
  type MapWorkbenchRenderStackAddRequestId,
} from './map-workbench/renderStackController';
import {
  areMapWorkbenchSpiderifyStacksEquivalent,
  buildMapWorkbenchSpiderifyLayout,
  selectMapWorkbenchSpiderifyFeatures,
  type MapWorkbenchSpiderifyCandidate,
  type MapWorkbenchSpiderifyPoint,
} from './map-workbench/spiderify';
import { buildMapWorkbenchAnchoredOverlayStyle } from './map-workbench/overlayPlacement';
import { resolveMapWorkbenchPopupIdentity } from './map-workbench/popupIdentity';
import { resolveMapWorkbenchPopupPresentation } from './map-workbench/popupPresentation';
import {
  buildMapWorkbenchSpiderifyChooserItemFromResolvedSummary,
  buildMapWorkbenchSpiderifyChooserItems,
  type MapWorkbenchSpiderifyChooserListItem,
  shouldUseMapWorkbenchSpiderifySheet,
} from './map-workbench/spiderifyChooser';
import { createCondition, createGroup, ensureLogicRoot } from './map-style-workbench/builderLogic';

export function resolveWorkbenchStyleReloadViewState({
  requestedView,
  liveView,
  defaultViewState,
}: {
  requestedView?: MapWorkbenchViewState | null;
  liveView?: MapWorkbenchViewState | null;
  defaultViewState: MapWorkbenchViewState;
}): MapWorkbenchViewState {
  return requestedView ?? liveView ?? defaultViewState;
}

const MAP_WORKBENCH_VIEW_STATE_EPSILON = 0.000001;

export function areMapWorkbenchViewStatesEquivalent(
  left?: MapWorkbenchViewState | null,
  right?: MapWorkbenchViewState | null,
): boolean {
  if (!left && !right) {
    return true;
  }
  if (!left || !right) {
    return false;
  }
  const leftCenter = left.center ?? null;
  const rightCenter = right.center ?? null;
  if (Boolean(leftCenter) !== Boolean(rightCenter)) {
    return false;
  }
  if (
    leftCenter &&
    rightCenter &&
    (Math.abs(leftCenter[0] - rightCenter[0]) > MAP_WORKBENCH_VIEW_STATE_EPSILON ||
      Math.abs(leftCenter[1] - rightCenter[1]) > MAP_WORKBENCH_VIEW_STATE_EPSILON)
  ) {
    return false;
  }
  return (
    Math.abs((left.zoom ?? 0) - (right.zoom ?? 0)) <= MAP_WORKBENCH_VIEW_STATE_EPSILON &&
    Math.abs((left.bearing ?? 0) - (right.bearing ?? 0)) <= MAP_WORKBENCH_VIEW_STATE_EPSILON &&
    Math.abs((left.pitch ?? 0) - (right.pitch ?? 0)) <= MAP_WORKBENCH_VIEW_STATE_EPSILON
  );
}
import {
  applyMapExperiencePreset,
  buildMapExperienceGeoJSONQueryRequest,
  buildMapExperiencePreset,
  buildMapExperienceTargetFilterExpression,
  buildMapExperienceTargetKey,
  compileStyleWithMapExperience,
  collectMapExperienceServerManagedFilters,
  createDefaultMapExperienceBuiltinValues,
  createEmptyMapExperienceMetadata as createEmptyMapExperienceState,
  loadMapExperienceMetadata as loadMapExperienceState,
  migrateLegacyLayerRulesToMapExperience,
  omitMapExperienceFilterValues,
  normalizeOptionalMapExperienceColorValue,
  removeMapExperiencePreset,
  resolveMapExperienceColorValue,
  resolveMapExperienceFilterValue,
  serializeMapExperienceMetadata as serializeMapExperienceState,
  upsertMapExperiencePreset,
  hasConfiguredMapExperienceFilterValue,
  type MapExperienceGeoJSONQueryRequest,
  type MapExperienceFilterDefinition,
  type MapExperienceFilterSet,
  type MapExperienceInteractivityGeometryKind,
  type MapExperienceLegendGroup as MapExperienceLegendSection,
  type MapExperienceCategoricalStyleProfile,
  type MapExperienceMetadataV1 as MapExperienceState,
  type MapExperiencePresetDefinition,
  type MapExperienceRangeStyleProfile,
  type MapExperienceResolverConnectionDefinition,
  type MapExperienceResolverDefinition,
  type MapExperienceSurfaceBlockDefinition,
  type MapExperienceSurfaceDefinition,
  type MapExperiencePipelineDefinition,
  type MapExperienceInteractivitySourceDefinition,
  type MapExperienceSingleStyleProfile,
  type MapExperienceStyleColorValue,
  type MapExperienceStyleProfile,
  type MapExperienceStyleRepresentation,
} from './map-experience';
import {
  runMapExperienceInteractivityPipelines,
  type MapExperienceResolverExecutionRequest,
  type RuntimeFeatureRef,
  type RuntimeFeatureStateRecord,
  type RuntimeFeatureStatePatch,
  type RuntimeInteractivityDefinitionLike,
  type RuntimePipelineExecution,
} from './map-experience-runtime';

export type MapWorkbenchShareScope = 'tenant' | 'shared' | 'public';
export type MapWorkbenchAssetMode = 'style' | 'custom-map';
export type MapWorkbenchAiProvider = 'openai' | 'anthropic';
export type MapWorkbenchMode = 'author' | 'view' | 'customize-save-copy';
type MapWorkbenchInteractivityEditorMode = 'create' | 'edit';

type MapWorkbenchInteractivityEditorState =
  | { kind: 'surface'; mode: MapWorkbenchInteractivityEditorMode; draft: LayerExperienceSurfaceDefinition }
  | { kind: 'pipeline'; mode: MapWorkbenchInteractivityEditorMode; draft: LayerExperiencePipelineDefinition };

export type MapWorkbenchToolRuntimeEvent =
  | { kind: 'tool_activated' | 'tool_deactivated' | 'drawing_cleared'; tool_id: MapBuiltInToolId; geometry: null }
  | {
      kind: 'drawing_started' | 'drawing_updated' | 'drawing_completed';
      tool_id: MapBuiltInToolId;
      geometry: MapDrawnGeometry;
      draft?: boolean;
    };

type MapDrawingEditTarget = {
  source: 'draft' | 'committed';
  index: number;
};

type WorkbenchDraftSnapshot = {
  editorText: string;
  mapExperience: MapExperienceState;
  activeSpriteId: string | null;
  glyphSetId: string | null;
};

type WorkbenchPreviewSyncState = {
  status: 'idle' | 'loading' | 'success' | 'error';
  error: string | null;
  appliedText: string;
  appliedMapExperience: string;
};

type WorkbenchViewerFilterRuntimeEntry = {
  enabled: boolean;
  value: unknown;
};

type WorkbenchViewerFilterRuntimeState = Record<string, WorkbenchViewerFilterRuntimeEntry>;

type WorkbenchDraftEngineState = {
  persisted: WorkbenchDraftSnapshot;
  draft: WorkbenchDraftSnapshot;
  preview: WorkbenchPreviewSyncState;
};

type WorkbenchDraftEngineAction =
  | { type: 'load_snapshot'; snapshot: WorkbenchDraftSnapshot }
  | { type: 'update_draft'; updater: (draft: WorkbenchDraftSnapshot) => WorkbenchDraftSnapshot }
  | { type: 'set_error'; error: string | null }
  | { type: 'preview_started' }
  | {
      type: 'preview_succeeded';
      appliedText?: string;
      mapExperience?: MapExperienceState;
    }
  | { type: 'preview_failed'; error: string }
  | { type: 'save_succeeded' };

export interface MapWorkbenchViewState {
  center?: [number, number];
  zoom?: number;
  bearing?: number;
  pitch?: number;
}

const createDefaultWorkbenchDraftSnapshot = (): WorkbenchDraftSnapshot => ({
  editorText: JSON.stringify(DEFAULT_STYLE_TEMPLATE, null, 2),
  mapExperience: createEmptyMapExperienceState(),
  activeSpriteId: null,
  glyphSetId: null,
});

const serializeWorkbenchMapExperience = (mapExperience: MapExperienceState): string =>
  JSON.stringify(serializeMapExperienceState(mapExperience));

const createWorkbenchPreviewSyncState = (snapshot: WorkbenchDraftSnapshot): WorkbenchPreviewSyncState => ({
  status: 'idle',
  error: null,
  appliedText: snapshot.editorText,
  appliedMapExperience: serializeWorkbenchMapExperience(snapshot.mapExperience),
});

const createWorkbenchDraftEngineState = (
  snapshot: WorkbenchDraftSnapshot = createDefaultWorkbenchDraftSnapshot(),
): WorkbenchDraftEngineState => ({
  persisted: snapshot,
  draft: snapshot,
  preview: createWorkbenchPreviewSyncState(snapshot),
});

const workbenchDraftEngineReducer = (
  state: WorkbenchDraftEngineState,
  action: WorkbenchDraftEngineAction,
): WorkbenchDraftEngineState => {
  if (action.type === 'load_snapshot') {
    return createWorkbenchDraftEngineState(action.snapshot);
  }
  if (action.type === 'update_draft') {
    return {
      ...state,
      draft: action.updater(state.draft),
    };
  }
  if (action.type === 'set_error') {
    return {
      ...state,
      preview: {
        ...state.preview,
        error: action.error,
      },
    };
  }
  if (action.type === 'preview_started') {
    return {
      ...state,
      preview: {
        ...state.preview,
        status: 'loading',
        error: null,
      },
    };
  }
  if (action.type === 'preview_succeeded') {
    const nextMapExperience = action.mapExperience ?? state.draft.mapExperience;
    return {
      ...state,
      preview: {
        status: 'success',
        error: null,
        appliedText: action.appliedText ?? state.draft.editorText,
        appliedMapExperience: serializeWorkbenchMapExperience(nextMapExperience),
      },
    };
  }
  if (action.type === 'preview_failed') {
    return {
      ...state,
      preview: {
        ...state.preview,
        status: 'error',
        error: action.error,
      },
    };
  }
  if (action.type === 'save_succeeded') {
    return {
      persisted: state.draft,
      draft: state.draft,
      preview: {
        status: 'success',
        error: null,
        appliedText: state.draft.editorText,
        appliedMapExperience: serializeWorkbenchMapExperience(state.draft.mapExperience),
      },
    };
  }
  return state;
};

export interface MapWorkbenchVectorLayerRecord {
  id?: string;
  layer?: string;
  name?: string;
  description?: string;
  minzoom?: number;
  minZoom?: number;
  min_zoom?: number;
  maxzoom?: number;
  maxZoom?: number;
  max_zoom?: number;
  geometry_type?: string;
  geometry?: string;
  geometryType?: string;
  geometry_types?: string[];
  geometryTypes?: string[];
  fields?: Record<string, unknown>;
}

export interface MapWorkbenchSource {
  id: string;
  tenant_id?: string | null;
  name?: string;
  description?: string | null;
  share_scope?: string | null;
  source_kind?: 'pmtiles' | 'geojson_http' | string | null;
  bounds?: number[] | null;
  min_zoom?: number | null;
  max_zoom?: number | null;
  vector_layers?: Array<MapWorkbenchVectorLayerRecord | string> | null;
  metadata?: Record<string, unknown> | null;
  request_recipe?: Record<string, unknown> | null;
  server_filtering?: Record<string, unknown> | null;
}

export interface MapWorkbenchStyleRecord {
  id?: string;
  tenant_id?: string | null;
  name?: string;
  description?: string | null;
  share_scope?: MapWorkbenchShareScope | null;
  style_json?: Record<string, unknown> | null;
  metadata?: Record<string, unknown> | null;
  updated_at?: string | null;
}

export interface MapWorkbenchSpriteSet {
  id?: string;
  tenant_id?: string | null;
  name?: string;
  description?: string | null;
  share_scope?: string | null;
  metadata?: Record<string, unknown> | null;
}

export interface MapWorkbenchSpriteDependency {
  id: string;
  spriteSetId: string;
  sortOrder: number;
}

export interface MapWorkbenchSpriteManifestEntry {
  width: number;
  height: number;
  x: number;
  y: number;
  pixelRatio?: number;
  sdf?: boolean;
}

export interface MapWorkbenchGlyphSet {
  id?: string;
  tenant_id?: string | null;
  name?: string;
  description?: string | null;
  share_scope?: string | null;
  fontstacks?: string[];
  metadata?: Record<string, unknown> | null;
}

export interface MapWorkbenchJsonPatchOperation {
  op: 'add' | 'replace' | 'remove';
  path: string;
  value?: unknown;
}

export interface MapWorkbenchFeatureInspection {
  id?: string | number;
  source?: string;
  sourceLayer?: string;
  layerId?: string;
  geometryType?: string;
  geometry?: Record<string, unknown>;
  bounds?: [number, number, number, number];
  screenPoint?: { x: number; y: number } | null;
  properties?: Record<string, unknown>;
  rawJson: Record<string, unknown>;
}

export interface MapWorkbenchHighlightTargetDescriptor {
  sourceAlias: string;
  sourceId: string;
  sourceLayer: string;
  geometry: string;
  featureIdField: string;
}

export interface MapWorkbenchRuntimeHighlightDescriptor {
  channel: 'hover' | 'selected';
  feature: MapWorkbenchFeatureInspection;
  target: MapWorkbenchHighlightTargetDescriptor;
}

export interface MapWorkbenchShareScopeOption {
  value: MapWorkbenchShareScope;
  label: string;
  disabled?: boolean;
}

export interface MapWorkbenchPreviewResponse {
  style_json?: Record<string, unknown> | null;
  view_state?: MapWorkbenchViewState | null;
}

export interface MapWorkbenchMetadataArgs {
  existingMetadata?: Record<string, unknown> | null;
  styleJson: Record<string, unknown>;
  authoredStyleJson?: Record<string, unknown> | null;
  sources: MapWorkbenchSource[];
  spriteSet?: MapWorkbenchSpriteSet | null;
  spriteEntries?: Array<{
    id: string;
    spriteSet: MapWorkbenchSpriteSet;
  }>;
  activeSpriteId?: string | null;
  glyphSet?: MapWorkbenchGlyphSet | null;
  mapExperience?: unknown | null;
  initialView?: MapWorkbenchViewState | null;
  isCustomMap: boolean;
}

export interface MapWorkbenchResolvedFeatureAction {
  pipeline: LayerExperiencePipelineDefinition;
  label: string;
  disabled?: boolean;
  disabledReason?: string | null;
}

export interface MapWorkbenchResolvedFeaturePopup {
  surface: LayerExperienceSurfaceDefinition;
  surfaces: LayerExperienceSurfaceDefinition[];
  pipelines: LayerExperiencePipelineDefinition[];
  title: string;
  subtitle?: string | null;
  summary: Array<{ key: string; label: string; value: string }>;
  badges: string[];
  actions: MapWorkbenchResolvedFeatureAction[];
  mode?: 'tooltip' | 'pinned' | null;
}

export interface MapWorkbenchResolvedFeaturePopupSummaryRow {
  key: string;
  label: string;
  value: string;
}

export interface MapWorkbenchFeatureActionContext {
  feature: MapWorkbenchFeatureInspection;
  popup: MapWorkbenchResolvedFeaturePopup;
}

export type MapWorkbenchResolvedFeatureSurface = MapWorkbenchResolvedFeaturePopup;

type MapWorkbenchResolvedFeatureSurfaceTabContent = {
  id: string;
  label: string;
  content?: ReactNode;
};

export interface MapWorkbenchRuntimeSurfaceSectionRenderArgs {
  feature: MapWorkbenchFeatureInspection;
  runtimeFeature: RuntimeFeatureRef | null;
  surface: MapWorkbenchResolvedFeatureSurface;
  // `custom` sections may delegate final rendering to a host-owned renderer.
  section: MapExperienceSurfaceBlockDefinition;
}

export interface MapWorkbenchRuntimeSurfacePanelProps {
  headerMode?: 'default' | 'compact';
  hideDescription?: boolean;
  hideSummary?: boolean;
  defaultTabId?: string | null;
  hideInspectAction?: boolean;
}

export interface MapWorkbenchRuntimeFeatureStateTarget {
  key: string;
  sourceAlias: string;
  sourceId: string;
  sourceLayer: string;
  label: string;
  geometry: MapSourceGeometryKind;
}

export interface MapWorkbenchRuntimeFeatureStateHydrationArgs {
  target: MapWorkbenchRuntimeFeatureStateTarget;
  features: RuntimeFeatureRef[];
}

export interface MapWorkbenchRuntimeFeatureStateHydrationResult {
  resolver_id?: string | null;
  records: RuntimeFeatureStateRecord[];
  warnings?: string[] | null;
}

type MapWorkbenchResolvedInteractivityState = {
  feature: MapWorkbenchFeatureInspection | null;
  runtimeFeature: RuntimeFeatureRef | null;
  popup: MapWorkbenchResolvedFeaturePopup | null;
  surface: MapWorkbenchResolvedFeatureSurface | null;
  highlightDescriptors: MapWorkbenchRuntimeHighlightDescriptor[];
  nextViewCenter: [number, number] | null;
  executions: RuntimePipelineExecution<string>[];
};

export interface MapWorkbenchBehavior {
  isCustomMapAsset: (style?: MapWorkbenchStyleRecord | null) => boolean;
  buildOverlayStyleFromRuntime: (
    styleJson?: Record<string, unknown> | null,
    metadata?: Record<string, unknown> | null
  ) => Record<string, unknown> | null;
  rewritePreviewStyleToRuntimeSources: (styleJson?: Record<string, unknown> | null) => Record<string, unknown> | null;
  buildMetadata: (args: MapWorkbenchMetadataArgs) => Record<string, unknown>;
  buildSpriteUrl: (id: string) => string;
  buildGlyphUrl: (id: string) => string;
  buildSpriteRuntimeUrl: (id: string) => string;
  buildGlyphRuntimeUrl: (id: string) => string;
  inferSpriteDependencies?: (styleJson?: Record<string, unknown> | null) => MapWorkbenchSpriteDependency[];
  inferSpriteSetId: (styleJson?: Record<string, unknown> | null) => string | null;
  inferGlyphSetId: (styleJson?: Record<string, unknown> | null) => string | null;
  composeStyleWithBasemap: (overlayStyle: Record<string, unknown>, basemapTheme: string) => Promise<Record<string, unknown>>;
  normalizeShareScope: (value?: string | null, fallback?: MapWorkbenchShareScope) => MapWorkbenchShareScope;
  performFeatureAction?: (args: {
    action: MapWorkbenchResolvedFeatureAction;
    context: MapWorkbenchFeatureActionContext;
  }) => void | Promise<void>;
}

export interface MapWorkbenchDataAdapter {
  loadStyle: (id: string) => Promise<MapWorkbenchStyleRecord>;
  listSources: (params: { share_scope?: MapWorkbenchShareScope }) => Promise<MapWorkbenchSource[]>;
  listSpriteSets: (params: { share_scope?: MapWorkbenchShareScope }) => Promise<MapWorkbenchSpriteSet[]>;
  getSpriteSetManifest: (id: string) => Promise<Record<string, MapWorkbenchSpriteManifestEntry>>;
  getSpriteSetImage: (id: string) => Promise<Blob>;
  getSpriteSetPreviewManifest?: (id: string) => Promise<Record<string, MapWorkbenchSpriteManifestEntry>>;
  getSpriteSetPreviewImage?: (id: string) => Promise<Blob>;
  listGlyphSets: (params: { share_scope?: MapWorkbenchShareScope }) => Promise<MapWorkbenchGlyphSet[]>;
  previewStyle: (styleJson: Record<string, unknown>) => Promise<MapWorkbenchPreviewResponse>;
  queryGeoJSONSource: (id: string, payload: MapExperienceGeoJSONQueryRequest) => Promise<Record<string, unknown>>;
  listInteractivityResolvers?: (params: {
    style_id: string;
    source_id?: string | null;
    source_layer?: string | null;
    asset_mode?: MapWorkbenchAssetMode;
  }) => Promise<MapExperienceResolverDefinition[]>;
  listInteractivityResolverConnections?: (params: {
    style_id: string;
    source_id?: string | null;
    source_layer?: string | null;
    asset_mode?: MapWorkbenchAssetMode;
  }) => Promise<MapExperienceResolverConnectionDefinition[]>;
  executeInteractivityResolver?: (
    payload: MapExperienceResolverExecutionRequest,
  ) => Promise<RuntimeFeatureStatePatch>;
  createStyle: (payload: {
    name: string;
    description?: string;
    share_scope: MapWorkbenchShareScope;
    style_json: Record<string, unknown>;
    metadata?: Record<string, unknown> | null;
  }) => Promise<MapWorkbenchStyleRecord>;
  updateStyle: (id: string, payload: {
    style_json: Record<string, unknown>;
    metadata?: Record<string, unknown> | null;
  }) => Promise<MapWorkbenchStyleRecord>;
}

export interface MapWorkbenchAiAdapter {
  loading?: boolean;
  error?: string | null;
  hasProviderKey: (provider: MapWorkbenchAiProvider) => boolean;
  openSettings: () => void;
  generatePatch: (args: {
    provider: MapWorkbenchAiProvider;
    prompt: string;
    styleJson: Record<string, unknown>;
    context: Record<string, unknown>;
  }) => Promise<{ patch: MapWorkbenchJsonPatchOperation[] }>;
}

export interface MapWorkbenchOwnershipState {
  readOnly: boolean;
  mode?: MapWorkbenchMode;
  notice?: string | null;
}

export type MapWorkbenchDesktopAuthoringPaneSection =
  | 'style'
  | 'filters'
  | 'interactions'
  | 'data'
  | 'source-info'
  | 'advanced'
  | 'sources';

export interface MapWorkbenchDesktopAuthoringPaneTarget {
  key: string;
  label: string;
  sourceId: string;
  sourceLayer: string;
  geometry: MapSourceGeometryKind;
  rendered: boolean;
  layerIndex: number;
  fields: WorkbenchFieldOption[];
}

export interface MapWorkbenchDesktopAuthoringPaneRenderArgs {
  mode: MapWorkbenchMode;
  activeSection: MapWorkbenchDesktopAuthoringPaneSection;
  availableFields: Array<{ name: string; type: string }>;
  stylePane: MapStyleAuthoringPaneProps | null;
  spriteRootSelector: MapWorkspaceSpriteRootSelectorConfig | null;
  footerMessage: string;
  layerExperience: LayerExperience | null;
  onApply: () => void;
  onSave: () => void;
  onSectionChange: (section: MapWorkbenchDesktopAuthoringPaneSection) => void;
  onSelectTarget: (targetKey: string) => void;
  onUpdateLayer: ((updater: (prev: LayerExperience) => LayerExperience) => void) | null;
  renderSection: (section: MapWorkbenchDesktopAuthoringPaneSection) => ReactNode;
  sections: MapWorkbenchDesktopAuthoringPaneSection[];
  target: MapWorkbenchDesktopAuthoringPaneTarget | null;
  targetOptions: MapWorkbenchDesktopAuthoringPaneTarget[];
  applyDisabled: boolean;
  applyLabel: string;
  canApply: boolean;
  canSave: boolean;
  saveDisabled: boolean;
  saveLabel: string;
}

export interface MapWorkbenchProps {
  styleId?: string;
  assetMode?: MapWorkbenchAssetMode;
  adapter: MapWorkbenchDataAdapter;
  behavior: MapWorkbenchBehavior;
  navigate: (path: string) => void;
  basemapTheme: string;
  basemapThemeLabel: string;
  canManageGlobal: boolean;
  defaultShareScope: MapWorkbenchShareScope;
  shareScopeOptions: MapWorkbenchShareScopeOption[];
  defaultViewState: MapWorkbenchViewState;
  defaultMode?: MapWorkbenchMode;
  mobileRightPaneDefaultOpen?: boolean;
  viewerHideIdleRightPane?: boolean;
  hideViewModeToolbar?: boolean;
  rootClassName?: string;
  runtimeOverlayGeoJSON?: Record<string, unknown> | null;
  runtimeOverlayStyle?: {
    fillPaint?: Record<string, unknown> | null;
    linePaint?: Record<string, unknown> | null;
    pointPaint?: Record<string, unknown> | null;
  } | null;
  runtimeOverlayInteractive?: boolean;
  isRuntimeOverlayFeatureInteractive?: (feature: {
    featureId: string | number | null;
    geometryType: string | null;
    properties: Record<string, unknown>;
  }) => boolean;
  onRuntimeOverlayFeatureHover?: (feature: {
    featureId: string | number | null;
    geometryType: string | null;
    properties: Record<string, unknown>;
  } | null) => void;
  onRuntimeOverlayFeatureClick?: (feature: {
    featureId: string | number | null;
    geometryType: string | null;
    properties: Record<string, unknown>;
  }) => void;
  onPinnedFeatureChange?: (
    feature: MapWorkbenchFeatureInspection | null,
    trigger: 'click' | 'context_menu',
  ) => void;
  ai?: MapWorkbenchAiAdapter | null;
  resolveOwnership?: (style: MapWorkbenchStyleRecord | null, isCustomMap: boolean) => MapWorkbenchOwnershipState;
  renderDesktopAuthoringPane?: (args: MapWorkbenchDesktopAuthoringPaneRenderArgs) => ReactNode;
  // Host-owned extension hook for `custom` runtime surface sections.
  // Intended for bounded, platform/system-managed experiences that need
  // richer UI than the shared declarative block set can express.
  renderRuntimeSurfaceSection?: (args: MapWorkbenchRuntimeSurfaceSectionRenderArgs) => ReactNode;
  runtimeSurfacePanelProps?: MapWorkbenchRuntimeSurfacePanelProps;
  hydrateRuntimeFeatureState?: (
    args: MapWorkbenchRuntimeFeatureStateHydrationArgs,
  ) => Promise<MapWorkbenchRuntimeFeatureStateHydrationResult | null>;
}

const DEFAULT_STYLE_TEMPLATE: Record<string, unknown> = {
  version: 8,
  name: 'Untitled Style',
  sources: {},
  layers: [],
};

const toAlias = (value: string) => {
  const cleaned = value.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '');
  return cleaned || 'source';
};

const AUTO_PREVIEW_DEBOUNCE_MS = 350;
const PMTILES_PREFIX = 'pmtiles://';
const GEOJSON_PREFIX = 'geojson://';
const MAP_SOURCE_ARCHIVE_PATTERN = /^\/api\/v1(?:\/mapping)?\/map-sources\/([^/]+)\/archive$/;
const MAP_SOURCE_TILE_TEMPLATE_PATTERN = /^\/api\/v1(?:\/mapping)?\/map-sources\/([^/]+)\/tiles\/\{z\}\/\{x\}\/\{y\}$/;
const MAP_SOURCE_GEOJSON_PATTERN = /^\/api\/v1(?:\/mapping)?\/map-sources\/([^/]+)\/data\.geojson$/;
const SPIDERIFY_MAX_CANDIDATES = 12;
const SPIDERIFY_OPEN_ANIMATION_MS = 160;
export const MAP_WORKBENCH_RUNTIME_FEATURE_STATE_REFRESH_EVENT = 'map-workbench:runtime-feature-state-refresh';
export const MAP_WORKBENCH_LIVE_GEOJSON_SOURCE_REFRESH_EVENT = 'map-workbench:live-geojson-source-refresh';

const logRuntimeFeatureStateDebug = (message: string, details?: Record<string, unknown>) => {
  if (typeof console === 'undefined' || typeof console.debug !== 'function') {
    return;
  }
  if (details) {
    console.debug(`[MapWorkbench][RuntimeFeatureState] ${message}`, details);
    return;
  }
  console.debug(`[MapWorkbench][RuntimeFeatureState] ${message}`);
};

const summarizeWorkbenchResolvedInteractivityState = (state: MapWorkbenchResolvedInteractivityState) => ({
  popupMode: state.popup?.mode ?? null,
  surfaceKind: state.surface?.surface.surface_kind ?? null,
  highlightCount: state.highlightDescriptors.length,
  nextViewCenter: state.nextViewCenter ?? null,
  executionCount: state.executions.length,
  effectKinds: state.executions.flatMap(execution => execution.effects.map(effect => effect.kind)),
});

export const isWorkbenchMobileViewport = () =>
  typeof window !== 'undefined' &&
  typeof window.matchMedia === 'function' &&
  window.matchMedia('(max-width: 768px)').matches;

export const resolveInitialWorkbenchRightPaneCollapsed = (mobileRightPaneDefaultOpen = true) =>
  isWorkbenchMobileViewport() ? !mobileRightPaneDefaultOpen : false;

let pmtilesProtocolInstance: { add: (archive: unknown) => void; tile: unknown } | null = null;
let pmtilesProtocolRegistered = false;
const pmtilesArchiveRegistry = new Set<string>();

const formatDate = (value?: string | null) => {
  if (!value) return '—';
  try {
    return new Date(value).toLocaleString();
  } catch {
    return value;
  }
};

const formatIconTokenLabel = (token: string) =>
  token
    .split(/[-_]+/)
    .filter(Boolean)
    .map(part => part.charAt(0).toUpperCase() + part.slice(1))
    .join(' ') || token;

const readLayoutIconImage = (layout?: Record<string, unknown> | null) => {
  const iconImage = typeof layout?.['icon-image'] === 'string' ? layout['icon-image'].trim() : '';
  return iconImage || null;
};

const cloneStyleOverrideRecord = (value?: Record<string, unknown> | null) =>
  value ? JSON.parse(JSON.stringify(value)) as Record<string, unknown> : null;

const loadImageElement = (blob: Blob) =>
  new Promise<HTMLImageElement>((resolve, reject) => {
    const objectUrl = URL.createObjectURL(blob);
    const image = new Image();
    image.onload = () => {
      URL.revokeObjectURL(objectUrl);
      resolve(image);
    };
    image.onerror = () => {
      URL.revokeObjectURL(objectUrl);
      reject(new Error('Failed to decode sprite sheet image.'));
    };
    image.src = objectUrl;
  });

const buildSpriteIconPreviewUrls = async (
  manifest: Record<string, MapWorkbenchSpriteManifestEntry>,
  spriteSheet: Blob,
) => {
  const tokens = Object.keys(manifest);
  if (!tokens.length || typeof document === 'undefined') {
    return {} as Record<string, string>;
  }

  const image = await loadImageElement(spriteSheet);
  const previews: Record<string, string> = {};

  for (const token of tokens) {
    const entry = manifest[token];
    if (!entry || entry.width <= 0 || entry.height <= 0) {
      continue;
    }
    const previewScale = Math.max(2, Math.ceil(entry.pixelRatio ?? 1) * 2);
    const sourceCanvas = document.createElement('canvas');
    sourceCanvas.width = entry.width;
    sourceCanvas.height = entry.height;
    const sourceContext = sourceCanvas.getContext('2d');
    if (!sourceContext) {
      continue;
    }
    sourceContext.imageSmoothingEnabled = false;
    sourceContext.clearRect(0, 0, sourceCanvas.width, sourceCanvas.height);
    sourceContext.drawImage(
      image,
      entry.x,
      entry.y,
      entry.width,
      entry.height,
      0,
      0,
      entry.width,
      entry.height,
    );
    const previewCanvas = document.createElement('canvas');
    previewCanvas.width = Math.max(entry.width * previewScale, 32);
    previewCanvas.height = Math.max(entry.height * previewScale, 32);
    const previewContext = previewCanvas.getContext('2d');
    if (!previewContext) {
      continue;
    }

    if (entry.sdf) {
      const maskCanvas = document.createElement('canvas');
      maskCanvas.width = Math.max(entry.width * previewScale * 2, 64);
      maskCanvas.height = Math.max(entry.height * previewScale * 2, 64);
      const maskContext = maskCanvas.getContext('2d');
      if (!maskContext) {
        continue;
      }
      // Let the distance field interpolate at a larger size first, then remap it to a
      // tight alpha band so the legend silhouette stays readable without the soft halo.
      maskContext.imageSmoothingEnabled = true;
      maskContext.clearRect(0, 0, maskCanvas.width, maskCanvas.height);
      maskContext.drawImage(
        sourceCanvas,
        0,
        0,
        sourceCanvas.width,
        sourceCanvas.height,
        0,
        0,
        maskCanvas.width,
        maskCanvas.height,
      );
      const imageData = maskContext.getImageData(0, 0, maskCanvas.width, maskCanvas.height);
      const data = imageData.data;
      const edgeStart = 118;
      const edgeEnd = 146;
      for (let index = 0; index < data.length; index += 4) {
        const alpha = data[index + 3];
        const luminance = Math.round((data[index] + data[index + 1] + data[index + 2]) / 3);
        const signal = alpha > 0 ? luminance : 0;
        const nextAlpha =
          signal <= edgeStart
            ? 0
            : signal >= edgeEnd
              ? 255
              : Math.round(((signal - edgeStart) / (edgeEnd - edgeStart)) * 255);
        data[index] = 255;
        data[index + 1] = 255;
        data[index + 2] = 255;
        data[index + 3] = nextAlpha;
      }
      maskContext.putImageData(imageData, 0, 0);

      previewContext.imageSmoothingEnabled = true;
      previewContext.clearRect(0, 0, previewCanvas.width, previewCanvas.height);
      previewContext.drawImage(
        maskCanvas,
        0,
        0,
        maskCanvas.width,
        maskCanvas.height,
        0,
        0,
        previewCanvas.width,
        previewCanvas.height,
      );
    } else {
      previewContext.imageSmoothingEnabled = false;
      previewContext.clearRect(0, 0, previewCanvas.width, previewCanvas.height);
      previewContext.drawImage(
        sourceCanvas,
        0,
        0,
        sourceCanvas.width,
        sourceCanvas.height,
        0,
        0,
        previewCanvas.width,
        previewCanvas.height,
      );
    }
    previews[token] = previewCanvas.toDataURL('image/png');
  }

  return previews;
};

const resolveMapRequestUrl = (url: string) => {
  if (!url) return url;
  const trimmed = url.trim();
  const hasProtocol = /^[a-z][a-z0-9+.-]*:/i.test(trimmed);
  if (hasProtocol) return trimmed;
  const origin = typeof window !== 'undefined' ? window.location.origin : null;
  if (!origin) return trimmed;
  try {
    return new URL(trimmed, origin).toString().replace(/%7B/gi, '{').replace(/%7D/gi, '}');
  } catch {
    return trimmed;
  }
};

const extractMapRequestPathname = (value: string) => {
  const trimmed = value.trim();
  if (!trimmed) {
    return '';
  }
  if (trimmed.startsWith('/')) {
    return trimmed;
  }
  try {
    return new URL(trimmed).pathname;
  } catch {
    return trimmed;
  }
};

const ensurePMTilesArchiveProtocol = async (url: string) => {
  const absoluteUrl = resolveMapRequestUrl(url);
  const [{ default: maplibregl }, { PMTiles, Protocol }] = await Promise.all([import('maplibre-gl'), import('pmtiles')]);

  if (!pmtilesProtocolInstance) {
    pmtilesProtocolInstance = new Protocol();
  }
  if (!pmtilesProtocolRegistered) {
    maplibregl.addProtocol('pmtiles', pmtilesProtocolInstance.tile);
    pmtilesProtocolRegistered = true;
  }
  if (!pmtilesArchiveRegistry.has(absoluteUrl)) {
    pmtilesProtocolInstance.add(new PMTiles(absoluteUrl));
    pmtilesArchiveRegistry.add(absoluteUrl);
  }

  return `pmtiles://${absoluteUrl}`;
};

const resolveSourceIdFromPrefixedRef = (value: string, prefix: string, runtimePattern: RegExp) => {
  if (!value.startsWith(prefix)) {
    return '';
  }

  const candidate = value.slice(prefix.length).split('?', 1)[0].trim();
  if (!candidate || candidate.includes('://') || candidate.startsWith('/')) {
    const runtimeMatch = extractMapRequestPathname(candidate).match(runtimePattern);
    return runtimeMatch?.[1] ? decodeURIComponent(runtimeMatch[1]) : '';
  }

  return candidate;
};

const resolveMapSourceIdFromStyleSource = (styleSource?: Record<string, unknown> | null) => {
  if (!styleSource || typeof styleSource !== 'object') {
    return '';
  }

  const rawUrl = typeof styleSource.url === 'string' ? styleSource.url.trim() : '';
  const sourceIdFromUrl = resolveSourceIdFromPrefixedRef(rawUrl, PMTILES_PREFIX, MAP_SOURCE_ARCHIVE_PATTERN);
  if (sourceIdFromUrl) {
    return sourceIdFromUrl;
  }

  const rawData = typeof styleSource.data === 'string' ? styleSource.data.trim() : '';
  const sourceIdFromData = resolveSourceIdFromPrefixedRef(rawData, GEOJSON_PREFIX, MAP_SOURCE_GEOJSON_PATTERN);
  if (sourceIdFromData) {
    return sourceIdFromData;
  }
  const runtimeGeoJSONMatch = extractMapRequestPathname(rawData).match(MAP_SOURCE_GEOJSON_PATTERN);
  return runtimeGeoJSONMatch?.[1] ? decodeURIComponent(runtimeGeoJSONMatch[1]) : '';
};

const canonicalizeStyleToInternalSourceRefs = (styleJson: Record<string, unknown>) => {
  return normalizeMapStyleSourcesForRequest(styleJson) ?? styleJson;
};

const readGeoJSONSourceServerFiltering = (source?: MapWorkbenchSource | null) => {
  const candidate =
    (source?.server_filtering && typeof source.server_filtering === 'object' ? source.server_filtering : null) ??
    (source?.metadata?.server_filtering && typeof source.metadata.server_filtering === 'object'
      ? (source.metadata.server_filtering as Record<string, unknown>)
      : null);
  if (!candidate) {
    return null;
  }
  const bindings = Array.isArray(candidate.bindings) ? candidate.bindings : [];
  return {
    enabled: candidate.enabled === true,
    bindings,
  };
};

const extractGeoJSONServerBindingKeys = (source?: MapWorkbenchSource | null) => {
  const filtering = readGeoJSONSourceServerFiltering(source);
  if (!filtering?.enabled) {
    return new Set<string>();
  }
  return new Set(
    filtering.bindings
      .map(binding =>
        binding && typeof binding === 'object' && typeof (binding as Record<string, unknown>).binding_key === 'string'
          ? ((binding as Record<string, unknown>).binding_key as string).trim()
          : '',
      )
      .filter(Boolean),
  );
};

const hasGeoJSONViewportBinding = (source?: MapWorkbenchSource | null) => {
  const filtering = readGeoJSONSourceServerFiltering(source);
  if (!filtering?.enabled) {
    return false;
  }
  return filtering.bindings.some(binding => {
    if (!binding || typeof binding !== 'object') {
      return false;
    }
    return (binding as Record<string, unknown>).binding_source === 'viewport';
  });
};

const resolveStyleSourceLayerForAlias = (
  styleJson: Record<string, unknown>,
  alias: string,
  fallbackSource?: Record<string, unknown> | null,
) => {
  const layers = Array.isArray(styleJson.layers) ? (styleJson.layers as Array<Record<string, unknown>>) : [];
  for (const layer of layers) {
    if (layer?.source !== alias) {
      continue
    }
    const metadata = layer.metadata && typeof layer.metadata === 'object' ? (layer.metadata as Record<string, unknown>) : null;
    const workbench =
      metadata?.workbench && typeof metadata.workbench === 'object'
        ? (metadata.workbench as Record<string, unknown>)
        : null;
    if (typeof workbench?.source_layer === 'string' && workbench.source_layer.trim()) {
      return workbench.source_layer.trim();
    }
    if (typeof layer['source-layer'] === 'string' && (layer['source-layer'] as string).trim()) {
      return (layer['source-layer'] as string).trim();
    }
  }
  if (fallbackSource?.type === 'geojson' || typeof fallbackSource?.data !== 'undefined') {
    return 'features';
  }
  return '';
};

type MapWorkbenchGeoJSONQueryPlan = {
  alias: string;
  sourceId: string;
  payload: MapExperienceGeoJSONQueryRequest;
  consumedFilters: ReturnType<typeof collectMapExperienceServerManagedFilters>;
};

const buildGeoJSONQueryPlans = (
  styleJson: Record<string, unknown>,
  sources: MapWorkbenchSource[],
  experience: MapExperienceState,
) => {
  const canonicalStyleJson = canonicalizeStyleToInternalSourceRefs(styleJson);
  const styleSources =
    canonicalStyleJson.sources && typeof canonicalStyleJson.sources === 'object'
      ? (canonicalStyleJson.sources as Record<string, Record<string, unknown>>)
      : {};
  const sourceLookup = new Map(
    sources
      .filter((source): source is MapWorkbenchSource & { id: string } => typeof source.id === 'string' && source.id.trim().length > 0)
      .map(source => [source.id, source]),
  );
  const plans: MapWorkbenchGeoJSONQueryPlan[] = [];
  for (const [alias, styleSource] of Object.entries(styleSources)) {
    const rawData = typeof styleSource?.data === 'string' ? styleSource.data.trim() : '';
    if (!rawData || !(rawData.startsWith(GEOJSON_PREFIX) || MAP_SOURCE_GEOJSON_PATTERN.test(extractMapRequestPathname(rawData)))) {
      continue;
    }
    const sourceId = resolveMapSourceIdFromStyleSource(styleSource);
    if (!sourceId) {
      continue;
    }
    const source = sourceLookup.get(sourceId) ?? null;
    if (!source || source.source_kind !== 'geojson_http') {
      continue;
    }
    const bindingKeys = extractGeoJSONServerBindingKeys(source);
    const sourceLayer = resolveStyleSourceLayerForAlias(canonicalStyleJson, alias, styleSource) || 'features';
    const consumedFilters = collectMapExperienceServerManagedFilters(experience, {
      sourceId,
      sourceAlias: alias,
      sourceLayer,
      availableBindingKeys: bindingKeys,
    });
    if (consumedFilters.length === 0 && !hasGeoJSONViewportBinding(source)) {
      continue;
    }
    const payload = buildMapExperienceGeoJSONQueryRequest(experience, {
      sourceId,
      sourceAlias: alias,
      sourceLayer,
      context: {
        evaluated_at: new Date().toISOString(),
      },
    });
    if (!payload) {
      continue;
    }
    plans.push({
      alias,
      sourceId,
      payload,
      consumedFilters,
    });
  }
  return plans;
};

const hydrateStyleGeoJSONSources = async (
  styleJson: Record<string, unknown>,
  queryPlans: MapWorkbenchGeoJSONQueryPlan[],
  adapter: Pick<MapWorkbenchDataAdapter, 'queryGeoJSONSource'>,
) => {
  if (!queryPlans.length) {
    return styleJson;
  }
  const nextStyle = typeof structuredClone === 'function' ? structuredClone(styleJson) : JSON.parse(JSON.stringify(styleJson));
  const styleSources =
    nextStyle.sources && typeof nextStyle.sources === 'object'
      ? (nextStyle.sources as Record<string, Record<string, unknown>>)
      : null;
  if (!styleSources) {
    return nextStyle;
  }
  await Promise.all(
    queryPlans.map(async plan => {
      const payload = await adapter.queryGeoJSONSource(plan.sourceId, plan.payload);
      styleSources[plan.alias] = {
        ...(styleSources[plan.alias] ?? {}),
        type: 'geojson',
        data: payload,
      };
      delete styleSources[plan.alias].url;
      delete styleSources[plan.alias].tiles;
    }),
  );
  return nextStyle;
};

const resolveRuntimeSourceArchiveUrl = (styleSource?: Record<string, unknown> | null) => {
  if (!styleSource || typeof styleSource !== 'object') {
    return null;
  }

  const rawUrl = typeof styleSource.url === 'string' ? styleSource.url.trim() : '';
  if (rawUrl.startsWith('pmtiles://')) {
    const candidate = rawUrl.slice('pmtiles://'.length).split('?', 1)[0].trim();
    if (candidate.startsWith('http://') || candidate.startsWith('https://') || candidate.startsWith('/')) {
      return candidate;
    }
  }

  if (MAP_SOURCE_ARCHIVE_PATTERN.test(extractMapRequestPathname(rawUrl))) {
    return rawUrl;
  }

  const tiles = Array.isArray(styleSource.tiles) ? styleSource.tiles : [];
  const tileTemplate = tiles.find((entry): entry is string => typeof entry === 'string' && entry.trim().length > 0);
  const sourceId = tileTemplate ? extractMapRequestPathname(tileTemplate).match(MAP_SOURCE_TILE_TEMPLATE_PATTERN)?.[1] ?? null : null;
  if (sourceId) {
    return `/api/v1/map-sources/${decodeURIComponent(sourceId)}/archive`;
  }

  return null;
};

const normalizeStyleUrls = async (style: Record<string, unknown>) => {
  const clone = typeof structuredClone === 'function' ? structuredClone(style) : JSON.parse(JSON.stringify(style));
  const remap = (value?: string) => (typeof value === 'string' ? resolveMapRequestUrl(value) : value);

  if (typeof (clone as Record<string, unknown>).sprite === 'string') {
    (clone as Record<string, unknown>).sprite = remap((clone as Record<string, unknown>).sprite as string);
  } else if (Array.isArray((clone as Record<string, unknown>).sprite)) {
    (clone as Record<string, unknown>).sprite = ((clone as Record<string, unknown>).sprite as Array<Record<string, unknown>>)
      .map(entry => {
        if (!entry || typeof entry !== 'object') {
          return entry;
        }
        return {
          ...entry,
          url: remap(typeof entry.url === 'string' ? entry.url : undefined),
        };
      });
  }
  if (typeof (clone as Record<string, unknown>).glyphs === 'string') {
    (clone as Record<string, unknown>).glyphs = remap((clone as Record<string, unknown>).glyphs as string);
  }
  if (clone.sources && typeof clone.sources === 'object') {
    Object.values(clone.sources as Record<string, any>).forEach(source => {
      if (!source || typeof source !== 'object') return;
      if (Array.isArray(source.tiles)) {
        source.tiles = source.tiles.map((tile: string) => remap(tile));
      }
      if (typeof source.url === 'string') {
        source.url = remap(source.url);
      }
      if (typeof source.data === 'string') {
        source.data = remap(source.data);
      }
    });

    for (const source of Object.values(clone.sources as Record<string, Record<string, unknown>>)) {
      if (!source || typeof source !== 'object') {
        continue;
      }
      const runtimeArchiveUrl = resolveRuntimeSourceArchiveUrl(source);
      if (!runtimeArchiveUrl) {
        continue;
      }

      source.url = await ensurePMTilesArchiveProtocol(runtimeArchiveUrl);
      delete source.tiles;
    }
  }

  return clone;
};

const sanitizeLayerId = (value: string, fallback: string) => {
  const cleaned = value.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '');
  return cleaned || fallback;
};

const getVectorLayerGeometryFlags = (geometry: MapSourceGeometryKind | string) => {
  const kinds = getMapSourceGeometryKinds(geometry);
  const isLine = kinds.includes('line');
  const isPoint = kinds.includes('point');
  const isPolygon = kinds.includes('polygon');
  return {
    isLine,
    isPoint,
    isPolygon,
    hasKnownGeometry: kinds.length > 0,
  };
};

const extractVectorLayers = (source?: MapWorkbenchSource | null) => {
  return readMapSourceVectorLayers(source?.vector_layers)
    .filter((layer: MapSourceVectorLayerRecord) => isMapSourceGeometryAuthoringReady(layer.effective_geometry))
    .map((layer: MapSourceVectorLayerRecord) => ({
      id: layer.id,
      geometry: layer.effective_geometry,
      minzoom: layer.minzoom,
      maxzoom: layer.maxzoom,
    }));
};

const buildStyleSourceForMapSource = (source: MapWorkbenchSource) =>
  source.source_kind === 'geojson_http'
    ? {
        type: 'geojson',
        data: `${GEOJSON_PREFIX}${source.id}`,
      }
    : {
        type: 'vector',
        url: `${PMTILES_PREFIX}${source.id}`,
      };

const normalizeFieldValueType = (value: unknown) => {
  if (typeof value !== 'string') return 'string' as const;
  const normalized = value.trim().toLowerCase();
  if (normalized.includes('bool')) return 'boolean' as const;
  if (normalized.includes('int') || normalized.includes('float') || normalized.includes('double') || normalized.includes('number')) {
    return 'number' as const;
  }
  if (normalized.includes('date') || normalized.includes('time')) return 'date' as const;
  return 'string' as const;
};

type WorkbenchFieldOption = {
  id: string;
  label: string;
  value_type: 'string' | 'number' | 'boolean' | 'date';
  operators: string[];
};

type StylePreviewKind = 'line' | 'point' | 'polygon' | 'mixed';

type StylePreviewModel = {
  kind: StylePreviewKind;
  colors: string[];
  description: string;
};

const debugMapWorkbenchGlyphs = (label: string, payload: Record<string, unknown>) => {
  if (!import.meta.env.DEV) {
    return;
  }
  console.debug(`[MapWorkbench glyph debug] ${label}`, payload);
};

const debugMapWorkbenchFilters = (label: string, payload: Record<string, unknown>) => {
  console.debug(`[MapWorkbench filter debug] ${label}`, payload);
};

const debugMapWorkbenchStyleLifecycle = (label: string, payload: Record<string, unknown>) => {
  console.debug(`[MapWorkbench style lifecycle] ${label}`, payload);
};

const debugMapWorkbenchRuntimeOverlay = (label: string, payload: Record<string, unknown>) => {
  if (typeof console === 'undefined' || typeof console.debug !== 'function') {
    return;
  }
  console.debug(`[MapWorkbench runtime overlay] ${label}`, payload);
};

const debugMapWorkbenchCameraLifecycle = (label: string, payload: Record<string, unknown>) => {
  if (typeof console === 'undefined' || typeof console.debug !== 'function') {
    return;
  }
  console.debug(`[MapWorkbench camera lifecycle] ${label}`, payload);
};

const summarizeWorkbenchRuntimeOverlay = (value: Record<string, unknown> | null | undefined) => {
  const features = Array.isArray(value?.features) ? (value.features as Array<Record<string, unknown>>) : [];
  return {
    featureCount: features.length,
    geometryTypes: features.map(feature => {
      const geometry = feature.geometry;
      return geometry && typeof geometry === 'object' && typeof (geometry as { type?: unknown }).type === 'string'
        ? (geometry as { type: string }).type
        : 'unknown';
    }),
    roles: features.map(feature =>
      feature.properties && typeof feature.properties === 'object' && typeof (feature.properties as { role?: unknown }).role === 'string'
        ? (feature.properties as { role: string }).role
        : 'unknown',
    ),
  };
};

const summarizeFeatureGeometryTypes = (features: Array<{ geometry?: { type?: unknown } | null }>) =>
  Array.from(
    new Set(
      features.map(feature =>
        feature?.geometry && typeof feature.geometry.type === 'string' ? feature.geometry.type : 'unknown',
      ),
    ),
  );

const collectRuntimeOverlayDiagnostics = (
  map: MapLibreMap,
  sourceId: string,
  fillLayerId: string,
  lineLayerId: string,
  pointLayerId: string,
) => {
  let sourceFeatureCount = 0;
  let sourceGeometryTypes: string[] = [];
  let sourceQueryError: string | null = null;
  try {
    const sourceFeatures = map.querySourceFeatures(sourceId);
    sourceFeatureCount = sourceFeatures.length;
    sourceGeometryTypes = summarizeFeatureGeometryTypes(sourceFeatures);
  } catch (error) {
    sourceQueryError = error instanceof Error ? error.message : String(error);
  }

  let renderedFeatureCount = 0;
  let renderedGeometryTypes: string[] = [];
  let renderedQueryError: string | null = null;
  const queryLayerIds = [fillLayerId, lineLayerId, pointLayerId].filter(layerId => Boolean(map.getLayer(layerId)));
  try {
    const renderedFeatures = queryLayerIds.length > 0 ? map.queryRenderedFeatures(undefined, { layers: queryLayerIds }) : [];
    renderedFeatureCount = renderedFeatures.length;
    renderedGeometryTypes = summarizeFeatureGeometryTypes(renderedFeatures);
  } catch (error) {
    renderedQueryError = error instanceof Error ? error.message : String(error);
  }

  const center = map.getCenter();
  return {
    sourceExists: Boolean(map.getSource(sourceId)),
    fillLayerExists: Boolean(map.getLayer(fillLayerId)),
    lineLayerExists: Boolean(map.getLayer(lineLayerId)),
    pointLayerExists: Boolean(map.getLayer(pointLayerId)),
    queryLayerIds,
    sourceFeatureCount,
    sourceGeometryTypes,
    sourceQueryError,
    renderedFeatureCount,
    renderedGeometryTypes,
    renderedQueryError,
    styleLoaded: map.isStyleLoaded(),
    center: [center.lng, center.lat],
    zoom: map.getZoom(),
    bearing: map.getBearing(),
    pitch: map.getPitch(),
  };
};

const summarizeWorkbenchRuntimeStyle = (style: Record<string, unknown> | null) => {
  const layers = Array.isArray(style?.layers) ? (style?.layers as Array<Record<string, unknown>>) : [];
  const sources = style?.sources && typeof style.sources === 'object' ? Object.keys(style.sources as Record<string, unknown>) : [];
  const runtimeHighlightLayerIds = layers
    .filter(layer => isWorkbenchRuntimeHighlightLayer(layer))
    .map(layer => (typeof layer.id === 'string' ? layer.id : ''))
    .filter(Boolean);
  return {
    layerCount: layers.length,
    sourceCount: sources.length,
    sourceIdsSample: sources.slice(0, 8),
    runtimeHighlightLayerIds,
    layerIdsSample: layers
      .map(layer => (typeof layer.id === 'string' ? layer.id : ''))
      .filter(Boolean)
      .slice(0, 12),
    center: Array.isArray(style?.center) ? style?.center : null,
    zoom: typeof style?.zoom === 'number' ? style.zoom : null,
    bearing: typeof style?.bearing === 'number' ? style.bearing : null,
    pitch: typeof style?.pitch === 'number' ? style.pitch : null,
  };
};

const getDebugFeatureIdentifier = (feature: { id?: unknown; properties?: Record<string, unknown> | null }) => {
  const properties = feature.properties ?? {};
  return (
    (typeof properties.feature_identity_key === 'string' && properties.feature_identity_key) ||
    (typeof properties.feature_id === 'string' && properties.feature_id) ||
    (typeof properties.location_id === 'string' && properties.location_id) ||
    (typeof feature.id === 'string' && feature.id) ||
    (typeof feature.id === 'number' ? String(feature.id) : null)
  );
};

const buildActiveFilterDebugSummary = (
  filter: MapExperienceFilterDefinition,
  rawValue: unknown,
  featureProperties: Record<string, unknown>,
) => {
  const resolvedValue = resolveMapExperienceFilterValue(filter, rawValue);
  if (!hasConfiguredMapExperienceFilterValue(filter.kind, resolvedValue)) {
    return null;
  }

  const hasField = Object.prototype.hasOwnProperty.call(featureProperties, filter.field);
  const fieldValue = hasField ? featureProperties[filter.field] : undefined;

  if (filter.kind === 'numeric_range') {
    const range = resolvedValue && typeof resolvedValue === 'object' ? (resolvedValue as Record<string, unknown>) : {};
    const min = range.min == null ? null : Number(range.min);
    const max = range.max == null ? null : Number(range.max);
    const numericValue =
      typeof fieldValue === 'number'
        ? fieldValue
        : typeof fieldValue === 'string' && fieldValue.trim()
          ? Number(fieldValue)
          : NaN;
    const isNumeric = Number.isFinite(numericValue);
    const passesMin = !Number.isFinite(min) || (isNumeric && numericValue >= min);
    const passesMax = !Number.isFinite(max) || (isNumeric && numericValue <= max);
    const passes = hasField && isNumeric && passesMin && passesMax;
    let failureReason = 'passed';
    if (!hasField) {
      failureReason = 'field_missing';
    } else if (!isNumeric) {
      failureReason = 'field_not_numeric';
    } else if (!passesMin) {
      failureReason = 'below_min';
    } else if (!passesMax) {
      failureReason = 'above_max';
    }
    return {
      filterId: filter.id,
      kind: filter.kind,
      field: filter.field,
      activeValue: resolvedValue,
      hasField,
      rawValue: fieldValue ?? null,
      numericValue: isNumeric ? numericValue : null,
      passes,
      failureReason,
    };
  }

  return {
    filterId: filter.id,
    kind: filter.kind,
    field: filter.field,
    activeValue: resolvedValue,
    hasField,
    rawValue: fieldValue ?? null,
    passes: true,
    failureReason: 'not_evaluated_for_debug',
  };
};

const summarizeManagedStyleLayers = (styleJson?: Record<string, unknown> | null) => {
  const rawLayers = Array.isArray(styleJson?.layers) ? styleJson.layers : [];
  return rawLayers
    .filter((layer): layer is Record<string, unknown> => Boolean(layer) && typeof layer === 'object')
    .map(layer => {
      const metadata = layer.metadata && typeof layer.metadata === 'object' ? (layer.metadata as Record<string, unknown>) : {};
      const workbench =
        metadata.workbench && typeof metadata.workbench === 'object'
          ? (metadata.workbench as Record<string, unknown>)
          : {};
      return {
        id: typeof layer.id === 'string' ? layer.id : null,
        type: typeof layer.type === 'string' ? layer.type : null,
        source: typeof layer.source === 'string' ? layer.source : null,
        sourceLayer: typeof layer['source-layer'] === 'string' ? layer['source-layer'] : null,
        filter: layer.filter ?? null,
        managed: workbench.managed ?? null,
        kind: workbench.kind ?? null,
        sourceId: workbench.source_id ?? null,
        sourceAlias: workbench.source_alias ?? null,
        workbenchSourceLayer: workbench.source_layer ?? null,
        renderStackEntryId: workbench.render_stack_entry_id ?? null,
        renderStackRole: workbench.render_stack_role ?? null,
      };
    });
};

const summarizePointStyleLayers = (styleJson?: Record<string, unknown> | null) => {
  const rawLayers = Array.isArray(styleJson?.layers) ? styleJson.layers : [];
  return rawLayers
    .filter((layer): layer is Record<string, unknown> => Boolean(layer) && typeof layer === 'object')
    .filter(layer => {
      const type = typeof layer.type === 'string' ? layer.type : '';
      return type === 'symbol' || type === 'circle';
    })
    .map(layer => {
      const layout = layer.layout && typeof layer.layout === 'object' ? (layer.layout as Record<string, unknown>) : {};
      const paint = layer.paint && typeof layer.paint === 'object' ? (layer.paint as Record<string, unknown>) : {};
      return {
        id: typeof layer.id === 'string' ? layer.id : null,
        type: typeof layer.type === 'string' ? layer.type : null,
        source: typeof layer.source === 'string' ? layer.source : null,
        sourceLayer: typeof layer['source-layer'] === 'string' ? layer['source-layer'] : null,
        textField: layout['text-field'] ?? null,
        iconImage: layout['icon-image'] ?? null,
        textFont: layout['text-font'] ?? null,
        textSize: layout['text-size'] ?? null,
        iconSize: layout['icon-size'] ?? null,
        circleRadius: paint['circle-radius'] ?? null,
      };
    });
};

const buildFilterFields = (fields?: Record<string, unknown> | null) => {
  if (!fields) return [];
  return Object.entries(fields)
    .map(([key, value]) => ({
      id: key,
      label: key,
      value_type: normalizeFieldValueType(value),
      operators: [],
    }))
    .filter(field => Boolean(field.id));
};

const INTERACTIVITY_OPERATORS_BY_FIELD_TYPE: Record<WorkbenchFieldOption['value_type'], string[]> = {
  string: ['eq', 'neq', 'in', 'not_in', 'contains', 'exists', 'not_exists'],
  number: ['eq', 'neq', 'in', 'not_in', 'lt', 'lte', 'gt', 'gte', 'between', 'exists', 'not_exists'],
  boolean: ['eq', 'neq', 'exists', 'not_exists'],
  date: ['eq', 'neq', 'lt', 'lte', 'gt', 'gte', 'between', 'exists', 'not_exists'],
};

const mapGeometryToInteractivityKind = (geometry: MapSourceGeometryKind): MapExperienceInteractivityGeometryKind => {
  switch (geometry) {
    case 'point':
      return 'point';
    case 'line':
      return 'line';
    case 'polygon':
      return 'polygon';
    case 'mixed':
      return 'mixed';
    default:
      return 'unknown';
  }
};

const inferFeatureIdField = (fields: WorkbenchFieldOption[]) => {
  const exactId = fields.find(field => field.id === 'id');
  if (exactId) return exactId.id;
  const explicitFeatureId = fields.find(field => field.id === 'feature_id');
  if (explicitFeatureId) return explicitFeatureId.id;
  const suffixedId = fields.find(field => /(^|_)id$/i.test(field.id));
  if (suffixedId) return suffixedId.id;
  return fields[0]?.id ?? 'id';
};

const buildDefaultInteractivityFieldDefinitions = (
  fields: WorkbenchFieldOption[],
): LayerExperienceSourceFieldDefinition[] =>
  fields.map(field => ({
    field_key: field.id,
    label: field.label,
    value_type: field.value_type,
    source_kind: 'feature_property',
    supported_operators: INTERACTIVITY_OPERATORS_BY_FIELD_TYPE[field.value_type] ?? [],
    description: null,
    enum_options: [],
    semantic_roles: /(^|_)status$/i.test(field.id) ? ['status'] : [],
    display_format: null,
    filter_safe: true,
    interaction_safe: true,
    sortable: field.value_type !== 'boolean',
    null_behavior: null,
  }));

const buildDefaultInteractivitySourceDefinition = (
  target: MapWorkbenchDesktopAuthoringPaneTarget,
): LayerExperienceSourceDefinition => ({
  source_id: target.sourceId,
  source_layer: target.sourceLayer,
  label: target.label,
  description: target.rendered ? 'Visible on map' : 'Hidden on map',
  geometry_kind: mapGeometryToInteractivityKind(target.geometry),
  identity: {
    feature_id_field: inferFeatureIdField(target.fields),
  },
  fields: buildDefaultInteractivityFieldDefinitions(target.fields),
  capabilities: {
    selectable: true,
    hoverable: true,
    popupable: true,
    inspectable: true,
    highlightable: true,
    filterable: true,
    spatial_query_mode: target.geometry === 'unknown' ? 'none' : 'rendered_only',
    supports_cluster_selection: null,
    supports_related_lookup: null,
    supports_compare_mode: null,
    supports_summary_card: true,
  },
  semantic_roles: Object.fromEntries(
    target.fields
      .filter(field => /(^|_)status$/i.test(field.id))
      .map(field => ['status', field.id] as const)
      .slice(0, 1),
  ),
  relation_hints: [],
  resolver_ids: [],
  surface_ids: [],
});

const stableJson = (value: unknown) => JSON.stringify(value ?? null);

const upsertByKey = <T,>(items: T[], nextItem: T, getKey: (item: T) => string) => {
  const nextKey = getKey(nextItem);
  const existingIndex = items.findIndex(item => getKey(item) === nextKey);
  if (existingIndex < 0) {
    return [...items, nextItem];
  }
  return items.map((item, index) => (index === existingIndex ? nextItem : item));
};

const FILTER_KIND_LABELS: Record<MapExperienceFilterDefinition['kind'], string> = {
  boolean: 'Boolean',
  single_select: 'Single select',
  multi_select: 'Multi select',
  numeric_range: 'Numeric range',
  date_range: 'Date range',
  text_contains: 'Text contains',
};

const FIELD_TYPE_LABELS: Record<WorkbenchFieldOption['value_type'], string> = {
  string: 'Text',
  number: 'Number',
  boolean: 'Boolean',
  date: 'Date',
};

const STYLE_KIND_LABELS: Record<MapExperienceStyleProfile['kind'], string> = {
  single: 'Solid color',
  categorical: 'By category',
  range: 'By range',
  rules: 'Rule Based (Advanced)',
};

type MapWorkbenchTab = 'styles' | 'filters' | 'sources' | 'legend' | 'presets' | 'diagnostics' | 'rules';

const AUTHOR_WORKBENCH_TAB_LABELS: Record<MapWorkbenchTab, string> = {
  styles: 'Layers',
  filters: 'Layers',
  rules: 'Layers',
  sources: 'Sources',
  legend: 'Legend',
  presets: 'Saved Views',
  diagnostics: 'Generated JSON',
} as const;

const VIEWER_WORKBENCH_TAB_LABELS: Record<MapWorkbenchTab, string> = {
  styles: 'Layers',
  filters: 'Layers',
  rules: 'Layers',
  sources: 'Sources',
  legend: 'Legend',
  presets: 'Saved Views',
  diagnostics: 'Generated JSON',
} as const;

const AUTHOR_VISIBLE_TABS: MapWorkbenchTab[] = ['styles', 'filters', 'rules', 'sources', 'diagnostics'];
const VIEWER_VISIBLE_TABS: MapWorkbenchTab[] = ['filters', 'legend'];
const DESKTOP_AUTHORING_PANE_SECTIONS: MapWorkbenchDesktopAuthoringPaneSection[] = [
  'sources',
  'style',
  'filters',
  'interactions',
  'data',
  'source-info',
  'advanced',
];

const WORKSPACE_MODE_LABELS: Record<MapWorkbenchMode, string> = {
  author: 'Author',
  view: 'View',
  'customize-save-copy': 'Customize',
};

const summarizeRuleLabels = (values: string[]) => {
  const normalized = values.map(value => value.trim()).filter(Boolean);
  if (normalized.length === 0) {
    return 'No rules defined yet';
  }
  if (normalized.length <= 3) {
    return normalized.join(', ');
  }
  return `${normalized.slice(0, 3).join(', ')} +${normalized.length - 3} more`;
};

const getFilterOptionLabel = (definition: MapExperienceFilterDefinition, optionValue: string) =>
  definition.options?.find(option => option.value === optionValue)?.label?.trim() || optionValue;

const summarizeFilterValue = (definition: MapExperienceFilterDefinition, value: unknown): string => {
  const resolvedValue = resolveMapExperienceFilterValue(definition, value);
  if (typeof resolvedValue === 'boolean') {
    return resolvedValue ? 'True' : 'False';
  }
  if (typeof resolvedValue === 'string') {
    if (!resolvedValue.trim()) {
      return definition.kind === 'text_contains' ? 'No text' : 'No value';
    }
    return definition.kind === 'single_select'
      ? getFilterOptionLabel(definition, resolvedValue)
      : resolvedValue;
  }
  if (Array.isArray(resolvedValue)) {
    const labels = resolvedValue
      .map(entry => String(entry).trim())
      .filter(Boolean)
      .map(entry => getFilterOptionLabel(definition, entry));
    return labels.length > 0 ? summarizeRuleLabels(labels) : 'No values';
  }
  if (resolvedValue && typeof resolvedValue === 'object') {
    const record = resolvedValue as Record<string, unknown>;
    const min = record.min == null ? '' : String(record.min);
    const max = record.max == null ? '' : String(record.max);
    if (definition.kind === 'numeric_range' && definition.viewer_presentation === 'max_slider') {
      return max ? `Up to ${max}` : 'No limit';
    }
    return [min, max].some(Boolean) ? `${min || 'Any'} to ${max || 'Any'}` : definition.kind === 'date_range' ? 'No date range' : 'No range';
  }
  return 'No value';
};

const hasConfiguredFilterValue = (definition: MapExperienceFilterDefinition, value: unknown): boolean =>
  hasConfiguredMapExperienceFilterValue(definition.kind, resolveMapExperienceFilterValue(definition, value));

const buildWorkbenchViewerFilterItemId = (filterSetId: string, filterId: string) => `${filterSetId}::${filterId}`;

const isWorkbenchViewerEditableFilter = (filter: MapExperienceFilterDefinition) =>
  filter.editable_in_view !== false && filter.kind !== 'date_range';

const resolveWorkbenchViewerFilterRuntimeEntry = (
  filter: MapExperienceFilterDefinition,
  rawValue: unknown,
): WorkbenchViewerFilterRuntimeEntry => {
  const value = resolveMapExperienceFilterValue(filter, rawValue);
  return {
    enabled: hasConfiguredMapExperienceFilterValue(filter.kind, value),
    value,
  };
};

const buildInitialWorkbenchViewerFilterRuntimeState = (
  experience: MapExperienceState,
): WorkbenchViewerFilterRuntimeState => {
  const next: WorkbenchViewerFilterRuntimeState = {};

  experience.filter_sets.forEach(filterSet => {
    filterSet.filters
      .filter(isWorkbenchViewerEditableFilter)
      .forEach(filter => {
        const itemId = buildWorkbenchViewerFilterItemId(filterSet.id, filter.id);
        next[itemId] = resolveWorkbenchViewerFilterRuntimeEntry(
          filter,
          experience.active_filter_values[filterSet.id]?.[filter.id],
        );
      });
  });

  return next;
};

const buildCompiledWorkbenchViewerActiveFilterValues = (
  experience: MapExperienceState,
  runtimeState: WorkbenchViewerFilterRuntimeState,
): Record<string, Record<string, unknown>> => {
  const next = Object.fromEntries(
    Object.entries(experience.active_filter_values).map(([filterSetId, values]) => [filterSetId, { ...values }]),
  ) as Record<string, Record<string, unknown>>;

  experience.filter_sets.forEach(filterSet => {
    filterSet.filters
      .filter(isWorkbenchViewerEditableFilter)
      .forEach(filter => {
        const itemId = buildWorkbenchViewerFilterItemId(filterSet.id, filter.id);
        const entry =
          runtimeState[itemId] ??
          resolveWorkbenchViewerFilterRuntimeEntry(
            filter,
            experience.active_filter_values[filterSet.id]?.[filter.id],
          );
        const normalizedValue = resolveMapExperienceFilterValue(filter, entry.value);
        const nextSet = { ...(next[filterSet.id] ?? {}) };

        delete nextSet[filter.id];

        if (entry.enabled && hasConfiguredMapExperienceFilterValue(filter.kind, normalizedValue)) {
          nextSet[filter.id] = normalizedValue;
        } else {
          nextSet[filter.id] = null;
        }

        if (Object.keys(nextSet).length > 0) {
          next[filterSet.id] = nextSet;
        } else {
          delete next[filterSet.id];
        }
      });
  });

  return next;
};

const toWorkbenchViewerFilterControl = (filter: MapExperienceFilterDefinition, value: unknown) => {
  switch (filter.kind) {
    case 'boolean':
      return {
        kind: 'boolean' as const,
        value: typeof value === 'boolean' ? value : false,
        enabledLabel: 'Apply filter',
      };
    case 'single_select':
      return {
        kind: 'single-select' as const,
        value: typeof value === 'string' ? value : '',
        options: (filter.options ?? []).map(option => ({
          id: option.value,
          label: option.label ?? option.value,
        })),
        placeholder: filter.placeholder ?? null,
      };
    case 'multi_select':
      return {
        kind: 'multi-select' as const,
        value: Array.isArray(value) ? value.map(String) : [],
        options: (filter.options ?? []).map(option => ({
          id: option.value,
          label: option.label ?? option.value,
        })),
        placeholder: filter.placeholder ?? null,
      };
    case 'numeric_range':
      return {
        kind: 'numeric-range' as const,
        presentation: filter.viewer_presentation === 'max_slider' ? 'max-slider' : 'range',
        value:
          value && typeof value === 'object'
            ? {
                min: typeof (value as { min?: unknown }).min === 'number' ? (value as { min: number }).min : null,
                max: typeof (value as { max?: unknown }).max === 'number' ? (value as { max: number }).max : null,
              }
            : { min: null, max: null },
        minBound: filter.min ?? null,
        maxBound: filter.max ?? null,
        step: filter.step ?? null,
        showSlider: filter.min != null || filter.max != null,
        minPlaceholder: filter.min != null ? String(filter.min) : 'Min',
        maxPlaceholder: filter.max != null ? String(filter.max) : 'Max',
      };
    case 'text_contains':
      return {
        kind: 'text' as const,
        value: typeof value === 'string' ? value : '',
        placeholder: filter.placeholder ?? null,
      };
    default:
      return {
        kind: 'text' as const,
        value: '',
        placeholder: filter.placeholder ?? null,
      };
  }
};

const describeStyleSummary = (
  activeProfile: MapExperienceStyleProfile | null,
  activeMode: MapExperienceStyleProfile['kind'] | null,
  enabledAdvancedRuleCount: number,
) => {
  if (activeProfile?.kind === 'rules') {
    const legendLabel = activeProfile.legend_label?.trim();
    if (legendLabel) {
      return legendLabel;
    }
    return enabledAdvancedRuleCount > 0
      ? `${enabledAdvancedRuleCount} rule${enabledAdvancedRuleCount === 1 ? '' : 's'} active`
      : 'One rule for all visible features';
  }
  if (activeProfile?.kind === 'single') {
    return activeProfile.legend_label?.trim() || 'One color for all visible features';
  }
  if (activeProfile?.kind === 'categorical') {
    const fieldLabel = activeProfile.field ? formatFieldLabel(activeProfile.field) : 'Choose a field';
    const labels = activeProfile.categories.map(category => category.label || category.value);
    return `${fieldLabel} · ${summarizeRuleLabels(labels)}`;
  }
  if (activeProfile?.kind === 'range') {
    const fieldLabel = activeProfile.field ? formatFieldLabel(activeProfile.field) : 'Choose a field';
    const labels = activeProfile.breaks.map(entry => entry.label);
    return `${fieldLabel} · ${summarizeRuleLabels(labels)}`;
  }
  return 'No style selected';
};

const buildDefaultStylePreview = (geometry: MapSourceGeometryKind | string, index: number): StylePreviewModel => {
  const hue = (index * 53) % 360;
  const lineColor = `hsl(${hue} 70% 42%)`;
  const lineCasingColor = `hsl(${hue} 24% 20% / 0.22)`;
  const fillColor = `hsl(${hue} 70% 55%)`;
  const pointStroke = `hsl(${hue} 70% 30%)`;
  const flags = getVectorLayerGeometryFlags(geometry);

  if (flags.isPolygon) {
    return {
      kind: 'polygon',
      colors: [fillColor, lineColor],
      description: 'Generated polygon fill and outline',
    };
  }
  if (flags.isPoint) {
    return {
      kind: 'point',
      colors: [fillColor, pointStroke],
      description: 'Generated point circle',
    };
  }
  if (!flags.hasKnownGeometry) {
    return {
      kind: 'mixed',
      colors: [fillColor, lineColor, pointStroke],
      description: 'Generated mixed geometry defaults',
    };
  }
  return {
    kind: 'line',
    colors: [lineColor, lineCasingColor],
    description: 'Generated line and casing',
  };
};

const previewKindToRepresentation = (kind: StylePreviewKind): NonNullable<NonNullable<LayerExperience['appearance']['variants'][number]['style']>['representation']> => {
  if (kind === 'polygon') return 'fill';
  if (kind === 'point') return 'circle';
  if (kind === 'mixed') return 'fill';
  return 'line';
};

const representationToPreviewKind = (
  representation: MapExperienceStyleRepresentation | null | undefined,
  fallback: StylePreviewKind,
): StylePreviewKind => {
  switch (representation) {
    case 'fill':
    case 'fill-extrusion':
      return 'polygon';
    case 'circle':
    case 'symbol':
      return 'point';
    case 'line':
      return 'line';
    default:
      return fallback;
  }
};

const getRepresentationOptionsForGeometry = (
  geometry: MapSourceGeometryKind,
): Array<{ value: MapExperienceStyleRepresentation; label: string }> => {
  switch (geometry) {
    case 'point':
      return [
        { value: 'circle', label: 'Point Circle' },
        { value: 'symbol', label: 'Symbol / Label' },
      ];
    case 'line':
      return [{ value: 'line', label: 'Line / Path' }];
    case 'polygon':
      return [
        { value: 'fill', label: 'Polygon Fill' },
        { value: 'line', label: 'Outline' },
        { value: 'fill-extrusion', label: '3D Extrusion' },
      ];
    default:
      return [
        { value: 'fill', label: 'Polygon Fill' },
        { value: 'line', label: 'Line / Path' },
        { value: 'circle', label: 'Point Circle' },
        { value: 'symbol', label: 'Symbol / Label' },
        { value: 'fill-extrusion', label: '3D Extrusion' },
      ];
  }
};

const coerceRepresentationForGeometry = (
  geometry: MapSourceGeometryKind,
  representation: MapExperienceStyleRepresentation,
) => {
  const options = getRepresentationOptionsForGeometry(geometry);
  if (options.some(option => option.value === representation)) {
    return representation;
  }
  return options[0]?.value ?? representation;
};

const findSpriteIconByToken = (
  iconSet: MapIconPickerSetGroup | null | undefined,
  token: string | null | undefined,
) => {
  if (!iconSet || !token?.trim()) {
    return null;
  }
  return iconSet.icons.find(icon => icon.token === token.trim()) ?? null;
};

const isSymbolIconTintable = (
  iconSet: MapIconPickerSetGroup | null | undefined,
  token: string | null | undefined,
) => Boolean(findSpriteIconByToken(iconSet, token)?.sdf);

const buildPaintFromColor = (representation: NonNullable<NonNullable<LayerExperience['appearance']['variants'][number]['style']>['representation']>, color: string) => {
  switch (representation) {
    case 'fill':
      return { 'fill-color': color, 'fill-opacity': 1 };
    case 'circle':
      return { 'circle-color': color, 'circle-radius': 5 };
    case 'symbol':
      return { 'text-color': color };
    case 'fill-extrusion':
      return { 'fill-extrusion-color': color, 'fill-extrusion-opacity': 1 };
    case 'line':
    default:
      return { 'line-color': color, 'line-width': 2, 'line-opacity': 1 };
  }
};

const getPrimaryPaintPropForRepresentation = (
  representation: NonNullable<NonNullable<LayerExperience['appearance']['variants'][number]['style']>['representation']>,
) => {
  switch (representation) {
    case 'fill':
      return 'fill-color';
    case 'circle':
      return 'circle-color';
    case 'symbol':
      return 'text-color';
    case 'fill-extrusion':
      return 'fill-extrusion-color';
    case 'line':
    default:
      return 'line-color';
  }
};

const applyPrimaryColorToPaint = (
  representation: NonNullable<NonNullable<LayerExperience['appearance']['variants'][number]['style']>['representation']>,
  paint: Record<string, unknown>,
  color: string,
) => {
  switch (representation) {
    case 'fill':
      paint['fill-color'] = color;
      break;
    case 'circle':
      paint['circle-color'] = color;
      break;
    case 'symbol':
      paint['text-color'] = color;
      break;
    case 'fill-extrusion':
      paint['fill-extrusion-color'] = color;
      break;
    case 'line':
    default:
      paint['line-color'] = color;
      break;
  }
};

const buildProfilePaintFromStoredStyle = (
  representation: NonNullable<NonNullable<LayerExperience['appearance']['variants'][number]['style']>['representation']>,
  color: MapExperienceStyleColorValue,
  overrides?: Record<string, unknown> | null,
  options?: {
    iconColorEnabled?: boolean;
    themeColors?: SemanticThemeColorPalette | null;
  },
) => {
  const resolvedColor = resolveMapExperienceColorValue(color, options?.themeColors);
  const paint = {
    ...buildPaintFromColor(representation, resolvedColor),
    ...(normalizeMapLibrePaintOverrides(overrides) ?? {}),
  };
  applyPrimaryColorToPaint(representation, paint, resolvedColor);
  return paint;
};

const buildEditorProfilePaintFromStoredStyle = (
  representation: NonNullable<NonNullable<LayerExperience['appearance']['variants'][number]['style']>['representation']>,
  color: MapExperienceStyleColorValue,
  overrides?: Record<string, unknown> | null,
  options?: {
    iconColorEnabled?: boolean;
    themeColors?: SemanticThemeColorPalette | null;
  },
) => {
  const paint = buildProfilePaintFromStoredStyle(representation, color, overrides, options);
  const primaryPaintProp = getPrimaryPaintPropForRepresentation(representation);
  paint[primaryPaintProp] = color;
  return paint;
};

const ADVANCED_RULE_PAINT_PROPS_BY_REPRESENTATION: Record<MapExperienceStyleRepresentation, string[]> = {
  fill: ['fill-color', 'fill-opacity', 'fill-outline-color', 'fill-pattern'],
  line: ['line-blur', 'line-color', 'line-gap-width', 'line-offset', 'line-opacity', 'line-pattern', 'line-width'],
  circle: [
    'circle-blur',
    'circle-color',
    'circle-opacity',
    'circle-radius',
    'circle-stroke-color',
    'circle-stroke-opacity',
    'circle-stroke-width',
  ],
  symbol: [
    'icon-color',
    'icon-halo-blur',
    'icon-halo-color',
    'icon-halo-width',
    'icon-opacity',
    'text-color',
    'text-halo-blur',
    'text-halo-color',
    'text-halo-width',
    'text-opacity',
  ],
  'fill-extrusion': [
    'fill-extrusion-base',
    'fill-extrusion-color',
    'fill-extrusion-height',
    'fill-extrusion-pattern',
  ],
};

const ADVANCED_RULE_LAYOUT_PROPS_BY_REPRESENTATION: Record<MapExperienceStyleRepresentation, string[]> = {
  fill: ['fill-sort-key'],
  line: ['line-join', 'line-sort-key'],
  circle: ['circle-sort-key'],
  symbol: [
    'icon-anchor',
    'icon-allow-overlap',
    'icon-image',
    'icon-ignore-placement',
    'icon-offset',
    'icon-rotate',
    'icon-size',
    'text-allow-overlap',
    'text-anchor',
    'text-field',
    'text-font',
    'text-ignore-placement',
    'text-justify',
    'text-letter-spacing',
    'text-max-width',
    'text-offset',
    'text-radial-offset',
    'text-rotate',
    'text-size',
    'text-transform',
  ],
  'fill-extrusion': [],
};

const allowedPaintPropsByRepresentation: Record<MapExperienceStyleRepresentation, Set<string>> = {
  fill: new Set(ADVANCED_RULE_PAINT_PROPS_BY_REPRESENTATION.fill),
  line: new Set(ADVANCED_RULE_PAINT_PROPS_BY_REPRESENTATION.line),
  circle: new Set(ADVANCED_RULE_PAINT_PROPS_BY_REPRESENTATION.circle),
  symbol: new Set(ADVANCED_RULE_PAINT_PROPS_BY_REPRESENTATION.symbol),
  'fill-extrusion': new Set(ADVANCED_RULE_PAINT_PROPS_BY_REPRESENTATION['fill-extrusion']),
};

const allowedLayoutPropsByRepresentation: Record<MapExperienceStyleRepresentation, Set<string>> = {
  fill: new Set(ADVANCED_RULE_LAYOUT_PROPS_BY_REPRESENTATION.fill),
  line: new Set(ADVANCED_RULE_LAYOUT_PROPS_BY_REPRESENTATION.line),
  circle: new Set(ADVANCED_RULE_LAYOUT_PROPS_BY_REPRESENTATION.circle),
  symbol: new Set(ADVANCED_RULE_LAYOUT_PROPS_BY_REPRESENTATION.symbol),
  'fill-extrusion': new Set(),
};

const RULES_PROFILE_SYMBOL_LAYOUT_PROPS = new Set([
  'icon-allow-overlap',
  'icon-ignore-placement',
  'text-allow-overlap',
  'text-ignore-placement',
]);

const sanitizePaintForRepresentation = (
  representation: NonNullable<NonNullable<LayerExperience['appearance']['variants'][number]['style']>['representation']>,
  paint: Record<string, unknown> | null | undefined,
  options?: {
    iconColorEnabled?: boolean;
  },
) => {
  const nextPaint = normalizeMapLibrePaintOverrides(paint ?? null);
  if (!nextPaint) {
    return nextPaint;
  }
  const allowed = allowedPaintPropsByRepresentation[representation];
  Object.keys(nextPaint).forEach(prop => {
    if (!allowed.has(prop)) {
      delete nextPaint[prop];
    }
  });
  if (representation === 'symbol' && !options?.iconColorEnabled) {
    delete nextPaint['icon-color'];
  }
  return nextPaint;
};

const sanitizeLayoutForRepresentation = (
  representation: NonNullable<NonNullable<LayerExperience['appearance']['variants'][number]['style']>['representation']>,
  layout: Record<string, unknown> | null | undefined,
) => {
  const nextLayout = normalizeMapLibreLayoutOverrides(layout ?? null);
  if (!nextLayout) {
    return nextLayout;
  }
  const allowed = allowedLayoutPropsByRepresentation[representation];
  Object.keys(nextLayout).forEach(prop => {
    if (!allowed.has(prop)) {
      delete nextLayout[prop];
    }
  });
  return nextLayout;
};

const sanitizeRulesProfileLayoutForRepresentation = (
  representation: NonNullable<NonNullable<LayerExperience['appearance']['variants'][number]['style']>['representation']>,
  layout: Record<string, unknown> | null | undefined,
) => {
  const nextLayout = sanitizeLayoutForRepresentation(representation, layout);
  if (!nextLayout) {
    return nextLayout;
  }
  if (representation !== 'symbol') {
    return null;
  }
  Object.keys(nextLayout).forEach(prop => {
    if (!RULES_PROFILE_SYMBOL_LAYOUT_PROPS.has(prop)) {
      delete nextLayout[prop];
    }
  });
  return Object.keys(nextLayout).length > 0 ? nextLayout : null;
};

const buildProfileLayoutFromStoredStyle = (
  iconImage?: string | null,
  overrides?: Record<string, unknown> | null,
) => {
  const layout = {
    ...(cloneStyleOverrideRecord(overrides) ?? {}),
  };
  if (iconImage) {
    layout['icon-image'] = iconImage;
  } else {
    delete layout['icon-image'];
  }
  return layout;
};

export const readPaintPrimaryColorValue = (
  representation: string | null | undefined,
  paint: Record<string, unknown> | null | undefined,
  fallback: MapExperienceStyleColorValue,
): MapExperienceStyleColorValue => {
  if (!paint) {
    return fallback;
  }
  const primaryProp =
    representation === 'fill'
      ? 'fill-color'
      : representation === 'circle'
        ? 'circle-color'
        : representation === 'symbol'
          ? 'text-color'
          : representation === 'fill-extrusion'
            ? 'fill-extrusion-color'
            : 'line-color';
  return normalizeOptionalMapExperienceColorValue(paint[primaryProp]) ?? fallback;
};

const readPaintPrimaryColor = (representation: string | null | undefined, paint: Record<string, unknown> | null | undefined, fallback: string) => {
  if (!paint) {
    return fallback;
  }
  if (representation === 'fill' && typeof paint['fill-color'] === 'string') {
    return paint['fill-color'];
  }
  if (representation === 'circle' && typeof paint['circle-color'] === 'string') {
    return paint['circle-color'];
  }
  if (representation === 'symbol' && typeof paint['text-color'] === 'string') {
    return paint['text-color'];
  }
  if (representation === 'fill-extrusion' && typeof paint['fill-extrusion-color'] === 'string') {
    return paint['fill-extrusion-color'];
  }
  if (typeof paint['line-color'] === 'string') {
    return paint['line-color'];
  }
  return fallback;
};

const preserveStoredStyleColorValue = (
  storedColor: MapExperienceStyleColorValue | null | undefined,
  nextColor: MapExperienceStyleColorValue,
  themeColors?: SemanticThemeColorPalette | null,
): MapExperienceStyleColorValue => {
  if (storedColor) {
    const fallbackColor = resolveMapExperienceColorValue(nextColor, themeColors, DEFAULT_ADVANCED_RULE_COLOR);
    const resolvedStoredColor = resolveMapExperienceColorValue(storedColor, themeColors, fallbackColor);
    const resolvedNextColor = resolveMapExperienceColorValue(nextColor, themeColors, fallbackColor);
    if (resolvedStoredColor === resolvedNextColor) {
      return storedColor;
    }
  }
  return nextColor;
};

const extractMountedSourceRefsFromStyle = (
  styleJson: Record<string, unknown>,
  sources: MapWorkbenchSource[],
) => {
  const styleSources =
    styleJson.sources && typeof styleJson.sources === 'object'
      ? (styleJson.sources as Record<string, Record<string, unknown>>)
      : {};

  return Object.entries(styleSources).map(([alias, sourceValue]) => {
    const sourceId = resolveMapSourceIdFromStyleSource(sourceValue);
    const sourceMeta = sources.find(entry => entry.id === sourceId) ?? null;
    return {
      alias,
      sourceId,
      name: sourceMeta?.name ?? alias,
      shareScope: sourceMeta?.share_scope ?? null,
    };
  });
};

type WorkbenchExplicitStateTargetSeed = {
  key: string;
  sourceId: string;
  sourceAlias: string;
  sourceLayer: string;
  geometry: MapSourceGeometryKind;
  layerIndex: number;
  fields: WorkbenchFieldOption[];
};

const buildRenderedTargetSeedsFromStyle = (
  styleJson: Record<string, unknown>,
  sources: MapWorkbenchSource[],
): WorkbenchExplicitStateTargetSeed[] => {
  const styleLayers = Array.isArray(styleJson.layers) ? (styleJson.layers as Array<Record<string, unknown>>) : [];
  const styleSources =
    styleJson.sources && typeof styleJson.sources === 'object'
      ? (styleJson.sources as Record<string, Record<string, unknown>>)
      : {};

  return extractMountedSourceRefsFromStyle(styleJson, sources).flatMap(sourceRef => {
    const sourceMeta = sources.find(entry => entry.id === sourceRef.sourceId) ?? null;
    const vectorLayers = extractVectorLayers(sourceMeta);

    return vectorLayers.flatMap((layerMeta, index) => {
      const rendered = styleLayers.some(layer =>
        matchesWorkbenchLayerTarget(layer, sourceRef.alias, layerMeta.id, styleSources),
      );
      if (!rendered) {
        return [];
      }
      const vectorLayerEntry = Array.isArray(sourceMeta?.vector_layers)
        ? (sourceMeta.vector_layers.find(entry => {
            if (!entry || typeof entry !== 'object') {
              return false;
            }
            const record = entry as Record<string, unknown>;
            return record.id === layerMeta.id || record.layer === layerMeta.id || record.name === layerMeta.id;
          }) as Record<string, unknown> | undefined)
        : undefined;
      return [{
        key: buildMapExperienceTargetKey(sourceRef.sourceId, layerMeta.id),
        sourceId: sourceRef.sourceId,
        sourceAlias: sourceRef.alias,
        sourceLayer: layerMeta.id,
        geometry: layerMeta.geometry,
        layerIndex: index,
        fields: buildFilterFields((vectorLayerEntry?.fields as Record<string, unknown> | undefined) ?? null),
      }];
    });
  });
};

const buildSeededSingleStyleProfile = (
  target: WorkbenchExplicitStateTargetSeed,
): MapExperienceSingleStyleProfile => {
  const preview = buildDefaultStylePreview(target.geometry, target.layerIndex);
  return {
    id: `single::${target.key}`,
    source_id: target.sourceId,
    source_layer: target.sourceLayer,
    kind: 'single',
    representation: previewKindToRepresentation(preview.kind),
    color: preview.colors[0] ?? 'rgb(59 130 246)',
    label: formatFieldLabel(target.sourceLayer),
    legend_label: `${formatFieldLabel(target.sourceLayer)} features`,
  };
};

const buildSeededRulesStyleProfile = (
  target: WorkbenchExplicitStateTargetSeed,
): Extract<MapExperienceStyleProfile, { kind: 'rules' }> => ({
  id: `rules::${target.key}`,
  source_id: target.sourceId,
  source_layer: target.sourceLayer,
  kind: 'rules',
  label: `${formatFieldLabel(target.sourceLayer)} rules`,
  representation: previewKindToRepresentation(buildDefaultStylePreview(target.geometry, target.layerIndex).kind),
  layout: null,
  mode: 'hide_non_matching',
  rules: [
    {
      id: `rule::${target.key}::default`,
      enabled: true,
      label: 'Default',
      color: normalizeAdvancedRuleColor(buildDefaultStylePreview(target.geometry, target.layerIndex).colors[0] ?? 'rgb(59 130 246)'),
      logic: createGroup({
        children: [createCondition('', 'match_everything')],
      }),
      representation: previewKindToRepresentation(buildDefaultStylePreview(target.geometry, target.layerIndex).kind),
    },
  ],
});

type MapWorkbenchRulesStyleProfile = Extract<MapExperienceStyleProfile, { kind: 'rules' }>;

const cloneRuleInteractionConfig = (
  value?: { hit_target_px?: number | null } | null,
): { hit_target_px?: number | null } | null => (
  value && typeof value === 'object'
    ? {
        hit_target_px:
          typeof value.hit_target_px === 'number' && Number.isFinite(value.hit_target_px)
            ? value.hit_target_px
            : null,
      }
    : null
);

const resolveRulesCompatibleColor = (value?: MapExperienceStyleColorValue | null): string => {
  if (typeof value === 'string' && value.trim()) {
    return normalizeAdvancedRuleColor(value);
  }
  return DEFAULT_ADVANCED_RULE_COLOR;
};

const buildRuleLayoutOverrides = (
  layout?: Record<string, unknown> | null,
  iconImage?: string | null,
): Record<string, unknown> | null => {
  const nextLayout = cloneStyleOverrideRecord(layout) ?? {};
  if (typeof iconImage === 'string' && iconImage.trim()) {
    nextLayout['icon-image'] = iconImage.trim();
  }
  return Object.keys(nextLayout).length > 0 ? nextLayout : null;
};

const createConditionWithValues = (fieldId: string, operatorId: string, values: string[]): ReturnType<typeof createCondition> => ({
  ...createCondition(fieldId, operatorId),
  values,
});

const buildCatchAllRule = (args: {
  id: string;
  label: string;
  color: string;
  representation: MapExperienceStyleRepresentation;
  enabled?: boolean;
  paint?: Record<string, unknown> | null;
  layout?: Record<string, unknown> | null;
}): LayerRuleDefinition => ({
  id: args.id,
  enabled: args.enabled !== false,
  label: args.label,
  color: normalizeAdvancedRuleColor(args.color),
  logic: createGroup({
    children: [createCondition('', 'match_everything')],
  }),
  representation: args.representation,
  paint: cloneStyleOverrideRecord(args.paint ?? null),
  layout: buildRuleLayoutOverrides(args.layout ?? null),
});

const buildRulesProfileFromStyleProfile = (
  target: WorkbenchExplicitStateTargetSeed,
  profile: MapExperienceStyleProfile,
  options?: {
    profileId?: string;
    profileLabel?: string | null;
    legendLabel?: string | null;
  },
): MapWorkbenchRulesStyleProfile => {
  const fallbackPreview = buildDefaultStylePreview(target.geometry, target.layerIndex);
  const representation = coerceRepresentationForGeometry(
    target.geometry,
    profile.representation ?? previewKindToRepresentation(fallbackPreview.kind),
  );
  const profileLabel = options?.profileLabel?.trim() || profile.label?.trim() || formatFieldLabel(target.sourceLayer);
  const legendLabel =
    options?.legendLabel?.trim()
    || ('legend_label' in profile && typeof profile.legend_label === 'string' && profile.legend_label.trim()
      ? profile.legend_label.trim()
      : `${profileLabel} features`);
  const baseProfile: MapWorkbenchRulesStyleProfile = {
    id: options?.profileId?.trim() || `rules::${target.key}`,
    source_id: target.sourceId,
    source_layer: target.sourceLayer,
    kind: 'rules',
    label: profileLabel,
    legend_label: legendLabel,
    representation,
    interaction: cloneRuleInteractionConfig(profile.interaction),
    layout:
      'layout' in profile
        ? sanitizeRulesProfileLayoutForRepresentation(representation, profile.layout ?? null)
        : null,
    mode: 'hide_non_matching',
    rules: [],
  };

  if (profile.kind === 'rules') {
    return {
      ...baseProfile,
      mode: profile.mode ?? 'hide_non_matching',
      rules: (profile.rules ?? []).map(rule => ({
        ...rule,
        color: normalizeAdvancedRuleColor(rule.color),
        logic: ensureLogicRoot(rule.logic),
        paint: cloneStyleOverrideRecord(rule.paint ?? null),
        layout: buildRuleLayoutOverrides(rule.layout ?? null),
      })),
    };
  }

  if (profile.kind === 'single') {
    return {
      ...baseProfile,
      rules: [
        buildCatchAllRule({
          id: `rule::${baseProfile.id}::default`,
          label: profile.legend_label?.trim() || profile.label?.trim() || 'Default',
          color: resolveRulesCompatibleColor(profile.color),
          representation,
          paint: profile.paint ?? null,
          layout: buildRuleLayoutOverrides(profile.layout ?? null, profile.icon_image ?? null),
        }),
      ],
    };
  }

  if (profile.kind === 'categorical') {
    const rules = profile.field
      ? profile.categories.map(category => ({
          id: `rule::${baseProfile.id}::${category.value || category.label || 'category'}`,
          enabled: category.hidden !== true,
          label: category.label || category.value || 'Category',
          color: resolveRulesCompatibleColor(category.color),
          logic: createGroup({
            children: [createConditionWithValues(profile.field, 'eq', [category.value])],
          }),
          representation,
          paint: cloneStyleOverrideRecord(category.paint ?? null),
          layout: buildRuleLayoutOverrides(category.layout ?? null, category.icon_image ?? null),
        }))
      : [];
    if (profile.fallback_color) {
      rules.push(buildCatchAllRule({
        id: `rule::${baseProfile.id}::fallback`,
        label: 'Other',
        color: resolveRulesCompatibleColor(profile.fallback_color),
        representation,
      }));
    }
    if (rules.length === 0) {
      rules.push(buildCatchAllRule({
        id: `rule::${baseProfile.id}::default`,
        label: profile.label?.trim() || 'Default',
        color: resolveRulesCompatibleColor(profile.categories[0]?.color ?? profile.fallback_color),
        representation,
        paint: profile.categories[0]?.paint ?? null,
        layout: buildRuleLayoutOverrides(profile.categories[0]?.layout ?? null, profile.categories[0]?.icon_image ?? null),
      }));
    }
    return {
      ...baseProfile,
      rules,
    };
  }

  const rangeRules = profile.field
    ? profile.breaks.map((entry, index) => {
        const conditions = [];
        if (typeof entry.min === 'number' && Number.isFinite(entry.min)) {
          conditions.push(createConditionWithValues(profile.field, 'gte', [String(entry.min)]));
        }
        if (typeof entry.max === 'number' && Number.isFinite(entry.max)) {
          conditions.push(createConditionWithValues(profile.field, 'lt', [String(entry.max)]));
        }
        return {
          id: entry.id || `rule::${baseProfile.id}::range-${index + 1}`,
          enabled: entry.hidden !== true,
          label: entry.label || `Range ${index + 1}`,
          color: resolveRulesCompatibleColor(entry.color),
          logic: createGroup({
            children: conditions.length > 0 ? conditions : [createCondition('', 'match_everything')],
          }),
          representation,
          paint: cloneStyleOverrideRecord(entry.paint ?? null),
          layout: buildRuleLayoutOverrides(entry.layout ?? null, entry.icon_image ?? null),
        } satisfies LayerRuleDefinition;
      })
    : [];
  if (profile.fallback_color) {
    rangeRules.push(buildCatchAllRule({
      id: `rule::${baseProfile.id}::fallback`,
      label: 'Other',
      color: resolveRulesCompatibleColor(profile.fallback_color),
      representation,
    }));
  }
  if (rangeRules.length === 0) {
    rangeRules.push(buildCatchAllRule({
      id: `rule::${baseProfile.id}::default`,
      label: profile.breaks[0]?.label || profile.label?.trim() || 'Default',
      color: resolveRulesCompatibleColor(profile.breaks[0]?.color ?? profile.fallback_color),
      representation,
      paint: profile.breaks[0]?.paint ?? null,
      layout: buildRuleLayoutOverrides(profile.breaks[0]?.layout ?? null, profile.breaks[0]?.icon_image ?? null),
    }));
  }
  return {
    ...baseProfile,
    rules: rangeRules,
  };
};

const buildRulesProfileFromEntryTemplate = (args: {
  target: WorkbenchExplicitStateTargetSeed;
  profileId: string;
  label: string;
  legendLabel?: string | null;
  representation: MapExperienceStyleRepresentation;
  color: string;
  paint?: Record<string, unknown> | null;
  layout?: Record<string, unknown> | null;
  interaction?: { hit_target_px?: number | null } | null;
  rules?: LayerRuleDefinition[] | null;
}): MapWorkbenchRulesStyleProfile => ({
  id: args.profileId,
  source_id: args.target.sourceId,
  source_layer: args.target.sourceLayer,
  kind: 'rules',
  label: args.label,
  legend_label: args.legendLabel?.trim() || `${args.label} features`,
  representation: coerceRepresentationForGeometry(args.target.geometry, args.representation),
  interaction: cloneRuleInteractionConfig(args.interaction),
  mode: 'hide_non_matching',
  rules:
    args.rules?.map(rule => ({
      ...rule,
      color: normalizeAdvancedRuleColor(rule.color),
      logic: ensureLogicRoot(rule.logic),
      paint: cloneStyleOverrideRecord(rule.paint ?? null),
      layout: buildRuleLayoutOverrides(rule.layout ?? null),
    }))
    ?? [
      buildCatchAllRule({
        id: `rule::${args.profileId}::default`,
        label: 'Default',
        color: args.color,
        representation: args.representation,
        paint: args.paint ?? null,
        layout: args.layout ?? null,
      }),
    ],
});

const ensureExplicitWorkbenchDraftState = (
  snapshot: WorkbenchDraftSnapshot,
  styleJson: Record<string, unknown>,
  sources: MapWorkbenchSource[],
): WorkbenchDraftSnapshot => {
  const renderedTargets = buildRenderedTargetSeedsFromStyle(styleJson, sources);
  if (renderedTargets.length === 0) {
    return snapshot;
  }

  const nextProfiles = [...snapshot.mapExperience.style_profiles];
  const nextActiveProfiles = { ...snapshot.mapExperience.active_style_profiles };
  const nextRenderStacks = [...(snapshot.mapExperience.render_stacks ?? [])];
  let changed = false;

  renderedTargets.forEach(target => {
    const targetProfiles = nextProfiles.filter(
      profile => profile.source_id === target.sourceId && profile.source_layer === target.sourceLayer,
    );
    const profilesById = new Map(targetProfiles.map(profile => [profile.id, profile]));
    const activeProfileId = nextActiveProfiles[target.key] ?? '';
    const existingStackIndex = nextRenderStacks.findIndex(
      stack => stack.source_id === target.sourceId && stack.source_layer === target.sourceLayer,
    );
    const existingStack = existingStackIndex >= 0 ? nextRenderStacks[existingStackIndex] : null;
    const nextRulesProfiles: MapWorkbenchRulesStyleProfile[] = [];
    const profileIdMap = new Map<string, string>();
    const registerRulesProfile = (
      profile: MapExperienceStyleProfile,
      options?: {
        profileId?: string;
        profileLabel?: string | null;
        legendLabel?: string | null;
      },
    ) => {
      const nextProfile = buildRulesProfileFromStyleProfile(target, profile, options);
      nextRulesProfiles.push(nextProfile);
      profileIdMap.set(profile.id, nextProfile.id);
      return nextProfile;
    };

    const baseSourceProfile =
      (existingStack?.entries.find(entry => entry.stack_role === 'base')
        ? profilesById.get(existingStack.entries.find(entry => entry.stack_role === 'base')?.profile_id ?? '')
        : null)
      ?? profilesById.get(activeProfileId)
      ?? targetProfiles[0]
      ?? buildSeededRulesStyleProfile(target);
    const baseRulesProfile = registerRulesProfile(baseSourceProfile, {
      profileId: `rules::${target.key}`,
      profileLabel: baseSourceProfile.label ?? formatFieldLabel(target.sourceLayer),
      legendLabel:
        'legend_label' in baseSourceProfile && typeof baseSourceProfile.legend_label === 'string'
          ? baseSourceProfile.legend_label
          : `${baseSourceProfile.label ?? formatFieldLabel(target.sourceLayer)} features`,
    });

    const existingOverlayEntries = existingStack?.entries.filter(entry => entry.stack_role !== 'base') ?? [];
    const normalizedEntries = [
      {
        entry_id: existingStack?.entries.find(entry => entry.stack_role === 'base')?.entry_id ?? `base-entry::${target.key}`,
        profile_id: baseRulesProfile.id,
        enabled: existingStack?.entries.find(entry => entry.stack_role === 'base')?.enabled !== false,
        label:
          existingStack?.entries.find(entry => entry.stack_role === 'base')?.label
          ?? baseRulesProfile.label
          ?? formatFieldLabel(target.sourceLayer),
        stack_role: 'base' as const,
        filter_logic: existingStack?.entries.find(entry => entry.stack_role === 'base')?.filter_logic ?? null,
      },
      ...existingOverlayEntries.map((entry, index) => {
        const sourceProfile = profilesById.get(entry.profile_id) ?? baseSourceProfile;
        const profileId = sourceProfile.kind === 'rules'
          ? sourceProfile.id
          : `rules::${target.key}::${entry.entry_id}`;
        const rulesProfile = registerRulesProfile(sourceProfile, {
          profileId,
          profileLabel: entry.label ?? sourceProfile.label ?? `Overlay ${index + 1}`,
          legendLabel:
            'legend_label' in sourceProfile && typeof sourceProfile.legend_label === 'string'
              ? sourceProfile.legend_label
              : `${entry.label ?? sourceProfile.label ?? `Overlay ${index + 1}`} features`,
        });
        return {
          ...entry,
          profile_id: rulesProfile.id,
          label: entry.label ?? rulesProfile.label ?? `Overlay ${index + 1}`,
        };
      }),
    ];

    const nextTargetProfilesSerialized = JSON.stringify(nextRulesProfiles);
    const currentTargetProfilesSerialized = JSON.stringify(targetProfiles);
    const nextStack = {
      source_id: target.sourceId,
      source_layer: target.sourceLayer,
      entries: normalizedEntries,
    };
    if (nextTargetProfilesSerialized !== currentTargetProfilesSerialized) {
      changed = true;
      nextProfiles.splice(
        0,
        nextProfiles.length,
        ...nextProfiles.filter(
          profile => profile.source_id !== target.sourceId || profile.source_layer !== target.sourceLayer,
        ),
        ...nextRulesProfiles,
      );
    }
    if (!existingStack || JSON.stringify(existingStack) !== JSON.stringify(nextStack)) {
      changed = true;
      if (existingStackIndex >= 0) {
        nextRenderStacks[existingStackIndex] = nextStack;
      } else {
        nextRenderStacks.push(nextStack);
      }
    }

    const mappedActiveProfileId = profileIdMap.get(activeProfileId) ?? baseRulesProfile.id;
    if (nextActiveProfiles[target.key] !== mappedActiveProfileId) {
      nextActiveProfiles[target.key] = mappedActiveProfileId;
      changed = true;
    }
  });

  if (!changed) {
    return snapshot;
  }

  return {
    ...snapshot,
    mapExperience: {
      ...snapshot.mapExperience,
      style_profiles: nextProfiles,
      active_style_profiles: nextActiveProfiles,
      render_stacks: nextRenderStacks,
    },
  };
};

export const normalizeWorkbenchDraftSnapshotForMode = (
  snapshot: WorkbenchDraftSnapshot,
  styleJson: Record<string, unknown>,
  sources: MapWorkbenchSource[],
  options?: {
    materializeRenderedTargets?: boolean;
  },
): WorkbenchDraftSnapshot => {
  if (options?.materializeRenderedTargets === false) {
    return snapshot;
  }
  return ensureExplicitWorkbenchDraftState(snapshot, styleJson, sources);
};

const decodeJsonPointer = (path: string) => {
  if (path === '') return [];
  if (!path.startsWith('/')) {
    throw new Error('Patch path must use JSON Pointer syntax.');
  }
  return path
    .slice(1)
    .split('/')
    .map(segment => segment.replace(/~1/g, '/').replace(/~0/g, '~'));
};

const cloneJsonValue = <T,>(value: T): T =>
  typeof structuredClone === 'function' ? structuredClone(value) : (JSON.parse(JSON.stringify(value)) as T);

const StylePreviewSwatch = ({ preview }: { preview: StylePreviewModel }) => {
  const primaryColor = preview.colors[0] ?? 'rgb(148 163 184)';
  const secondaryColor = preview.colors[1] ?? primaryColor;

  if (preview.kind === 'point') {
    return (
      <span className="inline-flex h-7 w-7 items-center justify-center rounded-full border border-border/60 bg-background">
        <span
          className="h-3.5 w-3.5 rounded-full border"
          style={{ background: primaryColor, borderColor: secondaryColor }}
          aria-hidden
        />
      </span>
    );
  }

  if (preview.kind === 'polygon') {
    return (
      <span className="inline-flex h-7 w-7 items-center justify-center rounded-md border border-border/60 bg-background">
        <span
          className="h-4 w-4 rounded-sm border"
          style={{ background: primaryColor, borderColor: secondaryColor }}
          aria-hidden
        />
      </span>
    );
  }

  if (preview.kind === 'mixed') {
    return (
      <span className="inline-flex h-7 w-7 items-center justify-center rounded-md border border-border/60 bg-background">
        <span className="relative block h-4 w-5" aria-hidden>
          <span
            className="absolute inset-x-0 bottom-0 h-3 rounded-sm border"
            style={{ background: primaryColor, borderColor: secondaryColor }}
          />
          <span
            className="absolute inset-x-0 top-1/2 h-[3px] -translate-y-1/2 rounded-full"
            style={{ background: secondaryColor }}
          />
          <span
            className="absolute right-0 top-0 h-2.5 w-2.5 rounded-full border bg-background"
            style={{ borderColor: secondaryColor, background: preview.colors[2] ?? primaryColor }}
          />
        </span>
      </span>
    );
  }

  return (
    <span className="inline-flex h-7 w-7 items-center justify-center rounded-md border border-border/60 bg-background">
      <span className="relative block h-3 w-5" aria-hidden>
        <span
          className="absolute inset-x-0 top-1/2 h-[6px] -translate-y-1/2 rounded-full"
          style={{ background: secondaryColor }}
        />
        <span
          className="absolute inset-x-0 top-1/2 h-[3px] -translate-y-1/2 rounded-full"
          style={{ background: primaryColor }}
        />
      </span>
    </span>
  );
};

const resolvePatchParent = (doc: any, pathSegments: string[]) => {
  let node = doc;
  for (const segment of pathSegments) {
    if (Array.isArray(node)) {
      if (segment === '-') {
        throw new Error('Patch path cannot use "-" in the middle of a pointer.');
      }
      const index = Number(segment);
      if (!Number.isInteger(index) || index < 0 || index >= node.length) {
        throw new Error('Patch path points outside the document.');
      }
      node = node[index];
      continue;
    }
    if (!node || typeof node !== 'object') {
      throw new Error('Patch path points outside the document.');
    }
    if (!(segment in node)) {
      throw new Error('Patch path points outside the document.');
    }
    node = node[segment];
  }
  return node;
};

const applyJsonPatch = (doc: Record<string, unknown>, patch: MapWorkbenchJsonPatchOperation[]) => {
  let next = cloneJsonValue(doc);
  patch.forEach(op => {
    const operation = op.op.toLowerCase();
    const pathSegments = decodeJsonPointer(op.path);
    if (pathSegments.length === 0) {
      if (operation === 'remove') {
        throw new Error('Patch cannot remove the entire document.');
      }
      next = op.value as Record<string, unknown>;
      return;
    }
    const parent = resolvePatchParent(next, pathSegments.slice(0, -1));
    const key = pathSegments[pathSegments.length - 1];

    if (Array.isArray(parent)) {
      if (operation === 'add') {
        if (key === '-') {
          parent.push(op.value);
          return;
        }
        const index = Number(key);
        if (!Number.isInteger(index) || index < 0 || index > parent.length) {
          throw new Error('Patch array index out of range.');
        }
        parent.splice(index, 0, op.value);
        return;
      }

      const index = Number(key);
      if (!Number.isInteger(index) || index < 0 || index >= parent.length) {
        throw new Error('Patch array index out of range.');
      }
      if (operation === 'replace') {
        parent[index] = op.value;
        return;
      }
      if (operation === 'remove') {
        parent.splice(index, 1);
        return;
      }
    } else if (parent && typeof parent === 'object') {
      if (operation === 'add' || operation === 'replace') {
        parent[key] = op.value;
        return;
      }
      if (operation === 'remove') {
        delete parent[key];
        return;
      }
    }

    throw new Error(`Unsupported patch operation: ${op.op}`);
  });
  return next;
};

const isValidMapLibreStyle = (value: unknown) => {
  if (!value || typeof value !== 'object') return false;
  const record = value as Record<string, unknown>;
  if (typeof record.version !== 'number') return false;
  if (!record.sources || typeof record.sources !== 'object') return false;
  if (!Array.isArray(record.layers)) return false;
  return true;
};

const resolveWorkbenchManagedSourceLayer = (layer?: Record<string, unknown> | null) => {
  if (!layer || typeof layer !== 'object') {
    return '';
  }
  const metadata =
    layer.metadata && typeof layer.metadata === 'object'
      ? (layer.metadata as Record<string, unknown>)
      : null;
  const workbench =
    metadata?.workbench && typeof metadata.workbench === 'object'
      ? (metadata.workbench as Record<string, unknown>)
      : null;
  if (typeof workbench?.source_layer === 'string' && workbench.source_layer.trim()) {
    return workbench.source_layer;
  }
  return typeof layer['source-layer'] === 'string' ? (layer['source-layer'] as string) : '';
};

const resolveWorkbenchManagedSourceId = (layer?: Record<string, unknown> | null) => {
  if (!layer || typeof layer !== 'object') {
    return '';
  }
  const metadata =
    layer.metadata && typeof layer.metadata === 'object'
      ? (layer.metadata as Record<string, unknown>)
      : null;
  const workbench =
    metadata?.workbench && typeof metadata.workbench === 'object'
      ? (metadata.workbench as Record<string, unknown>)
      : null;
  return typeof workbench?.source_id === 'string' ? workbench.source_id.trim() : '';
};

export const resolveStyleLayerSourceLayer = (
  layer: Record<string, unknown> | null | undefined,
  styleSources?: Record<string, Record<string, unknown>>,
) => {
  const explicit = resolveWorkbenchManagedSourceLayer(layer);
  if (explicit) {
    return explicit;
  }
  if (!layer || typeof layer !== 'object' || !styleSources) {
    return '';
  }
  const sourceAlias = typeof layer.source === 'string' ? layer.source : '';
  if (!sourceAlias) {
    return '';
  }
  const styleSource = styleSources[sourceAlias];
  if (styleSource && styleSource.type === 'geojson') {
    return 'features';
  }
  return '';
};

const resolveHighlightBlueprintKindsForTarget = ({
  styleJson,
  target,
}: {
  styleJson: Record<string, unknown>;
  target: MapWorkbenchHighlightTargetDescriptor;
}) => {
  const geometry = target.geometry.toLowerCase();
  if (geometry === 'point') {
    return ['point'] as const;
  }
  if (geometry === 'line') {
    return ['line'] as const;
  }
  if (geometry === 'polygon') {
    return ['polygon-fill', 'polygon-line'] as const;
  }

  const styleSources =
    styleJson.sources && typeof styleJson.sources === 'object'
      ? (styleJson.sources as Record<string, Record<string, unknown>>)
      : {};
  const styleLayers = Array.isArray(styleJson.layers) ? (styleJson.layers as Array<Record<string, unknown>>) : [];
  const layerTypes = new Set(
    styleLayers
      .filter(layer => matchesWorkbenchLayerTarget(layer, target.sourceAlias, target.sourceLayer, styleSources))
      .map(layer => (typeof layer.type === 'string' ? layer.type : ''))
      .filter(Boolean),
  );
  const blueprintKinds = new Set<'point' | 'line' | 'polygon-fill' | 'polygon-line'>();
  if (layerTypes.has('circle') || layerTypes.has('symbol')) {
    blueprintKinds.add('point');
  }
  if (layerTypes.has('line')) {
    blueprintKinds.add('line');
  }
  if (layerTypes.has('fill') || layerTypes.has('fill-extrusion')) {
    blueprintKinds.add('polygon-fill');
    blueprintKinds.add('polygon-line');
  }
  return Array.from(blueprintKinds);
};

const buildWorkbenchHighlightFeatureClause = ({
  feature,
  featureIdField,
}: {
  feature: MapWorkbenchFeatureInspection;
  featureIdField: string;
}) => {
  const propertyValue = feature.properties?.[featureIdField];
  if (propertyValue != null && propertyValue !== '') {
    return ['==', ['get', featureIdField], propertyValue];
  }
  if (feature.id != null && feature.id !== '') {
    return ['==', ['id'], feature.id];
  }
  return null;
};

const buildWorkbenchHighlightFilter = ({
  target,
  highlights,
}: {
  target: MapWorkbenchHighlightTargetDescriptor;
  highlights: MapWorkbenchRuntimeHighlightDescriptor[];
}) => {
  const clauses = highlights
    .map(entry => buildWorkbenchHighlightFeatureClause({ feature: entry.feature, featureIdField: target.featureIdField }))
    .filter((entry): entry is unknown[] => Array.isArray(entry) && entry.length > 0);
  if (clauses.length === 0) {
    return WORKBENCH_HIGHLIGHT_FALSE_FILTER;
  }
  if (clauses.length === 1) {
    return clauses[0];
  }
  return ['any', ...clauses];
};

const buildWorkbenchHighlightLayerMetadata = ({
  target,
  channel,
  kind,
}: {
  target: MapWorkbenchHighlightTargetDescriptor;
  channel: 'hover' | 'selected';
  kind: 'point' | 'line' | 'polygon-fill' | 'polygon-line';
}) => ({
  workbench: {
    managed: false,
    runtime_overlay: 'highlight',
    highlight_channel: channel,
    highlight_kind: kind,
    source_id: target.sourceId,
    source_alias: target.sourceAlias,
    source_layer: target.sourceLayer,
  },
});

const buildWorkbenchHighlightLayerId = ({
  target,
  channel,
  kind,
}: {
  target: MapWorkbenchHighlightTargetDescriptor;
  channel: 'hover' | 'selected';
  kind: 'point' | 'line' | 'polygon-fill' | 'polygon-line';
}) => `__workbench-highlight__${channel}__${sanitizeLayerId(`${target.sourceAlias}-${target.sourceLayer}-${kind}`)}`;

const buildWorkbenchHighlightPaint = ({
  channel,
  kind,
}: {
  channel: 'hover' | 'selected';
  kind: 'point' | 'line' | 'polygon-fill' | 'polygon-line';
}) => {
  const color = WORKBENCH_HIGHLIGHT_COLORS[channel];
  if (kind === 'point') {
    return {
      'circle-color': color,
      'circle-opacity': channel === 'selected' ? 0.12 : 0.08,
      'circle-radius': channel === 'selected' ? 12 : 10,
      'circle-stroke-color': color,
      'circle-stroke-opacity': 1,
      'circle-stroke-width': channel === 'selected' ? 3 : 2,
      'circle-blur': 0.15,
    };
  }
  if (kind === 'line') {
    return {
      'line-color': color,
      'line-opacity': channel === 'selected' ? 0.98 : 0.9,
      'line-width': channel === 'selected' ? 8 : 6,
      'line-blur': channel === 'selected' ? 0.2 : 0.1,
    };
  }
  if (kind === 'polygon-fill') {
    return {
      'fill-color': color,
      'fill-opacity': channel === 'selected' ? 0.16 : 0.1,
    };
  }
  return {
    'line-color': color,
    'line-opacity': 1,
    'line-width': channel === 'selected' ? 4 : 3,
  };
};

export const applyWorkbenchRuntimeHighlights = ({
  styleJson,
  targets,
  highlights,
}: {
  styleJson: Record<string, unknown> | null;
  targets: MapWorkbenchHighlightTargetDescriptor[];
  highlights: MapWorkbenchRuntimeHighlightDescriptor[];
}) => {
  if (!styleJson) {
    return null;
  }
  const styleSources =
    styleJson.sources && typeof styleJson.sources === 'object'
      ? (styleJson.sources as Record<string, Record<string, unknown>>)
      : {};
  const baseLayers = Array.isArray(styleJson.layers)
    ? (styleJson.layers as Array<Record<string, unknown>>).filter(layer => !isWorkbenchRuntimeHighlightLayer(layer))
    : [];
  const runtimeHighlightLayers: Array<Record<string, unknown>> = [];

  for (const target of targets) {
    const source = styleSources[target.sourceAlias];
    if (!source || typeof source !== 'object') {
      continue;
    }
    const blueprintKinds = resolveHighlightBlueprintKindsForTarget({ styleJson, target });
    if (blueprintKinds.length === 0) {
      continue;
    }
    for (const channel of ['hover', 'selected'] as const) {
      const matchingHighlights = highlights.filter(entry =>
        entry.channel === channel &&
        entry.target.sourceAlias === target.sourceAlias &&
        entry.target.sourceLayer === target.sourceLayer,
      );
      const filter = buildWorkbenchHighlightFilter({ target, highlights: matchingHighlights });
      for (const kind of blueprintKinds) {
        const layerType = kind === 'point' ? 'circle' : kind === 'line' || kind === 'polygon-line' ? 'line' : 'fill';
        const layer: Record<string, unknown> = {
          id: buildWorkbenchHighlightLayerId({ target, channel, kind }),
          type: layerType,
          source: target.sourceAlias,
          filter,
          paint: buildWorkbenchHighlightPaint({ channel, kind }),
          metadata: buildWorkbenchHighlightLayerMetadata({ target, channel, kind }),
          layout: {},
        };
        if (source.type !== 'geojson') {
          layer['source-layer'] = target.sourceLayer;
        }
        runtimeHighlightLayers.push(layer);
      }
    }
  }

  return {
    ...styleJson,
    layers: [...baseLayers, ...runtimeHighlightLayers],
  };
};

const matchesWorkbenchLayerTarget = (
  layer: Record<string, unknown>,
  sourceAlias: string,
  sourceLayerId: string,
  styleSources?: Record<string, Record<string, unknown>>,
) => {
  const nextSourceAlias = typeof layer.source === 'string' ? layer.source : '';
  return nextSourceAlias === sourceAlias && resolveStyleLayerSourceLayer(layer, styleSources) === sourceLayerId;
};

const buildDefaultLayersForVectorLayer = (
  source: MapWorkbenchSource,
  alias: string,
  layerMeta: { id: string; geometry: string; minzoom?: number; maxzoom?: number },
  alreadyRendered: boolean,
  index = 0
) => {
  if (alreadyRendered) return [];
  const hue = (index * 53) % 360;
  const baseId = sanitizeLayerId(`${alias}-${layerMeta.id}`, `layer-${index + 1}`);
  const workbenchMeta = (kind: 'fill' | 'line' | 'circle') =>
    typeof source.id === 'string'
      ? {
          metadata: {
            workbench: {
              managed: true,
              source_id: source.id,
              source_alias: alias,
              source_layer: layerMeta.id,
              kind,
            },
          },
        }
      : {};
  const { isLine, isPoint, isPolygon, hasKnownGeometry } = getVectorLayerGeometryFlags(layerMeta.geometry);
  const showLine = isLine || !hasKnownGeometry;
  const showPoint = isPoint || !hasKnownGeometry;
  const showPolygon = isPolygon || !hasKnownGeometry;
  const commonZoom = typeof layerMeta.minzoom === 'number' ? { minzoom: layerMeta.minzoom } : {};
  const sourceLayerProps = source.source_kind === 'geojson_http' ? {} : ({ 'source-layer': layerMeta.id } as const);
  const lineColor = `hsl(${hue} 70% 42%)`;
  const lineCasingColor = `hsl(${hue} 24% 20% / 0.22)`;
  const layers: Array<Record<string, unknown>> = [];
  if (showPolygon) {
    layers.push(
      {
        id: `${baseId}-fill`,
        type: 'fill',
        source: alias,
        ...sourceLayerProps,
        ...commonZoom,
        ...(workbenchMeta('fill') as any),
        paint: {
          'fill-color': `hsl(${hue} 70% 55%)`,
          'fill-opacity': 0.18,
        },
      },
      {
        id: `${baseId}-outline`,
        type: 'line',
        source: alias,
        ...sourceLayerProps,
        ...commonZoom,
        ...(workbenchMeta('line') as any),
        layout: {
          'line-cap': 'butt',
          'line-join': 'miter',
        },
        paint: {
          'line-color': `hsl(${hue} 70% 40%)`,
          'line-width': ['interpolate', ['linear'], ['zoom'], 5, 1, 9, 1.5, 13, 2, 17, 2.75],
        },
      }
    );
  }
  if (showLine) {
    layers.push(
      {
        id: `${baseId}-line-casing`,
        type: 'line',
        source: alias,
        ...sourceLayerProps,
        ...commonZoom,
        ...(workbenchMeta('line') as any),
        layout: {
          'line-cap': 'butt',
          'line-join': 'miter',
        },
        paint: {
          'line-color': lineCasingColor,
          'line-opacity': 0.95,
          'line-width': ['interpolate', ['linear'], ['zoom'], 5, 3, 9, 4, 13, 5.5, 17, 7],
        },
      },
      {
        id: `${baseId}-line`,
        type: 'line',
        source: alias,
        ...sourceLayerProps,
        ...commonZoom,
        ...(workbenchMeta('line') as any),
        layout: {
          'line-cap': 'butt',
          'line-join': 'miter',
        },
        paint: {
          'line-color': lineColor,
          'line-opacity': 0.98,
          'line-width': ['interpolate', ['linear'], ['zoom'], 5, 1.5, 9, 2.25, 13, 3.25, 17, 4.5],
        },
      }
    );
  }
  if (showPoint) {
    layers.push({
      id: `${baseId}-circle`,
      type: 'circle',
      source: alias,
      ...sourceLayerProps,
      ...commonZoom,
      ...(workbenchMeta('circle') as any),
      paint: {
        'circle-color': `hsl(${hue} 70% 55%)`,
        'circle-radius': ['interpolate', ['linear'], ['zoom'], 5, 3.5, 10, 4.5, 14, 5.5],
        'circle-stroke-color': `hsl(${hue} 70% 30%)`,
        'circle-stroke-width': 1.25,
      },
    });
  }
  return layers;
};

const buildDefaultLayers = (
  source: MapWorkbenchSource,
  alias: string,
  existingLayers: Array<Record<string, unknown>>
) => {
  const seen = new Set<string>();
  existingLayers.forEach(layer => {
    const sourceName = typeof layer.source === 'string' ? layer.source : '';
    const sourceLayer = typeof layer['source-layer'] === 'string' ? (layer['source-layer'] as string) : '';
    if (sourceName && sourceLayer) {
      seen.add(`${sourceName}::${sourceLayer}`);
    }
  });

  return extractVectorLayers(source).flatMap((layerMeta, index) =>
    buildDefaultLayersForVectorLayer(source, alias, layerMeta, seen.has(`${alias}::${layerMeta.id}`), index)
  );
};

const setStyleRootField = (
  style: Record<string, unknown>,
  key: 'sprite' | 'glyphs',
  value?: unknown,
) => {
  const next = { ...style };
  if (typeof value === 'string') {
    if (value.trim()) {
      next[key] = value;
      return next;
    }
    delete next[key];
    return next;
  }
  if (Array.isArray(value) ? value.length > 0 : value) {
    next[key] = value;
  } else {
    delete next[key];
  }
  return next;
};

const setStyleRootStringField = (
  style: Record<string, unknown>,
  key: 'sprite' | 'glyphs',
  value?: string | null
) => setStyleRootField(style, key, value);

const normalizeSpriteDependencies = (
  refs: MapWorkbenchSpriteDependency[] | null | undefined,
): MapWorkbenchSpriteDependency[] =>
  (refs ?? [])
    .map((ref, index) => ({
      id: ref.id.trim(),
      spriteSetId: ref.spriteSetId.trim(),
      sortOrder: Number.isFinite(ref.sortOrder) ? ref.sortOrder : index,
    }))
    .filter(ref => ref.id.length > 0 && ref.spriteSetId.length > 0)
    .sort((left, right) => left.sortOrder - right.sortOrder || left.id.localeCompare(right.id))
    .filter((ref, index, array) => array.findIndex(candidate => candidate.id === ref.id) === index);

const inferSpriteDependenciesFromStyle = (
  behavior: MapWorkbenchBehavior,
  styleJson?: Record<string, unknown> | null,
): MapWorkbenchSpriteDependency[] => {
  if (behavior.inferSpriteDependencies) {
    return normalizeSpriteDependencies(behavior.inferSpriteDependencies(styleJson));
  }
  const spriteSetId = behavior.inferSpriteSetId(styleJson);
  if (!spriteSetId) {
    return [];
  }
  return [{ id: 'default', spriteSetId, sortOrder: 0 }];
};

const buildSpriteRootValue = (
  refs: MapWorkbenchSpriteDependency[],
  buildSpriteUrl: (id: string) => string,
): string | Array<{ id: string; url: string }> | null => {
  const normalized = normalizeSpriteDependencies(refs);
  if (!normalized.length) {
    return null;
  }
  if (normalized.length === 1 && normalized[0].id === 'default') {
    return buildSpriteUrl(normalized[0].spriteSetId);
  }
  return normalized.map(ref => ({
    id: ref.id,
    url: buildSpriteUrl(ref.spriteSetId),
  }));
};

const resolveActiveSpriteDependency = (
  refs: MapWorkbenchSpriteDependency[],
  activeSpriteId?: string | null,
): MapWorkbenchSpriteDependency | null => {
  const normalized = normalizeSpriteDependencies(refs);
  if (!normalized.length) {
    return null;
  }
  if (activeSpriteId) {
    const match = normalized.find(ref => ref.id === activeSpriteId);
    if (match) {
      return match;
    }
  }
  return normalized[0] ?? null;
};

const buildInlineError = (message: string) => (
  <div className="rounded-lg border border-destructive/40 bg-destructive/10 px-3 py-2 text-sm text-destructive">
    {message}
  </div>
);

const asStringArray = (value: unknown): string[] =>
  Array.isArray(value) ? value.filter((entry): entry is string => typeof entry === 'string' && entry.trim().length > 0) : [];

const formatFieldLabel = (value: string) =>
  value
    .replace(/[_-]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
    .replace(/\b\w/g, char => char.toUpperCase());

const slugifyIdentifier = (value: string) => {
  const cleaned = value.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '');
  return cleaned || 'item';
};

const WORKBENCH_HIGHLIGHT_FALSE_FILTER: unknown[] = ['all', ['in', ['id'], ['literal', []]]];
const WORKBENCH_HIGHLIGHT_COLORS = {
  hover: 'rgb(14 165 233)',
  selected: 'rgb(249 115 22)',
} as const;

const isWorkbenchRuntimeHighlightLayer = (layer?: Record<string, unknown> | null) => {
  if (!layer || typeof layer !== 'object') {
    return false;
  }
  const metadata =
    layer.metadata && typeof layer.metadata === 'object'
      ? (layer.metadata as Record<string, unknown>)
      : null;
  const workbench =
    metadata?.workbench && typeof metadata.workbench === 'object'
      ? (metadata.workbench as Record<string, unknown>)
      : null;
  return workbench?.runtime_overlay === 'highlight';
};

const createExperienceId = (prefix: string, sourceId: string, sourceLayer: string) =>
  `${prefix}-${slugifyIdentifier(sourceId)}-${slugifyIdentifier(sourceLayer)}-${Date.now().toString(36)}`;

const buildAdvancedRuleId = (sourceId: string, sourceLayer: string) =>
  createExperienceId('rule', sourceId, sourceLayer);

const stringifyFieldValue = (value: unknown) => {
  if (value == null) return '—';
  if (typeof value === 'string') return value;
  if (typeof value === 'number' || typeof value === 'boolean') return String(value);
  try {
    return JSON.stringify(value);
  } catch {
    return String(value);
  }
};

const interpolateFeatureTemplate = (template: string | null | undefined, properties?: Record<string, unknown>) => {
  if (!template?.trim()) {
    return '';
  }
  return template.replace(/\{\{\s*([^}]+?)\s*\}\}/g, (_match, field) => stringifyFieldValue(properties?.[String(field).trim()]));
};

const summarizeFeatureField = (field: string, properties?: Record<string, unknown>) => ({
  key: field,
  label: formatFieldLabel(field),
  value: stringifyFieldValue(properties?.[field]),
});

export const resolveRenderedFeatureSourceIdentity = ({
  feature,
  styleJson,
}: {
  feature: MapWorkbenchFeatureInspection | null;
  styleJson: Record<string, unknown> | null;
}) => {
  if (!feature?.source || !styleJson) {
    return null;
  }
  const styleSources =
    styleJson.sources && typeof styleJson.sources === 'object'
      ? (styleJson.sources as Record<string, Record<string, unknown>>)
      : {};
  const styleSource = styleSources[feature.source];
  const styleLayers = Array.isArray(styleJson.layers) ? (styleJson.layers as Array<Record<string, unknown>>) : [];
  const styleLayer =
    typeof feature.layerId === 'string' && feature.layerId.trim()
      ? styleLayers.find(layer => layer && typeof layer === 'object' && layer.id === feature.layerId) ?? null
      : null;
  const sourceIdFromLayerMetadata = resolveWorkbenchManagedSourceId(styleLayer);
  const sourceId = resolveMapSourceIdFromStyleSource(styleSource) || sourceIdFromLayerMetadata;
  if (!sourceId) {
    return null;
  }
  const sourceLayer = feature.sourceLayer || resolveStyleLayerSourceLayer(styleLayer, styleSources);
  if (!sourceLayer) {
    return null;
  }
  return {
    sourceId,
    sourceLayer,
  };
};

const resolveInspectedFeatureSourceLayer = (feature: MapGeoJSONFeature | undefined) => {
  if (!feature) {
    return undefined;
  }
  if (typeof feature.sourceLayer === 'string' && feature.sourceLayer.trim()) {
    return feature.sourceLayer;
  }
  if (feature.layer && typeof feature.layer === 'object') {
    const fallback = resolveWorkbenchManagedSourceLayer(feature.layer as Record<string, unknown>);
    if (fallback) {
      return fallback;
    }
  }
  return undefined;
};

const isRecordValue = (value: unknown): value is Record<string, unknown> =>
  Boolean(value) && typeof value === 'object' && !Array.isArray(value);

const spriteSetHasPreviewBundle = (spriteSet?: MapWorkbenchSpriteSet | null): boolean => {
  if (!spriteSet || !isRecordValue(spriteSet.metadata)) {
    return false;
  }
  const bundle = spriteSet.metadata.sprite_preview_bundle;
  return isRecordValue(bundle) && bundle.available === true;
};

const resolveSurfaceBindingValue = (
  binding: unknown,
  properties: Record<string, unknown>,
): string | null => {
  if (typeof binding === 'string') {
    return interpolateFeatureTemplate(binding, properties).trim() || null;
  }
  if (!isRecordValue(binding)) {
    return null;
  }
  if (typeof binding.template === 'string') {
    return interpolateFeatureTemplate(binding.template, properties).trim() || null;
  }
  const field = typeof binding.field === 'string' ? binding.field.trim() : '';
  if (field) {
    return stringifyFieldValue(properties[field]);
  }
  const value = binding.value;
  if (typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean') {
    return String(value);
  }
  return null;
};

const resolveSurfaceSummaryEntries = (
  surface: LayerExperienceSurfaceDefinition,
  properties: Record<string, unknown>,
) =>
  surface.sections.flatMap(section => {
    if (section.kind !== 'details_grid' && section.kind !== 'stats_row' && section.kind !== 'list') {
      return [];
    }
    return ((section.items as Array<Record<string, unknown>> | null | undefined) ?? [])
      .map(item => {
        const field = typeof item.field === 'string' ? item.field.trim() : '';
        if (!field) {
          return null;
        }
        return {
          key: field,
          label: typeof item.label === 'string' && item.label.trim() ? item.label.trim() : formatFieldLabel(field),
          value: stringifyFieldValue(properties[field]),
        };
      })
      .filter((entry): entry is { key: string; label: string; value: string } => Boolean(entry));
  });

const buildSurfaceSectionKey = (
  section: { id?: string | null; kind: string },
  fallbackPrefix: string,
  index: number,
) => section.id ?? `${fallbackPrefix}:${section.kind}:${index}`;

const resolveSurfaceSectionNodes = ({
  feature,
  runtimeFeature,
  surface,
  sections,
  renderRuntimeSurfaceSection,
  sectionKeyPrefix,
}: {
  feature: MapWorkbenchFeatureInspection | null;
  runtimeFeature: RuntimeFeatureRef | null;
  surface: MapWorkbenchResolvedFeatureSurface | null;
  sections: LayerExperienceSurfaceDefinition['sections'];
  renderRuntimeSurfaceSection?: MapWorkbenchProps['renderRuntimeSurfaceSection'];
  sectionKeyPrefix: string;
}): ReactNode[] => {
  if (!feature || !surface) {
    return [];
  }
  const properties = feature.properties ?? {};
  return sections
    .map((section, index) => {
      const key = buildSurfaceSectionKey(section, sectionKeyPrefix, index);
      if (section.kind === 'custom') {
        if (!renderRuntimeSurfaceSection) {
          return null;
        }
        const rendered = renderRuntimeSurfaceSection({ feature, runtimeFeature, surface, section });
        return rendered ? <Fragment key={key}>{rendered}</Fragment> : null;
      }
      if (section.kind === 'details_grid' || section.kind === 'stats_row' || section.kind === 'list') {
        const items = ((section.items as Array<Record<string, unknown>> | null | undefined) ?? [])
          .map(item => {
            const field = typeof item.field === 'string' ? item.field.trim() : '';
            const label =
              typeof item.label === 'string' && item.label.trim()
                ? item.label.trim()
                : field
                  ? formatFieldLabel(field)
                  : null;
            if (!label) {
              return null;
            }
            const value =
              field
                ? stringifyFieldValue(properties[field])
                : typeof item.value === 'string' || typeof item.value === 'number' || typeof item.value === 'boolean'
                  ? String(item.value)
                  : '—';
            return { label, value };
          })
          .filter((entry): entry is { label: string; value: string } => Boolean(entry));
        if (items.length === 0) {
          return section.empty_state ? (
            <section
              key={key}
              className="rounded-lg border border-dashed border-border/60 px-4 py-3 text-sm text-muted-foreground"
            >
              {section.empty_state}
            </section>
          ) : null;
        }
        return (
          <section key={key} className="space-y-3">
            {section.label ? (
              <div className="text-[11px] font-black uppercase tracking-[0.14em] text-muted-foreground">
                {section.label}
              </div>
            ) : null}
            <div className="overflow-hidden rounded-lg border border-border/70 bg-border/60">
              {items.map(item => (
                <div
                  key={`${key}:${item.label}`}
                  className="flex items-start justify-between gap-4 border-b border-border/70 bg-background px-4 py-3 last:border-b-0"
                >
                  <span className="text-xs font-bold uppercase tracking-tight text-muted-foreground">
                    {item.label}
                  </span>
                  <span className="max-w-[60%] text-right text-sm font-medium leading-5 text-foreground">
                    {item.value}
                  </span>
                </div>
              ))}
            </div>
          </section>
        );
      }
      if (section.kind === 'text') {
        const value = resolveSurfaceBindingValue(section.binding, properties) ?? section.empty_state ?? null;
        if (!value) {
          return null;
        }
        return (
          <section key={key} className="space-y-2">
            {section.label ? (
              <div className="text-[11px] font-black uppercase tracking-[0.14em] text-muted-foreground">
                {section.label}
              </div>
            ) : null}
            <p className="text-sm leading-6 text-foreground/85">{value}</p>
          </section>
        );
      }
      if (section.kind === 'badge_row') {
        const badges = ((section.items as Array<Record<string, unknown>> | null | undefined) ?? [])
          .map(item => {
            if (typeof item.label === 'string' && item.label.trim() && !item.field) {
              return item.label.trim();
            }
            const field = typeof item.field === 'string' ? item.field.trim() : '';
            return field ? stringifyFieldValue(properties[field]) : null;
          })
          .filter((entry): entry is string => Boolean(entry) && entry !== '—');
        if (badges.length === 0) {
          return null;
        }
        return (
          <section key={key} className="space-y-3">
            {section.label ? (
              <div className="text-[11px] font-black uppercase tracking-[0.14em] text-muted-foreground">
                {section.label}
              </div>
            ) : null}
            <div className="flex flex-wrap gap-2">
              {badges.map((badge, badgeIndex) => (
                <span
                  key={`${key}:badge:${badgeIndex}`}
                  className="rounded-sm border border-border bg-muted/30 px-2.5 py-1 text-[10px] font-black uppercase tracking-[0.1em] text-foreground"
                >
                  {badge}
                </span>
              ))}
            </div>
          </section>
        );
      }
      if (section.kind === 'empty_state' || section.kind === 'loading_state' || section.kind === 'error_state') {
        const value = section.empty_state?.trim();
        if (!value) {
          return null;
        }
        return (
          <section
            key={key}
            className="rounded-lg border border-dashed border-border/60 px-4 py-3 text-sm text-muted-foreground"
          >
            {value}
          </section>
        );
      }
      return null;
    })
    .filter((entry): entry is ReactNode => Boolean(entry));
};

const resolveSurfaceBadges = (
  surface: LayerExperienceSurfaceDefinition,
  properties: Record<string, unknown>,
) =>
  surface.sections.flatMap(section => {
    if (section.kind !== 'badge_row') {
      return [];
    }
    return ((section.items as Array<Record<string, unknown>> | null | undefined) ?? [])
      .map(item => {
        if (typeof item.label === 'string' && item.label.trim() && !item.field) {
          return item.label.trim();
        }
        const field = typeof item.field === 'string' ? item.field.trim() : '';
        return field ? stringifyFieldValue(properties[field]) : null;
      })
      .filter((entry): entry is string => Boolean(entry) && entry !== '—');
  });

const resolveSurfaceActionEntries = ({
  surface,
  pipelines,
}: {
  surface: LayerExperienceSurfaceDefinition;
  pipelines: LayerExperiencePipelineDefinition[];
}) =>
  surface.sections.flatMap(section => {
    if (section.kind !== 'action_row') {
      return [];
    }
    return ((section.items as Array<Record<string, unknown>> | null | undefined) ?? [])
      .map(item => {
        const pipelineId = typeof item.pipeline_id === 'string' ? item.pipeline_id.trim() : '';
        if (!pipelineId) {
          return null;
        }
        const pipeline = pipelines.find(entry => entry.pipeline_id === pipelineId);
        if (!pipeline) {
          return {
            pipeline: {
              pipeline_id: pipelineId,
              label: pipelineId,
              trigger: { kind: 'manual', source: 'manual' },
              steps: [],
              effects: [],
            } satisfies LayerExperiencePipelineDefinition,
            label:
              typeof item.label === 'string' && item.label.trim()
                ? item.label.trim()
                : formatFieldLabel(pipelineId),
            disabled: true,
            disabledReason: 'Pipeline not found for this launcher.',
          } satisfies MapWorkbenchResolvedFeatureAction;
        }
        return {
          pipeline,
          label:
            typeof item.label === 'string' && item.label.trim()
              ? item.label.trim()
              : pipeline.label?.trim() || formatFieldLabel(pipeline.pipeline_id),
        } satisfies MapWorkbenchResolvedFeatureAction;
      })
      .filter((entry): entry is MapWorkbenchResolvedFeatureAction => Boolean(entry));
  });

const buildResolvedFeaturePopup = ({
  feature,
  surface,
  surfaces,
  pipelines,
  popupMode,
}: {
  feature: MapWorkbenchFeatureInspection;
  surface: LayerExperienceSurfaceDefinition;
  surfaces: LayerExperienceSurfaceDefinition[];
  pipelines: LayerExperiencePipelineDefinition[];
  popupMode?: 'tooltip' | 'pinned' | null;
}): MapWorkbenchResolvedFeaturePopup => {
  const properties = feature.properties ?? {};
  const summary = resolveSurfaceSummaryEntries(surface, properties);
  const authoredSubtitle =
    surface.sections
      .filter(section => section.kind === 'text')
      .map(section => resolveSurfaceBindingValue(section.binding, properties) ?? section.empty_state ?? null)
      .find((entry): entry is string => Boolean(entry && entry.trim())) ?? null;
  const identity = resolveMapWorkbenchPopupIdentity({
    feature,
    authoredTitle: resolveSurfaceBindingValue(surface.title_binding, properties),
    authoredSubtitle,
    summary,
  });

  return {
    surface,
    surfaces,
    pipelines,
    title: identity.title,
    subtitle: identity.subtitle,
    summary,
    badges: resolveSurfaceBadges(surface, properties),
    actions: resolveSurfaceActionEntries({ surface, pipelines }),
    mode: popupMode ?? null,
  };
};

export const collectInspectableLayerIds = (styleJson: Record<string, unknown> | null) => {
  if (!styleJson || !Array.isArray(styleJson.layers)) {
    return [];
  }
  return (styleJson.layers as Array<Record<string, unknown>>)
    .filter(layer => {
      const id = typeof layer.id === 'string' ? layer.id : '';
      const source = typeof layer.source === 'string' ? layer.source : '';
      const metadata =
        layer.metadata && typeof layer.metadata === 'object' ? (layer.metadata as Record<string, unknown>) : null;
      const workbench =
        metadata?.workbench && typeof metadata.workbench === 'object'
          ? (metadata.workbench as Record<string, unknown>)
          : null;
      return (
        Boolean(id) &&
        layer.type !== 'background' &&
        source !== 'system-basemap' &&
        metadata?.system_basemap !== true &&
        workbench?.managed === true &&
        !isWorkbenchRuntimeHighlightLayer(layer)
      );
    })
    .map(layer => layer.id as string);
};

const EMPTY_WORKBENCH_INTERACTIVITY_STATE: MapWorkbenchResolvedInteractivityState = {
  feature: null,
  runtimeFeature: null,
  popup: null,
  surface: null,
  highlightDescriptors: [],
  nextViewCenter: null,
  executions: [],
};

const buildResolvedFeatureSurface = ({
  feature,
  surface,
  surfaces,
  pipelines,
}: {
  feature: MapWorkbenchFeatureInspection;
  surface: LayerExperienceSurfaceDefinition;
  surfaces: LayerExperienceSurfaceDefinition[];
  pipelines: LayerExperiencePipelineDefinition[];
}): MapWorkbenchResolvedFeatureSurface =>
  buildResolvedFeaturePopup({
    feature,
    surface,
    surfaces,
    pipelines,
    popupMode: 'pinned',
  });

const resolveRuntimeSurfaceSectionContent = ({
  feature,
  runtimeFeature,
  surface,
  renderRuntimeSurfaceSection,
}: {
  feature: MapWorkbenchFeatureInspection | null;
  runtimeFeature: RuntimeFeatureRef | null;
  surface: MapWorkbenchResolvedFeatureSurface | null;
  renderRuntimeSurfaceSection?: MapWorkbenchProps['renderRuntimeSurfaceSection'];
}) => {
  if (!feature || !surface || !renderRuntimeSurfaceSection) {
    return [] as ReactNode[];
  }
  return surface.surface.sections
    .filter(section => section.kind === 'custom')
    .map((section, index) => {
      const rendered = renderRuntimeSurfaceSection({ feature, runtimeFeature, surface, section });
      if (!rendered) {
        return null;
      }
      return (
        <Fragment key={buildSurfaceSectionKey(section, `${surface.surface.surface_id}:inline`, index)}>
          {rendered}
        </Fragment>
      );
    })
    .filter((entry): entry is ReactNode => Boolean(entry));
};

const resolveRuntimeSurfaceTabContent = ({
  feature,
  runtimeFeature,
  surface,
  renderRuntimeSurfaceSection,
}: {
  feature: MapWorkbenchFeatureInspection | null;
  runtimeFeature: RuntimeFeatureRef | null;
  surface: MapWorkbenchResolvedFeatureSurface | null;
  renderRuntimeSurfaceSection?: MapWorkbenchProps['renderRuntimeSurfaceSection'];
}): MapWorkbenchResolvedFeatureSurfaceTabContent[] => {
  if (!feature || !surface || !surface.surface.tabs?.length) {
    return [];
  }
  return surface.surface.tabs.map((tab, index) => {
    const nodes = resolveSurfaceSectionNodes({
      feature,
      runtimeFeature,
      surface,
      sections: tab.sections,
      renderRuntimeSurfaceSection,
      sectionKeyPrefix: `${surface.surface.surface_id}:tab:${tab.tab_id || index}`,
    });
    return {
      id: tab.tab_id,
      label: tab.label,
      content: nodes.length > 0 ? <>{nodes}</> : undefined,
    };
  });
};

const surfaceHasResolvableSummaryContent = (surface: LayerExperienceSurfaceDefinition) =>
  Boolean(surface.title_binding) ||
  surface.sections.some(section => {
    if (section.kind === 'text') {
      return Boolean(section.binding || section.empty_state);
    }
    if (section.kind === 'details_grid' || section.kind === 'stats_row' || section.kind === 'list') {
      return Array.isArray(section.items) && section.items.length > 0;
    }
    return false;
  });

const selectAmbiguousSelectionSummarySurface = (surfaces: LayerExperienceSurfaceDefinition[]) =>
  surfaces.find(
    surface =>
      surface.surface_kind === 'ambiguous_selection_summary' &&
      surfaceHasResolvableSummaryContent(surface),
  ) ?? null;

const buildWorkbenchInteractivityDefinition = (
  layerExperience: LayerExperience | null,
): RuntimeInteractivityDefinitionLike<string> | null => {
  if (!layerExperience?.interactivity) {
    return null;
  }
  return {
    pipelines: layerExperience.interactivity.pipeline_definitions ?? [],
    surfaces: layerExperience.interactivity.surface_definitions ?? [],
    resolvers: layerExperience.enrichments?.resolver_definitions ?? [],
  };
};

const buildRuntimeFeatureRef = (
  feature: MapWorkbenchFeatureInspection,
  layerExperience: LayerExperience,
): RuntimeFeatureRef | null => {
  const propertyFeatureIDField =
    layerExperience.enrichments?.source_definition?.identity?.feature_id_field
      ?? layerExperience.enrichments?.entity_ref_field
      ?? null;
  const propertyFeatureID =
    (typeof propertyFeatureIDField === 'string' && propertyFeatureIDField.trim()
      ? feature.properties?.[propertyFeatureIDField]
      : undefined)
    ?? feature.properties?.feature_identity_key
    ?? feature.properties?.feature_id
    ?? feature.properties?.id;
  const featureId =
    propertyFeatureID != null && String(propertyFeatureID).trim()
      ? String(propertyFeatureID).trim()
      : typeof feature.id === 'number'
        ? feature.id
        : typeof feature.id === 'string' && feature.id.trim()
          ? feature.id.trim()
          : `${layerExperience.source_id}:${layerExperience.source_layer}:trigger-feature`;
  if (!layerExperience.source_id || !layerExperience.source_layer) {
    return null;
  }
  return {
    source_id: layerExperience.source_id,
    source_layer: layerExperience.source_layer,
    feature_id: featureId,
    properties: feature.properties ?? null,
    geometry: feature.geometry ?? null,
  };
};

const buildRuntimeFeatureRefForTarget = (
  feature: MapWorkbenchFeatureInspection,
  target: MapWorkbenchRuntimeFeatureStateTarget,
): RuntimeFeatureRef | null => {
  const propertyFeatureID =
    feature.properties?.feature_identity_key
    ?? feature.properties?.feature_id
    ?? feature.properties?.id;
  const featureId =
    propertyFeatureID != null && String(propertyFeatureID).trim()
      ? String(propertyFeatureID).trim()
      : typeof feature.id === 'number'
        ? feature.id
        : typeof feature.id === 'string' && feature.id.trim()
          ? feature.id.trim()
          : null;
  if (!featureId) {
    return null;
  }
  return {
    source_id: target.sourceId,
    source_layer: target.sourceLayer,
    feature_id: featureId,
    properties: feature.properties ?? null,
    geometry: feature.geometry ?? null,
  };
};

const buildWorkbenchHighlightIdentity = (
  feature: MapWorkbenchFeatureInspection,
  target: MapWorkbenchHighlightTargetDescriptor,
) => {
  const propertyValue = feature.properties?.[target.featureIdField];
  if (propertyValue != null && String(propertyValue).trim()) {
    return String(propertyValue).trim();
  }
  if (typeof feature.id === 'number') {
    return String(feature.id);
  }
  if (typeof feature.id === 'string' && feature.id.trim()) {
    return feature.id.trim();
  }
  return `${target.sourceAlias}:${target.sourceLayer}:trigger-feature`;
};

const appendWorkbenchHighlightDescriptor = (
  descriptors: MapWorkbenchRuntimeHighlightDescriptor[],
  descriptor: MapWorkbenchRuntimeHighlightDescriptor,
) => {
  const identity = buildWorkbenchHighlightIdentity(descriptor.feature, descriptor.target);
  if (
    descriptors.some(
      existing =>
        existing.channel === descriptor.channel &&
        existing.target.sourceAlias === descriptor.target.sourceAlias &&
        existing.target.sourceLayer === descriptor.target.sourceLayer &&
        buildWorkbenchHighlightIdentity(existing.feature, existing.target) === identity,
    )
  ) {
    return descriptors;
  }
  return [...descriptors, descriptor];
};

const buildWorkbenchInteractivityState = ({
  feature,
  event,
  layerExperience,
  executions,
  highlightTargets,
}: {
  feature: MapWorkbenchFeatureInspection;
  event: 'hover_enter' | 'click' | 'context_menu';
  layerExperience: LayerExperience;
  executions: RuntimePipelineExecution<string>[];
  highlightTargets: MapWorkbenchHighlightTargetDescriptor[];
}): MapWorkbenchResolvedInteractivityState => {
  const interactivity = buildWorkbenchInteractivityDefinition(layerExperience);
  if (!interactivity) {
    return EMPTY_WORKBENCH_INTERACTIVITY_STATE;
  }

  const surfaces = interactivity.surfaces as LayerExperienceSurfaceDefinition[];
  const pipelines = interactivity.pipelines as LayerExperiencePipelineDefinition[];
  const highlightTarget =
    highlightTargets.find(
      target =>
        target.sourceId === layerExperience.source_id &&
        target.sourceLayer === layerExperience.source_layer &&
        (!feature.source || feature.source === target.sourceAlias),
    ) ?? null;

  let popup: MapWorkbenchResolvedFeaturePopup | null = null;
  let surface: MapWorkbenchResolvedFeatureSurface | null = null;
  let highlightDescriptors: MapWorkbenchRuntimeHighlightDescriptor[] = [];
  let nextViewCenter: [number, number] | null = null;

  for (const execution of executions) {
    for (const effectExecution of execution.effects) {
      if (effectExecution.status !== 'ready') {
        continue;
      }
      const effectSurface = effectExecution.surface as LayerExperienceSurfaceDefinition | null;

      if (effectExecution.kind === 'open_surface' || effectExecution.kind === 'bind_surface_data') {
        if (!effectSurface) {
          continue;
        }
        if (effectSurface.surface_kind === 'ambiguous_selection_summary') {
          continue;
        }
        if (effectSurface.surface_kind === 'hover_popup') {
          popup = buildResolvedFeaturePopup({
            feature,
            surface: effectSurface,
            surfaces,
            pipelines,
            popupMode: event === 'hover_enter' ? 'tooltip' : 'pinned',
          });
          continue;
        }
        surface = buildResolvedFeatureSurface({
          feature,
          surface: effectSurface,
          surfaces,
          pipelines,
        });
        continue;
      }

      if (effectExecution.kind === 'close_surface') {
        if (!effectSurface || effectSurface.surface_kind === 'hover_popup') {
          popup = null;
        }
        if (!effectSurface || effectSurface.surface_kind !== 'hover_popup') {
          surface = null;
        }
        continue;
      }

      if (effectExecution.kind === 'highlight_set' && highlightTarget) {
        highlightDescriptors = appendWorkbenchHighlightDescriptor(highlightDescriptors, {
          channel: event === 'hover_enter' ? 'hover' : 'selected',
          feature,
          target: highlightTarget,
        });
        continue;
      }

      if (effectExecution.kind === 'clear_highlight_set') {
        highlightDescriptors = [];
        continue;
      }

      if (effectExecution.kind === 'fly_to_feature') {
        nextViewCenter = resolveFeatureCenter(feature);
      }
    }

    if (execution.stop_propagation) {
      break;
    }
  }

  return {
    feature,
    runtimeFeature: buildRuntimeFeatureRef(feature, layerExperience),
    popup,
    surface,
    highlightDescriptors,
    nextViewCenter,
    executions,
  };
};

const listProfilesForTarget = (
  experience: MapExperienceState,
  sourceId: string,
  sourceLayer: string,
): MapExperienceStyleProfile[] => {
  return experience.style_profiles.filter(
    profile => profile.source_id === sourceId && profile.source_layer === sourceLayer,
  );
};

const resolveFeatureCenter = (feature?: MapWorkbenchFeatureInspection | null): [number, number] | null => {
  if (!feature) {
    return null;
  }
  const pointCoordinates = feature.geometry?.coordinates;
  if (
    feature.geometryType === 'Point' &&
    Array.isArray(pointCoordinates) &&
    pointCoordinates.length >= 2 &&
    typeof pointCoordinates[0] === 'number' &&
    typeof pointCoordinates[1] === 'number'
  ) {
    return [pointCoordinates[0], pointCoordinates[1]];
  }
  if (feature.bounds) {
    const [minLng, minLat, maxLng, maxLat] = feature.bounds;
    return [(minLng + maxLng) / 2, (minLat + maxLat) / 2];
  }
  return null;
};

const normalizeInspectedFeature = (feature: MapGeoJSONFeature | undefined): MapWorkbenchFeatureInspection | null => {
  if (!feature) {
    return null;
  }
  const sourceLayer = resolveInspectedFeatureSourceLayer(feature);
  const geometry = feature.geometry && typeof feature.geometry === 'object'
    ? (feature.geometry as Record<string, unknown>)
    : undefined;
  const bounds = computeMapGeometryBounds(geometry);
  const normalized: MapWorkbenchFeatureInspection = {
    id: feature.id as string | number | undefined,
    source: feature.source,
    sourceLayer,
    layerId: feature.layer?.id,
    geometryType: feature.geometry?.type,
    geometry,
    bounds,
    screenPoint: null,
    properties: (feature.properties ?? {}) as Record<string, unknown>,
    rawJson: {
      id: feature.id as string | number | undefined,
      source: feature.source,
      sourceLayer,
      layerId: feature.layer?.id,
      geometryType: feature.geometry?.type,
      geometry,
      bounds,
      screenPoint: null,
      properties: (feature.properties ?? {}) as Record<string, unknown>,
    },
  };
  return normalized;
};

const buildFeatureStateRecordKey = (featureId: string | number) =>
  typeof featureId === 'number' ? `n:${featureId}` : `s:${featureId}`;

const collectRuntimeFeatureStateHydrationLayerIds = ({
  styleJson,
  target,
}: {
  styleJson: Record<string, unknown> | null;
  target: MapWorkbenchRuntimeFeatureStateTarget;
}) => {
  const styleSources =
    styleJson?.sources && typeof styleJson.sources === 'object'
      ? (styleJson.sources as Record<string, Record<string, unknown>>)
      : {};
  const styleLayers = Array.isArray(styleJson?.layers)
    ? (styleJson?.layers as Array<Record<string, unknown>>)
    : [];

  const resolved = styleLayers.filter(layer => {
    const metadata =
      layer.metadata && typeof layer.metadata === 'object'
        ? (layer.metadata as Record<string, unknown>)
        : {};
    const workbench =
      metadata.workbench && typeof metadata.workbench === 'object'
        ? (metadata.workbench as Record<string, unknown>)
        : {};
    const layerId = typeof layer.id === 'string' ? layer.id : '';
    if (!layerId) {
      return false;
    }
    if (
      workbench.source_id === target.sourceId &&
      workbench.source_layer === target.sourceLayer &&
      (workbench.render_stack_role === 'overlay_badge' ||
        workbench.render_stack_role === 'overlay_label' ||
        workbench.render_stack_role === 'overlay_symbol')
    ) {
      return false;
    }
    if (
      workbench.source_id === target.sourceId &&
      workbench.source_layer === target.sourceLayer
    ) {
      return true;
    }
    return matchesWorkbenchLayerTarget(layer, target.sourceAlias, target.sourceLayer, styleSources);
  });

  return resolved
    .map(layer => (typeof layer.id === 'string' ? layer.id : ''))
    .filter(Boolean);
};

type WorkbenchRuntimeLayerSnapshot = {
  id: string;
  structuralSignature: string;
  filterSignature: string;
  paint: Record<string, unknown>;
  layout: Record<string, unknown>;
};

type WorkbenchRuntimeStyleSnapshot = {
  rootSignature: string;
  layerOrder: string[];
  layersById: Map<string, WorkbenchRuntimeLayerSnapshot>;
};

type MapWorkbenchSpiderifyOverlayState = {
  anchor: MapWorkbenchSpiderifyPoint;
  trigger: 'click' | 'context_menu';
  candidates: Array<MapWorkbenchSpiderifyCandidate<MapWorkbenchFeatureInspection>>;
};

type MapWorkbenchSpiderifySheetState = {
  anchor: MapWorkbenchSpiderifyPoint;
  trigger: 'click' | 'context_menu';
  candidates: MapWorkbenchFeatureInspection[];
};

const toJsonSignature = (value: unknown) => JSON.stringify(value ?? null);

const normalizeLayerPatchRecord = (value: unknown) => {
  if (!value || typeof value !== 'object') {
    return {} as Record<string, unknown>;
  }
  return { ...(value as Record<string, unknown>) };
};

const cloneStyleInteractionConfig = (value: { hit_target_px?: number | null } | null | undefined) =>
  value
    ? {
        hit_target_px:
          typeof value.hit_target_px === 'number' && Number.isFinite(value.hit_target_px) ? value.hit_target_px : null,
      }
    : null;

const buildWorkbenchRuntimeStyleSnapshot = (style: Record<string, unknown> | null): WorkbenchRuntimeStyleSnapshot | null => {
  if (!style) {
    return null;
  }

  const rootSnapshot = {
    version: style.version ?? null,
    name: style.name ?? null,
    sprite: style.sprite ?? null,
    glyphs: style.glyphs ?? null,
    state: style.state ?? null,
    center: style.center ?? null,
    zoom: style.zoom ?? null,
    bearing: style.bearing ?? null,
    pitch: style.pitch ?? null,
    sources: style.sources ?? null,
    metadata: style.metadata ?? null,
    light: style.light ?? null,
    fog: style.fog ?? null,
    terrain: style.terrain ?? null,
    projection: style.projection ?? null,
    transition: style.transition ?? null,
  };

  const layerOrder: string[] = [];
  const layersById = new Map<string, WorkbenchRuntimeLayerSnapshot>();
  const layers = Array.isArray(style.layers) ? (style.layers as Array<Record<string, unknown>>) : [];

  layers.forEach(layer => {
    const id = typeof layer.id === 'string' ? layer.id : '';
    if (!id) {
      return;
    }

    const structuralLayer =
      typeof structuredClone === 'function' ? structuredClone(layer) : JSON.parse(JSON.stringify(layer));
    delete structuralLayer.filter;
    delete structuralLayer.paint;
    delete structuralLayer.layout;

    layerOrder.push(id);
    layersById.set(id, {
      id,
      structuralSignature: toJsonSignature(structuralLayer),
      filterSignature: toJsonSignature(layer.filter ?? null),
      paint: normalizeLayerPatchRecord(layer.paint),
      layout: normalizeLayerPatchRecord(layer.layout),
    });
  });

  return {
    rootSignature: toJsonSignature(rootSnapshot),
    layerOrder,
    layersById,
  };
};

const hasRemovedPatchKeys = (
  previousRecord: Record<string, unknown>,
  nextRecord: Record<string, unknown>,
  ignoredKeys: string[] = [],
) => {
  return Object.keys(previousRecord).some(key => !ignoredKeys.includes(key) && !(key in nextRecord));
};

const hasPatchKeySetChange = (
  previousRecord: Record<string, unknown>,
  nextRecord: Record<string, unknown>,
  ignoredKeys: string[] = [],
) => {
  const previousKeys = Object.keys(previousRecord).filter(key => !ignoredKeys.includes(key)).sort();
  const nextKeys = Object.keys(nextRecord).filter(key => !ignoredKeys.includes(key)).sort();
  if (previousKeys.length !== nextKeys.length) {
    return true;
  }
  return previousKeys.some((key, index) => key !== nextKeys[index]);
};

const requiresFullStyleReloadForPatchValue = (previousValue: unknown, nextValue: unknown) => {
  if (toJsonSignature(previousValue) === toJsonSignature(nextValue)) {
    return false;
  }
  const involvesStructuredValue =
    Array.isArray(previousValue) ||
    Array.isArray(nextValue) ||
    (Boolean(previousValue) && typeof previousValue === 'object') ||
    (Boolean(nextValue) && typeof nextValue === 'object');
  return involvesStructuredValue;
};

const canPatchWorkbenchRuntimeLayer = (
  previousLayer: WorkbenchRuntimeLayerSnapshot,
  nextLayer: WorkbenchRuntimeLayerSnapshot,
) => {
  if (previousLayer.structuralSignature !== nextLayer.structuralSignature) {
    return false;
  }
  if (hasRemovedPatchKeys(previousLayer.paint, nextLayer.paint)) {
    return false;
  }
  if (hasPatchKeySetChange(previousLayer.layout, nextLayer.layout, ['visibility'])) {
    return false;
  }
  const paintKeys = new Set([...Object.keys(previousLayer.paint), ...Object.keys(nextLayer.paint)]);
  for (const key of paintKeys) {
    if (requiresFullStyleReloadForPatchValue(previousLayer.paint[key], nextLayer.paint[key])) {
      return false;
    }
  }
  const layoutKeys = new Set([...Object.keys(previousLayer.layout), ...Object.keys(nextLayer.layout)]);
  for (const key of layoutKeys) {
    if (key === 'visibility') {
      continue;
    }
    if (requiresFullStyleReloadForPatchValue(previousLayer.layout[key], nextLayer.layout[key])) {
      return false;
    }
  }
  return true;
};

const layerIdSetForStyle = (style: Record<string, unknown> | null, predicate: (layer: Record<string, unknown>) => boolean) => {
  const layers = Array.isArray(style?.layers) ? (style.layers as Array<Record<string, unknown>>) : [];
  return new Set(
    layers
      .filter(predicate)
      .map(layer => (typeof layer.id === 'string' ? layer.id : ''))
      .filter(Boolean),
  );
};

const nonRuntimeHighlightLayerOrderForStyle = (style: Record<string, unknown> | null) => {
  const layers = Array.isArray(style?.layers) ? (style.layers as Array<Record<string, unknown>>) : [];
  return layers
    .filter(layer => !isWorkbenchRuntimeHighlightLayer(layer))
    .map(layer => (typeof layer.id === 'string' ? layer.id : ''))
    .filter(Boolean);
};

const buildStyleSignatureWithoutRuntimeHighlightLayers = (style: Record<string, unknown> | null) => {
  if (!style || typeof style !== 'object') {
    return '';
  }
  const layers = Array.isArray(style.layers) ? (style.layers as Array<Record<string, unknown>>) : [];
  return toJsonSignature({
    ...style,
    layers: layers.filter(layer => !isWorkbenchRuntimeHighlightLayer(layer)),
  });
};

const canSyncWorkbenchRuntimeHighlightLayers = (
  previousStyle: Record<string, unknown> | null,
  nextStyle: Record<string, unknown> | null,
) => {
  const previousSnapshot = buildWorkbenchRuntimeStyleSnapshot(previousStyle);
  const nextSnapshot = buildWorkbenchRuntimeStyleSnapshot(nextStyle);
  if (!previousSnapshot || !nextSnapshot) {
    return false;
  }
  if (
    buildStyleSignatureWithoutRuntimeHighlightLayers(previousStyle) !==
    buildStyleSignatureWithoutRuntimeHighlightLayers(nextStyle)
  ) {
    return false;
  }

  const previousRuntimeHighlightLayerIds = layerIdSetForStyle(previousStyle, isWorkbenchRuntimeHighlightLayer);
  const nextRuntimeHighlightLayerIds = layerIdSetForStyle(nextStyle, isWorkbenchRuntimeHighlightLayer);
  const layerIdsToCompare = new Set<string>([
    ...previousRuntimeHighlightLayerIds,
    ...nextRuntimeHighlightLayerIds,
  ]);

  for (const layerId of layerIdsToCompare) {
    const previousLayer = previousSnapshot.layersById.get(layerId);
    const nextLayer = nextSnapshot.layersById.get(layerId);
    if (!previousLayer || !nextLayer) {
      continue;
    }
    if (!canPatchWorkbenchRuntimeLayer(previousLayer, nextLayer)) {
      return false;
    }
  }

  return true;
};

export const canPatchWorkbenchRuntimeStyle = (
  previousStyle: Record<string, unknown> | null,
  nextStyle: Record<string, unknown> | null,
) => {
  const previousSnapshot = buildWorkbenchRuntimeStyleSnapshot(previousStyle);
  const nextSnapshot = buildWorkbenchRuntimeStyleSnapshot(nextStyle);
  if (!previousSnapshot || !nextSnapshot) {
    return false;
  }
  if (previousSnapshot.rootSignature !== nextSnapshot.rootSignature) {
    return false;
  }

  const previousBaseOrder = nonRuntimeHighlightLayerOrderForStyle(previousStyle);
  const nextBaseOrder = nonRuntimeHighlightLayerOrderForStyle(nextStyle);
  if (previousBaseOrder.length !== nextBaseOrder.length) {
    return false;
  }
  for (let index = 0; index < previousBaseOrder.length; index += 1) {
    if (previousBaseOrder[index] !== nextBaseOrder[index]) {
      return false;
    }
  }

  const previousRuntimeHighlightLayerIds = layerIdSetForStyle(previousStyle, isWorkbenchRuntimeHighlightLayer);
  const nextRuntimeHighlightLayerIds = layerIdSetForStyle(nextStyle, isWorkbenchRuntimeHighlightLayer);
  const layerIdsToCompare = new Set<string>([
    ...previousBaseOrder,
    ...nextBaseOrder,
    ...previousRuntimeHighlightLayerIds,
    ...nextRuntimeHighlightLayerIds,
  ]);

  for (const layerId of layerIdsToCompare) {
    const previousLayer = previousSnapshot.layersById.get(layerId);
    const nextLayer = nextSnapshot.layersById.get(layerId);

    const previousIsRuntimeHighlight = previousRuntimeHighlightLayerIds.has(layerId);
    const nextIsRuntimeHighlight = nextRuntimeHighlightLayerIds.has(layerId);
    if (!previousLayer || !nextLayer) {
      if (previousIsRuntimeHighlight || nextIsRuntimeHighlight) {
        continue;
      }
      return false;
    }
    if (!canPatchWorkbenchRuntimeLayer(previousLayer, nextLayer)) {
      return false;
    }
  }

  return true;
};

const diagnoseWorkbenchRuntimeLayerPatchFailure = (
  previousLayer: WorkbenchRuntimeLayerSnapshot,
  nextLayer: WorkbenchRuntimeLayerSnapshot,
) => {
  const removedPaintKeys = Object.keys(previousLayer.paint).filter(key => !(key in nextLayer.paint));
  const previousLayoutKeys = Object.keys(previousLayer.layout).filter(key => key !== 'visibility').sort();
  const nextLayoutKeys = Object.keys(nextLayer.layout).filter(key => key !== 'visibility').sort();
  const changedPaintKeys = Array.from(new Set([...Object.keys(previousLayer.paint), ...Object.keys(nextLayer.paint)])).filter(key =>
    requiresFullStyleReloadForPatchValue(previousLayer.paint[key], nextLayer.paint[key]),
  );
  const changedLayoutKeys = Array.from(new Set([...Object.keys(previousLayer.layout), ...Object.keys(nextLayer.layout)])).filter(
    key => key !== 'visibility' && requiresFullStyleReloadForPatchValue(previousLayer.layout[key], nextLayer.layout[key]),
  );

  return {
    structuralSignatureMatches: previousLayer.structuralSignature === nextLayer.structuralSignature,
    removedPaintKeys,
    layoutKeySetChanged:
      previousLayoutKeys.length !== nextLayoutKeys.length ||
      previousLayoutKeys.some((key, index) => key !== nextLayoutKeys[index]),
    changedPaintKeys,
    changedLayoutKeys,
  };
};

const diagnoseWorkbenchRuntimeStyleTransition = (
  previousStyle: Record<string, unknown> | null,
  nextStyle: Record<string, unknown> | null,
) => {
  const previousSnapshot = buildWorkbenchRuntimeStyleSnapshot(previousStyle);
  const nextSnapshot = buildWorkbenchRuntimeStyleSnapshot(nextStyle);
  const previousBaseOrder = nonRuntimeHighlightLayerOrderForStyle(previousStyle);
  const nextBaseOrder = nonRuntimeHighlightLayerOrderForStyle(nextStyle);
  const previousRuntimeHighlightLayerIds = layerIdSetForStyle(previousStyle, isWorkbenchRuntimeHighlightLayer);
  const nextRuntimeHighlightLayerIds = layerIdSetForStyle(nextStyle, isWorkbenchRuntimeHighlightLayer);
  const rootSignatureMatches = Boolean(
    previousSnapshot &&
      nextSnapshot &&
      previousSnapshot.rootSignature === nextSnapshot.rootSignature,
  );
  const baseOrderMismatchAt = previousBaseOrder.findIndex((layerId, index) => layerId !== nextBaseOrder[index]);
  const reasons: Array<Record<string, unknown>> = [];

  if (!previousSnapshot || !nextSnapshot) {
    reasons.push({
      kind: 'missing_snapshot',
      previousSnapshot: Boolean(previousSnapshot),
      nextSnapshot: Boolean(nextSnapshot),
    });
  }

  if (previousSnapshot && nextSnapshot && !rootSignatureMatches) {
    reasons.push({
      kind: 'root_signature_changed',
      previousSummary: summarizeWorkbenchRuntimeStyle(previousStyle),
      nextSummary: summarizeWorkbenchRuntimeStyle(nextStyle),
    });
  }

  if (
    previousSnapshot &&
    nextSnapshot &&
    (previousBaseOrder.length !== nextBaseOrder.length || baseOrderMismatchAt !== -1)
  ) {
    const mismatchIndex =
      baseOrderMismatchAt === -1 ? Math.min(previousBaseOrder.length, nextBaseOrder.length) : baseOrderMismatchAt;
    reasons.push({
      kind: 'base_layer_order_changed',
      mismatchIndex,
      previousLayerId: previousBaseOrder[mismatchIndex] ?? null,
      nextLayerId: nextBaseOrder[mismatchIndex] ?? null,
      previousBaseLayerCount: previousBaseOrder.length,
      nextBaseLayerCount: nextBaseOrder.length,
    });
  }

  if (previousSnapshot && nextSnapshot) {
    const layerIdsToCompare = new Set<string>([
      ...previousBaseOrder,
      ...nextBaseOrder,
      ...previousRuntimeHighlightLayerIds,
      ...nextRuntimeHighlightLayerIds,
    ]);

    for (const layerId of layerIdsToCompare) {
      const previousLayer = previousSnapshot.layersById.get(layerId);
      const nextLayer = nextSnapshot.layersById.get(layerId);
      const previousIsRuntimeHighlight = previousRuntimeHighlightLayerIds.has(layerId);
      const nextIsRuntimeHighlight = nextRuntimeHighlightLayerIds.has(layerId);

      if (!previousLayer || !nextLayer) {
        if (!previousIsRuntimeHighlight && !nextIsRuntimeHighlight) {
          reasons.push({
            kind: 'non_runtime_layer_missing',
            layerId,
            previousExists: Boolean(previousLayer),
            nextExists: Boolean(nextLayer),
          });
        }
        continue;
      }

      if (!canPatchWorkbenchRuntimeLayer(previousLayer, nextLayer)) {
        reasons.push({
          kind: 'layer_patch_incompatible',
          layerId,
          previousIsRuntimeHighlight,
          nextIsRuntimeHighlight,
          diagnostics: diagnoseWorkbenchRuntimeLayerPatchFailure(previousLayer, nextLayer),
        });
      }
    }
  }

  return {
    canPatch: canPatchWorkbenchRuntimeStyle(previousStyle, nextStyle),
    canSyncRuntimeHighlightOnly: canSyncWorkbenchRuntimeHighlightLayers(previousStyle, nextStyle),
    rootSignatureMatches,
    previousSummary: summarizeWorkbenchRuntimeStyle(previousStyle),
    nextSummary: summarizeWorkbenchRuntimeStyle(nextStyle),
    reasonCount: reasons.length,
    reasons: reasons.slice(0, 8),
  };
};

const applyWorkbenchRuntimeStylePatch = (map: MapLibreMap, previousStyle: Record<string, unknown> | null, nextStyle: Record<string, unknown>) => {
  const previousSnapshot = buildWorkbenchRuntimeStyleSnapshot(previousStyle);
  const nextSnapshot = buildWorkbenchRuntimeStyleSnapshot(nextStyle);
  if (!previousSnapshot || !nextSnapshot) {
    return;
  }

  const nextLayers = Array.isArray(nextStyle.layers) ? (nextStyle.layers as Array<Record<string, unknown>>) : [];
  const nextLayersById = new Map(
    nextLayers
      .map(layer => {
        const layerId = typeof layer.id === 'string' ? layer.id : '';
        return layerId ? [layerId, layer] : null;
      })
      .filter((entry): entry is [string, Record<string, unknown>] => Boolean(entry)),
  );
  const previousRuntimeHighlightLayerIds = Array.from(layerIdSetForStyle(previousStyle, isWorkbenchRuntimeHighlightLayer)).reverse();
  const nextRuntimeHighlightLayerIds = layerIdSetForStyle(nextStyle, isWorkbenchRuntimeHighlightLayer);

  previousRuntimeHighlightLayerIds.forEach(layerId => {
    if (!nextRuntimeHighlightLayerIds.has(layerId) && map.getLayer(layerId)) {
      map.removeLayer(layerId);
    }
  });

  const findInsertionAnchor = (layerId: string) => {
    const layerIndex = nextSnapshot.layerOrder.indexOf(layerId);
    if (layerIndex === -1) {
      return undefined;
    }
    for (let index = layerIndex + 1; index < nextSnapshot.layerOrder.length; index += 1) {
      const candidateId = nextSnapshot.layerOrder[index];
      if (map.getLayer(candidateId)) {
        return candidateId;
      }
    }
    return undefined;
  };

  nextSnapshot.layerOrder.forEach(layerId => {
    if (!map.getLayer(layerId)) {
      const nextLayerDefinition = nextLayersById.get(layerId);
      if (nextLayerDefinition && isWorkbenchRuntimeHighlightLayer(nextLayerDefinition)) {
        map.addLayer(nextLayerDefinition as any, findInsertionAnchor(layerId));
      }
    }
    if (!map.getLayer(layerId)) {
      return;
    }

    const previousLayer = previousSnapshot.layersById.get(layerId);
    const nextLayer = nextSnapshot.layersById.get(layerId);
    if (!nextLayer) {
      return;
    }
    if (!previousLayer) {
      return;
    }

    if (previousLayer.filterSignature !== nextLayer.filterSignature) {
      map.setFilter(layerId, JSON.parse(nextLayer.filterSignature) as any);
    }

    const paintKeys = new Set([...Object.keys(previousLayer.paint), ...Object.keys(nextLayer.paint)]);
    paintKeys.forEach(key => {
      if (!(key in nextLayer.paint)) {
        return;
      }
      if (toJsonSignature(previousLayer.paint[key]) === toJsonSignature(nextLayer.paint[key])) {
        return;
      }
      map.setPaintProperty(layerId, key, nextLayer.paint[key] as any);
    });

    const layoutKeys = new Set([...Object.keys(previousLayer.layout), ...Object.keys(nextLayer.layout)]);
    layoutKeys.forEach(key => {
      if (key === 'visibility') {
        const nextVisibility = nextLayer.layout.visibility === 'none' ? 'none' : 'visible';
        const previousVisibility = previousLayer.layout.visibility === 'none' ? 'none' : 'visible';
        if (previousVisibility !== nextVisibility) {
          map.setLayoutProperty(layerId, 'visibility', nextVisibility);
        }
        return;
      }
      if (!(key in nextLayer.layout)) {
        return;
      }
      if (toJsonSignature(previousLayer.layout[key]) === toJsonSignature(nextLayer.layout[key])) {
        return;
      }
      map.setLayoutProperty(layerId, key, nextLayer.layout[key] as any);
    });
  });
};

const syncWorkbenchRuntimeHighlightLayers = (
  map: MapLibreMap,
  previousStyle: Record<string, unknown> | null,
  nextStyle: Record<string, unknown>,
) => {
  const previousSnapshot = buildWorkbenchRuntimeStyleSnapshot(previousStyle);
  const nextSnapshot = buildWorkbenchRuntimeStyleSnapshot(nextStyle);
  if (!previousSnapshot || !nextSnapshot) {
    return;
  }

  const nextLayers = Array.isArray(nextStyle.layers) ? (nextStyle.layers as Array<Record<string, unknown>>) : [];
  const nextLayersById = new Map(
    nextLayers
      .map(layer => {
        const layerId = typeof layer.id === 'string' ? layer.id : '';
        return layerId && isWorkbenchRuntimeHighlightLayer(layer) ? [layerId, layer] : null;
      })
      .filter((entry): entry is [string, Record<string, unknown>] => Boolean(entry)),
  );
  const previousRuntimeHighlightLayerIds = Array.from(layerIdSetForStyle(previousStyle, isWorkbenchRuntimeHighlightLayer)).reverse();
  const nextRuntimeHighlightLayerIds = layerIdSetForStyle(nextStyle, isWorkbenchRuntimeHighlightLayer);

  previousRuntimeHighlightLayerIds.forEach(layerId => {
    if (!nextRuntimeHighlightLayerIds.has(layerId) && map.getLayer(layerId)) {
      map.removeLayer(layerId);
    }
  });

  const findInsertionAnchor = (layerId: string) => {
    const layerIndex = nextSnapshot.layerOrder.indexOf(layerId);
    if (layerIndex === -1) {
      return undefined;
    }
    for (let index = layerIndex + 1; index < nextSnapshot.layerOrder.length; index += 1) {
      const candidateId = nextSnapshot.layerOrder[index];
      if (map.getLayer(candidateId)) {
        return candidateId;
      }
    }
    return undefined;
  };

  nextSnapshot.layerOrder.forEach(layerId => {
    const nextLayerDefinition = nextLayersById.get(layerId);
    if (!nextLayerDefinition) {
      return;
    }
    if (!map.getLayer(layerId)) {
      map.addLayer(nextLayerDefinition as any, findInsertionAnchor(layerId));
    }
    if (!map.getLayer(layerId)) {
      return;
    }

    const previousLayer = previousSnapshot.layersById.get(layerId);
    const nextLayer = nextSnapshot.layersById.get(layerId);
    if (!previousLayer || !nextLayer) {
      return;
    }

    if (previousLayer.filterSignature !== nextLayer.filterSignature) {
      map.setFilter(layerId, JSON.parse(nextLayer.filterSignature) as any);
    }

    const paintKeys = new Set([...Object.keys(previousLayer.paint), ...Object.keys(nextLayer.paint)]);
    paintKeys.forEach(key => {
      if (!(key in nextLayer.paint)) {
        return;
      }
      if (toJsonSignature(previousLayer.paint[key]) === toJsonSignature(nextLayer.paint[key])) {
        return;
      }
      map.setPaintProperty(layerId, key, nextLayer.paint[key] as any);
    });

    const layoutKeys = new Set([...Object.keys(previousLayer.layout), ...Object.keys(nextLayer.layout)]);
    layoutKeys.forEach(key => {
      if (key === 'visibility') {
        const nextVisibility = nextLayer.layout.visibility === 'none' ? 'none' : 'visible';
        const previousVisibility = previousLayer.layout.visibility === 'none' ? 'none' : 'visible';
        if (previousVisibility !== nextVisibility) {
          map.setLayoutProperty(layerId, 'visibility', nextVisibility);
        }
        return;
      }
      if (!(key in nextLayer.layout)) {
        return;
      }
      if (toJsonSignature(previousLayer.layout[key]) === toJsonSignature(nextLayer.layout[key])) {
        return;
      }
      map.setLayoutProperty(layerId, key, nextLayer.layout[key] as any);
    });
  });
};

export interface MapWorkbenchRuntimePreviewProps {
  styleJson: Record<string, unknown> | null;
  mapExperience?: MapExperienceState;
  themeColorPalette?: SemanticThemeColorPalette | null;
  viewState?: MapWorkbenchViewState | null;
  status: 'idle' | 'loading' | 'success' | 'error';
  defaultViewState: MapWorkbenchViewState;
  inspectableLayerIds: string[];
  runtimeFeatureStateTargets?: MapWorkbenchRuntimeFeatureStateTarget[];
  hydrateRuntimeFeatureState?: (
    args: MapWorkbenchRuntimeFeatureStateHydrationArgs,
  ) => Promise<MapWorkbenchRuntimeFeatureStateHydrationResult | null>;
  runtimeOverlayGeoJSON?: Record<string, unknown> | null;
  runtimeOverlayStyle?: {
    fillPaint?: Record<string, unknown> | null;
    linePaint?: Record<string, unknown> | null;
    pointPaint?: Record<string, unknown> | null;
  } | null;
  runtimeOverlayInteractive?: boolean;
  isRuntimeOverlayFeatureInteractive?: (feature: {
    featureId: string | number | null;
    geometryType: string | null;
    properties: Record<string, unknown>;
  }) => boolean;
  activeToolId: MapBuiltInToolId;
  drawnGeometry: MapDrawnGeometry | null;
  showFilterControl?: boolean;
  filterControlActive?: boolean;
  filterControlBadge?: string | null;
  showLegendControl?: boolean;
  legendControlActive?: boolean;
  legendControlBadge?: string | null;
  hideFloatingControls?: boolean;
  scrollZoomEnabled?: boolean;
  runtimeOverlayDebugLabel?: string | null;
  onOpenFilters?: () => void;
  onOpenLegend?: () => void;
  resolveSpiderifyChooserItem?: (feature: MapWorkbenchFeatureInspection) => MapWorkbenchSpiderifyChooserListItem | null;
  onRuntimeOverlayFeatureHover?: (feature: {
    featureId: string | number | null;
    geometryType: string | null;
    properties: Record<string, unknown>;
  } | null) => void;
  onRuntimeOverlayFeatureClick?: (feature: {
    featureId: string | number | null;
    geometryType: string | null;
    properties: Record<string, unknown>;
  }) => void;
  onViewStateChange?: (next: MapWorkbenchViewState) => void;
  onHoverFeatureChange?: (feature: MapWorkbenchFeatureInspection | null) => void;
  onPinnedFeatureChange?: (
    feature: MapWorkbenchFeatureInspection | null,
    trigger: 'click' | 'context_menu',
  ) => void;
  onToolRuntimeEvent?: (event: MapWorkbenchToolRuntimeEvent) => void;
}

export function MapWorkbenchRuntimePreview({
  styleJson,
  mapExperience,
  themeColorPalette,
  viewState,
  status,
  defaultViewState,
  inspectableLayerIds,
  runtimeFeatureStateTargets,
  hydrateRuntimeFeatureState,
  runtimeOverlayGeoJSON,
  runtimeOverlayStyle = null,
  runtimeOverlayInteractive = false,
  isRuntimeOverlayFeatureInteractive,
  activeToolId,
  drawnGeometry,
  showFilterControl = false,
  filterControlActive = false,
  filterControlBadge = null,
  showLegendControl = false,
  legendControlActive = false,
  legendControlBadge = null,
  hideFloatingControls = false,
  scrollZoomEnabled = true,
  runtimeOverlayDebugLabel = null,
  onOpenFilters,
  onOpenLegend,
  resolveSpiderifyChooserItem,
  onRuntimeOverlayFeatureHover,
  onRuntimeOverlayFeatureClick,
  onViewStateChange,
  onHoverFeatureChange,
  onPinnedFeatureChange,
  onToolRuntimeEvent,
}: MapWorkbenchRuntimePreviewProps) {
  const DRAW_SOURCE_ID = '__workbench_draw_source__';
  const DRAW_FILL_LAYER_ID = '__workbench_draw_fill__';
  const DRAW_LINE_LAYER_ID = '__workbench_draw_line__';
  const DRAW_POINT_LAYER_ID = '__workbench_draw_point__';
  const DRAW_PREVIEW_LAYER_ID = '__workbench_draw_preview__';
  const DRAW_VERTEX_HIT_LAYER_ID = '__workbench_draw_vertex_hit__';
  const DRAW_VERTEX_LAYER_ID = '__workbench_draw_vertex__';
  const RUNTIME_OVERLAY_SOURCE_ID = '__workbench_runtime_overlay_source__';
  const RUNTIME_OVERLAY_FILL_LAYER_ID = '__workbench_runtime_overlay_fill__';
  const RUNTIME_OVERLAY_LINE_LAYER_ID = '__workbench_runtime_overlay_line__';
  const RUNTIME_OVERLAY_POINT_LAYER_ID = '__workbench_runtime_overlay_point__';
  const containerRef = useRef<HTMLDivElement | null>(null);
  const mapRef = useRef<MapLibreMap | null>(null);
  const viewCallbackRef = useRef(onViewStateChange);
  const hoverCallbackRef = useRef(onHoverFeatureChange);
  const pinCallbackRef = useRef(onPinnedFeatureChange);
  const runtimeOverlayHoverCallbackRef = useRef(onRuntimeOverlayFeatureHover);
  const runtimeOverlayClickCallbackRef = useRef(onRuntimeOverlayFeatureClick);
  const runtimeOverlayInteractivePredicateRef = useRef(isRuntimeOverlayFeatureInteractive);
  const toolRuntimeCallbackRef = useRef(onToolRuntimeEvent);
  const requestedViewRef = useRef<MapWorkbenchViewState | null | undefined>(viewState);
  const appliedStyleRef = useRef<Record<string, unknown> | null>(styleJson);
  const styleReloadGenerationRef = useRef(0);
  const runtimeFeatureStateGenerationRef = useRef(0);
  const runtimeFeatureStateRecordsRef = useRef<Map<string, Map<string, string[]>>>(new Map());
  const runtimeFeatureStateSignaturesRef = useRef<Map<string, string>>(new Map());
  const activeToolRef = useRef<MapBuiltInToolId>(activeToolId);
  const drawnGeometryRef = useRef<MapDrawnGeometry | null>(drawnGeometry);
  const drawingDraftRef = useRef<Array<[number, number]>>([]);
  const pointerPreviewRef = useRef<[number, number] | null>(null);
  const drawingSnapCloseRef = useRef(false);
  const draggedVertexRef = useRef<MapDrawingEditTarget | null>(null);
  const [spiderifyOverlay, setSpiderifyOverlay] = useState<MapWorkbenchSpiderifyOverlayState | null>(null);
  const spiderifyOverlayRef = useRef<MapWorkbenchSpiderifyOverlayState | null>(null);
  const spiderifyAnimationFrameRef = useRef<number | null>(null);
  const [spiderifyOverlayExpanded, setSpiderifyOverlayExpanded] = useState(false);
  const [activeSpiderCandidateKey, setActiveSpiderCandidateKey] = useState<string | null>(null);
  const [spiderifySheet, setSpiderifySheet] = useState<MapWorkbenchSpiderifySheetState | null>(null);
  const spiderifySheetRef = useRef<MapWorkbenchSpiderifySheetState | null>(null);
  const [preferSpiderifySheet, setPreferSpiderifySheet] = useState(false);
  const [isMobileViewport, setIsMobileViewport] = useState(false);
  const [mapLifecycleTick, setMapLifecycleTick] = useState(0);

  const bumpMapLifecycleTick = useCallback(() => {
    setMapLifecycleTick(current => current + 1);
  }, []);

  useEffect(() => {
    viewCallbackRef.current = onViewStateChange;
  }, [onViewStateChange]);

  useEffect(() => {
    hoverCallbackRef.current = onHoverFeatureChange;
  }, [onHoverFeatureChange]);

  useEffect(() => {
    pinCallbackRef.current = onPinnedFeatureChange;
  }, [onPinnedFeatureChange]);

  useEffect(() => {
    runtimeOverlayHoverCallbackRef.current = onRuntimeOverlayFeatureHover;
  }, [onRuntimeOverlayFeatureHover]);

  useEffect(() => {
    runtimeOverlayClickCallbackRef.current = onRuntimeOverlayFeatureClick;
  }, [onRuntimeOverlayFeatureClick]);

  useEffect(() => {
    runtimeOverlayInteractivePredicateRef.current = isRuntimeOverlayFeatureInteractive;
  }, [isRuntimeOverlayFeatureInteractive]);

  useEffect(() => {
    toolRuntimeCallbackRef.current = onToolRuntimeEvent;
  }, [onToolRuntimeEvent]);

  useEffect(() => {
    requestedViewRef.current = viewState;
  }, [viewState]);

  useEffect(() => {
    activeToolRef.current = activeToolId;
  }, [activeToolId]);

  useEffect(() => {
    drawnGeometryRef.current = drawnGeometry;
  }, [drawnGeometry]);

  useEffect(() => {
    spiderifyOverlayRef.current = spiderifyOverlay;
  }, [spiderifyOverlay]);

  useEffect(() => {
    spiderifySheetRef.current = spiderifySheet;
  }, [spiderifySheet]);

  useEffect(() => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') {
      return;
    }
    const coarsePointerQuery = window.matchMedia('(pointer: coarse)');
    const narrowViewportQuery = window.matchMedia('(max-width: 768px)');
    const updatePreference = () => {
      setIsMobileViewport(narrowViewportQuery.matches);
      setPreferSpiderifySheet(
        shouldUseMapWorkbenchSpiderifySheet({
          coarsePointer: coarsePointerQuery.matches,
          viewportWidth: narrowViewportQuery.matches ? 768 : window.innerWidth,
        }),
      );
    };
    updatePreference();

    const addListener = (query: MediaQueryList, listener: () => void) => {
      if (typeof query.addEventListener === 'function') {
        query.addEventListener('change', listener);
        return () => query.removeEventListener('change', listener);
      }
      query.addListener(listener);
      return () => query.removeListener(listener);
    };

    const removeCoarseListener = addListener(coarsePointerQuery, updatePreference);
    const removeNarrowListener = addListener(narrowViewportQuery, updatePreference);
    window.addEventListener('resize', updatePreference);
    return () => {
      removeCoarseListener();
      removeNarrowListener();
      window.removeEventListener('resize', updatePreference);
    };
  }, []);

  const reportViewState = useCallback(() => {
    if (!mapRef.current || !viewCallbackRef.current) {
      return;
    }
    const map = mapRef.current;
    const center = map.getCenter();
    viewCallbackRef.current({
      center: [center.lng, center.lat],
      zoom: map.getZoom(),
      bearing: map.getBearing(),
      pitch: map.getPitch(),
    });
  }, []);

  const withFeatureScreenPoint = useCallback(
    (
      feature: MapWorkbenchFeatureInspection | null,
      screenPoint: MapWorkbenchSpiderifyPoint | null,
    ): MapWorkbenchFeatureInspection | null => {
      if (!feature) {
        return null;
      }
      return {
        ...feature,
        screenPoint,
        rawJson: {
          ...feature.rawJson,
          screenPoint,
        },
      };
    },
    [],
  );

  const collapseSpiderifyOverlay = useCallback((options?: { clearHover?: boolean }) => {
    if (spiderifyAnimationFrameRef.current !== null && typeof window !== 'undefined') {
      window.cancelAnimationFrame(spiderifyAnimationFrameRef.current);
      spiderifyAnimationFrameRef.current = null;
    }
    setSpiderifyOverlayExpanded(false);
    setSpiderifyOverlay(null);
    setActiveSpiderCandidateKey(null);
    if (options?.clearHover !== false) {
      hoverCallbackRef.current?.(null);
    }
  }, []);

  const collapseSpiderifySheet = useCallback((options?: { clearHover?: boolean }) => {
    setSpiderifySheet(null);
    if (options?.clearHover !== false) {
      hoverCallbackRef.current?.(null);
    }
  }, []);

  const openSpiderifyOverlay = useCallback(
    ({
      anchor,
      trigger,
      features,
    }: {
      anchor: MapWorkbenchSpiderifyPoint;
      trigger: 'click' | 'context_menu';
      features: MapWorkbenchFeatureInspection[];
    }) => {
      const container = containerRef.current;
      const viewport =
        container
          ? {
              width: container.clientWidth,
              height: container.clientHeight,
            }
          : null;
      const candidates = buildMapWorkbenchSpiderifyLayout({
        anchor,
        features: features.map(feature =>
          withFeatureScreenPoint(feature, anchor),
        ).filter((feature): feature is MapWorkbenchFeatureInspection => Boolean(feature)),
        viewport,
      }).map(candidate => ({
        ...candidate,
        feature: withFeatureScreenPoint(candidate.feature, candidate.point) ?? candidate.feature,
      }));
      if (spiderifyAnimationFrameRef.current !== null && typeof window !== 'undefined') {
        window.cancelAnimationFrame(spiderifyAnimationFrameRef.current);
        spiderifyAnimationFrameRef.current = null;
      }
      setSpiderifyOverlay({
        anchor,
        trigger,
        candidates,
      });
      setSpiderifyOverlayExpanded(false);
      if (typeof window !== 'undefined') {
        spiderifyAnimationFrameRef.current = window.requestAnimationFrame(() => {
          spiderifyAnimationFrameRef.current = null;
          setSpiderifyOverlayExpanded(true);
        });
      } else {
        setSpiderifyOverlayExpanded(true);
      }
      setActiveSpiderCandidateKey(null);
      hoverCallbackRef.current?.(null);
    },
    [withFeatureScreenPoint],
  );

  const openSpiderifySheet = useCallback(
    ({
      anchor,
      trigger,
      features,
    }: {
      anchor: MapWorkbenchSpiderifyPoint;
      trigger: 'click' | 'context_menu';
      features: MapWorkbenchFeatureInspection[];
    }) => {
      setSpiderifySheet({
        anchor,
        trigger,
        candidates: features
          .map(feature => withFeatureScreenPoint(feature, anchor))
          .filter((feature): feature is MapWorkbenchFeatureInspection => Boolean(feature)),
      });
      hoverCallbackRef.current?.(null);
    },
    [withFeatureScreenPoint],
  );

  useEffect(() => {
    if (activeToolId !== 'select' && activeToolId !== 'inspect') {
      collapseSpiderifyOverlay();
      collapseSpiderifySheet();
    }
  }, [activeToolId, collapseSpiderifyOverlay, collapseSpiderifySheet]);

  useEffect(() => {
    if (status === 'loading' || status === 'error') {
      collapseSpiderifyOverlay();
      collapseSpiderifySheet();
    }
  }, [collapseSpiderifyOverlay, collapseSpiderifySheet, status]);

  useEffect(() => {
    return () => {
      debugMapWorkbenchStyleLifecycle('disposing map instance', {
        liveView: mapRef.current ? readCurrentMapViewState(mapRef.current) : null,
      });
      if (typeof window !== 'undefined') {
        const debugWindow = window as typeof window & { __debugMap?: MapLibreMap | null };
        if (debugWindow.__debugMap === mapRef.current) {
          debugWindow.__debugMap = null;
        }
      }
      if (spiderifyAnimationFrameRef.current !== null && typeof window !== 'undefined') {
        window.cancelAnimationFrame(spiderifyAnimationFrameRef.current);
        spiderifyAnimationFrameRef.current = null;
      }
      mapRef.current?.remove();
      mapRef.current = null;
    };
  }, []);

  useEffect(() => {
    if (typeof window === 'undefined') {
      return;
    }
    const map = mapRef.current;
    if (!map) {
      return;
    }
    const debugWindow = window as typeof window & { __debugMap?: MapLibreMap | null };
    debugWindow.__debugMap = map;
  }, [mapLifecycleTick]);

  useEffect(() => {
    const map = mapRef.current as (MapLibreMap & {
      setGlobalStateProperty?: (name: string, value: unknown) => void;
    }) | null;
    if (!map || !themeColorPalette) {
      return;
    }

    const applyThemeState = () => {
      if (!map.isStyleLoaded() || typeof map.setGlobalStateProperty !== 'function') {
        return;
      }
      Object.entries(themeColorPalette).forEach(([token, value]) => {
        if (typeof value === 'string' && value.trim()) {
          try {
            map.setGlobalStateProperty?.(token, value.trim());
          } catch {
            // Best-effort only; unsupported runtimes should keep style defaults.
          }
        }
      });
    };

    applyThemeState();
    map.on('styledata', applyThemeState);
    return () => {
      map.off('styledata', applyThemeState);
    };
  }, [mapLifecycleTick, themeColorPalette]);

  useEffect(() => {
    if (typeof window === 'undefined') {
      return;
    }
    const map = mapRef.current;
    if (!map || !styleJson) {
      return;
    }

    const logSalesLayer = () => {
      if (!map.isStyleLoaded()) {
        return;
      }
      const loadedStyle = map.getStyle();
      const salesLayers =
        loadedStyle?.layers?.filter(
          entry =>
            entry?.source === 'sales_locations' ||
            entry?.['source-layer'] === 'sales_locations' ||
            entry?.id?.includes('sales-locations'),
        ) ?? [];
      const debugWindow = window as typeof window & {
        __debugLoadedMapStyle?: Record<string, unknown> | null;
        __debugLoadedSalesLayers?: Array<Record<string, unknown>> | null;
        __debugInputStyleJson?: Record<string, unknown> | null;
      };
      debugWindow.__debugLoadedMapStyle = (loadedStyle as Record<string, unknown> | undefined) ?? null;
      debugWindow.__debugLoadedSalesLayers = salesLayers as Array<Record<string, unknown>>;
      debugWindow.__debugInputStyleJson = styleJson;
      console.debug('[MapWorkbench style debug] sales-locations-layers', salesLayers);
      console.debug('[MapWorkbench style debug] sales-style-state', {
        inputState: (styleJson as Record<string, unknown> | null | undefined)?.state ?? null,
        loadedState: (loadedStyle as Record<string, unknown> | null | undefined)?.state ?? null,
      });
    };

    logSalesLayer();
    map.on('styledata', logSalesLayer);
    return () => {
      map.off('styledata', logSalesLayer);
    };
  }, [mapLifecycleTick, styleJson]);

  useEffect(() => {
    const map = mapRef.current;
    if (!map || !styleJson || !mapExperience) {
      return;
    }

    const activeTargets = mapExperience.filter_sets
      .map(filterSet => {
        const activeFilters = filterSet.filters.filter(filter =>
          hasConfiguredMapExperienceFilterValue(
            filter.kind,
            resolveMapExperienceFilterValue(filter, mapExperience.active_filter_values[filterSet.id]?.[filter.id]),
          ),
        );
        return activeFilters.length > 0 ? { filterSet, activeFilters } : null;
      })
      .filter((entry): entry is { filterSet: MapExperienceFilterSet; activeFilters: MapExperienceFilterDefinition[] } => Boolean(entry));

    if (activeTargets.length === 0) {
      return;
    }

    const runDebug = () => {
      if (!map.isStyleLoaded()) {
        debugMapWorkbenchFilters('skipping source feature debug', {
          reason: 'style_not_loaded',
          targetCount: activeTargets.length,
        });
        return;
      }

      activeTargets.forEach(({ filterSet, activeFilters }) => {
        const compiledExpression = buildMapExperienceTargetFilterExpression(
          mapExperience,
          filterSet.source_id,
          filterSet.source_layer,
        );

        let sourceFeatures: Array<{ id?: unknown; properties?: Record<string, unknown> | null }> = [];
        try {
          sourceFeatures = map.querySourceFeatures(filterSet.source_id, {
            sourceLayer: filterSet.source_layer,
          }) as Array<{ id?: unknown; properties?: Record<string, unknown> | null }>;
        } catch (error) {
          debugMapWorkbenchFilters('querySourceFeatures failed', {
            sourceId: filterSet.source_id,
            sourceLayer: filterSet.source_layer,
            error: error instanceof Error ? error.message : String(error),
          });
          return;
        }

        const seen = new Set<string>();
        const uniqueFeatures = sourceFeatures.filter(feature => {
          const key = getDebugFeatureIdentifier(feature) ?? stableJson(feature.properties ?? {});
          if (seen.has(key)) {
            return false;
          }
          seen.add(key);
          return true;
        });

        const sampledFeatures = uniqueFeatures.slice(0, 25).map(feature => {
          const properties = feature.properties ?? {};
          const results = activeFilters
            .map(filter => buildActiveFilterDebugSummary(filter, mapExperience.active_filter_values[filterSet.id]?.[filter.id], properties))
            .filter((entry): entry is NonNullable<ReturnType<typeof buildActiveFilterDebugSummary>> => Boolean(entry));
          return {
            featureId: getDebugFeatureIdentifier(feature),
            results,
            included: results.every(result => result.passes),
          };
        });

        const failureCounts = sampledFeatures
          .flatMap(feature => feature.results.filter(result => !result.passes).map(result => result.failureReason))
          .reduce<Record<string, number>>((acc, reason) => {
            acc[reason] = (acc[reason] ?? 0) + 1;
            return acc;
          }, {});

        debugMapWorkbenchFilters('active target filter evaluation', {
          sourceId: filterSet.source_id,
          sourceLayer: filterSet.source_layer,
          filterSetId: filterSet.id,
          compiledExpression,
          activeValues: mapExperience.active_filter_values[filterSet.id] ?? {},
          queriedSourceFeatureCount: sourceFeatures.length,
          uniqueSourceFeatureCount: uniqueFeatures.length,
          sampledFeatureCount: sampledFeatures.length,
          sampledIncludedCount: sampledFeatures.filter(feature => feature.included).length,
          failureCounts,
          sampledFeatures,
        });
      });
    };

    if (typeof window === 'undefined') {
      runDebug();
      return;
    }

    const timer = window.setTimeout(runDebug, 0);
    return () => window.clearTimeout(timer);
  }, [mapExperience, mapLifecycleTick, styleJson]);

  const spiderifySheetItems = useMemo(
    () => {
      const candidates = spiderifySheet?.candidates ?? [];
      const fallbackItems = buildMapWorkbenchSpiderifyChooserItems(candidates);
      if (!resolveSpiderifyChooserItem) {
        return fallbackItems;
      }
      return fallbackItems.map((item, index) => {
        const candidate = candidates[index];
        if (!candidate) {
          return item;
        }
        return resolveSpiderifyChooserItem(candidate) ?? item;
      });
    },
    [resolveSpiderifyChooserItem, spiderifySheet],
  );
  const spiderifySheetTrigger = spiderifySheet?.trigger ?? 'click';
  const topControlItems = useMemo<MapViewerControlItem[]>(
    () => [
      ...(showFilterControl
        ? [
            {
              id: 'filters',
              label: 'Filters',
              icon: <Filter className="h-4 w-4" />,
              active: filterControlActive,
              badge: filterControlBadge,
            } satisfies MapViewerControlItem,
          ]
        : []),
      ...(showLegendControl
        ? [
            {
              id: 'legend',
              label: 'Legend',
              icon: <ScrollText className="h-4 w-4" />,
              active: legendControlActive,
              badge: legendControlBadge,
            } satisfies MapViewerControlItem,
          ]
        : []),
    ],
    [
      filterControlActive,
      filterControlBadge,
      legendControlActive,
      legendControlBadge,
      showFilterControl,
      showLegendControl,
    ],
  );
  const zoomControlItems = useMemo<MapViewerControlItem[]>(
    () => [
      { id: 'zoom-in', label: 'Zoom in', icon: <Plus className="h-4 w-4" /> },
      { id: 'zoom-out', label: 'Zoom out', icon: <Minus className="h-4 w-4" /> },
    ],
    [],
  );

  const handleControlAction = useCallback((actionId: string) => {
    const map = mapRef.current;
    if (actionId === 'filters') {
      onOpenFilters?.();
      return;
    }
    if (actionId === 'legend') {
      onOpenLegend?.();
      return;
    }
    if (!map) {
      return;
    }
    if (actionId === 'zoom-in') {
      map.zoomIn({ duration: 150 });
      return;
    }
    if (actionId === 'zoom-out') {
      map.zoomOut({ duration: 150 });
    }
  }, [onOpenFilters, onOpenLegend]);

  const buildPreviewSegments = useCallback((): Array<Array<[number, number]>> => {
    const draft = drawingDraftRef.current;
    const pointer = pointerPreviewRef.current;
    const currentTool = activeToolRef.current;
    if (!pointer || draft.length === 0 || !isDrawingToolId(currentTool) || currentTool === 'draw_point') {
      return [];
    }
    const previewSegments: Array<Array<[number, number]>> = [[draft[draft.length - 1], pointer]];
    if (currentTool === 'draw_polygon' && draft.length >= 2) {
      previewSegments.push([pointer, draft[0]]);
    }
    return previewSegments;
  }, []);

  const syncDrawingOverlay = useCallback(
    (map: MapLibreMap) => {
      const interactiveGeometry = buildInteractiveDrawingGeometry({
        activeToolId: activeToolRef.current,
        drawnGeometry,
        draftCoordinates: drawingDraftRef.current,
      });
      const data = buildDrawingOverlayFeatureCollection({
        geometry: interactiveGeometry,
        previewSegments: buildPreviewSegments(),
        showVertexHandles:
          isDrawingToolId(activeToolRef.current) &&
          activeToolRef.current !== 'draw_point' &&
          (Boolean(drawnGeometry && isGeometryCompatibleWithTool(activeToolRef.current, drawnGeometry)) ||
            drawingDraftRef.current.length > 0),
      });
      const existingSource = map.getSource(DRAW_SOURCE_ID) as { setData?: (value: unknown) => void } | undefined;
      if (existingSource?.setData) {
        existingSource.setData(data);
      } else {
        map.addSource(DRAW_SOURCE_ID, {
          type: 'geojson',
          data: data as any,
        } as any);
      }
      if (!map.getLayer(DRAW_FILL_LAYER_ID)) {
        map.addLayer({
          id: DRAW_FILL_LAYER_ID,
          type: 'fill',
          source: DRAW_SOURCE_ID,
          filter: ['all', ['==', ['geometry-type'], 'Polygon'], ['==', ['get', 'role'], 'geometry']],
          paint: {
            'fill-color': 'rgb(59 130 246)',
            'fill-opacity': 0.14,
          },
        } as any);
      }
      if (!map.getLayer(DRAW_LINE_LAYER_ID)) {
        map.addLayer({
          id: DRAW_LINE_LAYER_ID,
          type: 'line',
          source: DRAW_SOURCE_ID,
          filter: [
            'all',
            ['==', ['get', 'role'], 'geometry'],
            ['any', ['==', ['geometry-type'], 'LineString'], ['==', ['geometry-type'], 'Polygon']],
          ],
          paint: {
            'line-color': 'rgb(59 130 246)',
            'line-width': 2,
            'line-opacity': 0.9,
          },
        } as any);
      }
      if (!map.getLayer(DRAW_PREVIEW_LAYER_ID)) {
        map.addLayer({
          id: DRAW_PREVIEW_LAYER_ID,
          type: 'line',
          source: DRAW_SOURCE_ID,
          filter: ['==', ['get', 'role'], 'preview'],
          paint: {
            'line-color': 'rgb(59 130 246)',
            'line-width': 2,
            'line-opacity': 0.75,
            'line-dasharray': [2, 2],
          },
        } as any);
      }
      if (!map.getLayer(DRAW_POINT_LAYER_ID)) {
        map.addLayer({
          id: DRAW_POINT_LAYER_ID,
          type: 'circle',
          source: DRAW_SOURCE_ID,
          filter: ['all', ['==', ['geometry-type'], 'Point'], ['==', ['get', 'role'], 'geometry']],
          paint: {
            'circle-color': 'rgb(59 130 246)',
            'circle-radius': 6,
            'circle-stroke-color': 'rgb(255 255 255)',
            'circle-stroke-width': 2,
          },
        } as any);
      }
      if (!map.getLayer(DRAW_VERTEX_HIT_LAYER_ID)) {
        map.addLayer({
          id: DRAW_VERTEX_HIT_LAYER_ID,
          type: 'circle',
          source: DRAW_SOURCE_ID,
          filter: ['==', ['get', 'role'], 'vertex'],
          paint: {
            'circle-color': 'rgb(59 130 246)',
            'circle-radius': MAP_DRAW_VERTEX_HIT_TARGET_PX,
            'circle-opacity': 0.01,
          },
        } as any);
      }
      if (!map.getLayer(DRAW_VERTEX_LAYER_ID)) {
        map.addLayer({
          id: DRAW_VERTEX_LAYER_ID,
          type: 'circle',
          source: DRAW_SOURCE_ID,
          filter: ['==', ['get', 'role'], 'vertex'],
          paint: {
            'circle-color': 'rgb(255 255 255)',
            'circle-radius': 5,
            'circle-stroke-color': 'rgb(59 130 246)',
            'circle-stroke-width': 2,
          },
        } as any);
      }
    },
    [buildPreviewSegments, drawnGeometry],
  );

  const syncRuntimeOverlay = useCallback(
    (map: MapLibreMap) => {
      if (!map.isStyleLoaded()) {
        debugMapWorkbenchStyleLifecycle('syncRuntimeOverlay skipped: style not loaded', {
          overlay: summarizeWorkbenchRuntimeOverlay(runtimeOverlayGeoJSON),
        });
        return;
      }
      const data =
        runtimeOverlayGeoJSON ??
        ({
          type: 'FeatureCollection',
          features: [],
        } satisfies Record<string, unknown>);
      debugMapWorkbenchStyleLifecycle('syncRuntimeOverlay start', {
        overlay: summarizeWorkbenchRuntimeOverlay(runtimeOverlayGeoJSON),
        sourceExists: Boolean(map.getSource(RUNTIME_OVERLAY_SOURCE_ID)),
        fillLayerExists: Boolean(map.getLayer(RUNTIME_OVERLAY_FILL_LAYER_ID)),
        lineLayerExists: Boolean(map.getLayer(RUNTIME_OVERLAY_LINE_LAYER_ID)),
        pointLayerExists: Boolean(map.getLayer(RUNTIME_OVERLAY_POINT_LAYER_ID)),
      });
      const existingSource = map.getSource(RUNTIME_OVERLAY_SOURCE_ID) as { setData?: (value: unknown) => void } | undefined;
      if (existingSource?.setData) {
        existingSource.setData(data);
      } else {
        map.addSource(RUNTIME_OVERLAY_SOURCE_ID, {
          type: 'geojson',
          data: data as any,
        } as any);
      }
      if (!map.getLayer(RUNTIME_OVERLAY_FILL_LAYER_ID)) {
        map.addLayer({
          id: RUNTIME_OVERLAY_FILL_LAYER_ID,
          type: 'fill',
          source: RUNTIME_OVERLAY_SOURCE_ID,
          filter: [
            'any',
            ['==', ['geometry-type'], 'Polygon'],
            ['==', ['geometry-type'], 'MultiPolygon'],
          ],
          paint: {
            'fill-color': '#f97316',
            'fill-opacity': 0.18,
            ...(runtimeOverlayStyle?.fillPaint ?? {}),
          },
        } as any);
      }
      if (!map.getLayer(RUNTIME_OVERLAY_LINE_LAYER_ID)) {
        map.addLayer({
          id: RUNTIME_OVERLAY_LINE_LAYER_ID,
          type: 'line',
          source: RUNTIME_OVERLAY_SOURCE_ID,
          filter: [
            'any',
            ['==', ['geometry-type'], 'Polygon'],
            ['==', ['geometry-type'], 'MultiPolygon'],
            ['==', ['geometry-type'], 'LineString'],
            ['==', ['geometry-type'], 'MultiLineString'],
          ],
          paint: {
            'line-color': '#f97316',
            'line-width': ['interpolate', ['linear'], ['zoom'], 8, 2, 12, 4, 16, 6],
            'line-opacity': 0.95,
            ...(runtimeOverlayStyle?.linePaint ?? {}),
          },
        } as any);
      }
      if (!map.getLayer(RUNTIME_OVERLAY_POINT_LAYER_ID)) {
        map.addLayer({
          id: RUNTIME_OVERLAY_POINT_LAYER_ID,
          type: 'circle',
          source: RUNTIME_OVERLAY_SOURCE_ID,
          filter: ['==', ['geometry-type'], 'Point'],
          paint: {
            'circle-color': '#f97316',
            'circle-radius': 5,
            'circle-stroke-color': '#fff7ed',
            'circle-stroke-width': 2,
            ...(runtimeOverlayStyle?.pointPaint ?? {}),
          },
        } as any);
      }
      debugMapWorkbenchStyleLifecycle('syncRuntimeOverlay complete', {
        overlay: summarizeWorkbenchRuntimeOverlay(runtimeOverlayGeoJSON),
        sourceExists: Boolean(map.getSource(RUNTIME_OVERLAY_SOURCE_ID)),
        fillLayerExists: Boolean(map.getLayer(RUNTIME_OVERLAY_FILL_LAYER_ID)),
        lineLayerExists: Boolean(map.getLayer(RUNTIME_OVERLAY_LINE_LAYER_ID)),
        pointLayerExists: Boolean(map.getLayer(RUNTIME_OVERLAY_POINT_LAYER_ID)),
      });
      if (runtimeOverlayDebugLabel) {
        debugMapWorkbenchRuntimeOverlay('syncRuntimeOverlay diagnostics', {
          debugLabel: runtimeOverlayDebugLabel,
          overlay: summarizeWorkbenchRuntimeOverlay(runtimeOverlayGeoJSON),
          ...collectRuntimeOverlayDiagnostics(
            map,
            RUNTIME_OVERLAY_SOURCE_ID,
            RUNTIME_OVERLAY_FILL_LAYER_ID,
            RUNTIME_OVERLAY_LINE_LAYER_ID,
            RUNTIME_OVERLAY_POINT_LAYER_ID,
          ),
        });
        if (typeof globalThis.requestAnimationFrame === 'function') {
          globalThis.requestAnimationFrame(() => {
            if (!mapRef.current || mapRef.current !== map) {
              return;
            }
            debugMapWorkbenchRuntimeOverlay('post-render runtime overlay diagnostics', {
              debugLabel: runtimeOverlayDebugLabel,
              overlay: summarizeWorkbenchRuntimeOverlay(runtimeOverlayGeoJSON),
              ...collectRuntimeOverlayDiagnostics(
                map,
                RUNTIME_OVERLAY_SOURCE_ID,
                RUNTIME_OVERLAY_FILL_LAYER_ID,
                RUNTIME_OVERLAY_LINE_LAYER_ID,
                RUNTIME_OVERLAY_POINT_LAYER_ID,
              ),
            });
          });
        }
      }
    },
    [runtimeOverlayDebugLabel, runtimeOverlayGeoJSON, runtimeOverlayStyle],
  );

  const mapStyle = styleJson as StyleSpecification | null;

  const readCurrentMapViewState = useCallback((map: MapLibreMap): MapWorkbenchViewState => {
    const center = map.getCenter();
    return {
      center: [center.lng, center.lat],
      zoom: map.getZoom(),
      bearing: map.getBearing(),
      pitch: map.getPitch(),
    };
  }, []);

  useEffect(() => {
    let cancelled = false;
    if (!containerRef.current || !mapStyle) {
      return;
    }

    void (async () => {
      const { default: maplibregl } = await import('maplibre-gl');
      if (cancelled || !containerRef.current) {
        return;
      }

      const resolved = requestedViewRef.current ?? defaultViewState;
      if (!mapRef.current) {
        debugMapWorkbenchStyleLifecycle('creating map instance', {
          requestedView: requestedViewRef.current ?? null,
          defaultViewState,
          styleSummary: summarizeWorkbenchRuntimeStyle(styleJson),
        });
        mapRef.current = new maplibregl.Map({
          container: containerRef.current,
          style: mapStyle,
          center: resolved?.center ?? defaultViewState.center,
          zoom: resolved?.zoom ?? defaultViewState.zoom,
          bearing: resolved?.bearing ?? defaultViewState.bearing,
          pitch: resolved?.pitch ?? defaultViewState.pitch,
          attributionControl: false,
          transformRequest: (url: string) => ({ url: resolveMapRequestUrl(url) }),
        });
        bumpMapLifecycleTick();
        appliedStyleRef.current = styleJson;
        mapRef.current.on('moveend', reportViewState);
        mapRef.current.once('load', () => {
          reportViewState();
          requestedViewRef.current = null;
          if (mapRef.current) {
            syncDrawingOverlay(mapRef.current);
          }
          bumpMapLifecycleTick();
        });
        return;
      }

      const map = mapRef.current;
      const styleReloadGeneration = ++styleReloadGenerationRef.current;
      const liveView = readCurrentMapViewState(map);
      const styleTransitionDiagnostics = diagnoseWorkbenchRuntimeStyleTransition(appliedStyleRef.current, styleJson);
      debugMapWorkbenchStyleLifecycle('style update requested', {
        styleReloadGeneration,
        liveView,
        requestedView: requestedViewRef.current ?? null,
        diagnostics: styleTransitionDiagnostics,
      });
      if (styleTransitionDiagnostics.canPatch) {
        debugMapWorkbenchStyleLifecycle('patching runtime style in place', {
          styleReloadGeneration,
          liveView,
          diagnostics: styleTransitionDiagnostics,
        });
        applyWorkbenchRuntimeStylePatch(map, appliedStyleRef.current, styleJson);
        appliedStyleRef.current = styleJson;
        reportViewState();
        return;
      }
      debugMapWorkbenchStyleLifecycle('calling map.setStyle due to incompatible runtime style transition', {
        styleReloadGeneration,
        liveView,
        requestedView: requestedViewRef.current ?? null,
        diagnostics: styleTransitionDiagnostics,
      });
      map.setStyle(mapStyle, { diff: false });
      map.once('styledata', () => {
        if (cancelled || styleReloadGeneration !== styleReloadGenerationRef.current) {
          debugMapWorkbenchStyleLifecycle('skipping stale style reload callback', {
            styleReloadGeneration,
            activeStyleReloadGeneration: styleReloadGenerationRef.current,
            liveViewBeforeReload: liveView,
            requestedView: requestedViewRef.current ?? null,
          });
          return;
        }
        appliedStyleRef.current = styleJson;
        syncDrawingOverlay(map);
        debugMapWorkbenchStyleLifecycle('style reload completed', {
          liveViewBeforeReload: liveView,
          restoredView: liveView,
          requestedView: requestedViewRef.current ?? null,
        });
        reportViewState();
        requestedViewRef.current = null;
        bumpMapLifecycleTick();
      });
    })();

    return () => {
      cancelled = true;
    };
  }, [mapStyle, reportViewState, defaultViewState, syncDrawingOverlay, bumpMapLifecycleTick, readCurrentMapViewState]);

  useEffect(() => {
    if (!mapRef.current || !viewState) {
      return;
    }
    debugMapWorkbenchCameraLifecycle('external viewState jumpTo ignored', {
      currentView: readCurrentMapViewState(mapRef.current),
      requestedView: viewState,
    });
    requestedViewRef.current = null;
  }, [viewState, readCurrentMapViewState]);

  useEffect(() => {
    const map = mapRef.current;
    if (!map || !styleJson || !hydrateRuntimeFeatureState || !runtimeFeatureStateTargets?.length) {
      logRuntimeFeatureStateDebug('skipping effect bootstrap', {
        hasMap: Boolean(map),
        hasStyleJson: Boolean(styleJson),
        hasHydrator: Boolean(hydrateRuntimeFeatureState),
        targetCount: runtimeFeatureStateTargets?.length ?? 0,
      });
      return;
    }

    let cancelled = false;
    let refreshTimer: number | null = null;

    const clearScheduledRefresh = () => {
      if (refreshTimer != null && typeof window !== 'undefined') {
        window.clearTimeout(refreshTimer);
      }
      refreshTimer = null;
    };

    const applyFeatureStateRecord = (record: RuntimeFeatureStateRecord) => {
      const styleSourceId =
        runtimeFeatureStateTargets.find(
          target =>
            target.sourceId === record.source_id &&
            target.sourceLayer === record.source_layer,
        )?.sourceAlias ?? record.source_id;
      logRuntimeFeatureStateDebug('applying feature state record', {
        sourceId: record.source_id,
        styleSourceId,
        sourceLayer: record.source_layer,
        featureId: record.feature_id,
        stateKeys: Object.keys(record.state ?? {}),
        state: record.state ?? null,
      });
      try {
        map.setFeatureState(
          {
            source: styleSourceId,
            sourceLayer: record.source_layer,
            id: record.feature_id,
          },
          record.state as any,
        );
      } catch {
        // Feature state is best-effort because some rendered tiles may have paged out.
      }
    };

    const refreshRuntimeFeatureState = async () => {
      if (cancelled || !map.isStyleLoaded()) {
        logRuntimeFeatureStateDebug('aborting refresh before hydrate', {
          cancelled,
          styleLoaded: map.isStyleLoaded(),
        });
        return;
      }

      const generation = ++runtimeFeatureStateGenerationRef.current;
      logRuntimeFeatureStateDebug('starting refresh generation', {
        generation,
        targetCount: runtimeFeatureStateTargets.length,
        targets: runtimeFeatureStateTargets.map(target => ({
          key: target.key,
          sourceAlias: target.sourceAlias,
          sourceId: target.sourceId,
          sourceLayer: target.sourceLayer,
        })),
      });

      for (const target of runtimeFeatureStateTargets) {
        const queryLayerIds = collectRuntimeFeatureStateHydrationLayerIds({ styleJson, target })
          .filter(layerId => Boolean(map.getLayer(layerId)));
        logRuntimeFeatureStateDebug('resolved hydration layer ids', {
          generation,
          targetKey: target.key,
          sourceAlias: target.sourceAlias,
          sourceId: target.sourceId,
          sourceLayer: target.sourceLayer,
          queryLayerIds,
        });
        if (queryLayerIds.length === 0) {
          logRuntimeFeatureStateDebug('no queryable layers for target', {
            generation,
            targetKey: target.key,
            sourceAlias: target.sourceAlias,
            sourceId: target.sourceId,
            sourceLayer: target.sourceLayer,
          });
          continue;
        }

        const renderedFeatures = map.queryRenderedFeatures(undefined, { layers: queryLayerIds });
        const uniqueFeatures = new Map<string, RuntimeFeatureRef>();
        for (const feature of renderedFeatures) {
          const normalized = normalizeInspectedFeature(feature);
          if (!normalized) {
            continue;
          }
          const runtimeFeature = buildRuntimeFeatureRefForTarget(normalized, target);
          if (!runtimeFeature) {
            continue;
          }
          const featureKey = buildFeatureStateRecordKey(runtimeFeature.feature_id);
          if (!uniqueFeatures.has(featureKey)) {
            uniqueFeatures.set(featureKey, runtimeFeature);
          }
        }

        const featureList = Array.from(uniqueFeatures.values());
        logRuntimeFeatureStateDebug('collected visible features for target', {
          generation,
          targetKey: target.key,
          renderedFeatureCount: renderedFeatures.length,
          uniqueFeatureCount: featureList.length,
          featureIds: featureList.map(feature => feature.feature_id),
        });
        const featureSignature = JSON.stringify(
          featureList
            .map(feature => buildFeatureStateRecordKey(feature.feature_id))
            .sort(),
        );
        const previousRecords = runtimeFeatureStateRecordsRef.current.get(target.key) ?? new Map<string, string[]>();
        if (featureList.length === 0) {
          if (runtimeFeatureStateSignaturesRef.current.get(target.key) === featureSignature && previousRecords.size === 0) {
            logRuntimeFeatureStateDebug('no visible features and no changes for target', {
              generation,
              targetKey: target.key,
            });
            continue;
          }
          previousRecords.forEach((stateKeys, recordKey) => {
            const featureId = recordKey.startsWith('n:') ? Number(recordKey.slice(2)) : recordKey.slice(2);
            const clearedState = Object.fromEntries(stateKeys.map(key => [key, null]));
            applyFeatureStateRecord({
              source_id: target.sourceId,
              source_layer: target.sourceLayer,
              feature_id: featureId,
              state: clearedState,
            });
          });
          runtimeFeatureStateRecordsRef.current.delete(target.key);
          runtimeFeatureStateSignaturesRef.current.set(target.key, featureSignature);
          logRuntimeFeatureStateDebug('cleared feature state for empty target', {
            generation,
            targetKey: target.key,
            clearedCount: previousRecords.size,
          });
          continue;
        }

        if (runtimeFeatureStateSignaturesRef.current.get(target.key) === featureSignature) {
          logRuntimeFeatureStateDebug('skipping hydrate because feature signature is unchanged', {
            generation,
            targetKey: target.key,
            featureSignature,
          });
          continue;
        }

        logRuntimeFeatureStateDebug('calling hydrator', {
          generation,
          targetKey: target.key,
          featureCount: featureList.length,
          featureIds: featureList.map(feature => feature.feature_id),
        });
        const hydrated = await hydrateRuntimeFeatureState({
          target,
          features: featureList,
        });
        if (cancelled || generation !== runtimeFeatureStateGenerationRef.current) {
          logRuntimeFeatureStateDebug('discarding hydrator result because generation is stale', {
            generation,
            activeGeneration: runtimeFeatureStateGenerationRef.current,
            cancelled,
          });
          return;
        }
        logRuntimeFeatureStateDebug('hydrator returned records', {
          generation,
          targetKey: target.key,
          recordCount: hydrated?.records?.length ?? 0,
          recordFeatureIds: (hydrated?.records ?? []).map(record => record.feature_id),
        });

        const nextRecords = new Map<string, string[]>();
        for (const record of hydrated?.records ?? []) {
          const recordKey = buildFeatureStateRecordKey(record.feature_id);
          nextRecords.set(recordKey, Object.keys(record.state ?? {}));
          applyFeatureStateRecord(record);
        }

        previousRecords.forEach((stateKeys, recordKey) => {
          if (nextRecords.has(recordKey)) {
            return;
          }
          const featureId = recordKey.startsWith('n:') ? Number(recordKey.slice(2)) : recordKey.slice(2);
          const clearedState = Object.fromEntries(stateKeys.map(key => [key, null]));
          applyFeatureStateRecord({
            source_id: target.sourceId,
            source_layer: target.sourceLayer,
            feature_id: featureId,
            state: clearedState,
          });
        });

        runtimeFeatureStateRecordsRef.current.set(target.key, nextRecords);
        runtimeFeatureStateSignaturesRef.current.set(target.key, featureSignature);
        logRuntimeFeatureStateDebug('completed target refresh', {
          generation,
          targetKey: target.key,
          activeRecordCount: nextRecords.size,
          featureSignature,
        });
      }
    };

    const scheduleRefresh = (trigger: 'bootstrap' | 'moveend' | 'manual_refresh_event') => {
      clearScheduledRefresh();
      logRuntimeFeatureStateDebug('scheduling refresh', {
        targetCount: runtimeFeatureStateTargets.length,
        trigger,
      });
      if (typeof window === 'undefined') {
        void refreshRuntimeFeatureState();
        return;
      }
      refreshTimer = window.setTimeout(() => {
        refreshTimer = null;
        void refreshRuntimeFeatureState();
      }, 120);
    };

    const handleMoveEnd = () => {
      scheduleRefresh('moveend');
    };

    scheduleRefresh('bootstrap');
    map.on('moveend', handleMoveEnd);

    const handleRuntimeFeatureStateRefresh = () => {
      runtimeFeatureStateSignaturesRef.current.clear();
      logRuntimeFeatureStateDebug('received manual runtime feature-state refresh event');
      scheduleRefresh('manual_refresh_event');
    };
    if (typeof window !== 'undefined') {
      window.addEventListener(MAP_WORKBENCH_RUNTIME_FEATURE_STATE_REFRESH_EVENT, handleRuntimeFeatureStateRefresh);
    }

    return () => {
      cancelled = true;
      clearScheduledRefresh();
      map.off('moveend', handleMoveEnd);
      if (typeof window !== 'undefined') {
        window.removeEventListener(MAP_WORKBENCH_RUNTIME_FEATURE_STATE_REFRESH_EVENT, handleRuntimeFeatureStateRefresh);
      }
    };
  }, [hydrateRuntimeFeatureState, runtimeFeatureStateTargets, styleJson, mapLifecycleTick]);

  useEffect(() => {
    const map = mapRef.current;
    if (!map || !styleJson) {
      return;
    }

    const liveSources = Object.entries(
      styleJson.sources && typeof styleJson.sources === 'object'
        ? (styleJson.sources as Record<string, Record<string, unknown>>)
        : {},
    )
      .map(([alias, source]) => ({
        alias,
        dataUrl: typeof source?.data === 'string' ? source.data.trim() : '',
      }))
      .filter(({ dataUrl }) => Boolean(dataUrl) && MAP_SOURCE_GEOJSON_PATTERN.test(extractMapRequestPathname(dataUrl)));
    if (liveSources.length === 0) {
      return;
    }

    const buildRefreshUrl = (rawUrl: string) => {
      const absolute = resolveMapRequestUrl(rawUrl);
      try {
        const next = new URL(absolute);
        next.searchParams.set('_refresh', String(Date.now()));
        return next.toString();
      } catch {
        return absolute;
      }
    };

    const refreshLiveGeoJSONSources = () => {
      if (!map.isStyleLoaded()) {
        return;
      }
      liveSources.forEach(({ alias, dataUrl }) => {
        const source = map.getSource(alias) as { setData?: (value: unknown) => void } | undefined;
        if (!source?.setData) {
          return;
        }
        try {
          source.setData(buildRefreshUrl(dataUrl));
        } catch {
          // Live GeoJSON refresh is best-effort during style transitions.
        }
      });
    };

    if (typeof window === 'undefined') {
      return;
    }
    window.addEventListener(MAP_WORKBENCH_LIVE_GEOJSON_SOURCE_REFRESH_EVENT, refreshLiveGeoJSONSources);
    return () => {
      window.removeEventListener(MAP_WORKBENCH_LIVE_GEOJSON_SOURCE_REFRESH_EVENT, refreshLiveGeoJSONSources);
    };
  }, [styleJson]);

  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;
    if (isDrawingToolId(activeToolId)) {
      map.doubleClickZoom.disable();
      map.getCanvas().tabIndex = 0;
      map.getCanvas().focus();
      map.dragPan.disable();
      map.dragRotate.disable();
      map.touchZoomRotate.disableRotation();
    } else {
      map.doubleClickZoom.enable();
      map.dragPan.enable();
      map.dragRotate.enable();
      map.touchZoomRotate.enableRotation();
      drawingDraftRef.current = [];
      pointerPreviewRef.current = null;
      drawingSnapCloseRef.current = false;
      draggedVertexRef.current = null;
    }
    syncDrawingOverlay(map);
    if (activeToolId !== 'select' && activeToolId !== 'inspect') {
      hoverCallbackRef.current?.(null);
    }
  }, [activeToolId, drawnGeometry, syncDrawingOverlay]);

  useEffect(() => {
    const map = mapRef.current;
    if (!map) {
      return;
    }
    if (scrollZoomEnabled) {
      map.scrollZoom.enable();
      return;
    }
    map.scrollZoom.disable();
  }, [mapLifecycleTick, scrollZoomEnabled]);

  useEffect(() => {
    const map = mapRef.current;
    if (!map) {
      return;
    }
    let retrySettled = false;
    let handleLoad: (() => void) | null = null;
    let handleStyleData: (() => void) | null = null;
    const removeRetryListeners = () => {
      if (handleLoad) {
        map.off('load', handleLoad);
      }
      if (handleStyleData) {
        map.off('styledata', handleStyleData);
      }
    };
    const retrySyncRuntimeOverlay = (trigger: 'effect' | 'load' | 'styledata') => {
      if (retrySettled) {
        return;
      }
      if (!mapRef.current || mapRef.current !== map) {
        return;
      }
      debugMapWorkbenchStyleLifecycle('runtime overlay sync attempt', {
        trigger,
        styleLoaded: map.isStyleLoaded(),
        overlay: summarizeWorkbenchRuntimeOverlay(runtimeOverlayGeoJSON),
      });
      syncRuntimeOverlay(map);
      if (map.isStyleLoaded()) {
        retrySettled = true;
        removeRetryListeners();
      }
    };

    retrySyncRuntimeOverlay('effect');
    if (retrySettled) {
      return;
    }

    handleLoad = () => {
      retrySyncRuntimeOverlay('load');
    };
    handleStyleData = () => {
      retrySyncRuntimeOverlay('styledata');
    };

    map.on('load', handleLoad);
    map.on('styledata', handleStyleData);

    return () => {
      removeRetryListeners();
    };
  }, [mapLifecycleTick, runtimeOverlayGeoJSON, syncRuntimeOverlay]);

  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;

    const summarizeFeature = (feature: MapWorkbenchFeatureInspection | null) =>
      feature
        ? {
            id: feature.id ?? null,
            source: feature.source ?? null,
            sourceLayer: feature.sourceLayer ?? null,
            layerId: feature.layerId ?? null,
            geometryType: feature.geometryType ?? null,
          }
        : null;

    const hoverDebugEnabled = (() => {
      if (typeof window === 'undefined') {
        return false;
      }
      try {
        const searchParams = new URLSearchParams(window.location.search);
        if (searchParams.get('mapDebugHover') === '1') {
          return true;
        }
        if (searchParams.get('mapDebugInteractions') === '1') {
          return true;
        }
        return window.localStorage.getItem('map-workbench-debug-hover') === '1';
      } catch {
        return false;
      }
    })();
    const interactivityDebugEnabled = (() => {
      if (hoverDebugEnabled || typeof window === 'undefined') {
        return hoverDebugEnabled;
      }
      try {
        const searchParams = new URLSearchParams(window.location.search);
        if (searchParams.get('mapDebugInteractions') === '1') {
          return true;
        }
        return window.localStorage.getItem('map-workbench-debug-interactions') === '1';
      } catch {
        return false;
      }
    })();

    const logPreviewEvent = (eventName: string, payload?: Record<string, unknown>) => {
      if (!interactivityDebugEnabled) {
        return;
      }
      if (
        eventName === 'mousemove' ||
        eventName === 'mouseleave' ||
        eventName === 'click' ||
        eventName === 'contextmenu' ||
        eventName === 'listeners:attached' ||
        eventName === 'listeners:detached' ||
        eventName.startsWith('queryTopFeature') ||
        eventName.startsWith('queryFeatureStack') ||
        eventName.startsWith('mousemove:hover') ||
        eventName.startsWith('hover:') ||
        eventName.startsWith('click:') ||
        eventName.startsWith('context_menu:')
      ) {
        let serializedPayload = 'null';
        try {
          serializedPayload = JSON.stringify(payload ?? null);
        } catch {
          serializedPayload = '"[unserializable]"';
        }
        console.debug(`[map-workbench:interactivity] ${eventName} ${serializedPayload}`);
      }
    };

    const readLayerHitTargetPx = (layerId: string) => {
      const layer = map.getLayer(layerId) as {
        type?: string;
        metadata?: { workbench?: { interaction_hit_target_px?: unknown } };
      } | undefined;
      const rawValue = layer?.metadata?.workbench?.interaction_hit_target_px;
      const numericValue = typeof rawValue === 'number' ? rawValue : Number(rawValue);
      if (Number.isFinite(numericValue) && numericValue > 0) {
        return Math.round(numericValue);
      }
      if (layer?.type === 'symbol') {
        return 8;
      }
      return 0;
    };

    const readLayerPaintSnapshot = (layerId: string, layerType: string | null) => {
      if (!layerType) {
        return null;
      }
      const readPaint = (property: string) => {
        try {
          const value = map.getPaintProperty(layerId, property);
          return value === undefined ? null : value;
        } catch {
          return null;
        }
      };
      if (layerType === 'circle') {
        return {
          'circle-radius': readPaint('circle-radius'),
          'circle-stroke-width': readPaint('circle-stroke-width'),
          'circle-opacity': readPaint('circle-opacity'),
          'circle-blur': readPaint('circle-blur'),
          'circle-translate': readPaint('circle-translate'),
          'circle-pitch-scale': readPaint('circle-pitch-scale'),
          'circle-pitch-alignment': readPaint('circle-pitch-alignment'),
        };
      }
      if (layerType === 'symbol') {
        return {
          'icon-size': readPaint('icon-size'),
          'icon-opacity': readPaint('icon-opacity'),
          'text-opacity': readPaint('text-opacity'),
        };
      }
      return null;
    };

    const readLayerLayoutSnapshot = (layerId: string, layerType: string | null) => {
      if (!layerType) {
        return null;
      }
      const readLayout = (property: string) => {
        try {
          const value = map.getLayoutProperty(layerId, property);
          return value === undefined ? null : value;
        } catch {
          return null;
        }
      };
      if (layerType === 'circle') {
        return {
          visibility: readLayout('visibility'),
          sortKey: readLayout('circle-sort-key'),
        };
      }
      if (layerType === 'symbol') {
        return {
          visibility: readLayout('visibility'),
          'icon-image': readLayout('icon-image'),
          'icon-anchor': readLayout('icon-anchor'),
          'icon-allow-overlap': readLayout('icon-allow-overlap'),
          'text-field': readLayout('text-field'),
          'text-anchor': readLayout('text-anchor'),
          'text-allow-overlap': readLayout('text-allow-overlap'),
        };
      }
      return {
        visibility: readLayout('visibility'),
      };
    };

    const summarizeRuntimeLayer = (layerId: string) => {
      const layer = map.getLayer(layerId) as
        | {
            id?: string;
            type?: string;
            source?: string;
            'source-layer'?: string;
            layout?: { visibility?: unknown };
            filter?: unknown;
            metadata?: { workbench?: Record<string, unknown> };
          }
        | undefined;
      const layerType = layer?.type ?? null;
      return {
        id: layerId,
        exists: Boolean(layer),
        type: layerType,
        source: layer?.source ?? null,
        sourceLayer: layer?.['source-layer'] ?? null,
        visibility: layer?.layout?.visibility ?? null,
        filter: layer?.filter ?? null,
        runtimePaint: readLayerPaintSnapshot(layerId, layerType),
        runtimeLayout: readLayerLayoutSnapshot(layerId, layerType),
        workbench: layer?.metadata?.workbench ?? null,
      };
    };

    const summarizeReturnedFeature = (feature: MapGeoJSONFeature) => {
      const geometryCoordinates =
        feature.geometry?.type === 'Point' &&
        Array.isArray(feature.geometry.coordinates) &&
        feature.geometry.coordinates.length >= 2
          ? {
              lng: feature.geometry.coordinates[0],
              lat: feature.geometry.coordinates[1],
            }
          : null;
      const properties =
        feature.properties && typeof feature.properties === 'object'
          ? Object.fromEntries(
              Object.entries(feature.properties)
                .slice(0, 8)
                .map(([key, value]) => [key, value ?? null]),
            )
          : null;
      return {
        id: feature.id ?? null,
        source: feature.source ?? null,
        sourceLayer: feature.sourceLayer ?? null,
        layerId: feature.layer?.id ?? null,
        geometryType: feature.geometry?.type ?? null,
        coordinates: geometryCoordinates,
        properties,
      };
    };

    const queryFeatureStack = (point: PointLike) => {
      if (inspectableLayerIds.length === 0) {
        logPreviewEvent('queryFeatureStack:no-inspectable-layers');
        return {
          topFeature: null,
          candidates: [],
        };
      }
      const activeInspectableLayerIds = inspectableLayerIds.filter(layerId => Boolean(map.getLayer(layerId)));
      if (activeInspectableLayerIds.length === 0) {
        logPreviewEvent('queryFeatureStack:no-active-layers', {
          configuredInspectableLayerIds: inspectableLayerIds,
        });
        return {
          topFeature: null,
          candidates: [],
        };
      }
      const pointLike = point as { x?: number; y?: number };
      const groupedLayerIds = new Map<number, string[]>();
      activeInspectableLayerIds.forEach(layerId => {
        const hitTargetPx = readLayerHitTargetPx(layerId);
        const existing = groupedLayerIds.get(hitTargetPx) ?? [];
        existing.push(layerId);
        groupedLayerIds.set(hitTargetPx, existing);
      });
      // Prefer explicitly larger interaction targets before incidental geometry hits.
      // This prevents broad line/polygon layers from stealing clicks from authored point targets.
      const sortedGroups = Array.from(groupedLayerIds.entries()).sort(([left], [right]) => right - left);
      let features: MapGeoJSONFeature[] = [];
      for (const [hitTargetPx, layerIds] of sortedGroups) {
        const layerSummaries = layerIds.map(summarizeRuntimeLayer);
        const queryMode =
          hitTargetPx > 0 && typeof pointLike.x === 'number' && typeof pointLike.y === 'number'
            ? 'bbox'
            : 'point';
        const queryGeometry =
          queryMode === 'bbox'
            ? [
                [pointLike.x - hitTargetPx, pointLike.y - hitTargetPx],
                [pointLike.x + hitTargetPx, pointLike.y + hitTargetPx],
              ]
            : point;
        features = map.queryRenderedFeatures(queryGeometry as PointLike, { layers: layerIds });
        logPreviewEvent('queryFeatureStack:group-result', {
          point: typeof pointLike.x === 'number' && typeof pointLike.y === 'number' ? { x: pointLike.x, y: pointLike.y } : null,
          hitTargetPx,
          queryMode,
          queryGeometry: queryMode === 'bbox' ? queryGeometry : null,
          layerIds,
          layerSummaries,
          featureCount: features.length,
          returnedFeaturePreview: features.slice(0, 5).map(summarizeReturnedFeature),
          topRenderedFeature: features[0]
            ? {
                id: features[0].id ?? null,
                source: features[0].source ?? null,
                sourceLayer: features[0].sourceLayer ?? null,
                layerId: features[0].layer?.id ?? null,
                geometryType: features[0].geometry?.type ?? null,
              }
            : null,
        });
        if (features.length > 0) {
          break;
        }
        if (hitTargetPx > 0 && typeof pointLike.x === 'number' && typeof pointLike.y === 'number') {
          const fallbackHitTargetPx = 12;
          const fallbackFeatures = map.queryRenderedFeatures(
            [
              [pointLike.x - fallbackHitTargetPx, pointLike.y - fallbackHitTargetPx],
              [pointLike.x + fallbackHitTargetPx, pointLike.y + fallbackHitTargetPx],
            ] as PointLike,
            { layers: layerIds },
          );
          logPreviewEvent('queryFeatureStack:fallback-result', {
            point: { x: pointLike.x, y: pointLike.y },
            fallbackHitTargetPx,
            layerIds,
            featureCount: fallbackFeatures.length,
            returnedFeaturePreview: fallbackFeatures.slice(0, 5).map(summarizeReturnedFeature),
            topRenderedFeature: fallbackFeatures[0]
              ? {
                  id: fallbackFeatures[0].id ?? null,
                  source: fallbackFeatures[0].source ?? null,
                  sourceLayer: fallbackFeatures[0].sourceLayer ?? null,
                  layerId: fallbackFeatures[0].layer?.id ?? null,
                  geometryType: fallbackFeatures[0].geometry?.type ?? null,
                }
              : null,
          });
        }
      }
      const screenPoint =
        'x' in (point as any) && 'y' in (point as any)
          ? { x: Number((point as any).x), y: Number((point as any).y) }
          : null;
      const normalizedFeatures = features
        .map(feature => normalizeInspectedFeature(feature))
        .filter((feature): feature is MapWorkbenchFeatureInspection => Boolean(feature))
        .map(feature => withFeatureScreenPoint(feature, screenPoint))
        .filter((feature): feature is MapWorkbenchFeatureInspection => Boolean(feature));
      const topFeature = normalizedFeatures[0] ?? null;
      const candidates = selectMapWorkbenchSpiderifyFeatures({
        features: normalizedFeatures,
        maxCandidates: SPIDERIFY_MAX_CANDIDATES,
        predicate: feature => feature.geometryType === 'Point',
      });
      logPreviewEvent('queryFeatureStack:normalized', {
        point: typeof pointLike.x === 'number' && typeof pointLike.y === 'number' ? { x: pointLike.x, y: pointLike.y } : null,
        returnedFeatureCount: features.length,
        normalizedFeatureCount: normalizedFeatures.length,
        candidateCount: candidates.length,
        topFeature: summarizeFeature(topFeature),
      });
      return {
        topFeature,
        candidates,
      };
    };

    const queryVertexHandle = (point: PointLike): MapDrawingEditTarget | null => {
      const pointLike = point as { x?: number; y?: number };
      if (typeof pointLike.x !== 'number' || typeof pointLike.y !== 'number') {
        return null;
      }
      if (!isDrawingToolId(activeToolRef.current) || activeToolRef.current === 'draw_point') {
        return null;
      }
      const features = map.queryRenderedFeatures(
        [
          [pointLike.x - MAP_DRAW_VERTEX_HIT_TARGET_PX, pointLike.y - MAP_DRAW_VERTEX_HIT_TARGET_PX],
          [pointLike.x + MAP_DRAW_VERTEX_HIT_TARGET_PX, pointLike.y + MAP_DRAW_VERTEX_HIT_TARGET_PX],
        ] as PointLike,
        { layers: [DRAW_VERTEX_HIT_LAYER_ID] },
      );
      const match = features[0];
      const rawIndex = match?.properties?.vertex_index;
      const index = typeof rawIndex === 'number' ? rawIndex : Number(rawIndex);
      if (!Number.isFinite(index)) {
        return null;
      }
      return {
        source: drawnGeometryRef.current && isGeometryCompatibleWithTool(activeToolRef.current, drawnGeometryRef.current)
          ? 'committed'
          : 'draft',
        index,
      };
    };

    const queryRuntimeOverlayFeature = (point: PointLike) => {
      if (!runtimeOverlayInteractive) {
        return null;
      }
      const overlayLayerIds = [
        RUNTIME_OVERLAY_FILL_LAYER_ID,
        RUNTIME_OVERLAY_LINE_LAYER_ID,
        RUNTIME_OVERLAY_POINT_LAYER_ID,
      ].filter(layerId => Boolean(map.getLayer(layerId)));
      if (overlayLayerIds.length === 0) {
        return null;
      }
      const features = map.queryRenderedFeatures(point, { layers: overlayLayerIds });
      const feature = features[0];
      if (!feature) {
        return null;
      }
      const rawProperties =
        feature.properties && typeof feature.properties === 'object'
          ? (feature.properties as Record<string, unknown>)
          : {};
      const candidate = {
        featureId: feature.id ?? null,
        geometryType: feature.geometry?.type ?? null,
        properties: { ...rawProperties },
      };
      if (runtimeOverlayInteractivePredicateRef.current && !runtimeOverlayInteractivePredicateRef.current(candidate)) {
        return null;
      }
      return candidate;
    };

    const updatePointerPreview = (event: MapMouseEvent) => {
      const currentTool = activeToolRef.current;
      const draft = drawingDraftRef.current;
      if (!isDrawingToolId(currentTool) || currentTool === 'draw_point' || draft.length === 0) {
        pointerPreviewRef.current = null;
        drawingSnapCloseRef.current = false;
        return;
      }
      const livePoint: [number, number] = [event.lngLat.lng, event.lngLat.lat];
      if (currentTool === 'draw_polygon' && draft.length >= 3) {
        const start = draft[0];
        const projectedStart = map.project(start);
        const dx = projectedStart.x - event.point.x;
        const dy = projectedStart.y - event.point.y;
        const distance = Math.sqrt(dx * dx + dy * dy);
        if (distance <= MAP_DRAW_POLYGON_CLOSE_TOLERANCE_PX) {
          pointerPreviewRef.current = start;
          drawingSnapCloseRef.current = true;
          return;
        }
      }
      pointerPreviewRef.current = livePoint;
      drawingSnapCloseRef.current = false;
    };

    const emitDraftUpdate = (toolId: MapBuiltInToolId, draftCoordinates: Array<[number, number]>) => {
      const geometry = buildInteractiveDrawingGeometry({
        activeToolId: toolId,
        drawnGeometry: null,
        draftCoordinates,
      });
      if (geometry) {
        toolRuntimeCallbackRef.current?.({ kind: 'drawing_updated', tool_id: toolId, geometry, draft: true });
      }
    };

    const clearHoverState = (eventName: string) => {
      map.getCanvas().style.cursor = '';
      hoverCallbackRef.current?.(null);
      runtimeOverlayHoverCallbackRef.current?.(null);
      logPreviewEvent(eventName);
    };

    const completeCurrentDraft = (toolId: MapBuiltInToolId) => {
      const geometry = completeDrawingDraft(toolId, drawingDraftRef.current);
      if (!geometry) {
        return;
      }
      drawingDraftRef.current = [];
      pointerPreviewRef.current = null;
      drawingSnapCloseRef.current = false;
      toolRuntimeCallbackRef.current?.({ kind: 'drawing_completed', tool_id: toolId, geometry, draft: false });
      syncDrawingOverlay(map);
    };

    const handleMouseMove = (event: MapMouseEvent) => {
      const currentTool = activeToolRef.current;
      const buttonState =
        typeof event.originalEvent?.buttons === 'number'
          ? event.originalEvent.buttons
          : 0;
      logPreviewEvent('mousemove', {
        point: { x: event.point.x, y: event.point.y },
        lngLat: { lng: event.lngLat.lng, lat: event.lngLat.lat },
      });
      if (draggedVertexRef.current) {
        const dragPoint: [number, number] = [event.lngLat.lng, event.lngLat.lat];
        const dragTarget = draggedVertexRef.current;
        if (dragTarget.source === 'draft') {
          const nextDraft = updateCoordinateAtIndex(drawingDraftRef.current, dragTarget.index, dragPoint);
          drawingDraftRef.current = nextDraft;
          emitDraftUpdate(currentTool, nextDraft);
        } else if (drawnGeometryRef.current && isGeometryCompatibleWithTool(currentTool, drawnGeometryRef.current)) {
          const nextGeometry = updateDrawnGeometryVertex(drawnGeometryRef.current, dragTarget.index, dragPoint);
          drawnGeometryRef.current = nextGeometry;
          toolRuntimeCallbackRef.current?.({
            kind: 'drawing_updated',
            tool_id: currentTool,
            geometry: nextGeometry,
            draft: false,
          });
        }
        map.getCanvas().style.cursor = 'grabbing';
        syncDrawingOverlay(map);
        hoverCallbackRef.current?.(null);
        runtimeOverlayHoverCallbackRef.current?.(null);
        logPreviewEvent('mousemove:dragging-vertex', {
          dragTarget,
        });
        return;
      }
      if (currentTool !== 'select' && currentTool !== 'inspect') {
        updatePointerPreview(event);
        const handle = queryVertexHandle(event.point);
        map.getCanvas().style.cursor = handle ? 'grab' : drawingSnapCloseRef.current ? 'pointer' : 'crosshair';
        hoverCallbackRef.current?.(null);
        runtimeOverlayHoverCallbackRef.current?.(null);
        syncDrawingOverlay(map);
        logPreviewEvent('mousemove:drawing-mode', {
          currentTool,
          vertexHandle: handle,
          snapClose: drawingSnapCloseRef.current,
        });
        return;
      }
      if (buttonState !== 0 || map.isMoving()) {
        clearHoverState('mousemove:hover-suppressed-during-move');
        logPreviewEvent('mousemove:hover-suppressed-during-move', {
          buttonState,
          moving: map.isMoving(),
        });
        return;
      }
      if (spiderifyOverlayRef.current || spiderifySheetRef.current) {
        map.getCanvas().style.cursor = '';
        runtimeOverlayHoverCallbackRef.current?.(null);
        return;
      }
      const runtimeOverlayFeature = queryRuntimeOverlayFeature(event.point);
      const { topFeature: feature } = queryFeatureStack(event.point);
      if (runtimeOverlayFeature && feature?.geometryType !== 'Point') {
        map.getCanvas().style.cursor = 'pointer';
        hoverCallbackRef.current?.(null);
        runtimeOverlayHoverCallbackRef.current?.(runtimeOverlayFeature);
        logPreviewEvent('mousemove:runtime-overlay-hover', {
          point: { x: event.point.x, y: event.point.y },
          featureId: runtimeOverlayFeature.featureId,
          geometryType: runtimeOverlayFeature.geometryType,
        });
        return;
      }
      runtimeOverlayHoverCallbackRef.current?.(null);
      map.getCanvas().style.cursor = feature ? 'pointer' : '';
      hoverCallbackRef.current?.(feature);
      logPreviewEvent('mousemove:hover-result', {
        point: { x: event.point.x, y: event.point.y },
        feature: summarizeFeature(feature),
      });
    };

    const handleMouseLeave = () => {
      pointerPreviewRef.current = null;
      drawingSnapCloseRef.current = false;
      syncDrawingOverlay(map);
      if (spiderifyOverlayRef.current || spiderifySheetRef.current) {
        return;
      }
      clearHoverState('mouseleave');
    };

    const handleMouseDown = (event: MapMouseEvent) => {
      if (!isDrawingToolId(activeToolRef.current) || activeToolRef.current === 'draw_point') {
        return;
      }
      const handle = queryVertexHandle(event.point);
      if (!handle) {
        return;
      }
      draggedVertexRef.current = handle;
      event.preventDefault();
      hoverCallbackRef.current?.(null);
      runtimeOverlayHoverCallbackRef.current?.(null);
      map.getCanvas().style.cursor = 'grabbing';
    };

    const handleMouseUp = () => {
      if (!draggedVertexRef.current) {
        return;
      }
      draggedVertexRef.current = null;
      map.getCanvas().style.cursor = 'grab';
    };

    const handleMoveStart = () => {
      collapseSpiderifyOverlay();
      collapseSpiderifySheet();
      clearHoverState('hover:cleared-on-movestart');
    };

    const handleResolvedFeatureTrigger = (event: MapMouseEvent, trigger: 'click' | 'context_menu') => {
      const { topFeature, candidates } = queryFeatureStack(event.point);
      if (trigger === 'context_menu' && (topFeature || candidates.length > 1)) {
        event.originalEvent?.preventDefault?.();
      }
      if (candidates.length > 1) {
        const anchor = { x: event.point.x, y: event.point.y };
        const existingState = preferSpiderifySheet
          ? spiderifySheetRef.current
            ? {
                anchor: spiderifySheetRef.current.anchor,
                features: spiderifySheetRef.current.candidates,
              }
            : null
          : spiderifyOverlayRef.current
            ? {
                anchor: spiderifyOverlayRef.current.anchor,
                features: spiderifyOverlayRef.current.candidates.map(candidate => candidate.feature),
              }
            : null;
        if (
          existingState &&
          areMapWorkbenchSpiderifyStacksEquivalent({
            previousAnchor: existingState.anchor,
            nextAnchor: anchor,
            previousFeatures: existingState.features,
            nextFeatures: candidates,
          })
        ) {
          collapseSpiderifyOverlay();
          collapseSpiderifySheet();
          logPreviewEvent(`${trigger}:spiderify-collapse`, {
            point: anchor,
            candidateCount: candidates.length,
          });
          return;
        }
        collapseSpiderifyOverlay({ clearHover: false });
        collapseSpiderifySheet({ clearHover: false });
        if (preferSpiderifySheet) {
          openSpiderifySheet({
            anchor,
            trigger,
            features: candidates,
          });
        } else {
          openSpiderifyOverlay({
            anchor,
            trigger,
            features: candidates,
          });
        }
        logPreviewEvent(preferSpiderifySheet ? `${trigger}:spiderify-sheet-open` : `${trigger}:spiderify-open`, {
          point: anchor,
          candidateCount: candidates.length,
          topFeature: summarizeFeature(topFeature),
        });
        return;
      }
      collapseSpiderifyOverlay();
      collapseSpiderifySheet();
      logPreviewEvent(`${trigger}:pin-result`, {
        feature: summarizeFeature(topFeature),
      });
      pinCallbackRef.current?.(topFeature, trigger);
    };

    const handleClick = (event: MapMouseEvent) => {
      const currentTool = activeToolRef.current;
      logPreviewEvent('click', {
        point: { x: event.point.x, y: event.point.y },
        lngLat: { lng: event.lngLat.lng, lat: event.lngLat.lat },
      });
      if (isDrawingToolId(currentTool)) {
        const nextPoint: [number, number] = pointerPreviewRef.current ?? [event.lngLat.lng, event.lngLat.lat];
        if (currentTool === 'draw_point') {
          drawingDraftRef.current = [];
          pointerPreviewRef.current = null;
          const geometry: MapDrawnGeometry = { geometry_kind: 'point', coordinates: nextPoint };
          toolRuntimeCallbackRef.current?.({ kind: 'drawing_completed', tool_id: currentTool, geometry, draft: false });
          syncDrawingOverlay(map);
          return;
        }
        if (
          currentTool === 'draw_polygon' &&
          drawingSnapCloseRef.current &&
          drawingDraftRef.current.length >= 3
        ) {
          completeCurrentDraft(currentTool);
          return;
        }
        if (drawnGeometryRef.current && isGeometryCompatibleWithTool(currentTool, drawnGeometryRef.current)) {
          drawnGeometryRef.current = null;
          drawingDraftRef.current = [];
          toolRuntimeCallbackRef.current?.({ kind: 'drawing_started', tool_id: currentTool, geometry: { geometry_kind: getDrawingToolGeometryKind(currentTool) ?? 'line', coordinates: [nextPoint] } as MapDrawnGeometry, draft: true });
        }
        const nextDraft = [...drawingDraftRef.current, nextPoint];
        drawingDraftRef.current = nextDraft;
        pointerPreviewRef.current = null;
        drawingSnapCloseRef.current = false;
        const draftGeometry = buildInteractiveDrawingGeometry({
          activeToolId: currentTool,
          drawnGeometry: null,
          draftCoordinates: nextDraft,
        });
        if (draftGeometry) {
          toolRuntimeCallbackRef.current?.({
            kind: nextDraft.length === 1 ? 'drawing_started' : 'drawing_updated',
            tool_id: currentTool,
            geometry: draftGeometry,
            draft: true,
          });
        }
        if ((currentTool === 'measure_distance' || currentTool === 'street_distance') && nextDraft.length >= 2) {
          const completedGeometry: MapDrawnGeometry = { geometry_kind: 'line', coordinates: nextDraft.slice(0, 2) };
          drawingDraftRef.current = [];
          toolRuntimeCallbackRef.current?.({
            kind: 'drawing_completed',
            tool_id: currentTool,
            geometry: completedGeometry,
            draft: false,
          });
        }
        syncDrawingOverlay(map);
        return;
      }
      const runtimeOverlayFeature = queryRuntimeOverlayFeature(event.point);
      if (runtimeOverlayFeature) {
        const { topFeature } = queryFeatureStack(event.point);
        if (topFeature?.geometryType === 'Point') {
          handleResolvedFeatureTrigger(event, 'click');
          return;
        }
        runtimeOverlayClickCallbackRef.current?.(runtimeOverlayFeature);
        logPreviewEvent('click:runtime-overlay', {
          point: { x: event.point.x, y: event.point.y },
          featureId: runtimeOverlayFeature.featureId,
          geometryType: runtimeOverlayFeature.geometryType,
        });
        return;
      }
      handleResolvedFeatureTrigger(event, 'click');
    };

    const handleDoubleClick = (event: MapMouseEvent) => {
      const currentTool = activeToolRef.current;
      if (currentTool !== 'draw_line' && currentTool !== 'draw_polygon') {
        return;
      }
      event.preventDefault();
      if (canCompleteDrawingDraft(currentTool, drawingDraftRef.current)) {
        completeCurrentDraft(currentTool);
      }
    };

    const handleContextMenu = (event: MapMouseEvent) => {
      logPreviewEvent('contextmenu', {
        point: { x: event.point.x, y: event.point.y },
        lngLat: { lng: event.lngLat.lng, lat: event.lngLat.lat },
      });
      if (activeToolRef.current !== 'select' && activeToolRef.current !== 'inspect') {
        event.originalEvent?.preventDefault?.();
        logPreviewEvent('contextmenu:ignored-for-tool');
        return;
      }
      handleResolvedFeatureTrigger(event, 'context_menu');
    };

    const handleKeyDown = (event: KeyboardEvent) => {
      const currentTool = activeToolRef.current;
      if (event.key === 'Escape' && (spiderifyOverlayRef.current || spiderifySheetRef.current)) {
        collapseSpiderifyOverlay();
        collapseSpiderifySheet();
        return;
      }
      if (event.key === 'Escape' && drawingDraftRef.current.length > 0) {
        drawingDraftRef.current = [];
        pointerPreviewRef.current = null;
        drawingSnapCloseRef.current = false;
        draggedVertexRef.current = null;
        toolRuntimeCallbackRef.current?.({ kind: 'drawing_cleared', tool_id: currentTool, geometry: null });
        syncDrawingOverlay(map);
        return;
      }
      if (event.key !== 'Enter') {
        return;
      }
      if (!canCompleteDrawingDraft(currentTool, drawingDraftRef.current)) {
        return;
      }
      event.preventDefault();
      completeCurrentDraft(currentTool);
    };

    logPreviewEvent('listeners:attached', {
      mapLoaded: map.loaded(),
      canvasSize: {
        width: map.getCanvas().clientWidth,
        height: map.getCanvas().clientHeight,
      },
    });
    map.on('movestart', handleMoveStart);
    map.on('mousemove', handleMouseMove);
    map.on('mouseleave', handleMouseLeave);
    map.on('mousedown', handleMouseDown);
    map.on('mouseup', handleMouseUp);
    map.on('click', handleClick);
    map.on('dblclick', handleDoubleClick);
    map.on('contextmenu', handleContextMenu);
    map.getCanvas().addEventListener('keydown', handleKeyDown);
    return () => {
      logPreviewEvent('listeners:detached');
      map.off('movestart', handleMoveStart);
      map.off('mousemove', handleMouseMove);
      map.off('mouseleave', handleMouseLeave);
      map.off('mousedown', handleMouseDown);
      map.off('mouseup', handleMouseUp);
      map.off('click', handleClick);
      map.off('dblclick', handleDoubleClick);
      map.off('contextmenu', handleContextMenu);
      map.getCanvas().removeEventListener('keydown', handleKeyDown);
    };
  }, [
    inspectableLayerIds,
    mapLifecycleTick,
    onRuntimeOverlayFeatureClick,
    runtimeOverlayInteractive,
    syncDrawingOverlay,
  ]);

  return (
    <div className="relative h-full w-full">
      <div className="absolute inset-0">
        <div ref={containerRef} className="h-full w-full" />
      </div>
      {!hideFloatingControls && topControlItems.length > 0 ? (
        <div
          className="absolute right-3 top-14 z-20"
          style={{ right: 'calc(env(safe-area-inset-right, 0px) + 0.75rem)' }}
        >
          <MapViewerControlCluster mode="view" items={topControlItems} onAction={handleControlAction} />
        </div>
      ) : null}
      {!hideFloatingControls ? (
        <div
          className="absolute bottom-3 right-3 z-20"
          style={{
            right: isMobileViewport
              ? 'max(env(safe-area-inset-right, 0px), 0.75rem)'
              : 'calc(env(safe-area-inset-right, 0px) + 0.75rem)',
            bottom: isMobileViewport
              ? 'max(env(safe-area-inset-bottom, 0px), 5rem)'
              : 'calc(env(safe-area-inset-bottom, 0px) + 0.75rem)',
          }}
        >
          <MapViewerControlCluster mode="view" items={zoomControlItems} onAction={handleControlAction} />
        </div>
      ) : null}
      {spiderifyOverlay ? (
        <div className="pointer-events-none absolute inset-0 z-10">
          <svg className="absolute inset-0 h-full w-full" aria-hidden="true">
            {spiderifyOverlay.candidates.map(candidate => {
              const candidateX = spiderifyOverlayExpanded ? candidate.point.x : spiderifyOverlay.anchor.x;
              const candidateY = spiderifyOverlayExpanded ? candidate.point.y : spiderifyOverlay.anchor.y;
              return (
                <line
                  key={`line-${candidate.key}`}
                  x1={spiderifyOverlay.anchor.x}
                  y1={spiderifyOverlay.anchor.y}
                  x2={candidateX}
                  y2={candidateY}
                  stroke="currentColor"
                  strokeWidth="1.5"
                  className="text-border/80"
                  style={{
                    opacity: spiderifyOverlayExpanded ? 1 : 0,
                    transition: `opacity ${SPIDERIFY_OPEN_ANIMATION_MS}ms ease`,
                  }}
                />
              );
            })}
          </svg>
          <div
            className="absolute h-3 w-3 -translate-x-1/2 -translate-y-1/2 rounded-full border border-border/80 bg-background/95 shadow-sm"
            style={{
              left: spiderifyOverlay.anchor.x,
              top: spiderifyOverlay.anchor.y,
            }}
          />
          {spiderifyOverlay.candidates.map(candidate => {
            const active = activeSpiderCandidateKey === candidate.key;
            const label = String(candidate.index + 1);
            return (
              <button
                key={candidate.key}
                type="button"
                className={cn(
                  'pointer-events-auto absolute flex h-8 min-w-8 items-center justify-center rounded-full border px-2 text-[10px] font-semibold shadow-sm transition-colors',
                  active
                    ? 'border-primary bg-primary text-primary-foreground'
                    : 'border-border bg-background/95 text-foreground hover:border-primary/60 hover:bg-accent',
                )}
                style={{
                  left: spiderifyOverlayExpanded ? candidate.point.x : spiderifyOverlay.anchor.x,
                  top: spiderifyOverlayExpanded ? candidate.point.y : spiderifyOverlay.anchor.y,
                  opacity: spiderifyOverlayExpanded ? 1 : 0,
                  transform: spiderifyOverlayExpanded
                    ? 'translate(-50%, -50%) scale(1)'
                    : 'translate(-50%, -50%) scale(0.72)',
                  transition: `left ${SPIDERIFY_OPEN_ANIMATION_MS}ms ease, top ${SPIDERIFY_OPEN_ANIMATION_MS}ms ease, opacity ${SPIDERIFY_OPEN_ANIMATION_MS}ms ease, transform ${SPIDERIFY_OPEN_ANIMATION_MS}ms ease`,
                }}
                title={
                  candidate.feature.id != null
                    ? `${candidate.feature.sourceLayer ?? candidate.feature.layerId ?? 'Feature'} · ${String(candidate.feature.id)}`
                    : candidate.feature.sourceLayer ?? candidate.feature.layerId ?? 'Visible feature'
                }
                onMouseEnter={mouseEvent => {
                  mouseEvent.stopPropagation();
                  setActiveSpiderCandidateKey(candidate.key);
                  hoverCallbackRef.current?.(candidate.feature);
                }}
                onMouseLeave={mouseEvent => {
                  mouseEvent.stopPropagation();
                  setActiveSpiderCandidateKey(current => (current === candidate.key ? null : current));
                  hoverCallbackRef.current?.(null);
                }}
                onClick={mouseEvent => {
                  mouseEvent.preventDefault();
                  mouseEvent.stopPropagation();
                  collapseSpiderifyOverlay();
                  pinCallbackRef.current?.(candidate.feature, spiderifyOverlay.trigger);
                }}
                onContextMenu={mouseEvent => {
                  mouseEvent.preventDefault();
                  mouseEvent.stopPropagation();
                  collapseSpiderifyOverlay();
                  pinCallbackRef.current?.(candidate.feature, spiderifyOverlay.trigger);
                }}
              >
                <span className="max-w-[72px] truncate">{label}</span>
              </button>
            );
          })}
        </div>
      ) : null}
      <Sheet
        open={Boolean(spiderifySheet)}
        onOpenChange={open => {
          if (!open) {
            collapseSpiderifySheet();
          }
        }}
      >
        <SheetContent side="bottom" size="full" className="max-h-[80vh] overflow-hidden p-0">
          <div className="flex h-full flex-col">
            <SheetHeader className="px-4 pb-3 pt-4">
              <SheetTitle>Select a feature</SheetTitle>
              <SheetDescription>
                {spiderifySheet
                  ? `${spiderifySheet.candidates.length} features overlap at this location.`
                  : 'Choose one feature to continue.'}
              </SheetDescription>
            </SheetHeader>
            <div className="flex-1 overflow-y-auto px-4 pb-4">
              <div className="space-y-2">
                {spiderifySheet?.candidates.map((candidate, index) => {
                  const item = spiderifySheetItems[index];
                  return (
                    <button
                      key={item?.key ?? `${candidate.sourceLayer ?? candidate.layerId ?? 'feature'}:${candidate.id ?? index}:${index}`}
                      type="button"
                      className="flex w-full flex-col items-start gap-2 rounded-md border border-border bg-background px-4 py-3 text-left shadow-sm transition-colors hover:border-primary/50 hover:bg-accent"
                      onClick={() => {
                        collapseSpiderifySheet({ clearHover: false });
                        pinCallbackRef.current?.(candidate, spiderifySheetTrigger);
                      }}
                    >
                      <div className="flex w-full items-start justify-between gap-3">
                        <div className="min-w-0 space-y-1">
                          <div className="truncate text-sm font-medium text-foreground">
                            {item?.title ?? 'Visible feature'}
                          </div>
                          <div className="truncate text-xs text-muted-foreground">
                            {item?.subtitle ?? candidate.sourceLayer ?? candidate.layerId ?? 'Visible feature'}
                          </div>
                        </div>
                        <Badge variant="muted">{index + 1}</Badge>
                      </div>
                      {item && item.details.length > 0 ? (
                        <div className="flex flex-wrap gap-2 text-xs text-muted-foreground">
                          {item.details.map(detail => (
                            <span key={`${item.key}:${detail.label}`} className="truncate">
                              <span className="font-medium text-foreground">{detail.label}:</span> {detail.value}
                            </span>
                          ))}
                        </div>
                      ) : null}
                    </button>
                  );
                })}
              </div>
            </div>
          </div>
        </SheetContent>
      </Sheet>
      {status === 'loading' ? (
        <div className="absolute left-4 top-4 rounded bg-background/80 px-3 py-1 text-xs text-muted-foreground">
          Rendering preview…
        </div>
      ) : null}
    </div>
  );
}

export function MapWorkbench({
  styleId,
  assetMode,
  adapter,
  behavior,
  navigate,
  basemapTheme,
  basemapThemeLabel,
  canManageGlobal,
  defaultShareScope,
  shareScopeOptions,
  defaultViewState,
  defaultMode = 'author',
  mobileRightPaneDefaultOpen = true,
  viewerHideIdleRightPane = false,
  hideViewModeToolbar = false,
  rootClassName,
  runtimeOverlayGeoJSON,
  runtimeOverlayStyle = null,
  runtimeOverlayInteractive = false,
  isRuntimeOverlayFeatureInteractive,
  onRuntimeOverlayFeatureHover,
  onRuntimeOverlayFeatureClick,
  onPinnedFeatureChange,
  ai,
  resolveOwnership,
  renderDesktopAuthoringPane,
  renderRuntimeSurfaceSection,
  runtimeSurfacePanelProps,
  hydrateRuntimeFeatureState,
}: MapWorkbenchProps) {
  const centerPaneRef = useRef<HTMLDivElement | null>(null);
  const [style, setStyle] = useState<MapWorkbenchStyleRecord | null>(null);
  const [styleLoading, setStyleLoading] = useState(false);
  const [styleError, setStyleError] = useState<string | null>(null);

  const [sources, setSources] = useState<MapWorkbenchSource[]>([]);
  const [sourcesLoading, setSourcesLoading] = useState(false);
  const [sourcesError, setSourcesError] = useState<string | null>(null);
  const themeColorPalette = useResolvedThemeSemanticColorPalette(
    basemapTheme as Exclude<ThemeOption, 'auto'>,
  );

  const [spriteSets, setSpriteSets] = useState<MapWorkbenchSpriteSet[]>([]);
  const [spriteSetsLoading, setSpriteSetsLoading] = useState(false);
  const [spriteSetsError, setSpriteSetsError] = useState<string | null>(null);
  const [spriteIconSets, setSpriteIconSets] = useState<MapIconPickerSetGroup[]>([]);
  const [spriteIconsLoading, setSpriteIconsLoading] = useState(false);
  const [spriteIconsError, setSpriteIconsError] = useState<string | null>(null);

  const [glyphSets, setGlyphSets] = useState<MapWorkbenchGlyphSet[]>([]);
  const [glyphSetsLoading, setGlyphSetsLoading] = useState(false);
  const [glyphSetsError, setGlyphSetsError] = useState<string | null>(null);
  const [hostResolverCatalog, setHostResolverCatalog] = useState<MapExperienceResolverDefinition[] | null>(null);
  const [hostResolverConnections, setHostResolverConnections] = useState<MapExperienceResolverConnectionDefinition[] | null>(null);

  const [draftEngineState, dispatchDraftEngine] = useReducer(
    workbenchDraftEngineReducer,
    undefined,
    () => createWorkbenchDraftEngineState(),
  );
  const editorText = draftEngineState.draft.editorText;
  const appliedText = draftEngineState.preview.appliedText;
  const editorError = draftEngineState.preview.error;
  const previewStatus = draftEngineState.preview.status;
  const [previewStyle, setPreviewStyle] = useState<Record<string, unknown> | null>(null);
  const [previewView, setPreviewView] = useState<MapWorkbenchViewState | null>(null);
  const [advancedRulesWorkspaceOpen, setAdvancedRulesWorkspaceOpen] = useState(false);
  const [advancedRulesPreviewView, setAdvancedRulesPreviewView] = useState<MapWorkbenchViewState | null>(null);
  const [advancedRulesPreviewMode, setAdvancedRulesPreviewMode] = useState<MapStylingPreviewMode>('focused_sample');
  const [advancedRulesPreviewSampleSet, setAdvancedRulesPreviewSampleSet] = useState<MapStylingPreviewSampleSet>('viewport_sample');
  const [advancedRulesPreviewBackground, setAdvancedRulesPreviewBackground] = useState<MapStylingPreviewBackground>('none');
  const [advancedRulesLayout, setAdvancedRulesLayout] = useState<'standard' | 'editor_focus' | 'map_focus'>('standard');
  const [isMobileWorkbench, setIsMobileWorkbench] = useState(false);
  const [rightPaneWidth, setRightPaneWidth] = useState(480); // 30rem default
  const [activeRuleIdForStyleEditing, setActiveRuleIdForStyleEditing] = useState<string | null>(null);
  const isResizingRef = useRef(false);
  const previewViewRef = useRef<MapWorkbenchViewState | null>(null);
  const currentViewRef = useRef<MapWorkbenchViewState | null>(null);
  const previewViewWriteSequenceRef = useRef(0);
  const lastPreviewViewWriteRef = useRef<{
    sequence: number;
    source: string;
    previousPreviewView: MapWorkbenchViewState | null;
    previousCurrentView: MapWorkbenchViewState | null;
    nextView: MapWorkbenchViewState | null;
    metadata: Record<string, unknown> | null;
  } | null>(null);
  const previousCommittedPreviewViewRef = useRef<MapWorkbenchViewState | null>(null);
  const runtimeSurfacePanelSignatureRef = useRef<string | null>(null);
  const previewRequestIdRef = useRef(0);
  const themeSyncInitializedRef = useRef(false);

  const [saveError, setSaveError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [saveSheetOpen, setSaveSheetOpen] = useState(false);
  const [saveFormError, setSaveFormError] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);
  const [sourceFilter, setSourceFilter] = useState<'all' | MapWorkbenchShareScope>('all');
  const [sourceSearch, setSourceSearch] = useState('');
  const [sourcesPaneTab, setSourcesPaneTab] = useState<MapSourcesPaneTab>('layers');
  const [iconPickerSetFilter, setIconPickerSetFilter] = useState<'all' | string>('all');
  const [assetsSheetOpen, setAssetsSheetOpen] = useState(false);
  const [mountedSourceRefs, setMountedSourceRefs] = useState<Array<{
    alias: string;
    sourceId: string;
    name: string;
    shareScope: string | null;
  }>>([]);
  const [styleSheetTab, setStyleSheetTab] = useState<MapWorkbenchTab>('sources');
  const [desktopPaneSection, setDesktopPaneSection] = useState<MapWorkbenchDesktopAuthoringPaneSection>('sources');
  const [rightPaneCollapsed, setRightPaneCollapsed] = useState(() =>
    resolveInitialWorkbenchRightPaneCollapsed(mobileRightPaneDefaultOpen),
  );
  const [viewerFiltersSheetOpen, setViewerFiltersSheetOpen] = useState(false);
  const [viewerLegendSheetOpen, setViewerLegendSheetOpen] = useState(false);
  const [selectedStyleTargetKey, setSelectedStyleTargetKey] = useState<string | null>(null);
  const [editingStyleEntryId, setEditingStyleEntryId] = useState<string | null>(null);
  const [editingStyleOwner, setEditingStyleOwner] = useState<{ targetKey: string; profileId: string } | null>(null);
  const [selectedAdvancedRuleId, setSelectedAdvancedRuleId] = useState<string | null>(null);
  const [inspectionSheetOpen, setInspectionSheetOpen] = useState(false);
  const activeSpriteId = draftEngineState.draft.activeSpriteId;
  const glyphSetId = draftEngineState.draft.glyphSetId;
  const mapExperience = draftEngineState.draft.mapExperience;
  const mapExperienceRef = useRef(mapExperience);
  const previewMapExperienceRef = useRef(mapExperience);
  const debugRequestedMapExperienceRef = useRef<MapExperienceState | null>(null);
  const debugCompileExperienceRef = useRef<MapExperienceState | null>(null);
  const debugCompiledRuntimePreviewStyleRef = useRef<Record<string, unknown> | null>(null);
  const appliedMapExperience = draftEngineState.preview.appliedMapExperience;
  const [aiPanelOpen, setAiPanelOpen] = useState(false);
  const [aiProvider, setAiProvider] = useState<MapWorkbenchAiProvider>('openai');
  const [aiPrompt, setAiPrompt] = useState('');
  const [aiError, setAiError] = useState<string | null>(null);
  const [aiPatchText, setAiPatchText] = useState<string | null>(null);
  const [aiGenerating, setAiGenerating] = useState(false);
  const [hoveredFeature, setHoveredFeature] = useState<MapWorkbenchFeatureInspection | null>(null);
  const [pinnedFeature, setPinnedFeature] = useState<MapWorkbenchFeatureInspection | null>(null);
  const [pinnedFeatureTrigger, setPinnedFeatureTrigger] = useState<'click' | 'context_menu' | null>(null);
  const [activeToolId, setActiveToolId] = useState<MapBuiltInToolId>('select');
  const [drawnGeometry, setDrawnGeometry] = useState<MapDrawnGeometry | null>(null);
  const [actionPanelOpen, setActionPanelOpen] = useState(false);
  const [actionSurface, setActionSurface] = useState<MapWorkbenchResolvedFeatureSurface | null>(null);
  const [actionSurfaceFeature, setActionSurfaceFeature] = useState<MapWorkbenchFeatureInspection | null>(null);
  const [viewerFilterState, setViewerFilterState] = useState<WorkbenchViewerFilterRuntimeState>({});
  const [actionPanelSource, setActionPanelSource] = useState<'manual' | 'runtime' | null>(null);
  const [manualHighlightDescriptors, setManualHighlightDescriptors] = useState<MapWorkbenchRuntimeHighlightDescriptor[]>([]);
  const [hoverInteractivityState, setHoverInteractivityState] = useState<MapWorkbenchResolvedInteractivityState>(
    EMPTY_WORKBENCH_INTERACTIVITY_STATE,
  );
  const [clickInteractivityState, setClickInteractivityState] = useState<MapWorkbenchResolvedInteractivityState>(
    EMPTY_WORKBENCH_INTERACTIVITY_STATE,
  );
  const [contextMenuInteractivityState, setContextMenuInteractivityState] = useState<MapWorkbenchResolvedInteractivityState>(
    EMPTY_WORKBENCH_INTERACTIVITY_STATE,
  );
  const [presetDraftLabel, setPresetDraftLabel] = useState('');
  const [activeInteractivityEditor, setActiveInteractivityEditor] = useState<MapWorkbenchInteractivityEditorState | null>(null);
  const [saveForm, setSaveForm] = useState<{
    name: string;
    description: string;
    shareScope: MapWorkbenchShareScope;
  }>({
    name: '',
    description: '',
    shareScope: defaultShareScope,
  });

  const hoverDebugEnabled = useMemo(() => {
    if (typeof window === 'undefined') {
      return false;
    }
    try {
      const searchParams = new URLSearchParams(window.location.search);
      if (searchParams.get('mapDebugHover') === '1') {
        return true;
      }
      return window.localStorage.getItem('map-workbench-debug-hover') === '1';
    } catch {
      return false;
    }
  }, []);

  useEffect(() => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') {
      return;
    }
    const query = window.matchMedia('(max-width: 768px)');
    const update = () => setIsMobileWorkbench(query.matches);
    update();
    if (typeof query.addEventListener === 'function') {
      query.addEventListener('change', update);
      return () => query.removeEventListener('change', update);
    }
    query.addListener(update);
    return () => query.removeListener(update);
  }, []);

  useEffect(() => {
    if (isMobileWorkbench && !mobileRightPaneDefaultOpen) {
      setRightPaneCollapsed(true);
    }
  }, [isMobileWorkbench, mobileRightPaneDefaultOpen]);

  const summarizeHoverDebugFeature = useCallback((feature: MapWorkbenchFeatureInspection | null) => {
    if (!feature) {
      return null;
    }
    return {
      id: feature.id ?? null,
      source: feature.source ?? null,
      sourceLayer: feature.sourceLayer ?? null,
      layerId: feature.layerId ?? null,
      geometryType: feature.geometryType ?? null,
      screenPoint: feature.screenPoint ?? null,
    };
  }, []);

  const summarizeInteractivityTarget = useCallback(
    (
      target:
        | {
            key?: string | null;
            sourceId?: string | null;
            sourceLayer?: string | null;
            sourceAlias?: string | null;
            label?: string | null;
          }
        | null
        | undefined,
    ) => {
      if (!target) {
        return null;
      }
      return {
        key: target.key ?? null,
        sourceId: target.sourceId ?? null,
        sourceLayer: target.sourceLayer ?? null,
        sourceAlias: target.sourceAlias ?? null,
        label: target.label ?? null,
      };
    },
    [],
  );

  const logHoverStateEvent = useCallback(
    (eventName: string, payload?: Record<string, unknown>) => {
      if (!hoverDebugEnabled) {
        return;
      }
      let serializedPayload = 'null';
      try {
        serializedPayload = JSON.stringify(payload ?? null);
      } catch {
        serializedPayload = '"[unserializable]"';
      }
      console.debug(`[map-workbench:hover-state] ${eventName} ${serializedPayload}`);
    },
    [hoverDebugEnabled],
  );

  const logInteractivityEvent = useCallback(
    (eventName: string, payload?: Record<string, unknown>) => {
      if (!hoverDebugEnabled) {
        try {
          if (typeof window === 'undefined') {
            return;
          }
          const searchParams = new URLSearchParams(window.location.search);
          if (
            searchParams.get('mapDebugInteractions') !== '1' &&
            window.localStorage.getItem('map-workbench-debug-interactions') !== '1'
          ) {
            return;
          }
        } catch {
          return;
        }
      }
      let serializedPayload = 'null';
      try {
        serializedPayload = JSON.stringify(payload ?? null);
      } catch {
        serializedPayload = '"[unserializable]"';
      }
      console.debug(`[map-workbench:interactivity-state] ${eventName} ${serializedPayload}`);
    },
    [hoverDebugEnabled],
  );

  const handlePreviewHoverFeatureChange = useCallback(
    (feature: MapWorkbenchFeatureInspection | null) => {
      logHoverStateEvent('callback:onHoverFeatureChange', {
        incoming: summarizeHoverDebugFeature(feature),
      });
      setHoveredFeature(previous => {
        logHoverStateEvent('state:setHoveredFeature', {
          previous: summarizeHoverDebugFeature(previous),
          next: summarizeHoverDebugFeature(feature),
        });
        return feature;
      });
    },
    [logHoverStateEvent, summarizeHoverDebugFeature],
  );

  const loadDraftSnapshot = useCallback((snapshot: WorkbenchDraftSnapshot) => {
    dispatchDraftEngine({ type: 'load_snapshot', snapshot });
  }, []);

  const updateDraftState = useCallback((updater: (draft: WorkbenchDraftSnapshot) => WorkbenchDraftSnapshot) => {
    dispatchDraftEngine({ type: 'update_draft', updater });
  }, []);

  const updateEditorText = useCallback((nextText: string | ((current: string) => string)) => {
    updateDraftState(current => ({
      ...current,
      editorText: typeof nextText === 'function' ? nextText(current.editorText) : nextText,
    }));
  }, [updateDraftState]);

  const updateMapExperience = useCallback((updater: (current: MapExperienceState) => MapExperienceState) => {
    updateDraftState(current => ({
      ...current,
      mapExperience: updater(current.mapExperience),
    }));
  }, [updateDraftState]);

  const updateActiveSpriteId = useCallback((nextActiveSpriteId: string | null) => {
    updateDraftState(current => ({
      ...current,
      activeSpriteId: nextActiveSpriteId,
    }));
  }, [updateDraftState]);

  const updateGlyphSetId = useCallback((nextGlyphSetId: string | null) => {
    updateDraftState(current => ({
      ...current,
      glyphSetId: nextGlyphSetId,
    }));
  }, [updateDraftState]);

  const markPreviewStarted = useCallback(() => {
    dispatchDraftEngine({ type: 'preview_started' });
  }, []);

  const markPreviewFailed = useCallback((error: string) => {
    dispatchDraftEngine({ type: 'preview_failed', error });
  }, []);

  const setDraftEngineError = useCallback((error: string | null) => {
    dispatchDraftEngine({ type: 'set_error', error });
  }, []);

  const markPreviewApplied = useCallback((options?: {
    appliedText?: string;
    mapExperience?: MapExperienceState;
  }) => {
    dispatchDraftEngine({
      type: 'preview_succeeded',
      appliedText: options?.appliedText,
      mapExperience: options?.mapExperience,
    });
  }, []);

  const markDraftPersisted = useCallback(() => {
    dispatchDraftEngine({ type: 'save_succeeded' });
  }, []);

  const isScratchpad = !styleId;
  const isCustomMap = assetMode === 'custom-map' || behavior.isCustomMapAsset(style);
  const collectionPath = isCustomMap ? '/maps/custom-maps' : '/maps/styles';
  const ownership = resolveOwnership?.(style, isCustomMap) ?? { readOnly: false, mode: defaultMode };
  const resolvedMode = ownership.mode ?? (ownership.readOnly ? 'customize-save-copy' : defaultMode);
  const isAuthorMode = resolvedMode === 'author';
  const isViewMode = resolvedMode === 'view';
  const isCustomizeMode = resolvedMode === 'customize-save-copy';
  const canManageStructure = isAuthorMode;
  const canEditStyles = isAuthorMode;
  const canEditFilterDefinitions = isAuthorMode;
  const canEditFilterValues = true;
  const canEditPresets = !isViewMode;
  const canApplyChanges = true;
  const canSaveCurrent = isAuthorMode && !isScratchpad && !ownership.readOnly;
  const canSaveCopy = isScratchpad || isCustomizeMode || ownership.readOnly;
  const canUseAssets = isAuthorMode;
  const canUseAi = isAuthorMode && Boolean(ai);
  const canUseDiagnostics = isAuthorMode;
  const tabLabels = isAuthorMode ? AUTHOR_WORKBENCH_TAB_LABELS : VIEWER_WORKBENCH_TAB_LABELS;
  const visibleTabs = isAuthorMode ? AUTHOR_VISIBLE_TABS : VIEWER_VISIBLE_TABS;
  const routingToolSupported = false;
  const resolvedToolRailConfig = useMemo(
    () => ({
      default_tool_id:
        mapExperience.tools?.default_tool_id && mapExperience.tools.visible_tool_ids.includes(mapExperience.tools.default_tool_id)
          ? mapExperience.tools.default_tool_id
          : 'select',
      visible_tool_ids:
        mapExperience.tools?.visible_tool_ids && mapExperience.tools.visible_tool_ids.length > 0
          ? mapExperience.tools.visible_tool_ids
          : DEFAULT_VISIBLE_MAP_TOOL_IDS,
    }),
    [mapExperience.tools],
  );
  const visibleBuiltInTools = useMemo(
    () =>
      resolvedToolRailConfig.visible_tool_ids
        .map(toolId => MAP_BUILT_IN_TOOL_DEFINITIONS.find(entry => entry.id === toolId) ?? null)
        .filter((entry): entry is MapBuiltInToolDefinition => Boolean(entry)),
    [resolvedToolRailConfig.visible_tool_ids],
  );
  const activeFilterValueCount = useMemo(
    () =>
      mapExperience.filter_sets.reduce((count, filterSet) => {
        const activeValues = mapExperience.active_filter_values[filterSet.id] ?? {};
        return (
          count +
          filterSet.filters.filter(filter => hasConfiguredFilterValue(filter, activeValues[filter.id])).length
        );
      }, 0),
    [mapExperience.active_filter_values, mapExperience.filter_sets]
  );
  const viewerFilterDefinitions = useMemo(() => {
    const next = new Map<string, { filterSetId: string; filter: MapExperienceFilterDefinition }>();
    mapExperience.filter_sets.forEach(filterSet => {
      filterSet.filters
        .filter(isWorkbenchViewerEditableFilter)
        .forEach(filter => {
          next.set(buildWorkbenchViewerFilterItemId(filterSet.id, filter.id), {
            filterSetId: filterSet.id,
            filter,
          });
        });
    });
    return next;
  }, [mapExperience.filter_sets]);
  const previewMapExperience = useMemo<MapExperienceState>(
    () => ({
      ...mapExperience,
      active_filter_values: buildCompiledWorkbenchViewerActiveFilterValues(mapExperience, viewerFilterState),
    }),
    [mapExperience, viewerFilterState],
  );
  const viewerFilterSections = useMemo<MapViewerFilterSection[]>(() => {
    return mapExperience.filter_sets
      .map(filterSet => {
        const visibleFilters = filterSet.filters.filter(isWorkbenchViewerEditableFilter);
        if (visibleFilters.length === 0) {
          return null;
        }
        return {
          id: filterSet.id,
          title: filterSet.label?.trim() || formatFieldLabel(filterSet.source_layer),
          description: formatFieldLabel(filterSet.source_id),
          items: visibleFilters.map(filter => {
            const itemId = buildWorkbenchViewerFilterItemId(filterSet.id, filter.id);
            const runtimeEntry =
              viewerFilterState[itemId] ??
              resolveWorkbenchViewerFilterRuntimeEntry(
                filter,
                mapExperience.active_filter_values[filterSet.id]?.[filter.id],
              );
            return {
              id: itemId,
              label: filter.label?.trim() || formatFieldLabel(filter.field || filter.id),
              helperText: filter.description?.trim() || null,
              enabled: runtimeEntry.enabled,
              canToggleEnabled: filter.kind !== 'boolean',
              canEditValue: true,
              control: toWorkbenchViewerFilterControl(filter, runtimeEntry.value),
            };
          }),
        } satisfies MapViewerFilterSection;
      })
      .filter((entry): entry is MapViewerFilterSection => Boolean(entry));
  }, [mapExperience, viewerFilterState]);
  const serializedMapExperience = useMemo(
    () => JSON.stringify(serializeMapExperienceState(mapExperience)),
    [mapExperience, themeColorPalette]
  );
  const serializedPreviewMapExperience = useMemo(
    () => JSON.stringify(serializeMapExperienceState(previewMapExperience)),
    [previewMapExperience],
  );
  useEffect(() => {
    previewMapExperienceRef.current = previewMapExperience;
  }, [previewMapExperience]);
  const persistedSerializedMapExperience = useMemo(
    () => serializeWorkbenchMapExperience(draftEngineState.persisted.mapExperience),
    [draftEngineState.persisted.mapExperience],
  );
  const generatedJsonText = useMemo(
    () => (previewStyle ? JSON.stringify(previewStyle, null, 2) : ''),
    [previewStyle]
  );
  useEffect(() => {
    const nextDefault = resolvedToolRailConfig.default_tool_id ?? 'select';
    const visibleIds = resolvedToolRailConfig.visible_tool_ids;
    if (!visibleIds.includes(activeToolId)) {
      setActiveToolId(visibleIds[0] ?? nextDefault);
    }
  }, [activeToolId, resolvedToolRailConfig]);
  const hasPreviewPendingChanges =
    editorText.trim() !== appliedText.trim() ||
    serializedPreviewMapExperience !== appliedMapExperience;
  const hasUnsavedChanges =
    editorText.trim() !== draftEngineState.persisted.editorText.trim() ||
    serializedMapExperience !== persistedSerializedMapExperience;
  const activeAiKeySet = ai ? ai.hasProviderKey(aiProvider) : false;
  const statusBadgeLabel = isAuthorMode
    ? hasPreviewPendingChanges
      ? previewStatus === 'loading'
        ? 'Syncing'
        : 'Sync pending'
      : hasUnsavedChanges
        ? 'Unsaved'
        : 'Saved'
    : activeFilterValueCount === 0
      ? 'No filters'
      : `${activeFilterValueCount} active filter${activeFilterValueCount === 1 ? '' : 's'}`;

  useEffect(() => {
    if (!visibleTabs.includes(styleSheetTab)) {
      setStyleSheetTab(visibleTabs[0] ?? 'styles');
    }
  }, [styleSheetTab, visibleTabs]);

  useEffect(() => {
    if (styleSheetTab === 'styles') {
      setDesktopPaneSection('style');
      return;
    }
    if (styleSheetTab === 'filters') {
      setDesktopPaneSection('filters');
      return;
    }
    if (styleSheetTab === 'rules') {
      setDesktopPaneSection('advanced');
      return;
    }
    if (styleSheetTab === 'sources') {
      setDesktopPaneSection('sources');
    }
  }, [styleSheetTab]);

  useEffect(() => {
    if (canManageStructure) {
      return;
    }
    setAssetsSheetOpen(false);
    setAiPanelOpen(false);
  }, [canManageStructure]);

  useEffect(() => {
    mapExperienceRef.current = mapExperience;
  }, [mapExperience]);

  useEffect(() => {
    setViewerFilterState(buildInitialWorkbenchViewerFilterRuntimeState(mapExperience));
  }, [mapExperience.active_filter_values, mapExperience.filter_sets]);

  useEffect(() => {
    previewViewRef.current = previewView;
  }, [previewView]);

  const clearPinnedFeatureSelection = useCallback(() => {
    setPinnedFeature(null);
    setPinnedFeatureTrigger(null);
  }, []);

  const applyPreviewView = useCallback(
    (
      source: string,
      nextView: MapWorkbenchViewState | null,
      options?: {
        syncCurrentView?: boolean;
        metadata?: Record<string, unknown>;
      },
    ) => {
      const previousPreviewView = previewViewRef.current ?? null;
      const previousCurrentView = currentViewRef.current ?? null;
      const sequence = ++previewViewWriteSequenceRef.current;
      const metadata = options?.metadata ?? null;
      debugMapWorkbenchCameraLifecycle('previewView write requested', {
        sequence,
        source,
        previousPreviewView,
        previousCurrentView,
        nextView,
        metadata,
      });
      lastPreviewViewWriteRef.current = {
        sequence,
        source,
        previousPreviewView,
        previousCurrentView,
        nextView,
        metadata,
      };
      setPreviewView(nextView);
      if (options?.syncCurrentView) {
        currentViewRef.current = nextView;
      }
    },
    [],
  );

  const handlePreviewViewChange = useCallback((nextView: MapWorkbenchViewState) => {
    currentViewRef.current = nextView;
  }, []);

  useEffect(() => {
    debugMapWorkbenchCameraLifecycle('previewView state committed', {
      previousPreviewView: previousCommittedPreviewViewRef.current,
      committedPreviewView: previewView ?? null,
      currentView: currentViewRef.current ?? null,
      lastWrite: lastPreviewViewWriteRef.current,
    });
    previousCommittedPreviewViewRef.current = previewView ?? null;
  }, [previewView]);

  useEffect(() => {
    if (!advancedRulesWorkspaceOpen) {
      return;
    }
    setAdvancedRulesPreviewView(previewView ?? defaultViewState);
  }, [advancedRulesWorkspaceOpen, defaultViewState, previewView]);

  const handleResizeStart = useCallback((event: React.MouseEvent) => {
    event.preventDefault();
    isResizingRef.current = true;
    document.body.style.cursor = 'col-resize';
    document.body.style.userSelect = 'none';
  }, []);

  useEffect(() => {
    const handleResize = (event: MouseEvent) => {
      if (!isResizingRef.current) return;
      const nextWidth = window.innerWidth - event.clientX;
      if (nextWidth >= 320 && nextWidth <= window.innerWidth * 0.8) {
        setRightPaneWidth(nextWidth);
      }
    };

    const handleResizeEnd = () => {
      if (!isResizingRef.current) return;
      isResizingRef.current = false;
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
    };

    window.addEventListener('mousemove', handleResize);
    window.addEventListener('mouseup', handleResizeEnd);
    return () => {
      window.removeEventListener('mousemove', handleResize);
      window.removeEventListener('mouseup', handleResizeEnd);
    };
  }, []);

  useEffect(() => {
    let cancelled = false;
    if (!styleId) {
      setStyle(null);
      setStyleLoading(false);
      setStyleError(null);
      return;
    }
    setStyleLoading(true);
    setStyleError(null);
    void adapter
      .loadStyle(styleId)
      .then(nextStyle => {
        if (!cancelled) {
          setStyle(nextStyle);
        }
      })
      .catch(error => {
        if (!cancelled) {
          setStyleError(error instanceof Error ? error.message : 'Failed to load style.');
          setStyle(null);
        }
      })
      .finally(() => {
        if (!cancelled) {
          setStyleLoading(false);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [styleId, adapter]);

  useEffect(() => {
    let cancelled = false;
    setSourcesLoading(true);
    setSourcesError(null);
    void adapter
      .listSources({ share_scope: sourceFilter === 'all' ? undefined : sourceFilter })
      .then(items => {
        if (!cancelled) {
          setSources(items);
        }
      })
      .catch(error => {
        if (!cancelled) {
          setSourcesError(error instanceof Error ? error.message : 'Failed to load sources.');
          setSources([]);
        }
      })
      .finally(() => {
        if (!cancelled) {
          setSourcesLoading(false);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [sourceFilter, adapter]);

  useEffect(() => {
    let cancelled = false;
    setSpriteSetsLoading(true);
    setSpriteSetsError(null);
    void adapter
      .listSpriteSets({ share_scope: sourceFilter === 'all' ? undefined : sourceFilter })
      .then(items => {
        if (!cancelled) {
          setSpriteSets(items);
        }
      })
      .catch(error => {
        if (!cancelled) {
          setSpriteSetsError(error instanceof Error ? error.message : 'Failed to load sprite sets.');
          setSpriteSets([]);
        }
      })
      .finally(() => {
        if (!cancelled) {
          setSpriteSetsLoading(false);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [sourceFilter, adapter]);

  useEffect(() => {
    let cancelled = false;
    setGlyphSetsLoading(true);
    setGlyphSetsError(null);
    void adapter
      .listGlyphSets({ share_scope: sourceFilter === 'all' ? undefined : sourceFilter })
      .then(items => {
        if (!cancelled) {
          setGlyphSets(items);
        }
      })
      .catch(error => {
        if (!cancelled) {
          setGlyphSetsError(error instanceof Error ? error.message : 'Failed to load glyph sets.');
          setGlyphSets([]);
        }
      })
      .finally(() => {
        if (!cancelled) {
          setGlyphSetsLoading(false);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [sourceFilter, adapter]);

  useEffect(() => {
    let cancelled = false;

    const loadSpriteIcons = async () => {
      const spriteEntries = spriteSets.filter((entry): entry is MapWorkbenchSpriteSet & { id: string } => Boolean(entry.id));
      if (!spriteEntries.length) {
        setSpriteIconSets([]);
        setSpriteIconsError(null);
        setSpriteIconsLoading(false);
        return;
      }

      setSpriteIconsLoading(true);
      setSpriteIconsError(null);
      try {
        const manifests = await Promise.all(
          spriteEntries.map(async spriteSet => ({
            spriteSet,
            manifest: await adapter.getSpriteSetManifest(spriteSet.id),
            spriteSheet: await adapter.getSpriteSetImage(spriteSet.id),
            previewManifest:
              spriteSetHasPreviewBundle(spriteSet) && adapter.getSpriteSetPreviewManifest
                ? await adapter.getSpriteSetPreviewManifest(spriteSet.id)
                : null,
            previewSpriteSheet:
              spriteSetHasPreviewBundle(spriteSet) && adapter.getSpriteSetPreviewImage
                ? await adapter.getSpriteSetPreviewImage(spriteSet.id)
                : null,
          })),
        );

        if (cancelled) {
          return;
        }

        const nextIconSets: MapIconPickerSetGroup[] = await Promise.all(manifests.map(async ({
          spriteSet,
          manifest,
          spriteSheet,
          previewManifest,
          previewSpriteSheet,
        }) => {
          const setLabel = spriteSet.name || spriteSet.id;
          const previewUrls =
            previewSpriteSheet && previewManifest
              ? await buildSpriteIconPreviewUrls(previewManifest, previewSpriteSheet)
              : spriteSheet
                ? await buildSpriteIconPreviewUrls(manifest, spriteSheet)
                : {};
          const icons = Object.entries(manifest)
            .sort(([left], [right]) => left.localeCompare(right))
            .map(([token, entry]) => ({
              id: `${spriteSet.id}:${token}`,
              token,
              label: formatIconTokenLabel(token),
              setId: spriteSet.id,
              setLabel,
              previewUrl: previewUrls[token] ?? null,
              sdf: entry?.sdf ?? false,
              keywords: token.split(/[-_]+/).filter(Boolean),
            }));

          return {
            id: spriteSet.id,
            label: setLabel,
            description: spriteSet.description ?? null,
            shareScope: spriteSet.share_scope ? behavior.normalizeShareScope(spriteSet.share_scope) : null,
            badge: `${icons.length}`,
            icons,
          };
        }));

        if (cancelled) {
          return;
        }

        setSpriteIconSets(nextIconSets);
      } catch (error) {
        if (!cancelled) {
          setSpriteIconSets([]);
          setSpriteIconsError(error instanceof Error ? error.message : 'Failed to load sprite icons.');
        }
      } finally {
        if (!cancelled) {
          setSpriteIconsLoading(false);
        }
      }
    };

    void loadSpriteIcons();

    return () => {
      cancelled = true;
    };
  }, [adapter, behavior, spriteSets]);

  const parseJsonText = useCallback((text: string) => {
    if (!text.trim()) {
      return DEFAULT_STYLE_TEMPLATE;
    }
    return JSON.parse(text);
  }, []);

  const parseEditorJson = useCallback(() => parseJsonText(editorText), [parseJsonText, editorText]);
  const resolveAssetRootsForRuntime = useCallback(
    (styleJson: Record<string, unknown>) => {
      let nextStyle = styleJson;
      nextStyle = setStyleRootField(
        nextStyle,
        'sprite',
        buildSpriteRootValue(inferSpriteDependenciesFromStyle(behavior, styleJson), behavior.buildSpriteRuntimeUrl),
      );
      const glyphId = behavior.inferGlyphSetId(styleJson);
      if (glyphId) {
        nextStyle = setStyleRootStringField(nextStyle, 'glyphs', behavior.buildGlyphRuntimeUrl(glyphId));
      }
      return nextStyle;
    },
    [behavior],
  );

  const compileWorkbenchStyle = useCallback(
    (
      parsed: Record<string, unknown>,
      options?: {
        mapExperience?: MapExperienceState;
      }
    ) => {
      const requestedMapExperience = options?.mapExperience ?? previewMapExperienceRef.current;
      const canonicalParsed = canonicalizeStyleToInternalSourceRefs(parsed);
      const queryPlans = buildGeoJSONQueryPlans(canonicalParsed, sources, requestedMapExperience);
      const compileExperience = omitMapExperienceFilterValues(
        requestedMapExperience,
        queryPlans.flatMap(plan => plan.consumedFilters),
      );
      const experienceResult = compileStyleWithMapExperience(canonicalParsed, compileExperience, null, themeColorPalette);
      return {
        compiledStyle: experienceResult.styleJson,
        legends: experienceResult.legends,
        filterChips: [],
        queryPlans,
        requestedMapExperience,
      };
    },
    [sources, themeColorPalette]
  );

  const applyPreviewFromStyle = useCallback(
    async (
      parsed: Record<string, unknown>,
      options: {
        preserveView: boolean;
        nextText?: string;
        initialView?: MapWorkbenchViewState | null;
        mapExperience?: MapExperienceState;
        queryPlans?: MapWorkbenchGeoJSONQueryPlan[];
      }
    ) => {
      const requestId = ++previewRequestIdRef.current;
      const requestedMapExperience = options.mapExperience ?? previewMapExperienceRef.current;
      try {
        markPreviewStarted();
        const nextView = options.preserveView
          ? currentViewRef.current ?? previewViewRef.current
          : options.initialView ?? null;
        const canonicalParsed = canonicalizeStyleToInternalSourceRefs(parsed);
        const queryPlans =
          options.queryPlans ?? buildGeoJSONQueryPlans(canonicalParsed, sources, requestedMapExperience);
        const compileExperience = omitMapExperienceFilterValues(
          requestedMapExperience,
          queryPlans.flatMap(plan => plan.consumedFilters),
        );
        debugRequestedMapExperienceRef.current = requestedMapExperience;
        debugCompileExperienceRef.current = compileExperience;
        const derivedPreviewView = derivePreviewViewFromStyleBounds(canonicalParsed, sources);

        const localPreviewStyle =
          queryPlans.length > 0
            ? await hydrateStyleGeoJSONSources(canonicalParsed, queryPlans, adapter)
            : canonicalParsed;
        const assetResolvedPreviewStyle = resolveAssetRootsForRuntime(localPreviewStyle);
        const runtimePreviewStyle =
          behavior.rewritePreviewStyleToRuntimeSources(assetResolvedPreviewStyle) ?? assetResolvedPreviewStyle;
        const compiledRuntimePreviewStyle = compileStyleWithMapExperience(
          runtimePreviewStyle,
          compileExperience,
          null,
          themeColorPalette,
          'global-state',
        ).styleJson;
        debugCompiledRuntimePreviewStyleRef.current = compiledRuntimePreviewStyle;
        debugMapWorkbenchGlyphs('applyPreview.local', {
          authoredGlyphs: canonicalParsed.glyphs ?? null,
          assetResolvedGlyphs: assetResolvedPreviewStyle.glyphs ?? null,
          runtimeGlyphs: compiledRuntimePreviewStyle.glyphs ?? null,
          authoredSprite: canonicalParsed.sprite ?? null,
          assetResolvedSprite: assetResolvedPreviewStyle.sprite ?? null,
          runtimeSprite: compiledRuntimePreviewStyle.sprite ?? null,
          activeStyleProfiles: requestedMapExperience.active_style_profiles ?? {},
          renderStacks: requestedMapExperience.render_stacks ?? [],
          pointLayers: summarizePointStyleLayers(compiledRuntimePreviewStyle),
          managedLayers: summarizeManagedStyleLayers(compiledRuntimePreviewStyle),
        });
        const normalized = await normalizeStyleUrls(compiledRuntimePreviewStyle);
        const composed = await behavior.composeStyleWithBasemap(normalized, basemapTheme);
        if (requestId !== previewRequestIdRef.current) {
          return;
        }
        setPreviewStyle(composed);
        const nextPreviewView = nextView ?? derivedPreviewView ?? null;
        if (isViewMode && options.preserveView && !options.initialView) {
          debugMapWorkbenchCameraLifecycle('skipping previewView write from applyPreviewFromStyle in view mode', {
            requestId,
            preserveView: options.preserveView,
            requestedInitialView: options.initialView ?? null,
            currentView: currentViewRef.current ?? null,
            previewView: previewViewRef.current ?? null,
            derivedPreviewView: derivedPreviewView ?? null,
            skippedView: nextPreviewView,
          });
        } else {
          applyPreviewView('applyPreviewFromStyle', nextPreviewView, {
            metadata: {
              requestId,
              preserveView: options.preserveView,
              requestedInitialView: options.initialView ?? null,
              derivedPreviewView: derivedPreviewView ?? null,
            },
          });
        }
        markPreviewApplied({
          appliedText: options.nextText,
          mapExperience: requestedMapExperience,
        });
      } catch (err) {
        if (requestId !== previewRequestIdRef.current) {
          return;
        }
        const message = err instanceof Error ? err.message : 'Failed to render preview.';
        markPreviewFailed(message);
      }
    },
    [
      adapter,
      basemapTheme,
      behavior,
      markPreviewApplied,
      markPreviewFailed,
      markPreviewStarted,
      resolveAssetRootsForRuntime,
      isViewMode,
      applyPreviewView,
      sources,
      themeColorPalette,
    ]
  );

  const applyPreviewFromText = useCallback(
    async (
      text: string,
      options: {
        preserveView: boolean;
        initialView?: MapWorkbenchViewState | null;
        mapExperience?: MapExperienceState;
      }
    ) => {
      let parsed: Record<string, unknown>;
      const requestedMapExperience = options.mapExperience ?? previewMapExperienceRef.current;
      try {
        parsed = parseJsonText(text) as Record<string, unknown>;
      } catch (err) {
        const message = err instanceof Error ? err.message : 'Invalid JSON';
        markPreviewFailed(message);
        return;
      }
      const { queryPlans } = compileWorkbenchStyle(parsed, {
        mapExperience: requestedMapExperience,
      });
      await applyPreviewFromStyle(parsed, {
        preserveView: options.preserveView,
        nextText: text,
        initialView: options.initialView,
        mapExperience: requestedMapExperience,
        queryPlans,
      });
    },
    [applyPreviewFromStyle, compileWorkbenchStyle, parseJsonText]
  );

  useEffect(() => {
    if (styleId) {
      if (!style) return;
      const initialJson =
        behavior.buildOverlayStyleFromRuntime(style.style_json ?? DEFAULT_STYLE_TEMPLATE, style.metadata ?? null) ??
        DEFAULT_STYLE_TEMPLATE;
      debugMapWorkbenchGlyphs('loadStyle', {
        styleId,
        persistedGlyphs: (style.style_json as Record<string, unknown> | null | undefined)?.glyphs ?? null,
        persistedSprite: (style.style_json as Record<string, unknown> | null | undefined)?.sprite ?? null,
        overlayGlyphs:
          style.metadata?.overlay_style && typeof style.metadata.overlay_style === 'object'
            ? ((style.metadata.overlay_style as Record<string, unknown>).glyphs ?? null)
            : null,
        overlaySprite:
          style.metadata?.overlay_style && typeof style.metadata.overlay_style === 'object'
            ? ((style.metadata.overlay_style as Record<string, unknown>).sprite ?? null)
            : null,
        initialGlyphs: initialJson.glyphs ?? null,
        initialSprite: initialJson.sprite ?? null,
        inferredGlyphSetId:
          ((style.metadata?.glyph_ref as { id?: string } | undefined)?.id ?? behavior.inferGlyphSetId(initialJson)) ?? null,
        inferredSpriteRefs: inferSpriteDependenciesFromStyle(behavior, initialJson),
        activeSpriteId:
          ((style.metadata?.active_sprite_id as string | undefined) ?? inferSpriteDependenciesFromStyle(behavior, initialJson)[0]?.id) ?? null,
        activeStyleProfiles:
          style.metadata?.map_experience && typeof style.metadata.map_experience === 'object'
            ? ((style.metadata.map_experience as Record<string, unknown>).active_style_profiles ?? {})
            : {},
        renderStacks:
          style.metadata?.map_experience && typeof style.metadata.map_experience === 'object'
            ? ((style.metadata.map_experience as Record<string, unknown>).render_stacks ?? [])
            : [],
        managedLayers: summarizeManagedStyleLayers(initialJson),
      });
      const nextText = JSON.stringify(initialJson, null, 2);
      const loadedRules = loadLayerRuleBuilderState(style.metadata?.rule_builder);
      const loadedExperience = migrateLegacyLayerRulesToMapExperience(
        loadMapExperienceState(style.metadata?.map_experience),
        loadedRules,
      );
      const normalizedSnapshot = normalizeWorkbenchDraftSnapshotForMode(
        {
          editorText: nextText,
          mapExperience: loadedExperience,
          activeSpriteId:
            (typeof style.metadata?.active_sprite_id === 'string' && style.metadata.active_sprite_id.trim()) ||
            inferSpriteDependenciesFromStyle(behavior, initialJson)[0]?.id ||
            null,
          glyphSetId:
            (style.metadata?.glyph_ref as { id?: string } | undefined)?.id ??
            behavior.inferGlyphSetId(initialJson),
        },
        initialJson,
        sources,
        { materializeRenderedTargets: isAuthorMode },
      );
      const customMapRecord =
        style.metadata?.custom_map && typeof style.metadata.custom_map === 'object'
          ? (style.metadata.custom_map as { initial_view?: MapWorkbenchViewState | null })
          : null;
      const savedInitialView = customMapRecord?.initial_view ?? null;
      loadDraftSnapshot(normalizedSnapshot);
      setMountedSourceRefs(extractMountedSourceRefsFromStyle(initialJson, sources));
      setDraftEngineError(null);
      setSaveError(null);
      void applyPreviewFromText(nextText, {
        preserveView: false,
        initialView: savedInitialView,
        mapExperience: normalizedSnapshot.mapExperience,
      });
      return;
    }
    const nextText = JSON.stringify(DEFAULT_STYLE_TEMPLATE, null, 2);
    const emptySnapshot = normalizeWorkbenchDraftSnapshotForMode({
      editorText: nextText,
      mapExperience: createEmptyMapExperienceState(),
      activeSpriteId: null,
      glyphSetId: null,
    }, DEFAULT_STYLE_TEMPLATE, sources, { materializeRenderedTargets: isAuthorMode });
    loadDraftSnapshot(emptySnapshot);
    setMountedSourceRefs(extractMountedSourceRefsFromStyle(DEFAULT_STYLE_TEMPLATE, sources));
    setDraftEngineError(null);
    setSaveError(null);
    void applyPreviewFromText(nextText, {
      preserveView: false,
      initialView: null,
      mapExperience: emptySnapshot.mapExperience,
    });
  }, [applyPreviewFromText, behavior, isAuthorMode, loadDraftSnapshot, setDraftEngineError, sources, style, styleId]);

  useEffect(() => {
    if (!editorText.trim() || !hasPreviewPendingChanges) {
      return;
    }
    const timeoutId = window.setTimeout(() => {
      void applyPreviewFromText(editorText, { preserveView: true });
    }, AUTO_PREVIEW_DEBOUNCE_MS);
    return () => {
      window.clearTimeout(timeoutId);
    };
  }, [applyPreviewFromText, editorText, hasPreviewPendingChanges, serializedPreviewMapExperience]);

  useEffect(() => {
    if (!themeSyncInitializedRef.current) {
      themeSyncInitializedRef.current = true;
      return;
    }
    if (!editorText.trim()) {
      return;
    }
    void applyPreviewFromText(editorText, { preserveView: true });
  }, [applyPreviewFromText, basemapTheme, editorText]);

  useEffect(() => {
    if (!saveSheetOpen) {
      setSaveForm({
        name: '',
        description: '',
        shareScope: defaultShareScope,
      });
      setSaveFormError(null);
      setCreating(false);
    }
  }, [saveSheetOpen, defaultShareScope]);

  useEffect(() => {
    setHoveredFeature(null);
    setPinnedFeature(null);
    setPinnedFeatureTrigger(null);
    setPinnedFeatureTrigger(null);
    setDrawnGeometry(null);
    setActiveToolId(resolvedToolRailConfig.default_tool_id ?? 'select');
    setInspectionSheetOpen(false);
  }, [previewStyle, resolvedToolRailConfig.default_tool_id]);

  const filteredSources = useMemo(() => {
    const needle = sourceSearch.trim().toLowerCase();
    const authoringReadySources = sources.filter(source => extractVectorLayers(source).length > 0);
    if (!needle) return authoringReadySources;
    return authoringReadySources.filter(source =>
      [source.name, source.description, source.id]
        .filter(Boolean)
        .some(value => value!.toLowerCase().includes(needle))
    );
  }, [sources, sourceSearch]);

  const availableSourcesContext = useMemo(
    () =>
      sources
        .filter(source => extractVectorLayers(source).length > 0)
        .map(source => ({
        id: source.id,
        name: source.name,
        description: source.description,
        share_scope: source.share_scope,
        bounds: source.bounds,
        min_zoom: source.min_zoom,
        max_zoom: source.max_zoom,
        vector_layers: extractVectorLayers(source),
        raw_vector_layers: source.vector_layers ?? null,
        metadata: source.metadata ?? null,
      })),
    [sources]
  );

  const attachedSpriteRefs = useMemo(() => {
    try {
      return inferSpriteDependenciesFromStyle(behavior, parseEditorJson() as Record<string, unknown>);
    } catch {
      return [] as MapWorkbenchSpriteDependency[];
    }
  }, [behavior, parseEditorJson]);
  const activeSpriteRef = useMemo(
    () => resolveActiveSpriteDependency(attachedSpriteRefs, activeSpriteId),
    [activeSpriteId, attachedSpriteRefs],
  );
  const selectedSpriteSet = useMemo(
    () => spriteSets.find(entry => entry.id === activeSpriteRef?.spriteSetId) ?? null,
    [activeSpriteRef?.spriteSetId, spriteSets],
  );
  const activeSpriteIconSet = useMemo(
    () =>
      activeSpriteRef?.spriteSetId
        ? spriteIconSets.find(set => set.id === activeSpriteRef.spriteSetId) ?? null
        : null,
    [activeSpriteRef?.spriteSetId, spriteIconSets],
  );
  const activeSpriteIconSdfByToken = useMemo(
    () =>
      Object.fromEntries(
        (activeSpriteIconSet?.icons ?? []).map(icon => [icon.token, icon.sdf === true] as const),
      ),
    [activeSpriteIconSet],
  );
  const attachedSpriteIconSets = useMemo(
    () => spriteIconSets.filter(set => attachedSpriteRefs.some(ref => ref.spriteSetId === set.id)),
    [attachedSpriteRefs, spriteIconSets],
  );
  const legendIconMetadataByToken = useMemo(() => {
    const orderedSetIds = [
      ...attachedSpriteRefs.map(ref => ref.spriteSetId),
      ...spriteIconSets.map(set => set.id).filter(id => !attachedSpriteRefs.some(ref => ref.spriteSetId === id)),
    ];
    const next = new Map<string, { previewUrl: string | null; sdf: boolean }>();
    orderedSetIds.forEach(setId => {
      const iconSet = spriteIconSets.find(set => set.id === setId);
      iconSet?.icons.forEach(icon => {
        if (!next.has(icon.token)) {
          next.set(icon.token, {
            previewUrl: icon.previewUrl ?? null,
            sdf: icon.sdf === true,
          });
        }
      });
    });
    return next;
  }, [attachedSpriteRefs, spriteIconSets]);
  const resolvedIconPickerSetFilter = useMemo(
    () =>
      iconPickerSetFilter === 'all' || spriteIconSets.some(set => set.id === iconPickerSetFilter)
        ? iconPickerSetFilter
        : (activeSpriteRef?.spriteSetId ?? 'all'),
    [activeSpriteRef?.spriteSetId, iconPickerSetFilter, spriteIconSets],
  );

  const selectedGlyphSet = useMemo(
    () => {
      const inferredId =
        glyphSetId ??
        (() => {
          try {
            const parsed = parseEditorJson() as Record<string, unknown>;
            return behavior.inferGlyphSetId(parsed);
          } catch {
            return null;
          }
        })();
      return glyphSets.find(entry => entry.id === inferredId) ?? null;
    },
    [behavior, glyphSetId, glyphSets, parseEditorJson]
  );
  const buildResolvedGlyphUrl = useCallback((glyphSetId: string) => behavior.buildGlyphUrl(glyphSetId), [behavior]);
  const normalizeDraftAssetRoots = useCallback(
    (styleJson: Record<string, unknown>) => {
      let nextStyle = styleJson;
      nextStyle = setStyleRootField(
        nextStyle,
        'sprite',
        buildSpriteRootValue(inferSpriteDependenciesFromStyle(behavior, styleJson), behavior.buildSpriteUrl),
      );
      const glyphId = behavior.inferGlyphSetId(styleJson);
      if (glyphId) {
        nextStyle = setStyleRootStringField(nextStyle, 'glyphs', behavior.buildGlyphUrl(glyphId));
      }
      return nextStyle;
    },
    [behavior],
  );
  const ensureGlyphRootForSymbolText = useCallback(() => {
    let parsed: Record<string, unknown>;
    try {
      parsed = parseEditorJson() as Record<string, unknown>;
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Invalid JSON';
      setDraftEngineError(message);
      return;
    }

    if (typeof parsed.glyphs === 'string' && parsed.glyphs.trim()) {
      return;
    }

    const fallbackGlyphSet = selectedGlyphSet ?? glyphSets.find(entry => Boolean(entry.id)) ?? null;
    if (!fallbackGlyphSet?.id) {
      // A composed basemap may already provide a valid glyph root for preview/runtime text.
      // Avoid surfacing a false hard error when no tenant glyph bundles are mounted.
      setDraftEngineError(null);
      return;
    }

    updateGlyphSetId(fallbackGlyphSet.id);
    const nextStyle = setStyleRootStringField(parsed, 'glyphs', buildResolvedGlyphUrl(fallbackGlyphSet.id));
    updateEditorText(JSON.stringify(nextStyle, null, 2));
    setDraftEngineError(null);
  }, [buildResolvedGlyphUrl, glyphSets, parseEditorJson, selectedGlyphSet, setDraftEngineError, updateEditorText, updateGlyphSetId]);

  useEffect(() => {
    setMountedSourceRefs(current =>
      current.map(entry => {
        const sourceMeta = sources.find(source => source.id === entry.sourceId) ?? null;
        return {
          ...entry,
          name: sourceMeta?.name ?? entry.name,
          shareScope: sourceMeta?.share_scope ?? entry.shareScope,
        };
      }),
    );
  }, [sources]);

  const sourceLayerGroups = useMemo(() => {
    try {
      const parsed = parseEditorJson() as Record<string, unknown>;
      const styleLayers = Array.isArray(parsed.layers) ? (parsed.layers as Array<Record<string, unknown>>) : [];
      const styleSources =
        parsed.sources && typeof parsed.sources === 'object'
          ? (parsed.sources as Record<string, Record<string, unknown>>)
          : {};

      return mountedSourceRefs.map(sourceRef => {
        const sourceMeta = sources.find(entry => entry.id === sourceRef.sourceId) ?? null;
        const vectorLayers = extractVectorLayers(sourceMeta);

        return {
          ...sourceRef,
          vectorLayers: vectorLayers.map((layerMeta, index) => {
            const matchingLayers = styleLayers.filter(layer =>
              matchesWorkbenchLayerTarget(layer, sourceRef.alias, layerMeta.id, styleSources),
            );

            const managedLayerCount = matchingLayers.filter(layer => {
              const workbenchMeta =
                layer.metadata && typeof layer.metadata === 'object'
                  ? ((layer.metadata as Record<string, unknown>).workbench as Record<string, unknown> | undefined)
                  : undefined;
              return Boolean(workbenchMeta?.managed);
            }).length;

            return {
              ...layerMeta,
              index,
              rendered: matchingLayers.length > 0,
              managedRendered: managedLayerCount > 0,
              styleLayerCount: matchingLayers.length,
            };
          }),
          renderedCount: vectorLayers.filter(layerMeta =>
            styleLayers.some(layer => matchesWorkbenchLayerTarget(layer, sourceRef.alias, layerMeta.id, styleSources))
          ).length,
        };
      });
    } catch {
      return [];
    }
  }, [mountedSourceRefs, parseEditorJson, sources]);

  const sourceById = useMemo(
    () => new Map(sources.map(source => [source.id, source])),
    [sources]
  );

  const workbenchTargets = useMemo(
    () =>
      sourceLayerGroups.flatMap(group =>
        group.vectorLayers.map(layer => {
          const sourceMeta = sourceById.get(group.sourceId);
          const vectorLayerEntry = Array.isArray(sourceMeta?.vector_layers)
            ? (sourceMeta.vector_layers.find(entry => {
                if (!entry || typeof entry !== 'object') {
                  return false;
                }
                const record = entry as Record<string, unknown>;
                return record.id === layer.id || record.layer === layer.id || record.name === layer.id;
              }) as Record<string, unknown> | undefined)
            : undefined;
          return {
            key: buildMapExperienceTargetKey(group.sourceId, layer.id),
            sourceAlias: group.alias,
            sourceId: group.sourceId,
            sourceLayer: layer.id,
            label: `${group.name} / ${layer.id}`,
            rendered: layer.rendered,
            geometry: layer.geometry,
            layerIndex: layer.index,
            fields: buildFilterFields((vectorLayerEntry?.fields as Record<string, unknown> | undefined) ?? null),
          };
        }),
      ),
    [sourceById, sourceLayerGroups]
  );

  const renderedWorkbenchTargets = useMemo(
    () => workbenchTargets.filter(target => target.rendered),
    [workbenchTargets]
  );
  const runtimeFeatureStateTargets = useMemo<MapWorkbenchRuntimeFeatureStateTarget[]>(
    () =>
      renderedWorkbenchTargets.map(target => ({
        key: target.key,
        sourceAlias: target.sourceAlias,
        sourceId: target.sourceId,
        sourceLayer: target.sourceLayer,
        label: target.label,
        geometry: target.geometry,
      })),
    [renderedWorkbenchTargets],
  );
  const workbenchHighlightTargets = useMemo<MapWorkbenchHighlightTargetDescriptor[]>(
    () =>
      workbenchTargets.map(target => ({
        sourceAlias: target.sourceAlias,
        sourceId: target.sourceId,
        sourceLayer: target.sourceLayer,
        geometry: target.geometry,
        featureIdField: inferFeatureIdField(target.fields),
      })),
    [workbenchTargets],
  );

  useEffect(() => {
    if (workbenchTargets.length === 0) {
      setSelectedStyleTargetKey(null);
      setEditingStyleEntryId(null);
      setEditingStyleOwner(null);
      return;
    }
    const selectedTarget = selectedStyleTargetKey
      ? workbenchTargets.find(target => target.key === selectedStyleTargetKey) ?? null
      : null;
    if (!selectedTarget) {
      setSelectedStyleTargetKey(renderedWorkbenchTargets[0]?.key ?? workbenchTargets[0]?.key ?? null);
      setEditingStyleEntryId(null);
      setEditingStyleOwner(null);
    }
  }, [renderedWorkbenchTargets, selectedStyleTargetKey, workbenchTargets]);

  const resolveTargetStyleState = useCallback(
    (target: (typeof workbenchTargets)[number]) => {
      const activeProfileId = mapExperience.active_style_profiles[target.key] ?? '';
      const targetProfiles = listProfilesForTarget(mapExperience, target.sourceId, target.sourceLayer);
      const explicitlyActiveProfile = targetProfiles.find(profile => profile.id === activeProfileId) ?? null;
      const activeProfile =
        explicitlyActiveProfile ??
        targetProfiles.find(profile => profile.kind === 'rules') ??
        targetProfiles.find(profile => profile.kind !== 'rules') ??
        null;
      const activeRulesProfile = activeProfile?.kind === 'rules' ? activeProfile : null;
      const enabledAdvancedRuleCount = activeRulesProfile?.rules?.filter(rule => rule.enabled).length ?? 0;
      const hasAdvancedRules = Boolean(activeRulesProfile && (activeRulesProfile.rules?.length ?? 0) > 0);
      const targetFields = target.fields as WorkbenchFieldOption[];
      const activeMode: MapExperienceStyleProfile['kind'] | null = activeProfile?.kind ?? null;
      const defaultPreview = buildDefaultStylePreview(target.geometry, target.layerIndex);
      const previewKind = representationToPreviewKind(activeProfile?.representation, defaultPreview.kind);
      const renderStackController = buildMapWorkbenchRenderStackController({
        experience: mapExperience,
        target: {
          sourceId: target.sourceId,
          sourceLayer: target.sourceLayer,
          geometry: target.geometry,
        },
        spriteIconSdfByToken: activeSpriteIconSdfByToken,
        themeColors: themeColorPalette,
        selectedEntryId: editingStyleOwner?.targetKey === target.key ? editingStyleEntryId : null,
        selectedProfileId: activeProfile?.id ?? null,
      });
      const enabledRenderStackEntries = renderStackController.entries.filter(entry => entry.enabled);
      const stylePreview: StylePreviewModel =
        activeProfile?.kind === 'single'
          ? {
              kind: previewKind,
              colors: [resolveMapExperienceColorValue(activeProfile.color, themeColorPalette)],
              description: activeProfile.legend_label?.trim() || 'Solid color style',
            }
          : activeProfile?.kind === 'categorical'
            ? {
                kind: previewKind,
                colors: activeProfile.categories.map(category => resolveMapExperienceColorValue(category.color, themeColorPalette)).slice(0, 3),
                description: activeProfile.field ? `${formatFieldLabel(activeProfile.field)} categories` : 'Category style',
              }
            : activeProfile?.kind === 'range'
              ? {
                  kind: previewKind,
                  colors: activeProfile.breaks.map(entry => resolveMapExperienceColorValue(entry.color, themeColorPalette)).slice(0, 3),
                  description: activeProfile.field ? `${formatFieldLabel(activeProfile.field)} ranges` : 'Range style',
                }
              : activeMode === 'rules' && enabledAdvancedRuleCount > 0
              ? {
                kind: previewKind,
                colors: activeRulesProfile?.rules?.filter(rule => rule.enabled).map(rule => rule.color).slice(0, 3) ?? defaultPreview.colors,
                description: `${enabledAdvancedRuleCount} rule${enabledAdvancedRuleCount === 1 ? '' : 's'} active`,
              }
                : {
                    ...defaultPreview,
                    description:
                      enabledRenderStackEntries.length > 1
                        ? `Base + ${enabledRenderStackEntries.length - 1} overlay${enabledRenderStackEntries.length === 2 ? '' : 's'}`
                        : defaultPreview.description,
                  };
      const summaryText = activeProfile
        ? enabledRenderStackEntries.length > 1 && activeMode !== 'rules'
          ? `${describeStyleSummary(activeProfile, activeMode, enabledAdvancedRuleCount)} · ${enabledRenderStackEntries.length - 1} overlay${enabledRenderStackEntries.length === 2 ? '' : 's'}`
          : describeStyleSummary(activeProfile, activeMode, enabledAdvancedRuleCount)
        : 'No style selected';
      const modeLabel = activeMode ? STYLE_KIND_LABELS[activeMode] : 'No style selected';

      return {
        activeProfile,
        activeMode,
        enabledAdvancedRuleCount,
        hasAdvancedRules,
        modeLabel,
        stylePreview,
        summaryText,
        targetFields,
        renderStackController,
      };
    },
    [editingStyleEntryId, editingStyleOwner?.targetKey, mapExperience, themeColorPalette]
  );

  const experiencePreviewModel = useMemo(() => {
    try {
      return compileWorkbenchStyle(parseEditorJson() as Record<string, unknown>, {
        mapExperience,
      });
    } catch {
      return {
        compiledStyle: DEFAULT_STYLE_TEMPLATE,
        legends: [] as MapExperienceLegendSection[],
      };
    }
  }, [compileWorkbenchStyle, mapExperience, parseEditorJson]);

  const activeLegendSections = experiencePreviewModel.legends;

  const selectedWorkbenchTarget = useMemo(
    () => workbenchTargets.find(target => target.key === selectedStyleTargetKey) ?? workbenchTargets[0] ?? null,
    [selectedStyleTargetKey, workbenchTargets],
  );

  useEffect(() => {
    if (!styleId || !adapter.listInteractivityResolvers) {
      setHostResolverCatalog(null);
      return;
    }
    let cancelled = false;
    setHostResolverCatalog(null);
    void adapter
      .listInteractivityResolvers({
        style_id: styleId,
        source_id: selectedWorkbenchTarget?.sourceId ?? null,
        source_layer: selectedWorkbenchTarget?.sourceLayer ?? null,
        asset_mode: assetMode,
      })
      .then(catalog => {
        if (!cancelled) {
          setHostResolverCatalog(Array.isArray(catalog) ? catalog : []);
        }
      })
      .catch(() => {
        if (!cancelled) {
          setHostResolverCatalog(null);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [adapter, assetMode, selectedWorkbenchTarget?.sourceId, selectedWorkbenchTarget?.sourceLayer, styleId]);

  useEffect(() => {
    if (!styleId || !adapter.listInteractivityResolverConnections) {
      setHostResolverConnections(null);
      return;
    }
    let cancelled = false;
    setHostResolverConnections(null);
    void adapter
      .listInteractivityResolverConnections({
        style_id: styleId,
        source_id: selectedWorkbenchTarget?.sourceId ?? null,
        source_layer: selectedWorkbenchTarget?.sourceLayer ?? null,
        asset_mode: assetMode,
      })
      .then(connections => {
        if (!cancelled) {
          setHostResolverConnections(Array.isArray(connections) ? connections : []);
        }
      })
      .catch(() => {
        if (!cancelled) {
          setHostResolverConnections(null);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [adapter, assetMode, selectedWorkbenchTarget?.sourceId, selectedWorkbenchTarget?.sourceLayer, styleId]);

  useEffect(() => {
    setAdvancedRulesWorkspaceOpen(false);
  }, [selectedWorkbenchTarget?.key]);

  const selectedWorkbenchStyleState = useMemo(
    () => (selectedWorkbenchTarget ? resolveTargetStyleState(selectedWorkbenchTarget) : null),
    [resolveTargetStyleState, selectedWorkbenchTarget],
  );

  const editingWorkbenchTarget = useMemo(
    () => (
      editingStyleOwner
        ? workbenchTargets.find(target => target.key === editingStyleOwner.targetKey) ?? null
        : null
    ),
    [editingStyleOwner, workbenchTargets],
  );

  const activeStyleEditorTarget = editingWorkbenchTarget ?? selectedWorkbenchTarget;
  const activeStyleEditorState = useMemo(
    () => (activeStyleEditorTarget ? resolveTargetStyleState(activeStyleEditorTarget) : null),
    [activeStyleEditorTarget, resolveTargetStyleState],
  );

  useEffect(() => {
    if (!editingStyleEntryId) {
      return;
    }
    const hasEditingEntry = activeStyleEditorState?.renderStackController.entries.some(
      entry => entry.id === editingStyleEntryId,
    );
    if (!hasEditingEntry) {
      setEditingStyleEntryId(null);
      setEditingStyleOwner(null);
    }
  }, [activeStyleEditorState, editingStyleEntryId]);

  const selectedFilterSet = useMemo(() => {
    if (!selectedWorkbenchTarget) {
      return null;
    }
    return (
      mapExperience.filter_sets.find(
        entry =>
          entry.source_id === selectedWorkbenchTarget.sourceId &&
          entry.source_layer === selectedWorkbenchTarget.sourceLayer,
      ) ?? null
    );
  }, [mapExperience.filter_sets, selectedWorkbenchTarget]);

  const selectedFilterValues = selectedFilterSet ? mapExperience.active_filter_values[selectedFilterSet.id] ?? {} : {};
  const selectedFilterValueCount = useMemo(
    () =>
      selectedFilterSet?.filters.filter(filter => hasConfiguredFilterValue(filter, selectedFilterValues[filter.id])).length ?? 0,
    [selectedFilterSet, selectedFilterValues]
  );
  const selectedRulesProfileId =
    selectedWorkbenchStyleState?.renderStackController.selected_entry?.profile_id
    ?? selectedWorkbenchStyleState?.activeProfile?.id
    ?? null;
  const selectedRulesProfile = useMemo(() => {
    if (!selectedWorkbenchTarget || !selectedRulesProfileId) {
      return null;
    }
    return (
      mapExperience.style_profiles.find(
        (profile): profile is Extract<MapExperienceStyleProfile, { kind: 'rules' }> =>
          profile.id === selectedRulesProfileId &&
          profile.source_id === selectedWorkbenchTarget.sourceId &&
          profile.source_layer === selectedWorkbenchTarget.sourceLayer &&
          profile.kind === 'rules',
      ) ?? null
    );
  }, [mapExperience.style_profiles, selectedRulesProfileId, selectedWorkbenchTarget]);
  const selectedRuleLayer = useMemo(() => {
    if (!selectedWorkbenchTarget || !selectedRulesProfile) {
      return null;
    }
    const profileLayout = cloneStyleOverrideRecord(selectedRulesProfile.layout ?? null);
    return {
      sourceId: selectedWorkbenchTarget.sourceId,
      sourceAlias: selectedWorkbenchTarget.sourceAlias,
      sourceLayer: selectedWorkbenchTarget.sourceLayer,
      mode: selectedRulesProfile.mode ?? 'hide_non_matching',
      fields: selectedWorkbenchTarget.fields,
      rules: (selectedRulesProfile.rules ?? []).map(rule => ({
        ...rule,
        representation: rule.representation ?? selectedRulesProfile.representation ?? null,
        layout: profileLayout || rule.layout ? { ...(profileLayout ?? {}), ...(rule.layout ?? {}) } : null,
      })),
    } satisfies LayerRuleBuilderState['layers'][number];
  }, [selectedRulesProfile, selectedWorkbenchTarget]);
  useEffect(() => {
    const nextRuleId = selectedRuleLayer?.rules[0]?.id ?? null;
    if (!selectedRuleLayer || selectedRuleLayer.rules.length === 0) {
      setSelectedAdvancedRuleId(null);
      return;
    }
    if (!selectedAdvancedRuleId || !selectedRuleLayer.rules.some(rule => rule.id === selectedAdvancedRuleId)) {
      setSelectedAdvancedRuleId(nextRuleId);
    }
  }, [selectedAdvancedRuleId, selectedRuleLayer]);
  const buildLayerExperienceForTarget = useCallback((target: (typeof workbenchTargets)[number]): LayerExperience => {
    const targetStyleState = resolveTargetStyleState(target);
    const targetFilterSet =
      mapExperience.filter_sets.find(
        entry =>
          entry.source_id === target.sourceId &&
          entry.source_layer === target.sourceLayer,
      ) ?? null;
    const targetProfiles = listProfilesForTarget(
      mapExperience,
      target.sourceId,
      target.sourceLayer,
    );
    const targetFilterValues = targetFilterSet ? mapExperience.active_filter_values[targetFilterSet.id] ?? {} : {};

    const preview = targetStyleState.stylePreview ?? buildDefaultStylePreview(target.geometry, target.layerIndex);
    const entryProfiles = targetStyleState.renderStackController.entries.map(entry => ({
      entry,
      profile:
        targetProfiles.find(profile => profile.id === entry.profile_id)
        ?? buildSeededRulesStyleProfile(target),
    }));
    const variants: LayerExperience['appearance']['variants'] = entryProfiles.map(({ entry, profile }) => {
      const representation = coerceRepresentationForGeometry(
        target.geometry,
        profile.representation ?? previewKindToRepresentation(preview.kind),
      );
      const rulesProfile =
        profile.kind === 'rules'
          ? profile
          : buildRulesProfileFromStyleProfile(target, profile, {
              profileId: profile.id,
              profileLabel: entry.label ?? profile.label ?? null,
            });
      return {
        id: rulesProfile.id,
        kind: 'rules',
        label: rulesProfile.label ?? entry.label ?? null,
        style: {
          representation,
          paint: {},
          layout: sanitizeRulesProfileLayoutForRepresentation(representation, rulesProfile.layout ?? null) ?? {},
          interaction: cloneStyleInteractionConfig(rulesProfile.interaction),
        },
        mode: rulesProfile.mode ?? 'hide_non_matching',
        rules: (rulesProfile.rules ?? []).map(rule => ({
          id: rule.id,
          label: rule.label,
          color: rule.color,
          enabled: rule.enabled,
          logic: rule.logic,
          representation: null,
          paint: rule.paint ?? null,
          layout: rule.layout ?? null,
        })),
      };
    });

    const defaultSourceDefinition = buildDefaultInteractivitySourceDefinition(target);
    const selectedSourceDefinition =
      mapExperience.interactivity?.sources.find(
        entry =>
          entry.source_id === target.sourceId &&
          entry.source_layer === target.sourceLayer,
      ) ?? defaultSourceDefinition;
    const selectedResolverDefinitions = (
      mapExperience.interactivity?.resolvers.filter(
        entry =>
          (entry.source_id ?? target.sourceId) === target.sourceId &&
          (entry.source_layer ?? target.sourceLayer) === target.sourceLayer,
      ) ?? []
    ) as LayerExperienceResolverDefinition[];
    const selectedSurfaceDefinitions = (
      mapExperience.interactivity?.surfaces.filter(
        entry =>
          (entry.source_id ?? target.sourceId) === target.sourceId &&
          (entry.source_layer ?? target.sourceLayer) === target.sourceLayer,
      ) ?? []
    ) as LayerExperienceSurfaceDefinition[];
    const selectedPipelineDefinitions = (
      mapExperience.interactivity?.pipelines.filter(
        entry =>
          (entry.source_id ?? target.sourceId) === target.sourceId &&
          (entry.source_layer ?? target.sourceLayer) === target.sourceLayer,
      ) ?? []
    ) as LayerExperiencePipelineDefinition[];
    const selectedBuiltinValues = (
      mapExperience.interactivity?.builtin_values?.length
        ? mapExperience.interactivity.builtin_values
        : createDefaultMapExperienceBuiltinValues()
    ) as LayerExperienceBuiltinValueDefinition[];

    return {
      id: target.key,
      source_id: target.sourceId,
      source_layer: target.sourceLayer,
      label: target.label,
      description: target.rendered ? 'Visible on map' : 'Hidden on map',
      visibility: {
        default_visible: target.rendered,
      },
      appearance: {
        active_variant_id:
          targetStyleState.renderStackController.selected_entry?.profile_id
          ?? targetStyleState.activeProfile?.id
          ?? variants[0]?.id
          ?? null,
        variants,
        legend: {
          title: targetStyleState.activeProfile?.label ?? target.sourceLayer,
          label:
            targetStyleState.activeProfile?.kind === 'rules'
              ? targetStyleState.activeProfile.legend_label ?? null
              : null,
          hidden: false,
        },
      },
      filters: {
        id: targetFilterSet?.id ?? null,
        label: targetFilterSet?.label ?? null,
        definitions:
          targetFilterSet?.filters.map(filter => ({
            id: filter.id,
            kind: filter.kind,
            field: filter.field,
            operator: filter.operator ?? null,
            value: filter.value ?? null,
            label: filter.label ?? null,
            helper_text: filter.description ?? null,
            runtime: {
              editable_in_view: filter.editable_in_view ?? true,
              options:
                filter.options?.map(option => ({
                  value: option.value,
                  label: option.label ?? null,
                })) ?? [],
              placeholder: filter.placeholder ?? null,
              min: typeof filter.min === 'number' ? filter.min : null,
              max: typeof filter.max === 'number' ? filter.max : null,
              step: typeof filter.step === 'number' ? filter.step : null,
              default_value:
                filter.kind === 'numeric_range' || filter.kind === 'date_range'
                  ? null
                  : (filter.default_value as boolean | string | string[] | null | undefined) ?? null,
              default_min:
                filter.kind === 'numeric_range' &&
                filter.default_value &&
                typeof filter.default_value === 'object' &&
                Number.isFinite(Number((filter.default_value as Record<string, unknown>).min))
                  ? Number((filter.default_value as Record<string, unknown>).min)
                  : null,
              default_max:
                filter.kind === 'numeric_range' &&
                filter.default_value &&
                typeof filter.default_value === 'object' &&
                Number.isFinite(Number((filter.default_value as Record<string, unknown>).max))
                  ? Number((filter.default_value as Record<string, unknown>).max)
                  : null,
            },
          })) ?? [],
        active_values: targetFilterValues,
      },
      interactivity: {
        surface_definitions: selectedSurfaceDefinitions,
        pipeline_definitions: selectedPipelineDefinitions,
      },
      enrichments: {
        entity_type: null,
        entity_ref_field: selectedSourceDefinition.identity.feature_id_field,
        action_profile_field: null,
        capability_fields: null,
        derived_fields: [],
        relations: selectedSourceDefinition.relation_hints ?? [],
        source_definition: selectedSourceDefinition,
        resolver_definitions: selectedResolverDefinitions,
        builtin_values: selectedBuiltinValues,
      },
      presets: {
        include_in_saved_views: true,
      },
    };
  }, [
    activeSpriteIconSet,
    mapExperience,
    resolveTargetStyleState,
    sourceById,
    themeColorPalette,
  ]);
  const selectedLayerExperience = useMemo<LayerExperience | null>(
    () => (selectedWorkbenchTarget ? buildLayerExperienceForTarget(selectedWorkbenchTarget) : null),
    [buildLayerExperienceForTarget, selectedWorkbenchTarget],
  );
  const activeStyleEditorLayerExperience = useMemo<LayerExperience | null>(
    () => (activeStyleEditorTarget ? buildLayerExperienceForTarget(activeStyleEditorTarget) : null),
    [activeStyleEditorTarget, buildLayerExperienceForTarget],
  );
  const resolveFeatureInteractivityContext = useCallback(
    (feature: MapWorkbenchFeatureInspection | null) => {
      if (!feature || !previewStyle) {
        logInteractivityEvent('context:skipped', {
          reason: !feature ? 'no-feature' : 'no-preview-style',
          feature: summarizeHoverDebugFeature(feature),
        });
        return null;
      }
      const sourceIdentity = resolveRenderedFeatureSourceIdentity({ feature, styleJson: previewStyle });
      if (!sourceIdentity) {
        logInteractivityEvent('context:no-source-identity', {
          feature: summarizeHoverDebugFeature(feature),
          styleLayerId: feature.layerId ?? null,
          styleLayer: typeof feature.layerId === 'string'
            ? (Array.isArray(previewStyle.layers)
                ? (previewStyle.layers as Array<Record<string, unknown>>).find(layer => layer?.id === feature.layerId) ?? null
                : null)
            : null,
        });
        return null;
      }
      const target =
        workbenchTargets.find(
          entry =>
            entry.sourceId === sourceIdentity.sourceId &&
            entry.sourceLayer === sourceIdentity.sourceLayer,
        ) ?? null;
      if (!target) {
        logInteractivityEvent('context:no-target', {
          feature: summarizeHoverDebugFeature(feature),
          sourceIdentity,
          availableTargets: workbenchTargets.slice(0, 12).map(summarizeInteractivityTarget),
        });
        return null;
      }
      const layerExperience = buildLayerExperienceForTarget(target);
      const runtimeFeature = buildRuntimeFeatureRef(feature, layerExperience);
      if (!runtimeFeature) {
        logInteractivityEvent('context:no-runtime-feature', {
          feature: summarizeHoverDebugFeature(feature),
          sourceIdentity,
          target: summarizeInteractivityTarget(target),
          featureIdField: layerExperience.identity?.feature_id_field ?? null,
        });
        return null;
      }
      logInteractivityEvent('context:resolved', {
        feature: summarizeHoverDebugFeature(feature),
        sourceIdentity,
        target: summarizeInteractivityTarget(target),
        runtimeFeature: {
          source_id: runtimeFeature.source_id,
          source_layer: runtimeFeature.source_layer,
          feature_id: runtimeFeature.feature_id,
          geometry_type: runtimeFeature.geometry_type,
        },
      });
      return {
        target,
        layerExperience,
        sourceIdentity,
        runtimeFeature,
      };
    },
    [
      buildLayerExperienceForTarget,
      logInteractivityEvent,
      previewStyle,
      summarizeHoverDebugFeature,
      summarizeInteractivityTarget,
      workbenchTargets,
    ],
  );
  const resolveSpiderifyChooserItem = useCallback(
    (feature: MapWorkbenchFeatureInspection): MapWorkbenchSpiderifyChooserListItem | null => {
      const context = resolveFeatureInteractivityContext(feature);
      if (!context) {
        return null;
      }
      const surfaces = (context.layerExperience.interactivity?.surface_definitions ?? []) as LayerExperienceSurfaceDefinition[];
      const pipelines = (context.layerExperience.interactivity?.pipeline_definitions ?? []) as LayerExperiencePipelineDefinition[];
      const surface = selectAmbiguousSelectionSummarySurface(surfaces);
      if (!surface) {
        return null;
      }
      const resolvedSurface = buildResolvedFeatureSurface({
        feature,
        surface,
        surfaces,
        pipelines,
      });
      if (!resolvedSurface.title && !resolvedSurface.subtitle && resolvedSurface.summary.length === 0) {
        return null;
      }
      return buildMapWorkbenchSpiderifyChooserItemFromResolvedSummary({
        feature,
        resolved: {
          title: resolvedSurface.title,
          subtitle: resolvedSurface.subtitle,
          summary: resolvedSurface.summary,
        },
      });
    },
    [resolveFeatureInteractivityContext],
  );
  const executeFeatureInteractivity = useCallback(
    async ({
      feature,
      event,
    }: {
      feature: MapWorkbenchFeatureInspection | null;
      event: 'hover_enter' | 'click' | 'context_menu';
    }): Promise<MapWorkbenchResolvedInteractivityState> => {
      logInteractivityEvent('pipeline:start', {
        event,
        feature: summarizeHoverDebugFeature(feature),
      });
      const context = resolveFeatureInteractivityContext(feature);
      if (!context) {
        logHoverStateEvent('popup:resolve:skipped', {
          reason: !feature ? 'no-feature' : !previewStyle ? 'no-preview-style' : 'no-target-context',
          event,
          feature: summarizeHoverDebugFeature(feature),
        });
        return EMPTY_WORKBENCH_INTERACTIVITY_STATE;
      }

      const interactivity = buildWorkbenchInteractivityDefinition(context.layerExperience);
      if (!interactivity) {
        logInteractivityEvent('pipeline:no-interactivity-definition', {
          event,
          feature: summarizeHoverDebugFeature(feature),
          target: summarizeInteractivityTarget(context.target),
        });
        return EMPTY_WORKBENCH_INTERACTIVITY_STATE;
      }

      const executions = await runMapExperienceInteractivityPipelines({
        interactivity,
        trigger: {
          kind: event,
          feature: context.runtimeFeature,
        },
        executeResolver: adapter.executeInteractivityResolver,
      });
      logInteractivityEvent('pipeline:executed', {
        event,
        feature: summarizeHoverDebugFeature(feature),
        target: summarizeInteractivityTarget(context.target),
        executionCount: executions.length,
        executions: executions.map(execution => ({
          pipeline_id: execution.pipeline_id,
          stop_propagation: execution.stop_propagation,
          effectCount: execution.effects.length,
          effects: execution.effects.map(effect => ({
            kind: effect.kind,
            status: effect.status,
            surface_id: 'surface_id' in effect && typeof effect.surface_id === 'string' ? effect.surface_id : null,
          })),
        })),
      });
      const resolvedState = buildWorkbenchInteractivityState({
        feature: feature!,
        event,
        layerExperience: context.layerExperience,
        executions,
        highlightTargets: workbenchHighlightTargets,
      });

      logHoverStateEvent('popup:resolve', {
        event,
        feature: summarizeHoverDebugFeature(feature),
        sourceIdentity: context.sourceIdentity,
        target: {
          key: context.target.key,
          sourceId: context.target.sourceId,
          sourceLayer: context.target.sourceLayer,
        },
        popup: resolvedState.popup
          ? {
              mode: resolvedState.popup.mode,
              surfaceId: resolvedState.popup.surface.surface_id,
              surfaceKind: resolvedState.popup.surface.surface_kind,
            }
          : null,
        surface: resolvedState.surface
          ? {
              surfaceId: resolvedState.surface.surface.surface_id,
              surfaceKind: resolvedState.surface.surface.surface_kind,
            }
          : null,
        executionCount: resolvedState.executions.length,
      });

      return resolvedState;
    },
    [
      adapter.executeInteractivityResolver,
      logHoverStateEvent,
      logInteractivityEvent,
      previewStyle,
      resolveFeatureInteractivityContext,
      summarizeHoverDebugFeature,
      summarizeInteractivityTarget,
      workbenchHighlightTargets,
    ],
  );
  useEffect(() => {
    let cancelled = false;
    void executeFeatureInteractivity({ feature: hoveredFeature, event: 'hover_enter' }).then(result => {
      if (!cancelled) {
        debugMapWorkbenchCameraLifecycle('hover interactivity resolved', {
          feature: summarizeHoverDebugFeature(hoveredFeature),
          state: summarizeWorkbenchResolvedInteractivityState(result),
        });
        setHoverInteractivityState(result);
      }
    });
    return () => {
      cancelled = true;
    };
  }, [executeFeatureInteractivity, hoveredFeature]);
  useEffect(() => {
    let cancelled = false;
    void executeFeatureInteractivity({ feature: pinnedFeature, event: 'click' }).then(result => {
      if (!cancelled) {
        debugMapWorkbenchCameraLifecycle('click interactivity resolved', {
          feature: summarizeHoverDebugFeature(pinnedFeature),
          state: summarizeWorkbenchResolvedInteractivityState(result),
        });
        setClickInteractivityState(result);
      }
    });
    return () => {
      cancelled = true;
    };
  }, [executeFeatureInteractivity, pinnedFeature]);
  useEffect(() => {
    let cancelled = false;
    void executeFeatureInteractivity({ feature: pinnedFeature, event: 'context_menu' }).then(result => {
      if (!cancelled) {
        debugMapWorkbenchCameraLifecycle('context menu interactivity resolved', {
          feature: summarizeHoverDebugFeature(pinnedFeature),
          state: summarizeWorkbenchResolvedInteractivityState(result),
        });
        setContextMenuInteractivityState(result);
      }
    });
    return () => {
      cancelled = true;
    };
  }, [executeFeatureInteractivity, pinnedFeature]);
  const activeHoverPopup = hoverInteractivityState.popup;
  const activePinnedPopup =
    pinnedFeatureTrigger === 'context_menu' ? contextMenuInteractivityState.popup : clickInteractivityState.popup;
  const authoredPinnedPopupMode = activePinnedPopup?.mode ?? null;
  const authoredPinnedPopupIsPinned = Boolean(pinnedFeature && authoredPinnedPopupMode === 'pinned');

  useEffect(() => {
    logHoverStateEvent('state:hoveredFeature', {
      hoveredFeature: summarizeHoverDebugFeature(hoveredFeature),
      pinnedFeature: summarizeHoverDebugFeature(pinnedFeature),
      activeHoverPopup: activeHoverPopup
        ? {
            mode: activeHoverPopup.mode,
            surfaceId: activeHoverPopup.surface.surface_id,
            surfaceKind: activeHoverPopup.surface.surface_kind,
          }
        : null,
      activePinnedPopup: activePinnedPopup
        ? {
            mode: activePinnedPopup.mode,
            surfaceId: activePinnedPopup.surface.surface_id,
            surfaceKind: activePinnedPopup.surface.surface_kind,
          }
        : null,
    });
  }, [
    activeHoverPopup,
    activePinnedPopup,
    hoveredFeature,
    logHoverStateEvent,
    pinnedFeature,
    summarizeHoverDebugFeature,
  ]);
  const runtimeHighlightDescriptors = useMemo<MapWorkbenchRuntimeHighlightDescriptor[]>(() => {
    if (!previewStyle) {
      return manualHighlightDescriptors;
    }

    const resolveDescriptor = (
      feature: MapWorkbenchFeatureInspection | null,
      channel: 'hover' | 'selected',
    ): MapWorkbenchRuntimeHighlightDescriptor | null => {
      if (!feature) {
        return null;
      }
      const sourceIdentity = resolveRenderedFeatureSourceIdentity({ feature, styleJson: previewStyle });
      if (!sourceIdentity) {
        return null;
      }
      const target =
        workbenchHighlightTargets.find(
          entry => entry.sourceId === sourceIdentity.sourceId && entry.sourceLayer === sourceIdentity.sourceLayer,
        ) ?? null;
      if (!target || !feature.source || feature.source !== target.sourceAlias) {
        return null;
      }
      return {
        channel,
        feature,
        target,
      };
    };

    const descriptors: MapWorkbenchRuntimeHighlightDescriptor[] = [];
    const selectedDescriptor = resolveDescriptor(pinnedFeature, 'selected');
    if (selectedDescriptor) {
      descriptors.push(selectedDescriptor);
    }
    const hoverDescriptor = resolveDescriptor(hoveredFeature, 'hover');
    if (
      hoverDescriptor &&
      !(
        selectedDescriptor &&
        selectedDescriptor.target.sourceAlias === hoverDescriptor.target.sourceAlias &&
        selectedDescriptor.target.sourceLayer === hoverDescriptor.target.sourceLayer &&
        buildWorkbenchHighlightIdentity(selectedDescriptor.feature, selectedDescriptor.target) ===
          buildWorkbenchHighlightIdentity(hoverDescriptor.feature, hoverDescriptor.target)
      )
    ) {
      descriptors.push(hoverDescriptor);
    }
    const authoredHighlights =
      pinnedFeatureTrigger === 'context_menu'
        ? [...hoverInteractivityState.highlightDescriptors, ...contextMenuInteractivityState.highlightDescriptors]
        : [...hoverInteractivityState.highlightDescriptors, ...clickInteractivityState.highlightDescriptors];
    authoredHighlights.forEach(descriptor => {
      descriptors.splice(0, descriptors.length, ...appendWorkbenchHighlightDescriptor(descriptors, descriptor));
    });
    manualHighlightDescriptors.forEach(descriptor => {
      descriptors.splice(0, descriptors.length, ...appendWorkbenchHighlightDescriptor(descriptors, descriptor));
    });
    return descriptors;
  }, [
    clickInteractivityState.highlightDescriptors,
    contextMenuInteractivityState.highlightDescriptors,
    hoverInteractivityState.highlightDescriptors,
    hoveredFeature,
    manualHighlightDescriptors,
    pinnedFeature,
    pinnedFeatureTrigger,
    previewStyle,
    workbenchHighlightTargets,
  ]);

  const runtimePreviewStyle = useMemo(
    () =>
      applyWorkbenchRuntimeHighlights({
        styleJson: previewStyle,
        targets: workbenchHighlightTargets,
        highlights: runtimeHighlightDescriptors,
      }),
    [previewStyle, runtimeHighlightDescriptors, workbenchHighlightTargets],
  );
  useEffect(() => {
    if (typeof window === 'undefined') {
      return;
    }
    const debugWindow = window as typeof window & {
      __debugPreviewStyle?: Record<string, unknown> | null;
      __debugRuntimePreviewStyle?: Record<string, unknown> | null;
      __debugCompiledRuntimePreviewStyle?: Record<string, unknown> | null;
      __debugRequestedMapExperience?: MapExperienceState | null;
      __debugCompileExperience?: MapExperienceState | null;
      __debugThemeColorPalette?: SemanticThemeColorPalette | null;
    };
    debugWindow.__debugPreviewStyle = previewStyle;
    debugWindow.__debugRuntimePreviewStyle = runtimePreviewStyle;
    debugWindow.__debugCompiledRuntimePreviewStyle = debugCompiledRuntimePreviewStyleRef.current;
    debugWindow.__debugRequestedMapExperience = debugRequestedMapExperienceRef.current;
    debugWindow.__debugCompileExperience = debugCompileExperienceRef.current;
    debugWindow.__debugThemeColorPalette = themeColorPalette;
  }, [previewStyle, runtimePreviewStyle, themeColorPalette]);
  const activeFeaturePopup = authoredPinnedPopupIsPinned ? activePinnedPopup ?? activeHoverPopup : activeHoverPopup ?? activePinnedPopup;
  const activeRuntimeSurfaceState =
    authoredPinnedPopupIsPinned
      ? pinnedFeatureTrigger === 'context_menu'
        ? contextMenuInteractivityState.surface
          ? contextMenuInteractivityState
          : hoverInteractivityState.surface
            ? hoverInteractivityState
            : null
        : clickInteractivityState.surface
          ? clickInteractivityState
          : hoverInteractivityState.surface
            ? hoverInteractivityState
            : null
      : hoverInteractivityState.surface
        ? hoverInteractivityState
        : pinnedFeatureTrigger === 'context_menu'
          ? contextMenuInteractivityState.surface
            ? contextMenuInteractivityState
            : null
          : clickInteractivityState.surface
            ? clickInteractivityState
            : null;
  const activeRuntimeSurface = activeRuntimeSurfaceState?.surface ?? null;
  const activeRuntimeSurfaceFeature = activeRuntimeSurfaceState?.feature ?? null;
  const activeRuntimeSurfaceSignature = useMemo(
    () =>
      activeRuntimeSurface
        ? JSON.stringify({
            surfaceId: activeRuntimeSurface.surface.surface_id,
            surfaceKind: activeRuntimeSurface.surface.surface_kind,
            title: activeRuntimeSurface.title,
            summaryKeys: activeRuntimeSurface.summary.map(item => item.key),
            actionIds: activeRuntimeSurface.actions.map(item => item.pipeline.pipeline_id),
          })
        : null,
    [activeRuntimeSurface],
  );
  useEffect(() => {
    const previousSignature = runtimeSurfacePanelSignatureRef.current;
    if (previousSignature === activeRuntimeSurfaceSignature) {
      return;
    }
    runtimeSurfacePanelSignatureRef.current = activeRuntimeSurfaceSignature;
    if (activeRuntimeSurface && activeRuntimeSurface.surface.surface_kind !== 'floating_summary_card') {
      setActionSurface(activeRuntimeSurface);
      setActionSurfaceFeature(activeRuntimeSurfaceFeature);
      if (activeRuntimeSurface.surface.surface_kind === 'side_panel') {
        setRightPaneCollapsed(false);
        setActionPanelOpen(false);
      } else {
        setActionPanelOpen(true);
      }
      setActionPanelSource('runtime');
      return;
    }
    if (previousSignature && actionPanelSource === 'runtime') {
      setActionPanelOpen(false);
      setActionSurface(null);
      setActionSurfaceFeature(null);
      setActionPanelSource(null);
    }
  }, [actionPanelSource, activeRuntimeSurface, activeRuntimeSurfaceFeature, activeRuntimeSurfaceSignature]);
  const interactivityEditorFieldOptions = useMemo<MapInteractivityFieldOption[]>(
    () =>
      (selectedLayerExperience?.enrichments?.source_definition?.fields ?? []).map(field => ({
        id: field.field_key,
        label: field.label ?? field.field_key,
        type: field.value_type,
        semanticRoles: field.semantic_roles ?? [],
        description: field.description ?? null,
      })),
    [selectedLayerExperience],
  );
  const availableResolverCatalog = hostResolverCatalog ?? mapExperience.interactivity?.resolvers ?? [];
  const availableResolverConnections = hostResolverConnections ?? [];
  const interactivityEditorCapabilities = useMemo<MapInteractivityEditorCapabilities>(
    () => ({
      geolocationSupported: true,
      readOnly: !isAuthorMode,
      supportedSurfaceKinds: ['hover_popup', 'side_panel', 'bottom_sheet', 'floating_summary_card'],
      resolverSupport: true,
      triggerSupport: {
        manual: 'enabled',
        hover_enter: 'enabled',
        hover_leave: 'not_implemented',
        click: 'enabled',
        context_menu: 'enabled',
        tool_activated: 'not_implemented',
        tool_deactivated: 'not_implemented',
        selection_changed: 'not_implemented',
        filter_value_changed: 'not_implemented',
        preset_applied: 'not_implemented',
        map_loaded: 'not_implemented',
        location_updated: 'not_implemented',
        drawing_started: 'not_implemented',
        drawing_updated: 'not_implemented',
        drawing_completed: 'not_implemented',
        drawing_cleared: 'not_implemented',
      },
      stepSupport: {
        resolve: 'enabled',
        target_set: 'not_implemented',
        condition: 'not_implemented',
      },
      effectSupport: {
        open_surface: 'enabled',
        close_surface: 'enabled',
        bind_surface_data: 'enabled',
        bind_feature_state: 'not_implemented',
        highlight_set: 'enabled',
        clear_highlight_set: 'enabled',
        dim_others: 'not_implemented',
        clear_dimming: 'not_implemented',
        fly_to_feature: 'enabled',
        fly_to_bounds: 'not_implemented',
        open_route: 'enabled',
        open_external_url: 'enabled',
        copy_field: 'enabled',
        set_runtime_filter_value: 'not_implemented',
        set_runtime_filter_enabled: 'not_implemented',
        select_feature_set: 'not_implemented',
        clear_selection: 'enabled',
        activate_builtin_tool: 'enabled',
        deactivate_builtin_tool: 'not_implemented',
        clear_drawings: 'enabled',
        export_target_set_csv: 'not_implemented',
      },
    }),
    [isAuthorMode],
  );
  useEffect(() => {
    setActiveInteractivityEditor(null);
  }, [selectedWorkbenchTarget?.key]);
  const selectedActiveRuleCount = selectedRuleLayer?.rules.filter(rule => rule.enabled).length ?? 0;
  const selectedActiveFilterValueSummaries = useMemo(
    () =>
      selectedFilterSet?.filters
        .filter(filter => hasConfiguredFilterValue(filter, selectedFilterValues[filter.id]))
        .map(filter => summarizeFilterValue(filter, selectedFilterValues[filter.id]))
        .slice(0, 3) ?? [],
    [selectedFilterSet, selectedFilterValues],
  );
  const canEditRulesLegendConfig =
    canEditStyles &&
    (!selectedWorkbenchStyleState?.activeProfile ||
      selectedWorkbenchStyleState.activeProfile.kind === 'rules');
  const selectedRulesLegendHeading =
    selectedRulesProfile?.label?.trim() ||
    (selectedWorkbenchTarget ? formatFieldLabel(selectedWorkbenchTarget.sourceLayer) : '');
  const selectedRulesLegendLabel =
    selectedRulesProfile?.legend_label?.trim() ||
    (selectedWorkbenchTarget ? `${formatFieldLabel(selectedWorkbenchTarget.sourceLayer)} features` : '');
  const buildStyleSourcesContext = (styleJson: Record<string, unknown>) => {
    const sourcesValue = styleJson.sources;
    if (!sourcesValue || typeof sourcesValue !== 'object') return [];
    return Object.entries(sourcesValue as Record<string, unknown>).map(([id, source]) => ({
      id,
      value: source,
    }));
  };

  const buildStyleLayersContext = (styleJson: Record<string, unknown>) => {
    if (!Array.isArray(styleJson.layers)) return [];
    return styleJson.layers
      .map(layer => {
        if (!layer || typeof layer !== 'object') return null;
        const record = layer as Record<string, unknown>;
        return {
          id: record.id,
          type: record.type,
          source: record.source,
          source_layer: resolveWorkbenchManagedSourceLayer(record),
          minzoom: record.minzoom,
          maxzoom: record.maxzoom,
          filter: record.filter,
          layout: record.layout,
          paint: record.paint,
          metadata: record.metadata,
        };
      })
      .filter(Boolean);
  };

  const handleOpenSaveSheet = () => {
    setSaveFormError(null);
    let name = saveForm.name;
    if (!name) {
      try {
        const parsed = parseEditorJson() as Record<string, unknown>;
        if (typeof parsed.name === 'string') {
          name = parsed.name;
        }
      } catch {
        name = '';
      }
    }
    setSaveForm(prev => ({ ...prev, name }));
    setSaveSheetOpen(true);
  };

  const openWorkbenchTab = useCallback((tab: MapWorkbenchTab) => {
    if (!visibleTabs.includes(tab)) {
      return;
    }
    setStyleSheetTab(tab);
    setRightPaneCollapsed(false);
  }, [visibleTabs]);

  const handleDesktopPaneSectionChange = useCallback((section: MapWorkbenchDesktopAuthoringPaneSection) => {
    setDesktopPaneSection(section);
    if (section !== 'interactions') {
      setActiveInteractivityEditor(null);
    }
    if (section === 'style') {
      openWorkbenchTab('styles');
      return;
    }
    if (section === 'filters') {
      openWorkbenchTab('filters');
      return;
    }
    if (section === 'advanced') {
      openWorkbenchTab('rules');
      return;
    }
  }, [openWorkbenchTab]);

  const handleSelectWorkbenchTarget = useCallback((targetKey: string) => {
    setSelectedStyleTargetKey(targetKey);
    setActiveInteractivityEditor(null);
    setRightPaneCollapsed(false);
  }, []);

  const handleSelectLayerManagerTarget = useCallback(
    (sourceAlias: string, sourceLayerId: string) => {
      const target = workbenchTargets.find(
        entry => entry.sourceAlias === sourceAlias && entry.sourceLayer === sourceLayerId,
      );
      if (!target) {
        return;
      }
      handleSelectWorkbenchTarget(target.key);
    },
    [handleSelectWorkbenchTarget, workbenchTargets]
  );

  const updateMapExperienceState = useCallback((updater: (current: MapExperienceState) => MapExperienceState) => {
    updateMapExperience(updater);
  }, [updateMapExperience]);

  const buildUniqueId = useCallback((base: string, existingIds: Set<string>) => {
    let nextId = base;
    let counter = 1;
    while (existingIds.has(nextId)) {
      nextId = `${base}-${counter}`;
      counter += 1;
    }
    return nextId;
  }, []);
  const handleUpdateRulesLegendConfig = useCallback(
    (field: 'label' | 'legend_label', value: string) => {
      if (!selectedWorkbenchTarget || !selectedRulesProfile || !canEditRulesLegendConfig) {
        return;
      }
      updateMapExperienceState(current => {
        return {
          ...current,
          style_profiles: current.style_profiles.map(profile =>
            profile.id === selectedRulesProfile.id && profile.kind === 'rules'
              ? {
                  ...profile,
                  [field]: value,
                }
              : profile,
          ),
        };
      });
    },
    [
      canEditRulesLegendConfig,
      selectedRulesProfile,
      selectedWorkbenchTarget,
      updateMapExperienceState,
    ],
  );

  const updateSelectedRuleLayer = useCallback(
    (
      updater: (
        current: LayerRuleBuilderState['layers'][number],
      ) => LayerRuleBuilderState['layers'][number],
    ) => {
      if (!selectedWorkbenchTarget || !selectedRulesProfile) {
        return;
      }
      updateMapExperienceState(current => {
        const existingProfile = current.style_profiles.find(
          (profile): profile is Extract<MapExperienceStyleProfile, { kind: 'rules' }> =>
            profile.id === selectedRulesProfile.id &&
            profile.source_id === selectedWorkbenchTarget.sourceId &&
            profile.source_layer === selectedWorkbenchTarget.sourceLayer &&
            profile.kind === 'rules',
        ) ?? null;
        const baseProfile = existingProfile ?? selectedRulesProfile;
        const nextLayer = updater({
          sourceId: selectedWorkbenchTarget.sourceId,
          sourceAlias: selectedWorkbenchTarget.sourceAlias,
          sourceLayer: selectedWorkbenchTarget.sourceLayer,
          mode: baseProfile.mode ?? 'hide_non_matching',
          fields: selectedWorkbenchTarget.fields,
          rules: baseProfile.rules ?? [],
        });
        const nextProfile: Extract<MapExperienceStyleProfile, { kind: 'rules' }> = {
          ...baseProfile,
          mode: nextLayer.mode,
          rules: nextLayer.rules,
        };
        return {
          ...current,
          style_profiles: existingProfile
            ? current.style_profiles.map(profile => (profile.id === selectedRulesProfile.id ? nextProfile : profile))
            : [...current.style_profiles, nextProfile],
        };
      });
    },
    [selectedRulesProfile, selectedWorkbenchTarget, updateMapExperienceState],
  );

  const handleAddAdvancedRule = useCallback(() => {
    if (!selectedWorkbenchTarget) {
      return;
    }
    const fallbackField = selectedWorkbenchTarget.fields[0];
    const fallbackOperatorId =
      (fallbackField?.value_type === 'number' || fallbackField?.value_type === 'date')
        ? 'gt'
        : fallbackField?.value_type === 'boolean'
          ? 'eq'
          : fallbackField?.value_type === 'string'
            ? 'eq'
            : 'match_everything';
    const nextRuleId = buildAdvancedRuleId(selectedWorkbenchTarget.sourceId, selectedWorkbenchTarget.sourceLayer);
    updateSelectedRuleLayer(current => ({
      ...current,
      mode: 'hide_non_matching',
      rules: [
        ...current.rules,
        {
          id: nextRuleId,
          enabled: true,
          label: `Rule ${current.rules.length + 1}`,
          color: DEFAULT_ADVANCED_RULE_COLOR,
          logic: fallbackField
            ? createGroup({ children: [createCondition(fallbackField.id, fallbackOperatorId)] })
            : createGroup({ children: [createCondition('', 'match_everything')] }),
        },
      ],
    }));
    setSelectedAdvancedRuleId(nextRuleId);
  }, [selectedWorkbenchTarget, updateSelectedRuleLayer]);

  const handleDuplicateAdvancedRule = useCallback(
    (ruleId: string) => {
      updateSelectedRuleLayer(current => {
        const sourceRule = current.rules.find(rule => rule.id === ruleId);
        if (!sourceRule) {
          return current;
        }
        const duplicateId = `${ruleId}-copy-${Date.now().toString(36)}`;
        const nextRule = {
          ...sourceRule,
          id: duplicateId,
          label: `${sourceRule.label} Copy`,
        };
        setSelectedAdvancedRuleId(duplicateId);
        return {
          ...current,
          rules: [...current.rules, nextRule],
        };
      });
    },
    [updateSelectedRuleLayer],
  );

  const handleDeleteAdvancedRule = useCallback(
    (ruleId: string) => {
      updateSelectedRuleLayer(current => {
        const nextRules = current.rules.filter(rule => rule.id !== ruleId);
        setSelectedAdvancedRuleId(currentSelected =>
          currentSelected === ruleId ? (nextRules[0]?.id ?? null) : currentSelected,
        );
        return {
          ...current,
          rules: nextRules,
        };
      });
    },
    [updateSelectedRuleLayer],
  );

  const handleAdvancedRuleChange = useCallback(
    (ruleId: string, nextRule: MapWorkbenchAdvancedRuleDefinition) => {
      updateSelectedRuleLayer(current => ({
        ...current,
        rules: current.rules.map(rule =>
          rule.id === ruleId
            ? {
                ...rule,
                label: nextRule.label ?? rule.label,
                enabled: typeof nextRule.enabled === 'boolean' ? nextRule.enabled : rule.enabled,
                color: normalizeAdvancedRuleColor(nextRule.color ?? rule.color),
                logic: convertAdvancedRuleLogicToStyleRuleLogic(nextRule.logic ?? rule.logic),
                representation: null,
                paint: nextRule.paint ?? rule.paint ?? null,
                layout: nextRule.layout ?? rule.layout ?? null,
              }
            : rule,
        ),
      }));
      if (!selectedWorkbenchTarget || !selectedRulesProfile) {
        return;
      }
      updateMapExperienceState(current => {
        const existingProfile = current.style_profiles.find(
          (profile): profile is Extract<MapExperienceStyleProfile, { kind: 'rules' }> =>
            profile.id === selectedRulesProfile.id &&
            profile.source_id === selectedWorkbenchTarget.sourceId &&
            profile.source_layer === selectedWorkbenchTarget.sourceLayer &&
            profile.kind === 'rules',
        ) ?? null;
        const baseProfile = existingProfile ?? selectedRulesProfile;
        const nextRepresentation =
          nextRule.representation
            ?? baseProfile.representation
            ?? previewKindToRepresentation(
              selectedWorkbenchStyleState?.stylePreview.kind
                ?? buildDefaultStylePreview(selectedWorkbenchTarget.geometry, selectedWorkbenchTarget.layerIndex).kind,
            );
        const nextRules = (baseProfile.rules ?? []).map(rule =>
          rule.id === ruleId
            ? {
                ...rule,
                label: nextRule.label ?? rule.label,
                enabled: typeof nextRule.enabled === 'boolean' ? nextRule.enabled : rule.enabled,
                color: normalizeAdvancedRuleColor(nextRule.color ?? rule.color),
                logic: convertAdvancedRuleLogicToStyleRuleLogic(nextRule.logic ?? rule.logic),
                representation: null,
                paint: nextRule.paint ?? rule.paint ?? null,
                layout: nextRule.layout ?? rule.layout ?? null,
              }
            : rule,
        );
        const nextProfile: Extract<MapExperienceStyleProfile, { kind: 'rules' }> = {
          ...baseProfile,
          source_id: selectedWorkbenchTarget.sourceId,
          source_layer: selectedWorkbenchTarget.sourceLayer,
          kind: 'rules',
          label: baseProfile.label ?? `${formatFieldLabel(selectedWorkbenchTarget.sourceLayer)} rules`,
          legend_label: baseProfile.legend_label ?? `${formatFieldLabel(selectedWorkbenchTarget.sourceLayer)} features`,
          representation: nextRepresentation,
          mode: baseProfile.mode ?? 'hide_non_matching',
          rules: nextRules,
        };

        return {
          ...current,
          style_profiles: existingProfile
            ? current.style_profiles.map(profile => (profile.id === selectedRulesProfile.id ? nextProfile : profile))
            : [...current.style_profiles, nextProfile],
        };
      });
      if (
        nextRule.representation === 'symbol' &&
        typeof nextRule.layout?.['text-field'] === 'string' &&
        nextRule.layout['text-field'].trim().length > 0
      ) {
        ensureGlyphRootForSymbolText();
      }
    },
    [
      ensureGlyphRootForSymbolText,
      selectedRulesProfile,
      selectedWorkbenchStyleState?.stylePreview.kind,
      selectedWorkbenchTarget,
      updateMapExperienceState,
      updateSelectedRuleLayer,
    ],
  );

  const buildNewStyleProfile = useCallback(
    (
      target: { sourceId: string; sourceLayer: string; geometry?: string; layerIndex?: number },
      kind: MapExperienceStyleProfile['kind']
    ): MapExperienceStyleProfile => {
      const existingIds = new Set(mapExperience.style_profiles.map(profile => profile.id));
      const profileId = buildUniqueId(createExperienceId(kind, target.sourceId, target.sourceLayer), existingIds);
      const defaultRepresentation = previewKindToRepresentation(
        buildDefaultStylePreview(target.geometry ?? '', target.layerIndex ?? 0).kind,
      );
      if (kind === 'single') {
        return {
          id: profileId,
          source_id: target.sourceId,
          source_layer: target.sourceLayer,
          label: formatFieldLabel(target.sourceLayer),
          kind: 'single',
          representation: defaultRepresentation,
          color: 'rgb(59 130 246)',
          legend_label: `${formatFieldLabel(target.sourceLayer)} features`,
        };
      }
      if (kind === 'categorical') {
        return {
          id: profileId,
          source_id: target.sourceId,
          source_layer: target.sourceLayer,
          label: `${formatFieldLabel(target.sourceLayer)} by category`,
          kind: 'categorical',
          representation: defaultRepresentation,
          field: '',
          categories: [
            { value: 'default', label: 'Default', color: 'rgb(59 130 246)' },
          ],
          fallback_color: 'rgb(148 163 184)',
        };
      }
      if (kind === 'range') {
        return {
          id: profileId,
          source_id: target.sourceId,
          source_layer: target.sourceLayer,
          label: `${formatFieldLabel(target.sourceLayer)} by range`,
          kind: 'range',
          representation: defaultRepresentation,
          field: '',
          breaks: [
            { label: 'Low', min: 0, max: 25, color: 'rgb(59 130 246)' },
            { label: 'High', min: 25, max: undefined, color: 'rgb(249 115 22)' },
          ],
          fallback_color: 'rgb(148 163 184)',
        };
      }
      return {
        id: profileId,
        source_id: target.sourceId,
        source_layer: target.sourceLayer,
        label: `${formatFieldLabel(target.sourceLayer)} rules`,
        kind: 'rules',
        representation: defaultRepresentation,
      };
    },
    [buildUniqueId, mapExperience.style_profiles]
  );

  const handleSelectStyleMode = useCallback(
    (target: { key: string; sourceId: string; sourceLayer: string; sourceAlias?: string }, kind: MapExperienceStyleProfile['kind']) => {
      if (kind === 'rules') {
        updateMapExperienceState(current => {
          const seedTarget =
            workbenchTargets.find(entry => entry.key === target.key) ?? {
              key: target.key,
              sourceId: target.sourceId,
              sourceAlias: target.sourceAlias ?? target.sourceId,
              sourceLayer: target.sourceLayer,
              geometry: '' as MapSourceGeometryKind,
              layerIndex: 0,
              fields: [],
            };
          const existingRulesProfile = current.style_profiles.find(
            profile =>
              profile.source_id === target.sourceId &&
              profile.source_layer === target.sourceLayer &&
              profile.kind === 'rules',
          );
          const nextRulesProfile = existingRulesProfile ?? buildSeededRulesStyleProfile(seedTarget);
          return {
            ...current,
            style_profiles: existingRulesProfile ? current.style_profiles : [...current.style_profiles, nextRulesProfile],
            active_style_profiles: {
              ...current.active_style_profiles,
              [target.key]: nextRulesProfile.id,
            },
          };
        });
        setStyleSheetTab('styles');
        return;
      }

      const existingProfile = mapExperience.style_profiles.find(
        profile =>
          profile.source_id === target.sourceId &&
          profile.source_layer === target.sourceLayer &&
          profile.kind === kind,
      );

      if (existingProfile) {
        updateMapExperienceState(current => ({
          ...current,
          active_style_profiles: {
            ...current.active_style_profiles,
            [target.key]: existingProfile.id,
          },
        }));
        return;
      }

      const nextProfile = buildNewStyleProfile(target, kind);
      updateMapExperienceState(current => ({
        ...current,
        style_profiles: [...current.style_profiles, nextProfile],
        active_style_profiles: {
          ...current.active_style_profiles,
          [target.key]: nextProfile.id,
        },
      }));
    },
    [buildNewStyleProfile, mapExperience.style_profiles, updateMapExperienceState, workbenchTargets]
  );

  const handleUpdateStyleProfile = (profileId: string, updater: (profile: MapExperienceStyleProfile) => MapExperienceStyleProfile) => {
    updateMapExperienceState(current => ({
      ...current,
      style_profiles: current.style_profiles.map(profile => (profile.id === profileId ? updater(profile) : profile)),
    }));
  };

  const handleAddCategoryEntry = (profileId: string) => {
    handleUpdateStyleProfile(profileId, profile => {
      if (profile.kind !== 'categorical') {
        return profile;
      }
      return {
        ...profile,
        categories: [
          ...profile.categories,
          { value: '', label: '', color: 'rgb(59 130 246)' },
        ],
      };
    });
  };

  const handleAddRangeBreak = (profileId: string) => {
    handleUpdateStyleProfile(profileId, profile => {
      if (profile.kind !== 'range') {
        return profile;
      }
      return {
        ...profile,
        breaks: [
          ...profile.breaks,
          { min: '', max: '', label: '', color: 'rgb(59 130 246)' },
        ],
      };
    });
  };

  const handleDeleteStyleProfile = (target: { key: string }, profileId: string) => {
    updateMapExperienceState(current => {
      const nextActive = { ...current.active_style_profiles };
      if (nextActive[target.key] === profileId) {
        delete nextActive[target.key];
      }
      return {
        ...current,
        style_profiles: current.style_profiles.filter(profile => profile.id !== profileId),
        active_style_profiles: nextActive,
      };
    });
  };

  const ensureFilterSet = useCallback(
    (target: { sourceId: string; sourceLayer: string }) => {
      const existing = mapExperience.filter_sets.find(
        entry => entry.source_id === target.sourceId && entry.source_layer === target.sourceLayer,
      );
      if (existing) {
        return existing;
      }
      const id = buildUniqueId(
        createExperienceId('filters', target.sourceId, target.sourceLayer),
        new Set(mapExperience.filter_sets.map(entry => entry.id)),
      );
      const created: MapExperienceFilterSet = {
        id,
        label: `${formatFieldLabel(target.sourceLayer)} filters`,
        source_id: target.sourceId,
        source_layer: target.sourceLayer,
        filters: [],
      };
      updateMapExperience(current => ({
        ...current,
        filter_sets: [...current.filter_sets, created],
      }));
      return created;
    },
    [buildUniqueId, mapExperience.filter_sets]
  );

  const handleAddFilterDefinition = (target: { sourceId: string; sourceLayer: string }) => {
    const filterSet = ensureFilterSet(target);
    const existingIds = new Set(filterSet.filters.map(filter => filter.id));
    const nextId = buildUniqueId(`filter:${filterSet.source_layer}`, existingIds);
    updateMapExperienceState(current => ({
      ...current,
      filter_sets: current.filter_sets.map(entry =>
        entry.id === filterSet.id
          ? {
              ...entry,
              filters: [
                ...entry.filters,
                {
                  id: nextId,
                  kind: 'single_select',
                  field: '',
                  label: 'New filter',
                  description: null,
                  editable_in_view: true,
                  options: [],
                  placeholder: null,
                  min: null,
                  max: null,
                  step: null,
                  default_value: null,
                },
              ],
            }
          : entry,
      ),
    }));
  };

  const handleUpdateFilterDefinition = (
    filterSetId: string,
    filterId: string,
    updater: (definition: MapExperienceFilterDefinition) => MapExperienceFilterDefinition,
  ) => {
    updateMapExperienceState(current => ({
      ...current,
      filter_sets: current.filter_sets.map(entry =>
        entry.id === filterSetId
          ? {
              ...entry,
              filters: entry.filters.map(definition =>
                definition.id === filterId ? updater(definition) : definition,
              ),
            }
          : entry,
      ),
    }));
  };

  const handleDeleteFilterDefinition = (filterSetId: string, filterId: string) => {
    updateMapExperienceState(current => {
      const nextActive = { ...current.active_filter_values };
      const activeSet = { ...(nextActive[filterSetId] ?? {}) };
      delete activeSet[filterId];
      nextActive[filterSetId] = activeSet;
      return {
        ...current,
        filter_sets: current.filter_sets.map(entry =>
          entry.id === filterSetId
            ? {
                ...entry,
                filters: entry.filters.filter(definition => definition.id !== filterId),
              }
            : entry,
        ),
        active_filter_values: nextActive,
      };
    });
  };

  const handleDesktopPaneLayerUpdateForTarget = useCallback((
    target: (typeof workbenchTargets)[number],
    currentLayerExperience: LayerExperience,
    currentStyleState: NonNullable<typeof selectedWorkbenchStyleState>,
    updater: (prev: LayerExperience) => LayerExperience,
  ) => {
    if (!target) {
      return;
    }

    const nextLayer = updater(currentLayerExperience);
    const appearance = nextLayer.appearance;
    const nextActiveVariantId = appearance.active_variant_id ?? appearance.variants[0]?.id ?? null;
    const fallbackRepresentation = previewKindToRepresentation(
      currentStyleState.stylePreview.kind ?? buildDefaultStylePreview(target.geometry, target.layerIndex).kind,
    );

    const nextProfiles = appearance.variants.flatMap(profile => {
      if (profile.kind === 'rules') {
        return [{
          id: profile.id,
          source_id: target.sourceId,
          source_layer: target.sourceLayer,
          kind: 'rules' as const,
          representation: profile.style?.representation ?? fallbackRepresentation,
          label: profile.label ?? null,
          legend_label: appearance.legend?.label ?? null,
          interaction: cloneStyleInteractionConfig(profile.style?.interaction),
          layout: sanitizeRulesProfileLayoutForRepresentation(
            profile.style?.representation ?? fallbackRepresentation,
            profile.style?.layout ?? null,
          ),
          mode: profile.mode ?? 'hide_non_matching',
          rules: (profile.rules ?? []).map(rule => ({
            id: rule.id,
            label: rule.label,
            color: rule.color,
            enabled: rule.enabled,
            logic: rule.logic,
            representation: null,
            paint: rule.paint ?? null,
            layout: rule.layout ?? null,
          })),
        }];
      }
      const nextRepresentation = profile.style?.representation ?? fallbackRepresentation;
      if (profile.kind === 'single') {
        const existingSingleProfile =
          mapExperience.style_profiles.find(
            (entry): entry is Extract<MapExperienceStyleProfile, { kind: 'single' }> =>
              entry.id === profile.id &&
              entry.source_id === target.sourceId &&
              entry.source_layer === target.sourceLayer &&
              entry.kind === 'single',
          ) ?? null;
        const nextColor = readPaintPrimaryColorValue(
          nextRepresentation,
          profile.style?.paint ?? null,
          currentStyleState.stylePreview.colors[0] ?? 'rgb(59 130 246)',
        );
        return [{
          id: profile.id,
          source_id: target.sourceId,
          source_layer: target.sourceLayer,
          kind: 'single' as const,
          representation: nextRepresentation,
          label: profile.label ?? appearance.legend?.title ?? formatFieldLabel(target.sourceLayer),
          interaction: cloneStyleInteractionConfig(profile.style?.interaction),
          color: preserveStoredStyleColorValue(existingSingleProfile?.color, nextColor, themeColorPalette),
          legend_label: profile.legend_label ?? appearance.legend?.label ?? `${formatFieldLabel(target.sourceLayer)} features`,
          icon_image: readLayoutIconImage(profile.style?.layout ?? null),
          paint: sanitizePaintForRepresentation(
            nextRepresentation,
            profile.style?.paint ?? null,
            {
              iconColorEnabled: isSymbolIconTintable(
                activeSpriteIconSet,
                readLayoutIconImage(profile.style?.layout ?? null),
              ),
            },
          ),
          layout: cloneStyleOverrideRecord(profile.style?.layout ?? null),
        }];
      }
      if (profile.kind === 'categorical') {
        const existingCategoricalProfile =
          mapExperience.style_profiles.find(
            (entry): entry is Extract<MapExperienceStyleProfile, { kind: 'categorical' }> =>
              entry.id === profile.id &&
              entry.source_id === target.sourceId &&
              entry.source_layer === target.sourceLayer &&
              entry.kind === 'categorical',
          ) ?? null;
        return [{
          id: profile.id,
          source_id: target.sourceId,
          source_layer: target.sourceLayer,
          kind: 'categorical' as const,
          representation: nextRepresentation,
          label: profile.label ?? formatFieldLabel(target.sourceLayer),
          interaction: cloneStyleInteractionConfig(profile.style?.interaction),
          field: profile.field ?? '',
          categories: profile.categories.map(category => {
            const existingCategory = existingCategoricalProfile?.categories.find(entry => entry.value === category.value) ?? null;
            const nextColor = readPaintPrimaryColorValue(
              nextRepresentation,
              category.paint ?? null,
              'rgb(59 130 246)',
            );
            return {
              value: category.value,
              label: category.label ?? category.value,
              color: preserveStoredStyleColorValue(existingCategory?.color, nextColor, themeColorPalette),
              hidden: category.hidden ?? false,
              icon_image: readLayoutIconImage(category.layout ?? null),
              paint: sanitizePaintForRepresentation(
                nextRepresentation,
                category.paint ?? null,
                {
                  iconColorEnabled: isSymbolIconTintable(
                    activeSpriteIconSet,
                    readLayoutIconImage(category.layout ?? null),
                  ),
                },
              ),
              layout: cloneStyleOverrideRecord(category.layout ?? null),
            };
          }),
          fallback_color: 'rgb(148 163 184)',
        }];
      }
      const existingRangeProfile =
        mapExperience.style_profiles.find(
          (entry): entry is Extract<MapExperienceStyleProfile, { kind: 'range' }> =>
            entry.id === profile.id &&
            entry.source_id === target.sourceId &&
            entry.source_layer === target.sourceLayer &&
            entry.kind === 'range',
        ) ?? null;
      return [{
        id: profile.id,
        source_id: target.sourceId,
        source_layer: target.sourceLayer,
        kind: 'range' as const,
        representation: nextRepresentation,
        label: profile.label ?? formatFieldLabel(target.sourceLayer),
        interaction: cloneStyleInteractionConfig(profile.style?.interaction),
        field: profile.field ?? '',
          breaks: profile.breaks.map((entry, index) => {
            const existingBreak =
              (entry.id
                ? existingRangeProfile?.breaks.find(candidate => candidate.id === entry.id)
                : null) ?? existingRangeProfile?.breaks[index] ?? null;
            const nextColor = readPaintPrimaryColorValue(
              nextRepresentation,
              entry.paint ?? null,
              'rgb(59 130 246)',
            );
            return {
              id: entry.id,
              label: entry.label,
              min: typeof entry.min === 'number' && Number.isFinite(entry.min) ? entry.min : null,
              max: typeof entry.max === 'number' && Number.isFinite(entry.max) ? entry.max : null,
              color: preserveStoredStyleColorValue(existingBreak?.color, nextColor, themeColorPalette),
              hidden: entry.hidden ?? false,
              icon_image: readLayoutIconImage(entry.layout ?? null),
              paint: sanitizePaintForRepresentation(
                nextRepresentation,
                entry.paint ?? null,
                {
                  iconColorEnabled: isSymbolIconTintable(
                    activeSpriteIconSet,
                    readLayoutIconImage(entry.layout ?? null),
                  ),
                },
              ),
              layout: cloneStyleOverrideRecord(entry.layout ?? null),
            };
          }),
          fallback_color: 'rgb(148 163 184)',
        }];
    });

    const nextProfilesNeedGlyphs = nextProfiles.some(profile => {
      if (profile.representation !== 'symbol') {
        return false;
      }
      if (profile.kind === 'rules') {
        return profile.rules.some(rule =>
          typeof rule.layout?.['text-field'] === 'string' && rule.layout['text-field'].trim().length > 0,
        );
      }
      if (profile.kind === 'single') {
        return typeof profile.layout?.['text-field'] === 'string' && profile.layout['text-field'].trim().length > 0;
      }
      if (profile.kind === 'categorical') {
        return profile.categories.some(category =>
          typeof category.layout?.['text-field'] === 'string' && category.layout['text-field'].trim().length > 0,
        );
      }
      return profile.breaks.some(entry =>
        typeof entry.layout?.['text-field'] === 'string' && entry.layout['text-field'].trim().length > 0,
      );
    });

    if (nextProfilesNeedGlyphs) {
      ensureGlyphRootForSymbolText();
    }

    const defaultSourceDefinition = buildDefaultInteractivitySourceDefinition(target);
    const nextSourceDefinition = (nextLayer.enrichments?.source_definition ??
      defaultSourceDefinition) as MapExperienceInteractivitySourceDefinition;
    const nextResolverDefinitions =
      (nextLayer.enrichments?.resolver_definitions ?? []) as MapExperienceResolverDefinition[];
    const nextSurfaceDefinitions =
      (nextLayer.interactivity?.surface_definitions ?? []) as MapExperienceSurfaceDefinition[];
    const nextPipelineDefinitions =
      (nextLayer.interactivity?.pipeline_definitions ?? []) as MapExperiencePipelineDefinition[];
    const nextBuiltinValues = nextLayer.enrichments?.builtin_values?.length
      ? nextLayer.enrichments.builtin_values
      : createDefaultMapExperienceBuiltinValues();
    const hasSourceDefinitionChanges =
      stableJson(nextSourceDefinition) !== stableJson(defaultSourceDefinition) ||
      Boolean(nextLayer.enrichments?.entity_type?.trim()) ||
      Boolean(nextLayer.enrichments?.entity_ref_field?.trim()) ||
      Boolean(nextLayer.enrichments?.action_profile_field?.trim()) ||
      Object.keys(nextLayer.enrichments?.capability_fields ?? {}).length > 0 ||
      nextResolverDefinitions.length > 0 ||
      nextSurfaceDefinitions.length > 0 ||
      nextPipelineDefinitions.length > 0;
    const hasBuiltinValueChanges = stableJson(nextBuiltinValues) !== stableJson(createDefaultMapExperienceBuiltinValues());

    updateMapExperienceState(current => {
      const targetProfiles = current.style_profiles.filter(
        profile =>
          profile.source_id === target.sourceId &&
          profile.source_layer === target.sourceLayer,
      );
      const nextProfileIds = new Set(nextProfiles.map(profile => profile.id));
      const preservedTargetProfiles = targetProfiles.filter(
        (profile): profile is Extract<MapExperienceStyleProfile, { kind: 'rules' }> =>
          profile.kind === 'rules' && !nextProfileIds.has(profile.id),
      );
      const remainingProfiles = current.style_profiles.filter(
        profile =>
          profile.source_id !== target.sourceId ||
          profile.source_layer !== target.sourceLayer,
      );
      const existingFilterSet =
        current.filter_sets.find(
          entry =>
            entry.source_id === target.sourceId &&
            entry.source_layer === target.sourceLayer,
        ) ?? null;
      const nextFilterDefinitions = nextLayer.filters.definitions.map(definition => ({
        id: definition.id,
        kind: definition.kind,
        field: definition.field,
        operator: definition.operator ?? null,
        value: definition.value ?? null,
        label: definition.label ?? null,
        description: definition.helper_text ?? null,
        editable_in_view: definition.runtime?.editable_in_view ?? true,
        options:
          definition.runtime?.options?.map(option => ({
            value: option.value,
            label: option.label ?? null,
          })) ?? [],
        placeholder: definition.runtime?.placeholder ?? null,
        min: typeof definition.runtime?.min === 'number' ? definition.runtime.min : null,
        max: typeof definition.runtime?.max === 'number' ? definition.runtime.max : null,
        step: typeof definition.runtime?.step === 'number' ? definition.runtime.step : null,
        default_value:
          definition.kind === 'numeric_range'
            ? {
                min: typeof definition.runtime?.default_min === 'number' ? definition.runtime.default_min : null,
                max: typeof definition.runtime?.default_max === 'number' ? definition.runtime.default_max : null,
              }
            : definition.runtime?.default_value ?? null,
      }));
      const shouldPersistFilterSet =
        Boolean(existingFilterSet) ||
        nextFilterDefinitions.length > 0 ||
        Boolean(nextLayer.filters.label?.trim());
      const nextFilterSetId = shouldPersistFilterSet
        ? nextLayer.filters.id ??
          existingFilterSet?.id ??
          buildUniqueId(
            createExperienceId('filters', target.sourceId, target.sourceLayer),
            new Set(current.filter_sets.map(entry => entry.id)),
          )
        : null;
      const nextFilterSets = shouldPersistFilterSet && nextFilterSetId
        ? [
            ...current.filter_sets.filter(entry => entry.id !== existingFilterSet?.id),
            {
              id: nextFilterSetId,
              label:
                nextLayer.filters.label?.trim() ||
                existingFilterSet?.label ||
                `${formatFieldLabel(target.sourceLayer)} filters`,
              source_id: target.sourceId,
              source_layer: target.sourceLayer,
              filters: nextFilterDefinitions,
            },
          ]
        : current.filter_sets.filter(entry => entry.id !== existingFilterSet?.id);
      const nextActiveFilterValues = { ...current.active_filter_values };
      if (existingFilterSet?.id && (!shouldPersistFilterSet || existingFilterSet.id !== nextFilterSetId)) {
        delete nextActiveFilterValues[existingFilterSet.id];
      }
      if (shouldPersistFilterSet && nextFilterSetId) {
        const sourceValues = nextLayer.filters.active_values ?? current.active_filter_values[nextFilterSetId] ?? {};
        const allowedDefinitionIds = new Set(nextFilterDefinitions.map(definition => definition.id));
        nextActiveFilterValues[nextFilterSetId] = Object.fromEntries(
          Object.entries(sourceValues).filter(([filterId]) => allowedDefinitionIds.has(filterId)),
        );
      }
      const nextActiveStyleProfiles = { ...current.active_style_profiles };
      if (!nextActiveVariantId) {
        delete nextActiveStyleProfiles[target.key];
      } else {
        nextActiveStyleProfiles[target.key] = nextActiveVariantId;
      }

      const currentInteractivity = current.interactivity ?? {
        sources: [],
        builtin_values: createDefaultMapExperienceBuiltinValues(),
        resolvers: [],
        surfaces: [],
        pipelines: [],
      };
      const nextSources = hasSourceDefinitionChanges
        ? [
            ...currentInteractivity.sources.filter(
              entry =>
                !(
                  entry.source_id === target.sourceId &&
                  entry.source_layer === target.sourceLayer
                ),
            ),
            {
              ...nextSourceDefinition,
              source_id: target.sourceId,
              source_layer: target.sourceLayer,
            },
          ]
        : currentInteractivity.sources.filter(
            entry =>
              !(
                entry.source_id === target.sourceId &&
                entry.source_layer === target.sourceLayer
              ),
          );
      const nextResolvers = [
        ...currentInteractivity.resolvers.filter(
          entry =>
            !(
              (entry.source_id ?? target.sourceId) === target.sourceId &&
              (entry.source_layer ?? target.sourceLayer) === target.sourceLayer
            ),
        ),
        ...nextResolverDefinitions.map(entry => ({
          ...entry,
          source_id: entry.source_id ?? target.sourceId,
          source_layer: entry.source_layer ?? target.sourceLayer,
        })),
      ];
      const nextSurfaces = [
        ...currentInteractivity.surfaces.filter(
          entry =>
            !(
              (entry.source_id ?? target.sourceId) === target.sourceId &&
              (entry.source_layer ?? target.sourceLayer) === target.sourceLayer
            ),
        ),
        ...nextSurfaceDefinitions.map(entry => ({
          ...entry,
          source_id: entry.source_id ?? target.sourceId,
          source_layer: entry.source_layer ?? target.sourceLayer,
        })),
      ];
      const nextPipelines = [
        ...currentInteractivity.pipelines.filter(
          entry =>
            !(
              (entry.source_id ?? target.sourceId) === target.sourceId &&
              (entry.source_layer ?? target.sourceLayer) === target.sourceLayer
            ),
        ),
        ...nextPipelineDefinitions.map(entry => ({
          ...entry,
          source_id: entry.source_id ?? target.sourceId,
          source_layer: entry.source_layer ?? target.sourceLayer,
        })),
      ];
      const nextInteractivity =
        nextSources.length > 0 ||
        nextResolvers.length > 0 ||
        nextSurfaces.length > 0 ||
        nextPipelines.length > 0 ||
        hasBuiltinValueChanges
          ? {
              sources: nextSources,
              builtin_values: nextBuiltinValues,
              resolvers: nextResolvers,
              surfaces: nextSurfaces,
              pipelines: nextPipelines,
            }
          : null;

      return {
        ...current,
        version: nextInteractivity ? 2 : current.version,
        style_profiles: [...remainingProfiles, ...preservedTargetProfiles, ...nextProfiles],
        filter_sets: nextFilterSets,
        active_filter_values: nextActiveFilterValues,
        active_style_profiles: nextActiveStyleProfiles,
        interactivity: nextInteractivity,
      };
    });
  }, [
    activeSpriteIconSet,
    buildUniqueId,
    ensureGlyphRootForSymbolText,
    mapExperience.style_profiles,
    themeColorPalette,
    updateMapExperienceState,
  ]);

  const handleDesktopPaneLayerUpdate = useCallback((updater: (prev: LayerExperience) => LayerExperience) => {
    if (!selectedWorkbenchTarget || !selectedLayerExperience || !selectedWorkbenchStyleState) {
      return;
    }
    handleDesktopPaneLayerUpdateForTarget(
      selectedWorkbenchTarget,
      selectedLayerExperience,
      selectedWorkbenchStyleState,
      updater,
    );
  }, [
    handleDesktopPaneLayerUpdateForTarget,
    selectedLayerExperience,
    selectedWorkbenchStyleState,
    selectedWorkbenchTarget,
  ]);

  const handleFilterValueChange = (filterSetId: string, filterId: string, value: unknown) => {
    updateMapExperienceState(current => ({
      ...current,
      active_filter_values: {
        ...current.active_filter_values,
        [filterSetId]: {
          ...(current.active_filter_values[filterSetId] ?? {}),
          [filterId]: value,
        },
      },
    }));
  };

  const handleToggleViewerFiltersSheet = useCallback(() => {
    setViewerLegendSheetOpen(false);
    setViewerFiltersSheetOpen(current => !current);
  }, []);

  const handleToggleViewerLegendSheet = useCallback(() => {
    setViewerFiltersSheetOpen(false);
    setViewerLegendSheetOpen(current => !current);
  }, []);

  const handleViewerFilterEnabledChange = useCallback(
    (itemId: string, nextEnabled: boolean) => {
      const target = viewerFilterDefinitions.get(itemId);
      if (!target) return;
      setViewerFilterState(current => {
        const existing =
          current[itemId] ??
          resolveWorkbenchViewerFilterRuntimeEntry(
            target.filter,
            mapExperience.active_filter_values[target.filterSetId]?.[target.filter.id],
          );
        const configuredExistingValue = resolveMapExperienceFilterValue(target.filter, existing.value);
        const nextValue = nextEnabled
          ? hasConfiguredMapExperienceFilterValue(target.filter.kind, configuredExistingValue)
            ? configuredExistingValue
            : resolveMapExperienceFilterValue(target.filter, undefined)
          : resolveMapExperienceFilterValue(target.filter, null);
        return {
          ...current,
          [itemId]: {
            enabled: nextEnabled,
            value: nextValue,
          },
        };
      });
    },
    [mapExperience.active_filter_values, viewerFilterDefinitions],
  );

  const handleViewerFilterValueChange = useCallback(
    (itemId: string, value: MapViewerFilterValue) => {
      const target = viewerFilterDefinitions.get(itemId);
      if (!target) return;
      const nextValue = resolveMapExperienceFilterValue(target.filter, value);
      setViewerFilterState(current => {
        const fallback =
          current[itemId] ??
          resolveWorkbenchViewerFilterRuntimeEntry(
            target.filter,
            mapExperience.active_filter_values[target.filterSetId]?.[target.filter.id],
          );
        return {
          ...current,
          [itemId]: {
            enabled: target.filter.kind === 'boolean' ? Boolean(nextValue) : fallback.enabled,
            value: nextValue,
          },
        };
      });
    },
    [mapExperience.active_filter_values, viewerFilterDefinitions],
  );

  const handleViewerFilterReset = useCallback(
    (itemId: string) => {
      const target = viewerFilterDefinitions.get(itemId);
      if (!target) return;
      setViewerFilterState(current => ({
        ...current,
        [itemId]: resolveWorkbenchViewerFilterRuntimeEntry(target.filter, undefined),
      }));
    },
    [viewerFilterDefinitions],
  );

  const handleViewerResetAllFilters = useCallback(() => {
    setViewerFilterState(buildInitialWorkbenchViewerFilterRuntimeState(mapExperience));
  }, [mapExperience]);

  const handleCreateInteractivityItem = useCallback((kind: MapInteractivityManagedItemKind) => {
    if (!selectedWorkbenchTarget || !selectedLayerExperience) {
      return;
    }
    const interactivity = selectedLayerExperience.interactivity ?? {};
    const firstField = interactivityEditorFieldOptions[0]?.id ?? 'id';
    if (kind === 'surface') {
      const surfaceId = buildUniqueId(
        `surface:${selectedWorkbenchTarget.sourceLayer}`,
        new Set((interactivity.surface_definitions ?? []).map(surface => surface.surface_id)),
      );
      setActiveInteractivityEditor({
        kind,
        mode: 'create',
        draft: {
          surface_id: surfaceId,
          surface_kind: 'side_panel',
          label: 'New Surface',
          title_binding: firstField ? { field: firstField } : null,
          data_binding: { source: 'selected_feature' },
          sections: [
            {
              id: `${surfaceId}:section:details`,
              kind: 'details_grid',
              label: 'Details',
              items: firstField ? [{ label: interactivityEditorFieldOptions[0]?.label ?? firstField, field: firstField }] : [],
            },
          ],
        },
      });
      return;
    }
    const pipelineId = buildUniqueId(
      `pipeline:${selectedWorkbenchTarget.sourceLayer}`,
      new Set((interactivity.pipeline_definitions ?? []).map(pipeline => pipeline.pipeline_id)),
    );
    const invokableSurfaces =
      interactivity.surface_definitions?.filter(surface => surface.surface_kind !== 'ambiguous_selection_summary') ?? [];
    setActiveInteractivityEditor({
      kind: 'pipeline',
      mode: 'create',
      draft: {
        pipeline_id: pipelineId,
        label: 'New Pipeline',
        trigger: { kind: 'click', source: 'feature' },
        steps: [],
        effects: invokableSurfaces[0]
          ? [{ effect_id: `${pipelineId}:effect:open`, kind: 'open_surface', surface_id: invokableSurfaces[0].surface_id }]
          : [],
        stop_propagation: false,
      },
    });
  }, [
    buildUniqueId,
    interactivityEditorFieldOptions,
    selectedLayerExperience,
    selectedWorkbenchTarget,
  ]);

  const handleEditInteractivityItem = useCallback((kind: MapInteractivityManagedItemKind, id: string) => {
    const interactivity = selectedLayerExperience?.interactivity;
    if (!interactivity) {
      return;
    }
    if (kind === 'surface') {
      const draft = interactivity.surface_definitions?.find(surface => surface.surface_id === id);
      if (draft) {
        setActiveInteractivityEditor({ kind, mode: 'edit', draft: cloneJsonValue(draft) });
      }
      return;
    }
    const draft = interactivity.pipeline_definitions?.find(pipeline => pipeline.pipeline_id === id);
    if (draft) {
      setActiveInteractivityEditor({ kind: 'pipeline', mode: 'edit', draft: cloneJsonValue(draft) });
    }
  }, [selectedLayerExperience]);

  const handleSaveInteractivityEditor = useCallback(() => {
    if (!activeInteractivityEditor) {
      return;
    }
    handleDesktopPaneLayerUpdate(prev => {
      const currentInteractivity = prev.interactivity ?? {
        surface_definitions: [],
        pipeline_definitions: [],
      };
      if (activeInteractivityEditor.kind === 'surface') {
        return {
          ...prev,
          interactivity: {
            ...currentInteractivity,
            surface_definitions: upsertByKey(
              currentInteractivity.surface_definitions ?? [],
              activeInteractivityEditor.draft,
              surface => surface.surface_id,
            ),
          },
        };
      }
      return {
        ...prev,
        interactivity: {
          ...currentInteractivity,
          pipeline_definitions: upsertByKey(
            currentInteractivity.pipeline_definitions ?? [],
            activeInteractivityEditor.draft,
            pipeline => pipeline.pipeline_id,
          ),
        },
      };
    });
    setActiveInteractivityEditor(null);
  }, [activeInteractivityEditor, handleDesktopPaneLayerUpdate]);
  const activeDedicatedInteractivityEditor = activeInteractivityEditor;
  const handleInteractivityEditorOpenChange = useCallback((open: boolean) => {
    if (!open) {
      setActiveInteractivityEditor(null);
    }
  }, []);
  const activeInteractivityEditorTitle = useMemo(() => {
    if (!activeDedicatedInteractivityEditor || !selectedWorkbenchTarget) {
      return null;
    }
    const editorLabel =
      activeDedicatedInteractivityEditor.kind === 'surface'
        ? activeDedicatedInteractivityEditor.mode === 'create'
          ? 'Add Surface'
          : 'Edit Surface'
        : activeDedicatedInteractivityEditor.mode === 'create'
          ? 'Add Interaction'
          : 'Edit Interaction';
    return (
      <div className="flex items-center gap-2 text-[10px] font-bold uppercase tracking-[0.2em] text-muted-foreground/60">
        <span>Workbench</span>
        <ChevronRight className="h-3 w-3" />
        <span className="text-foreground">{selectedWorkbenchTarget.label}</span>
        <ChevronRight className="h-3 w-3" />
        <span className="text-primary">{editorLabel}</span>
      </div>
    );
  }, [activeDedicatedInteractivityEditor, selectedWorkbenchTarget]);

  const captureLayerVisibility = () =>
    Object.fromEntries(workbenchTargets.map(target => [target.key, target.rendered]));

  const applyLayerVisibilityState = useCallback(
    (styleText: string, layerVisibility: Record<string, boolean>) => {
      let parsed: Record<string, unknown>;
      try {
        parsed = parseJsonText(styleText) as Record<string, unknown>;
      } catch {
        return styleText;
      }

      let nextLayers = Array.isArray(parsed.layers) ? [...(parsed.layers as Array<Record<string, unknown>>)] : [];
      workbenchTargets.forEach(target => {
        const desired = layerVisibility[target.key];
        if (typeof desired !== 'boolean') {
          return;
        }
        const isRendered = nextLayers.some(layer => matchesWorkbenchLayerTarget(layer, target.sourceAlias, target.sourceLayer));
        if (desired === isRendered) {
          return;
        }
        if (desired) {
          const sourceMeta = sourceById.get(target.sourceId);
          if (!sourceMeta) {
            return;
          }
          const layerMeta = extractVectorLayers(sourceMeta).find(entry => entry.id === target.sourceLayer) ?? {
            id: target.sourceLayer,
            geometry: '',
          };
          nextLayers = [
            ...nextLayers,
            ...buildDefaultLayersForVectorLayer(sourceMeta, target.sourceAlias, layerMeta, false),
          ];
          return;
        }
        nextLayers = nextLayers.filter(
          layer => !matchesWorkbenchLayerTarget(layer, target.sourceAlias, target.sourceLayer),
        );
      });

      return JSON.stringify(
        {
          ...parsed,
          layers: nextLayers,
        },
        null,
        2,
      );
    },
    [parseJsonText, sourceById, workbenchTargets]
  );

  const handleSavePreset = () => {
    const label = presetDraftLabel.trim();
    if (!label) {
      return;
    }
    const existingIds = new Set(mapExperience.presets.map(preset => preset.id));
    const id = buildUniqueId(`preset-${toAlias(label)}`, existingIds);
    const preset = buildMapExperiencePreset({
      id,
      label,
      viewport: currentViewRef.current ?? previewViewRef.current ?? null,
      layerVisibility: captureLayerVisibility(),
      activeStyleProfiles: mapExperience.active_style_profiles,
      activeFilterValues: mapExperience.active_filter_values,
    });
    setPresetDraftLabel('');
    updateMapExperienceState(current => upsertMapExperiencePreset(current, preset));
  };

  const handleDeletePreset = (presetId: string) => {
    updateMapExperienceState(current => removeMapExperiencePreset(current, presetId));
  };

  const handleApplyPreset = (preset: MapExperiencePresetDefinition) => {
    const applied = applyMapExperiencePreset(mapExperience, preset.id);
    updateMapExperience(() => applied.experience);
    if (applied.viewport) {
      applyPreviewView('preset apply', applied.viewport, {
        syncCurrentView: true,
        metadata: {
          presetId: preset.id,
        },
      });
    }
    if (applied.layerVisibility) {
      updateEditorText(current => applyLayerVisibilityState(current, applied.layerVisibility ?? {}));
    }
  };

  const handleFeaturePopupAction = async (
    popup: MapWorkbenchResolvedFeaturePopup,
    action: MapWorkbenchResolvedFeatureAction,
    feature: MapWorkbenchFeatureInspection,
  ) => {
    if (action.disabled) {
      return;
    }
    const pipeline = action.pipeline;
    const properties = feature.properties ?? {};
    const pendingExternalWindow = pipeline.effects.some(effect => effect.kind === 'open_external_url')
      ? prepareMapExternalNavigationWindow()
      : null;
    let externalNavigationCommitted = false;
    const context = resolveFeatureInteractivityContext(feature);
    const manualPipeline: LayerExperiencePipelineDefinition = {
      ...pipeline,
      trigger: {
        ...pipeline.trigger,
        kind: 'manual',
        source: 'manual',
      },
    };
    const executions = await runMapExperienceInteractivityPipelines({
      interactivity: {
        pipelines: [manualPipeline],
        surfaces: popup.surfaces,
        resolvers: context?.layerExperience.enrichments?.resolver_definitions ?? [],
      },
      trigger: {
        kind: 'manual',
        feature: context?.runtimeFeature ?? undefined,
      },
      executeResolver: adapter.executeInteractivityResolver,
    });
    const highlightTarget =
      workbenchHighlightTargets.find(
        target =>
          target.sourceId === context?.layerExperience.source_id &&
          target.sourceLayer === context?.layerExperience.source_layer &&
          (!feature.source || feature.source === target.sourceAlias),
      ) ?? null;

    for (const execution of executions) {
      for (const effectExecution of execution.effects) {
        if (effectExecution.status !== 'ready') {
          continue;
        }
        const effect = effectExecution.effect;
        if (effect.kind === 'copy_field') {
          const field =
            typeof effect.input === 'string'
              ? effect.input
              : isRecordValue(effect.input) && typeof effect.input.field === 'string'
                ? effect.input.field
                : typeof effect.value === 'string'
                  ? effect.value
                  : '';
          const value = field ? properties[field] : null;
          if (value != null && navigator?.clipboard) {
            await navigator.clipboard.writeText(String(value));
          }
          continue;
        }
        if (effect.kind === 'open_route') {
          const template =
            typeof effect.input === 'string'
              ? effect.input
              : isRecordValue(effect.input) && typeof effect.input.template === 'string'
                ? effect.input.template
                : '';
          const route = template ? renderEncodedFeatureTemplate(template, properties) : '';
          if (route.startsWith('/')) {
            navigate(route);
          }
          continue;
        }
        if (effect.kind === 'open_external_url') {
          const template =
            typeof effect.input === 'string'
              ? effect.input
              : isRecordValue(effect.input) && typeof effect.input.template === 'string'
                ? effect.input.template
                : '';
          const url = resolveMapExternalNavigationUrl(template, properties);
          if (url) {
            openMapExternalNavigationUrl(url, pendingExternalWindow);
            externalNavigationCommitted = true;
          }
          continue;
        }
        if (effect.kind === 'fly_to_feature') {
          const center = resolveFeatureCenter(feature);
          if (!center) {
            continue;
          }
          const nextView = {
            center,
            zoom: Math.max((currentViewRef.current?.zoom ?? defaultViewState.zoom ?? 10) + 1, 12),
            bearing: currentViewRef.current?.bearing ?? defaultViewState.bearing,
            pitch: currentViewRef.current?.pitch ?? defaultViewState.pitch,
          } satisfies MapWorkbenchViewState;
          applyPreviewView('popup action fly_to_feature', nextView, {
            syncCurrentView: true,
            metadata: {
              pipelineId: execution.pipeline_id,
              feature: summarizeHoverDebugFeature(feature),
            },
          });
          continue;
        }
        if (effect.kind === 'highlight_set' && highlightTarget) {
          setManualHighlightDescriptors(current =>
            appendWorkbenchHighlightDescriptor(current, {
              channel: 'selected',
              feature,
              target: highlightTarget,
            }),
          );
          continue;
        }
        if (effect.kind === 'clear_highlight_set') {
          setManualHighlightDescriptors([]);
          continue;
        }
        if (effect.kind === 'activate_builtin_tool' && effect.tool_id) {
          setActiveToolId(effect.tool_id);
          continue;
        }
        if (effect.kind === 'clear_drawings') {
          setDrawnGeometry(null);
          continue;
        }
        if (effect.kind === 'clear_selection') {
          setPinnedFeature(null);
          setPinnedFeatureTrigger(null);
          setManualHighlightDescriptors([]);
          continue;
        }
        if ((effect.kind === 'open_surface' || effect.kind === 'bind_surface_data') && effect.surface_id) {
          const surface = effectExecution.surface as LayerExperienceSurfaceDefinition | null;
          if (!surface || surface.surface_kind === 'hover_popup' || surface.surface_kind === 'ambiguous_selection_summary') {
            continue;
          }
          const resolvedSurface = buildResolvedFeatureSurface({
            feature,
            surface,
            surfaces: popup.surfaces,
            pipelines: popup.pipelines,
          });
          setActionSurface({
            ...resolvedSurface,
            title: resolvedSurface.title || action.label,
          });
          setActionSurfaceFeature(feature);
          if (resolvedSurface.surface.surface_kind === 'side_panel') {
            setRightPaneCollapsed(false);
            setActionPanelOpen(false);
          } else {
            setActionPanelOpen(true);
          }
          setActionPanelSource('manual');
          continue;
        }
        if (effect.kind === 'close_surface') {
          setActionPanelOpen(false);
          setActionSurface(null);
          setActionSurfaceFeature(null);
          setActionPanelSource(null);
        }
      }
    }
    if (pendingExternalWindow && !externalNavigationCommitted && !pendingExternalWindow.closed) {
      pendingExternalWindow.close();
    }
    if (behavior.performFeatureAction) {
      await behavior.performFeatureAction({
        action,
        context: {
          feature,
          popup,
        },
      });
    }
  };

  const handleToolRuntimeEvent = useCallback((event: MapWorkbenchToolRuntimeEvent) => {
    if (event.kind === 'drawing_cleared') {
      setDrawnGeometry(null);
      return;
    }
    if (event.kind === 'drawing_completed') {
      setDrawnGeometry(event.geometry);
      return;
    }
    if (event.kind === 'drawing_updated' && event.draft === false) {
      setDrawnGeometry(event.geometry);
      return;
    }
    setDrawnGeometry(null);
  }, []);

  const handleToolRailAction = useCallback(
    (toolId: MapBuiltInToolId) => {
      if (toolId === 'clear_drawings') {
        setDrawnGeometry(null);
        setHoveredFeature(null);
        setPinnedFeature(null);
        setPinnedFeatureTrigger(null);
        setActiveToolId(resolvedToolRailConfig.default_tool_id ?? 'select');
        return;
      }
      setHoveredFeature(null);
      setPinnedFeature(null);
      setPinnedFeatureTrigger(null);
      setActiveToolId(toolId);
    },
    [resolvedToolRailConfig.default_tool_id],
  );

  const toolRailItems = useMemo(
    () =>
      visibleBuiltInTools.map(tool => {
        const icon =
          tool.id === 'select' ? (
            <MousePointer2 className="h-4 w-4" />
          ) : tool.id === 'inspect' ? (
            <ScanSearch className="h-4 w-4" />
          ) : tool.id === 'draw_point' ? (
            <MapPin className="h-4 w-4" />
          ) : tool.id === 'draw_line' || tool.id === 'draw_polygon' ? (
            <PencilLine className="h-4 w-4" />
          ) : tool.id === 'measure_distance' ? (
            <Ruler className="h-4 w-4" />
          ) : tool.id === 'street_distance' ? (
            <Route className="h-4 w-4" />
          ) : (
            <Trash2 className="h-4 w-4" />
          );
        return {
          id: tool.id,
          label: tool.label,
          description: tool.requires_capability === 'routing' ? 'Requires routing support.' : null,
          active: tool.id === activeToolId,
          disabled: tool.requires_capability === 'routing' ? !routingToolSupported : false,
          icon,
        };
      }),
    [activeToolId, routingToolSupported, visibleBuiltInTools],
  );
  // Temporarily hide the built-in map tool rail while the mobile workbench layout is being simplified.
  const showToolRail = false;

  const handleCopyJson = async () => {
    if (!navigator?.clipboard) {
      setDraftEngineError('Clipboard is not available in this browser.');
      return;
    }
    try {
      await navigator.clipboard.writeText(generatedJsonText);
      setDraftEngineError(null);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to copy JSON.';
      setDraftEngineError(message);
    }
  };

  const handleApplyPreview = async () => {
    await applyPreviewFromText(editorText, { preserveView: true });
  };

  const handleSave = async () => {
    if (!styleId) return;
    setSaveError(null);
    let parsed: Record<string, unknown>;
    try {
      parsed = parseEditorJson() as Record<string, unknown>;
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Invalid JSON';
      setSaveError(message);
      return;
    }

    setSaving(true);
    try {
      const canonicalParsed = normalizeDraftAssetRoots(canonicalizeStyleToInternalSourceRefs(parsed));
      const { compiledStyle } = compileWorkbenchStyle(canonicalParsed);
      debugMapWorkbenchGlyphs('saveStyle', {
        styleId,
        draftGlyphs: parsed.glyphs ?? null,
        canonicalGlyphs: canonicalParsed.glyphs ?? null,
        compiledGlyphs: compiledStyle.glyphs ?? null,
        draftSprite: parsed.sprite ?? null,
        canonicalSprite: canonicalParsed.sprite ?? null,
        compiledSprite: compiledStyle.sprite ?? null,
        selectedGlyphSetId: selectedGlyphSet?.id ?? null,
        activeSpriteId: activeSpriteId ?? null,
        attachedSpriteRefs: inferSpriteDependenciesFromStyle(behavior, canonicalParsed),
      });
      const attachedSpriteEntries = inferSpriteDependenciesFromStyle(behavior, canonicalParsed)
        .map(ref => {
          const spriteSet = spriteSets.find(entry => entry.id === ref.spriteSetId);
          if (!spriteSet?.id) {
            return null;
          }
          return {
            id: ref.id,
            spriteSet,
          };
        })
        .filter((entry): entry is { id: string; spriteSet: MapWorkbenchSpriteSet } => Boolean(entry));
      const metadata = behavior.buildMetadata({
        existingMetadata: style?.metadata ?? null,
        styleJson: compiledStyle,
        authoredStyleJson: canonicalParsed,
        sources,
        spriteSet: selectedSpriteSet,
        spriteEntries: attachedSpriteEntries,
        activeSpriteId: activeSpriteId ?? null,
        glyphSet: selectedGlyphSet,
        mapExperience: serializeMapExperienceState(mapExperience),
        initialView: currentViewRef.current ?? previewViewRef.current,
        isCustomMap,
      });
      const updatedStyle = await adapter.updateStyle(styleId, {
        style_json: compiledStyle,
        metadata,
      });
      markDraftPersisted();
      setStyle(updatedStyle);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Save failed.';
      setSaveError(message);
    } finally {
      setSaving(false);
    }
  };

  const handleSaveAsStyle = async () => {
    setSaveFormError(null);
    const name = saveForm.name.trim();
    if (!name) {
      setSaveFormError('Name is required.');
      return;
    }
    let parsed: Record<string, unknown>;
    try {
      parsed = parseEditorJson() as Record<string, unknown>;
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Invalid JSON';
      setSaveFormError(message);
      return;
    }
    setCreating(true);
    try {
      const canonicalParsed = normalizeDraftAssetRoots(canonicalizeStyleToInternalSourceRefs(parsed));
      const { compiledStyle } = compileWorkbenchStyle(canonicalParsed);
      const attachedSpriteEntries = inferSpriteDependenciesFromStyle(behavior, canonicalParsed)
        .map(ref => {
          const spriteSet = spriteSets.find(entry => entry.id === ref.spriteSetId);
          if (!spriteSet?.id) {
            return null;
          }
          return {
            id: ref.id,
            spriteSet,
          };
        })
        .filter((entry): entry is { id: string; spriteSet: MapWorkbenchSpriteSet } => Boolean(entry));
      const created = await adapter.createStyle({
        name,
        share_scope: saveForm.shareScope,
        description: saveForm.description.trim() || undefined,
        style_json: compiledStyle,
        metadata: behavior.buildMetadata({
          styleJson: compiledStyle,
          authoredStyleJson: canonicalParsed,
          sources,
          spriteSet: selectedSpriteSet,
          spriteEntries: attachedSpriteEntries,
          activeSpriteId: activeSpriteId ?? null,
          glyphSet: selectedGlyphSet,
          mapExperience: serializeMapExperienceState(mapExperience),
          initialView: currentViewRef.current ?? previewViewRef.current,
          isCustomMap,
        }),
      });
      setSaveSheetOpen(false);
      if (created?.id) {
        navigate(`${collectionPath}/${created.id}/workbench`);
      }
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Save failed.';
      setSaveFormError(message);
    } finally {
      setCreating(false);
    }
  };

  const primarySaveActionLabel = canSaveCurrent
    ? 'Save'
    : canSaveCopy
    ? isCustomMap
      ? 'Save as map'
      : 'Save as style'
    : isCustomMap
      ? 'Custom map'
      : 'Style';

  const handlePrimarySaveAction = () => {
    if (!canSaveCurrent && canSaveCopy) {
      handleOpenSaveSheet();
      return;
    }
    if (canSaveCurrent && hasUnsavedChanges) {
      void handleSave();
    }
  };

  const handleGenerateAiPatch = async () => {
    if (!ai) {
      return;
    }
    setAiError(null);
    setAiPatchText(null);

    if (!activeAiKeySet) {
      setAiError('Add an API key in Settings before using the AI helper.');
      return;
    }
    const prompt = aiPrompt.trim();
    if (!prompt) {
      setAiError('Tell the helper what you want to change.');
      return;
    }

    let parsed: Record<string, unknown>;
    try {
      parsed = parseJsonText(editorText) as Record<string, unknown>;
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Invalid JSON';
      setAiError(`Fix the JSON before using AI. ${message}`);
      return;
    }

    const context = {
      available_sources: availableSourcesContext,
      available_sprite_sets: spriteSets.map(entry => ({
        id: entry.id,
        name: entry.name,
        description: entry.description,
        share_scope: entry.share_scope,
      })),
      available_glyph_sets: glyphSets.map(entry => ({
        id: entry.id,
        name: entry.name,
        description: entry.description,
        share_scope: entry.share_scope,
        fontstacks: entry.fontstacks ?? [],
      })),
      style_sources: buildStyleSourcesContext(parsed),
      style_layers: buildStyleLayersContext(parsed),
    };

    setAiGenerating(true);
    try {
      const response = await ai.generatePatch({
        provider: aiProvider,
        prompt,
        styleJson: parsed,
        context,
      });
      const patch = response.patch ?? [];
      if (!patch.length) {
        setAiError('AI response did not include a patch.');
        return;
      }

      const patchText = JSON.stringify(patch, null, 2);
      setAiPatchText(patchText);

      let nextStyle: Record<string, unknown>;
      try {
        nextStyle = applyJsonPatch(parsed, patch);
      } catch (err) {
        const message = err instanceof Error ? err.message : 'Patch could not be applied.';
        setAiError(message);
        return;
      }

      if (!isValidMapLibreStyle(nextStyle)) {
        setAiError('AI patch produced an invalid MapLibre style.');
        return;
      }

      const nextText = JSON.stringify(nextStyle, null, 2);
      setMountedSourceRefs(extractMountedSourceRefsFromStyle(nextStyle, sources));
      updateEditorText(nextText);
      await applyPreviewFromStyle(nextStyle, { preserveView: true, nextText });
    } catch (err) {
      const message = err instanceof Error ? err.message : 'AI patch generation failed.';
      setAiError(message);
    } finally {
      setAiGenerating(false);
    }
  };

  const handleAddSource = (source: MapWorkbenchSource) => {
    if (!source.id) return;
    let parsed: Record<string, unknown>;
    try {
      parsed = parseEditorJson() as Record<string, unknown>;
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Invalid JSON';
      setDraftEngineError(message);
      return;
    }

    const currentSources =
      parsed.sources && typeof parsed.sources === 'object' ? (parsed.sources as Record<string, unknown>) : {};

    let aliasBase = toAlias(source.name ?? source.id ?? 'source');
    if (!aliasBase) {
      aliasBase = 'source';
    }
    let alias = aliasBase;
    let counter = 1;
    while (currentSources[alias]) {
      alias = `${aliasBase}-${counter}`;
      counter += 1;
    }

    const nextSources = {
      ...currentSources,
      [alias]: buildStyleSourceForMapSource(source),
    };

    const existingLayers = Array.isArray(parsed.layers) ? (parsed.layers as Array<Record<string, unknown>>) : [];
    const nextLayers = [...existingLayers, ...buildDefaultLayers(source, alias, existingLayers)];
    const nextStyle = {
      ...parsed,
      sources: nextSources,
      layers: nextLayers,
    };
    const initialLayer = extractVectorLayers(source)[0] ?? null;

    setMountedSourceRefs(current => [
      ...current,
      {
        alias,
        sourceId: source.id,
        name: source.name ?? alias,
        shareScope: source.share_scope ?? null,
      },
    ]);
    if (initialLayer?.id) {
      setSelectedStyleTargetKey(buildMapExperienceTargetKey(source.id, initialLayer.id));
    }
    const nextText = JSON.stringify(nextStyle, null, 2);
    const normalizedSnapshot = ensureExplicitWorkbenchDraftState(
      {
        ...draftEngineState.draft,
        editorText: nextText,
        mapExperience: mapExperienceRef.current,
      },
      nextStyle,
      sources,
    );
    updateDraftState(() => normalizedSnapshot);
    void applyPreviewFromText(nextText, {
      preserveView: true,
      mapExperience: normalizedSnapshot.mapExperience,
    });
  };

  const handleRemoveMountedSource = (alias: string) => {
    let parsed: Record<string, unknown>;
    try {
      parsed = parseEditorJson() as Record<string, unknown>;
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Invalid JSON';
      setDraftEngineError(message);
      return;
    }

    const nextSources =
      parsed.sources && typeof parsed.sources === 'object'
        ? { ...(parsed.sources as Record<string, unknown>) }
        : {};
    delete nextSources[alias];
    const nextLayers = Array.isArray(parsed.layers)
      ? (parsed.layers as Array<Record<string, unknown>>).filter(layer => {
          const sourceAlias = typeof layer.source === 'string' ? layer.source : '';
          return sourceAlias !== alias;
        })
      : [];

    if (workbenchTargets.some(target => target.key === selectedStyleTargetKey && target.sourceAlias === alias)) {
      setSelectedStyleTargetKey(null);
    }

    const nextStyle = {
      ...parsed,
      sources: nextSources,
      layers: nextLayers,
    };
    const nextText = JSON.stringify(nextStyle, null, 2);
    setMountedSourceRefs(current => current.filter(entry => entry.alias !== alias));
    updateDraftState(current => ({
      ...current,
      editorText: nextText,
    }));
    void applyPreviewFromText(nextText, { preserveView: true });
  };

  const handleToggleSourceLayer = (sourceAlias: string, sourceLayerId: string, enabled: boolean) => {
    let parsed: Record<string, unknown>;
    try {
      parsed = parseEditorJson() as Record<string, unknown>;
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Invalid JSON';
      setDraftEngineError(message);
      return;
    }

    const styleLayers = Array.isArray(parsed.layers) ? [...(parsed.layers as Array<Record<string, unknown>>)] : [];
    const sourceValue =
      parsed.sources && typeof parsed.sources === 'object'
        ? ((parsed.sources as Record<string, Record<string, unknown>>)[sourceAlias] ?? null)
        : null;
    const sourceId = resolveMapSourceIdFromStyleSource(sourceValue);
    const sourceMeta = sources.find(entry => entry.id === sourceId);
    if (!sourceMeta) {
      setDraftEngineError('Source layer metadata is unavailable for this source.');
      return;
    }

    const nextLayers = enabled
      ? [
          ...styleLayers,
          ...buildDefaultLayersForVectorLayer(
            sourceMeta,
            sourceAlias,
            extractVectorLayers(sourceMeta).find(layer => layer.id === sourceLayerId) ?? {
              id: sourceLayerId,
              geometry: '',
            },
            false
          ),
        ]
      : styleLayers.filter(layer => {
          return !matchesWorkbenchLayerTarget(layer, sourceAlias, sourceLayerId);
        });

    if (enabled) {
      setSelectedStyleTargetKey(buildMapExperienceTargetKey(sourceMeta.id ?? sourceId, sourceLayerId));
    } else {
      const disabledTarget = workbenchTargets.find(
        target => target.sourceAlias === sourceAlias && target.sourceLayer === sourceLayerId,
      );
      if (disabledTarget?.key === selectedStyleTargetKey) {
        setSelectedStyleTargetKey(null);
      }
    }

    const nextStyle = {
      ...parsed,
      layers: nextLayers,
    };
    const nextText = JSON.stringify(nextStyle, null, 2);
    if (enabled) {
      const normalizedSnapshot = ensureExplicitWorkbenchDraftState(
        {
          ...draftEngineState.draft,
          editorText: nextText,
          mapExperience: mapExperienceRef.current,
        },
        nextStyle,
        sources,
      );
      updateDraftState(() => normalizedSnapshot);
      void applyPreviewFromText(nextText, {
        preserveView: true,
        mapExperience: normalizedSnapshot.mapExperience,
      });
      return;
    }
    updateDraftState(current => ({
      ...current,
      editorText: nextText,
    }));
    void applyPreviewFromText(nextText, { preserveView: true });
  };

  const handleSelectSpriteSet = (spriteSet: MapWorkbenchSpriteSet) => {
    if (!spriteSet.id) return;
    let parsed: Record<string, unknown>;
    try {
      parsed = parseEditorJson() as Record<string, unknown>;
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Invalid JSON';
      setDraftEngineError(message);
      return;
    }
    const existingRefs = inferSpriteDependenciesFromStyle(behavior, parsed);
    const existingRef = existingRefs.find(ref => ref.spriteSetId === spriteSet.id) ?? null;
    if (existingRef) {
      updateActiveSpriteId(existingRef.id);
      return;
    }
    const baseId =
      existingRefs.length === 0
        ? 'default'
        : sanitizeLayerId(spriteSet.name || spriteSet.id || 'sprite', 'sprite');
    const usedIds = new Set(existingRefs.map(ref => ref.id));
    let nextSpriteId = baseId;
    let suffix = 2;
    while (usedIds.has(nextSpriteId) || !nextSpriteId.trim()) {
      nextSpriteId = `${baseId}-${suffix}`;
      suffix += 1;
    }
    const nextRefs = [...existingRefs, { id: nextSpriteId, spriteSetId: spriteSet.id, sortOrder: existingRefs.length }];
    const nextStyle = setStyleRootField(parsed, 'sprite', buildSpriteRootValue(nextRefs, behavior.buildSpriteUrl));
    updateActiveSpriteId(nextSpriteId);
    updateEditorText(JSON.stringify(nextStyle, null, 2));
  };

  const handleClearSpriteSet = () => {
    updateActiveSpriteId(null);
  };

  const handleRemoveAttachedSprite = (spriteId: string) => {
    let parsed: Record<string, unknown>;
    try {
      parsed = parseEditorJson() as Record<string, unknown>;
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Invalid JSON';
      setDraftEngineError(message);
      return;
    }
    const nextRefs = inferSpriteDependenciesFromStyle(behavior, parsed)
      .filter(ref => ref.id !== spriteId)
      .map((ref, index) => ({ ...ref, sortOrder: index }));
    const nextStyle = setStyleRootField(parsed, 'sprite', buildSpriteRootValue(nextRefs, behavior.buildSpriteUrl));
    const nextActive = resolveActiveSpriteDependency(nextRefs, activeSpriteId === spriteId ? null : activeSpriteId);
    updateActiveSpriteId(nextActive?.id ?? null);
    updateEditorText(JSON.stringify(nextStyle, null, 2));
  };

  const handleMoveAttachedSprite = (spriteId: string, direction: 'up' | 'down') => {
    let parsed: Record<string, unknown>;
    try {
      parsed = parseEditorJson() as Record<string, unknown>;
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Invalid JSON';
      setDraftEngineError(message);
      return;
    }
    const currentRefs = inferSpriteDependenciesFromStyle(behavior, parsed);
    const currentIndex = currentRefs.findIndex(ref => ref.id === spriteId);
    if (currentIndex < 0) {
      return;
    }
    const nextIndex = direction === 'up' ? currentIndex - 1 : currentIndex + 1;
    if (nextIndex < 0 || nextIndex >= currentRefs.length) {
      return;
    }
    const reordered = [...currentRefs];
    const [moved] = reordered.splice(currentIndex, 1);
    reordered.splice(nextIndex, 0, moved);
    const nextRefs = reordered.map((ref, index) => ({ ...ref, sortOrder: index }));
    const nextStyle = setStyleRootField(parsed, 'sprite', buildSpriteRootValue(nextRefs, behavior.buildSpriteUrl));
    updateEditorText(JSON.stringify(nextStyle, null, 2));
  };

  const handleSelectWorkbenchIcon = (selection: {
    iconId: string;
    token: string;
    setId: string;
    setLabel: string;
  }) => {
    const spriteSet = spriteSets.find(entry => entry.id === selection.setId);
    if (!spriteSet?.id) {
      return;
    }

    let parsed: Record<string, unknown>;
    try {
      parsed = parseEditorJson() as Record<string, unknown>;
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Invalid JSON';
      setDraftEngineError(message);
      return;
    }

    const currentSpriteRef = resolveActiveSpriteDependency(
      inferSpriteDependenciesFromStyle(behavior, parsed),
      activeSpriteId,
    );
    if (currentSpriteRef?.spriteSetId === spriteSet.id) {
      if (currentSpriteRef.id !== activeSpriteId) {
        updateActiveSpriteId(currentSpriteRef.id);
      }
      return;
    }

    handleSelectSpriteSet(spriteSet);
  };

  const handleSelectGlyphSet = (glyphSet: MapWorkbenchGlyphSet) => {
    if (!glyphSet.id) return;
    updateGlyphSetId(glyphSet.id);
    let parsed: Record<string, unknown>;
    try {
      parsed = parseEditorJson() as Record<string, unknown>;
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Invalid JSON';
      setDraftEngineError(message);
      return;
    }
    const nextStyle = setStyleRootStringField(parsed, 'glyphs', buildResolvedGlyphUrl(glyphSet.id));
    updateEditorText(JSON.stringify(nextStyle, null, 2));
  };

  const handleClearGlyphSet = () => {
    updateGlyphSetId(null);
    let parsed: Record<string, unknown>;
    try {
      parsed = parseEditorJson() as Record<string, unknown>;
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Invalid JSON';
      setDraftEngineError(message);
      return;
    }
    const nextStyle = setStyleRootStringField(parsed, 'glyphs', null);
    updateEditorText(JSON.stringify(nextStyle, null, 2));
  };

  const inspectableLayerIds = useMemo(() => collectInspectableLayerIds(runtimePreviewStyle), [runtimePreviewStyle]);
  useEffect(() => {
    logInteractivityEvent('runtime:state', {
      inspectableLayerIds,
      workbenchTargets: workbenchTargets.map(summarizeInteractivityTarget),
      highlightTargets: workbenchHighlightTargets.map(summarizeInteractivityTarget),
      pointLayers: summarizePointStyleLayers(previewStyle),
      managedLayers: summarizeManagedStyleLayers(previewStyle),
    });
  }, [
    inspectableLayerIds,
    logInteractivityEvent,
    previewStyle,
    summarizeInteractivityTarget,
    workbenchHighlightTargets,
    workbenchTargets,
  ]);

  const pinnedFeatureEntries = Object.entries(pinnedFeature?.properties ?? {});
  const popupPresentation = resolveMapWorkbenchPopupPresentation({
    isAuthorMode,
    hasHoveredFeature: Boolean(hoveredFeature),
    hasPinnedFeature: Boolean(pinnedFeature),
    hasActiveHoverPopup: Boolean(activeHoverPopup),
    hasActivePinnedPopup: Boolean(activePinnedPopup),
    authoredPinnedPopupIsPinned,
  });
  const popupFeature =
    popupPresentation.featureSource === 'pinned'
      ? pinnedFeature
      : popupPresentation.featureSource === 'hover'
        ? hoveredFeature
        : null;
  const popupPinned = popupPresentation.pinned;
  const floatingSurfaceFeature = activeRuntimeSurface?.surface.surface_kind === 'floating_summary_card' ? activeRuntimeSurfaceFeature : null;
  const floatingSurfacePinned = Boolean(
    floatingSurfaceFeature &&
      pinnedFeature &&
      floatingSurfaceFeature.id === pinnedFeature.id &&
      floatingSurfaceFeature.layerId === pinnedFeature.layerId &&
      floatingSurfaceFeature.sourceLayer === pinnedFeature.sourceLayer,
  );
  const overlayViewport = centerPaneRef.current
    ? {
        width: centerPaneRef.current.clientWidth,
        height: centerPaneRef.current.clientHeight,
      }
    : null;
  const popupAnchorStyle = buildMapWorkbenchAnchoredOverlayStyle({
    anchor: popupFeature?.screenPoint,
    viewport: overlayViewport,
    preferredWidth: 256,
    preferredHeight: popupPinned ? 260 : 180,
  });
  const popupIdentity = popupFeature
    ? resolveMapWorkbenchPopupIdentity({
        feature: popupFeature,
        authoredTitle: activeFeaturePopup?.title,
        authoredSubtitle: activeFeaturePopup?.subtitle,
        summary: activeFeaturePopup?.summary,
      })
    : null;
  const floatingSurfaceAnchorStyle = buildMapWorkbenchAnchoredOverlayStyle({
    anchor: floatingSurfaceFeature?.screenPoint,
    viewport: overlayViewport,
    preferredWidth: 288,
    preferredHeight: floatingSurfacePinned ? 320 : 240,
  });

  const rightPaneFooterMessage = canApplyChanges
    ? hasPreviewPendingChanges
      ? previewStatus === 'loading'
        ? 'Updating the preview to match your latest edits.'
        : 'Preview will sync automatically in a moment.'
      : hasUnsavedChanges && canSaveCurrent
        ? 'Preview is up to date. Save when you want to keep these workspace changes.'
        : hasUnsavedChanges && canSaveCopy
          ? 'Preview is up to date. Save a copy when you want to keep these workspace changes.'
          : canSaveCurrent
            ? 'Preview and saved state are up to date.'
            : canSaveCopy
              ? 'Preview is up to date. Save a copy when you want to keep these workspace changes.'
              : 'Preview is up to date. Use filters, layers, and saved views to work with this map.'
    : 'Use saved views and filters to work with the prepared map.';
  const commandBarSecondaryActions = [];
  const commandBarPrimaryActions = [
    ...(canApplyChanges
      ? [
          {
            id: 'apply',
            label: previewStatus === 'loading' ? 'Refreshing' : previewStatus === 'error' ? 'Retry preview' : 'Refresh preview',
            disabled: previewStatus === 'loading',
            tone: previewStatus === 'error' ? 'danger' : 'default',
          } as const,
        ]
      : []),
    ...(canSaveCurrent
      ? [
          {
            id: 'save',
            label: saving ? 'Saving' : 'Save',
            disabled: saving || !hasUnsavedChanges,
            tone: 'success',
          } as const,
          {
            id: 'save-as',
            label: 'Save As',
            disabled: saving,
          } as const,
        ]
      : canSaveCopy
        ? [
            {
              id: 'save-as',
              label: isCustomMap ? 'Save As Map' : 'Save As Style',
              disabled: saving || creating,
              tone: 'success',
            } as const,
          ]
        : []),
  ];
  const commandBarStatusBadges = [
    { id: 'mode', label: WORKSPACE_MODE_LABELS[resolvedMode] },
    { id: 'status', label: statusBadgeLabel, tone: hasPreviewPendingChanges || hasUnsavedChanges ? 'warning' : 'default' },
    { id: 'preview', label: previewStatus, tone: previewStatus === 'error' ? 'danger' : previewStatus === 'success' ? 'success' : 'muted' },
    {
      id: isAuthorMode ? 'sources' : 'views',
      label: isAuthorMode ? `Sources ${mountedSourceRefs.length}` : `Views ${mapExperience.presets.length}`,
      tone: 'muted',
    },
  ];
  const buildLegendEntry = useCallback(
    (entry: MapExperienceLegendSection['entries'][number]) => {
      const iconMetadata = entry.icon_token ? legendIconMetadataByToken.get(entry.icon_token) : null;
      return {
        id: entry.id,
        label: entry.label,
        color: entry.color ?? null,
        representation: entry.representation ?? null,
        iconToken: entry.icon_token ?? null,
        iconPreviewUrl: iconMetadata?.previewUrl ?? null,
        iconSdf: iconMetadata?.sdf ?? false,
        hidden: false,
      };
    },
    [legendIconMetadataByToken],
  );
  const legendPaneSections = activeLegendSections.map(section => ({
    id: section.id,
    title: section.title,
    subtitle: section.source_layer ?? null,
    entries: section.entries.map(buildLegendEntry),
  }));
  const viewerLegendSections = activeLegendSections.map(section => ({
    id: section.id,
    title: section.title,
    description: section.source_layer ?? null,
    entries: section.entries.map(entry => ({
      ...buildLegendEntry(entry),
      description: null,
    })),
  }));
  const presetPanePresets = mapExperience.presets.map(preset => ({
    id: preset.id,
    label: preset.label,
    summary:
      preset.viewport?.center && typeof preset.viewport.zoom === 'number'
        ? `${preset.viewport.center[1].toFixed(3)}, ${preset.viewport.center[0].toFixed(3)} · z${preset.viewport.zoom.toFixed(1)}`
        : preset.id,
    active: false,
    disabled: false,
  }));
  const diagnosticsSummaryItems = [
    { id: 'sources', label: 'Mounted Sources', value: String(mountedSourceRefs.length) },
    { id: 'views', label: 'Saved Views', value: String(mapExperience.presets.length) },
    { id: 'status', label: 'Preview', value: previewStatus },
  ];
  const sourceCatalogItems = filteredSources.map(source => ({
    id: source.id,
    label: source.name || source.id,
    description: source.description ?? source.id,
    shareScope: behavior.normalizeShareScope(source.share_scope),
    alreadyMounted: mountedSourceRefs.some(entry => entry.sourceId === source.id),
  }));
  const sourceCatalogScopeOptions = [
    { value: 'all' as const, label: 'All scopes' },
    { value: 'public' as const, label: 'Public' },
    { value: 'shared' as const, label: 'Shared' },
    { value: 'tenant' as const, label: 'Tenant' },
  ];
  const sourceLayerManagerGroups = sourceLayerGroups.map(group => ({
    sourceId: group.alias,
    alias: group.alias,
    label: group.name,
    shareScope: group.shareScope ? behavior.normalizeShareScope(group.shareScope) : null,
    renderedCount: group.renderedCount,
    layers: group.vectorLayers.map(layer => ({
      id: layer.id,
      label: layer.id,
      geometryLabel: layer.geometry || null,
      rendered: layer.rendered,
      current: selectedWorkbenchTarget?.key === buildMapExperienceTargetKey(group.sourceId, layer.id),
      styleLayerCount: layer.styleLayerCount ?? null,
    })),
  }));
  const assetDependencyMountedSources = mountedSourceRefs.map(source => ({
    id: source.alias,
    alias: source.alias,
    label: source.name,
    shareScope: source.shareScope ? behavior.normalizeShareScope(source.shareScope) : null,
    mounted: true,
    description: source.sourceId,
  }));
  const spriteOptions = attachedSpriteRefs.map(ref => {
    const sprite = spriteSets.find(entry => entry.id === ref.spriteSetId);
    return {
      id: ref.spriteSetId,
      label: sprite?.name || ref.spriteSetId,
      description: sprite?.description ?? sprite?.id ?? null,
      shareScope: behavior.normalizeShareScope(sprite?.share_scope),
      selected: activeSpriteRef?.id === ref.id,
      disabled: false,
    };
  });
  const attachedSpriteCatalog = attachedSpriteRefs.map((ref, index) => {
    const sprite = spriteSets.find(entry => entry.id === ref.spriteSetId) ?? null;
    return {
      id: ref.id,
      label: sprite?.name || ref.spriteSetId,
      description: sprite?.description ?? ref.spriteSetId,
      shareScope: sprite?.share_scope ? behavior.normalizeShareScope(sprite.share_scope) : null,
      order: index + 1,
      active: activeSpriteRef?.id === ref.id,
      canMoveUp: index > 0,
      canMoveDown: index < attachedSpriteRefs.length - 1,
    };
  });
  const availableSpriteCatalogItems = spriteSets.map(sprite => ({
    id: sprite.id ?? '',
    label: sprite.name || sprite.id || 'Unnamed sprite set',
    description: sprite.description ?? sprite.id ?? null,
    shareScope: behavior.normalizeShareScope(sprite.share_scope),
    selected: attachedSpriteRefs.some(ref => ref.spriteSetId === sprite.id),
    alreadyAttached: attachedSpriteRefs.some(ref => ref.spriteSetId === sprite.id),
    disabled: false,
  }));
  const desktopPaneIconPicker: MapStyleRenderStackEditorIconPickerConfig | null = {
    loading: spriteSetsLoading || spriteIconsLoading,
    errorMessage: spriteSetsError ?? spriteIconsError ?? null,
    unavailableMessage:
      spriteSetsLoading || spriteIconsLoading || spriteSetsError || spriteIconsError
        ? null
        : spriteIconSets.length > 0
        ? spriteIconSets.some(set => set.icons.length > 0)
          ? null
          : 'The available sprite sheets do not contain any icons.'
        : 'Add a sprite sheet in Sources to browse icon markers.',
    iconSets: spriteIconSets,
    setFilterOptions: spriteIconSets.length > 1
      ? [
          { value: 'all', label: 'All sprite sheets' },
          ...spriteIconSets.map(set => ({ value: set.id, label: set.label })),
        ]
      : spriteIconSets.map(set => ({ value: set.id, label: set.label })),
    selectedSetId: selectedSpriteSet?.id ?? null,
    activeSetFilter: resolvedIconPickerSetFilter,
    onSetFilterChange: setIconPickerSetFilter,
    onSelectIcon: handleSelectWorkbenchIcon,
  };
  const desktopPaneSpriteRootSelector: MapWorkspaceSpriteRootSelectorConfig | null = {
    loading: spriteSetsLoading,
    errorMessage: spriteSetsError,
    helperText: 'Choose the sprite sheet used for symbol icon markers.',
    unavailableMessage:
      spriteSetsLoading || spriteSetsError || spriteOptions.length > 0
        ? null
        : 'Attach a sprite sheet from Sources to focus symbol authoring here.',
    selectedSetId: selectedSpriteSet?.id ?? null,
    options: spriteOptions,
    onSelectSprite: spriteId => {
      const ref = attachedSpriteRefs.find(entry => entry.spriteSetId === spriteId);
      if (ref) {
        updateActiveSpriteId(ref.id);
      }
    },
    onClearSprite: handleClearSpriteSet,
  };
  const glyphOptions = glyphSets.map(glyph => ({
    id: glyph.id ?? '',
    label: glyph.name || glyph.id || 'Unnamed glyph set',
    description: glyph.description ?? glyph.id ?? null,
    shareScope: behavior.normalizeShareScope(glyph.share_scope),
    selected: selectedGlyphSet?.id === glyph.id,
    disabled: false,
  }));
  const popupActionItems =
    activeFeaturePopup?.actions.map(action => ({
      id: action.pipeline.pipeline_id,
      label: action.label,
      disabled: action.disabled,
      disabledReason: action.disabledReason ?? null,
    })) ?? [];
  const floatingSurfaceActionItems =
    activeRuntimeSurface?.actions.map(action => ({
      id: action.pipeline.pipeline_id,
      label: action.label,
      disabled: action.disabled,
      disabledReason: action.disabledReason ?? null,
    })) ?? [];
  const actionSurfaceItems =
    actionSurface?.actions.map(action => ({
      id: action.pipeline.pipeline_id,
      label: action.label,
      disabled: action.disabled,
      disabledReason: action.disabledReason ?? null,
    })) ?? [];
  const inspectionPropertyItems = pinnedFeatureEntries.map(([key, value]) => ({
    key,
    label: key,
    value: typeof value === 'string' ? value : JSON.stringify(value),
  }));
  const openInspectionForFeature = useCallback((feature: MapWorkbenchFeatureInspection) => {
    setPinnedFeature(feature);
    setPinnedFeatureTrigger(current => current ?? 'click');
    setInspectionSheetOpen(true);
  }, []);
  const handleSelectStylePresentation = useCallback(
    (targetKey: string, profileId: string) => {
      setSelectedStyleTargetKey(targetKey);
      updateMapExperienceState(current => ({
        ...current,
        active_style_profiles: {
          ...current.active_style_profiles,
          [targetKey]: profileId,
        },
      }));
    },
    [updateMapExperienceState],
  );
  const handleAddStylePresentation = useCallback(
    (
      target: (typeof workbenchTargets)[number],
      requestedTemplateId: MapWorkbenchRenderStackAddRequestId,
    ) => {
      const templateId = normalizeMapWorkbenchRenderStackTemplateId(requestedTemplateId);
      if (!templateId) {
        return;
      }
      setSelectedStyleTargetKey(target.key);
      updateMapExperienceState(current => {
        const existingProfileIds = new Set(current.style_profiles.map(profile => profile.id));
        const targetProfiles = current.style_profiles.filter(
          profile =>
            profile.source_id === target.sourceId &&
            profile.source_layer === target.sourceLayer,
        );
        const activeProfileId = current.active_style_profiles[target.key] ?? '';
        const activeProfile =
          targetProfiles.find(profile => profile.id === activeProfileId) ??
          targetProfiles[0] ??
          buildSeededRulesStyleProfile(target);
        const overlayProfileId = buildUniqueId(
          createExperienceId('overlay', target.sourceId, target.sourceLayer),
          existingProfileIds,
        );
        const overlayLabelBase =
          templateId === 'badge'
            ? `${formatFieldLabel(target.sourceLayer)} badge`
            : templateId === 'label'
              ? `${formatFieldLabel(target.sourceLayer)} label`
              : templateId === 'symbol_overlay'
                ? `${formatFieldLabel(target.sourceLayer)} symbols`
                : `${formatFieldLabel(target.sourceLayer)} overlay`;
        const defaultStringField = target.fields.find(field => field.value_type === 'string')?.id ?? target.fields[0]?.id ?? '';
        const activeRulesProfile =
          activeProfile.kind === 'rules'
            ? activeProfile
            : buildRulesProfileFromStyleProfile(target, activeProfile, {
                profileId: activeProfile.id,
                profileLabel: activeProfile.label ?? formatFieldLabel(target.sourceLayer),
              });
        const defaultOverlayRepresentation = coerceRepresentationForGeometry(
          target.geometry,
          templateId === 'badge' ? 'circle' : 'symbol',
        );
        const overlayProfile = templateId === 'duplicate_base_as_overlay'
          ? buildRulesProfileFromEntryTemplate({
              target,
              profileId: overlayProfileId,
              label: overlayLabelBase,
              legendLabel: `${overlayLabelBase} features`,
              representation: activeRulesProfile.representation ?? previewKindToRepresentation(buildDefaultStylePreview(target.geometry, target.layerIndex).kind),
              color: activeRulesProfile.rules?.[0]?.color ?? DEFAULT_ADVANCED_RULE_COLOR,
              interaction: activeRulesProfile.interaction,
              rules: (activeRulesProfile.rules ?? []).map((rule, index) => ({
                ...rule,
                id: `rule::${overlayProfileId}::${index + 1}`,
                paint: cloneStyleOverrideRecord(rule.paint ?? null),
                layout: buildRuleLayoutOverrides(rule.layout ?? null),
                logic: ensureLogicRoot(rule.logic),
              })),
            })
          : buildRulesProfileFromEntryTemplate({
              target,
              profileId: overlayProfileId,
              label: overlayLabelBase,
              legendLabel: `${overlayLabelBase} features`,
              representation: defaultOverlayRepresentation,
              color: activeRulesProfile.rules?.[0]?.color ?? DEFAULT_ADVANCED_RULE_COLOR,
              interaction: activeRulesProfile.interaction,
              paint:
                templateId === 'badge'
                  ? { 'circle-color': activeRulesProfile.rules?.[0]?.color ?? DEFAULT_ADVANCED_RULE_COLOR, 'circle-radius': 6 }
                  : { 'text-color': activeRulesProfile.rules?.[0]?.color ?? DEFAULT_ADVANCED_RULE_COLOR },
              layout:
                templateId === 'label'
                  ? {
                      'text-field': defaultStringField ? `{${defaultStringField}}` : formatFieldLabel(target.sourceLayer),
                      'text-size': 12,
                    }
                  : templateId === 'symbol_overlay'
                    ? defaultStringField
                      ? { 'text-field': `{${defaultStringField}}`, 'text-size': 12 }
                      : null
                    : null,
            });

        const currentStacks = current.render_stacks ?? [];
        const existingStack = currentStacks.find(
          stack => stack.source_id === target.sourceId && stack.source_layer === target.sourceLayer,
        ) ?? null;
        const existingEntries = existingStack?.entries ?? [];
        const existingEntryIds = new Set(existingEntries.map(entry => entry.entry_id));
        const baseSourceProfile = (
          existingEntries.find(entry => entry.stack_role === 'base')
            ? targetProfiles.find(profile => profile.id === existingEntries.find(entry => entry.stack_role === 'base')?.profile_id)
            : null
        ) ?? activeRulesProfile;
        const baseProfile =
          baseSourceProfile.kind === 'rules'
            ? baseSourceProfile
            : buildRulesProfileFromStyleProfile(target, baseSourceProfile, {
                profileId: `rules::${target.key}`,
                profileLabel: baseSourceProfile.label ?? formatFieldLabel(target.sourceLayer),
              });
        const baseEntry =
          existingEntries.find(entry => entry.stack_role === 'base') ??
          {
            entry_id: buildUniqueId(createExperienceId('base-entry', target.sourceId, target.sourceLayer), existingEntryIds),
            profile_id: baseProfile.id,
            enabled: true,
            label: baseProfile.label ?? formatFieldLabel(target.sourceLayer),
            stack_role: 'base' as const,
            filter_logic: null,
          };
        const nextOverlayEntry = {
          entry_id: buildUniqueId(createExperienceId('overlay-entry', target.sourceId, target.sourceLayer), new Set([
            ...existingEntryIds,
            baseEntry.entry_id,
          ])),
          profile_id: overlayProfile.id,
          enabled: true,
          label: overlayProfile.label ?? overlayLabelBase,
          stack_role:
            templateId === 'badge'
              ? 'overlay_badge'
              : templateId === 'label'
                ? 'overlay_label'
                : 'overlay_symbol',
          filter_logic: null,
        };
        const normalizedExistingEntries = existingEntries.map(entry =>
          entry.stack_role === 'base'
            ? {
                ...entry,
                profile_id: baseProfile.id,
                label: entry.label ?? baseProfile.label ?? formatFieldLabel(target.sourceLayer),
              }
            : entry,
        );
        const nextEntries = [
          ...(existingEntries.some(entry => entry.stack_role === 'base') ? [] : [baseEntry]),
          ...normalizedExistingEntries,
          nextOverlayEntry,
        ];
        const nextRenderStacks = [
          ...currentStacks.filter(
            stack => stack.source_id !== target.sourceId || stack.source_layer !== target.sourceLayer,
          ),
          {
            source_id: target.sourceId,
            source_layer: target.sourceLayer,
            entries: nextEntries,
          },
        ];
        const needsBaseProfileInsert = !current.style_profiles.some(profile => profile.id === baseProfile.id);

        return {
          ...current,
          style_profiles: [
            ...current.style_profiles,
            ...(needsBaseProfileInsert ? [baseProfile] : []),
            overlayProfile,
          ],
          render_stacks: nextRenderStacks,
          active_style_profiles: {
            ...current.active_style_profiles,
            [target.key]: overlayProfile.id,
          },
        };
      });
    },
    [buildUniqueId, selectedWorkbenchStyleState?.stylePreview.colors, updateMapExperienceState, workbenchTargets],
  );
  const handleDeleteStylePresentation = useCallback(
    (target: (typeof workbenchTargets)[number], entryId: string) => {
      updateMapExperienceState(current => {
        const currentStacks = current.render_stacks ?? [];
        const existingStack = currentStacks.find(
          stack => stack.source_id === target.sourceId && stack.source_layer === target.sourceLayer,
        );
        if (!existingStack) {
          return current;
        }
        const entryToDelete = existingStack.entries.find(entry => entry.entry_id === entryId);
        if (!entryToDelete || entryToDelete.stack_role === 'base') {
          return current;
        }
        const nextEntries = existingStack.entries.filter(entry => entry.entry_id !== entryId);
        const nextRenderStacks = [
          ...currentStacks.filter(
            stack => stack.source_id !== target.sourceId || stack.source_layer !== target.sourceLayer,
          ),
          ...(nextEntries.length > 0
            ? [
                {
                  source_id: target.sourceId,
                  source_layer: target.sourceLayer,
                  entries: nextEntries,
                },
              ]
            : []),
        ];
        const nextProfiles = current.style_profiles.filter(profile => profile.id !== entryToDelete.profile_id);
        const nextActiveStyleProfiles = { ...current.active_style_profiles };
        if (nextActiveStyleProfiles[target.key] === entryToDelete.profile_id) {
          const fallbackEntry =
            nextEntries.find(entry => entry.stack_role === 'base') ??
            nextEntries[0] ??
            null;
          if (fallbackEntry) {
            nextActiveStyleProfiles[target.key] = fallbackEntry.profile_id;
          } else {
            delete nextActiveStyleProfiles[target.key];
          }
        }
        return {
          ...current,
          style_profiles: nextProfiles,
          render_stacks: nextRenderStacks,
          active_style_profiles: nextActiveStyleProfiles,
        };
      });
    },
    [updateMapExperienceState],
  );
  const handleToggleStylePresentationEnabled = useCallback(
    (target: (typeof workbenchTargets)[number], entryId: string, enabled: boolean) => {
      updateMapExperienceState(current => {
        const currentStacks = current.render_stacks ?? [];
        const existingStack = currentStacks.find(
          stack => stack.source_id === target.sourceId && stack.source_layer === target.sourceLayer,
        );
        if (!existingStack) {
          return current;
        }
        return {
          ...current,
          render_stacks: [
            ...currentStacks.filter(
              stack => stack.source_id !== target.sourceId || stack.source_layer !== target.sourceLayer,
            ),
            {
              ...existingStack,
              entries: existingStack.entries.map(entry =>
                entry.entry_id === entryId ? { ...entry, enabled } : entry,
              ),
            },
          ],
        };
      });
    },
    [updateMapExperienceState],
  );
  const renderDesktopPaneTargetSelector = (args?: { label?: string; helperText?: string }) =>
    desktopAuthoringPaneTargetOptions.length > 0 ? (
      <MapLayerSelector
        className="w-full"
        label={args?.label ?? 'Source Layer'}
        helperText={args?.helperText ?? null}
        value={desktopAuthoringPaneTarget?.key ?? desktopAuthoringPaneTargetOptions[0]?.key ?? ''}
        options={desktopAuthoringPaneTargetOptions.map(option => ({
          id: option.key,
          label: option.label,
          metaLabel: `${option.sourceId}:${option.sourceLayer}`,
          description: option.rendered ? 'Visible on map' : 'Mounted but currently hidden on the map',
        }))}
        disabled={desktopAuthoringPaneTargetOptions.length <= 1}
        onValueChange={handleSelectWorkbenchTarget}
      />
    ) : null;

  const renderTargetScopedSectionShell = (
    sectionLabel: string,
    body: ReactNode,
  ) => (
    <div className="flex flex-col gap-6">
      {renderDesktopPaneTargetSelector()}
      {selectedWorkbenchTarget && !selectedWorkbenchTarget.rendered ? (
        <div className="rounded-xl border border-dashed border-border/70 bg-muted/15 px-4 py-3 text-sm text-muted-foreground">
          This source layer is mounted but hidden on the map. Its scoped settings will apply when the layer is rendered.
        </div>
      ) : null}
      {body}
    </div>
  );
  const advancedRuleEditorPanelProps = selectedWorkbenchTarget
    ? buildAdvancedRuleEditorPanelProps({
        ruleLayer: selectedRuleLayer,
        iconPreviewByToken: Object.fromEntries(legendIconMetadataByToken.entries()),
        selectedRuleId: selectedAdvancedRuleId ?? undefined,
        onSelectedRuleChange: setSelectedAdvancedRuleId,
        onAddRule: handleAddAdvancedRule,
        onDuplicateRule: handleDuplicateAdvancedRule,
        onDeleteRule: handleDeleteAdvancedRule,
        onRuleChange: handleAdvancedRuleChange,
        onEditStyle: setActiveRuleIdForStyleEditing,
        fields: selectedWorkbenchTarget.fields,
        legendHeading: selectedRulesLegendHeading,
        legendLabel: selectedRulesLegendLabel,
        onLegendHeadingChange: value => handleUpdateRulesLegendConfig('label', value),
        onLegendLabelChange: value => handleUpdateRulesLegendConfig('legend_label', value),
        canEditLegend: canEditRulesLegendConfig,
        readOnly: !canEditStyles,
      })
    : null;
  const advancedRuleValidation = advancedRuleEditorPanelProps?.validation ?? [];
  const advancedRuleErrorCount = advancedRuleValidation.filter(message => message.level === 'error').length;
  const advancedRuleWarningCount = advancedRuleValidation.filter(message => message.level === 'warning').length;
  const advancedRuleCount = selectedRuleLayer?.rules.length ?? 0;
  const advancedRulePreviewSampleFeatures = useMemo<StylingPreviewCanvasSampleFeature[]>(() => {
    if (!selectedWorkbenchTarget) {
      return [];
    }
    const matchesTarget = (feature: MapWorkbenchFeatureInspection | null) =>
      Boolean(feature) && feature?.sourceLayer === selectedWorkbenchTarget.sourceLayer;

    const samples: StylingPreviewCanvasSampleFeature[] = [];
    if (matchesTarget(pinnedFeature)) {
      samples.push({
        id: pinnedFeature?.id ?? 'pinned-feature',
        label: pinnedFeature?.id != null ? `Pinned feature ${String(pinnedFeature.id)}` : 'Pinned feature',
        description: pinnedFeature?.geometryType ? `${pinnedFeature.geometryType} sample` : 'Pinned representative sample',
        badges: ['Pinned'],
        sourceLayer: pinnedFeature?.sourceLayer ?? null,
        selected: true,
      });
    }
    if (matchesTarget(hoveredFeature) && hoveredFeature?.id !== pinnedFeature?.id) {
      samples.push({
        id: hoveredFeature?.id ?? 'hover-feature',
        label: hoveredFeature?.id != null ? `Hovered feature ${String(hoveredFeature.id)}` : 'Hovered feature',
        description: hoveredFeature?.geometryType ? `${hoveredFeature.geometryType} sample` : 'Current hover sample',
        badges: ['Hover'],
        sourceLayer: hoveredFeature?.sourceLayer ?? null,
        selected: samples.length === 0,
      });
    }
    return samples;
  }, [hoveredFeature, pinnedFeature, selectedWorkbenchTarget]);
  const advancedRulePreviewCanvasProps =
    selectedWorkbenchTarget && previewStyle
      ? buildStylingPreviewCanvasProps({
          title: 'Focused styling preview',
          description: 'Isolate this layer while editing advanced rules in a dedicated workspace.',
          target: {
            label: selectedWorkbenchTarget.label,
            sourceId: selectedWorkbenchTarget.sourceId,
            sourceLayer: selectedWorkbenchTarget.sourceLayer,
            geometryKind: selectedWorkbenchTarget.geometry,
            sampleSummary:
              advancedRulePreviewSampleFeatures.length > 0
                ? `${advancedRulePreviewSampleFeatures.length} sample feature${advancedRulePreviewSampleFeatures.length === 1 ? '' : 's'} ready`
                : 'No sample features pinned yet',
          },
          mode: advancedRulesPreviewMode,
          onModeChange: setAdvancedRulesPreviewMode,
          sampleSetValue: advancedRulesPreviewSampleSet,
          onSampleSetChange: setAdvancedRulesPreviewSampleSet,
          backgroundValue: advancedRulesPreviewBackground,
          onBackgroundChange: setAdvancedRulesPreviewBackground,
          sampleFeatures: advancedRulePreviewSampleFeatures,
          onSampleFeatureSelect: featureId => {
            const pinnedSample = advancedRulePreviewSampleFeatures.find(feature => feature.id === featureId);
            if (!pinnedSample) {
              return;
            }
            if (pinnedFeature && String(pinnedFeature.id ?? 'pinned-feature') === featureId) {
              setAdvancedRulesPreviewSampleSet('pinned_examples');
              return;
            }
            if (hoveredFeature && String(hoveredFeature.id ?? 'hover-feature') === featureId) {
              setPinnedFeature(hoveredFeature);
              setPinnedFeatureTrigger('click');
              setAdvancedRulesPreviewSampleSet('pinned_examples');
            }
          },
          viewport: <div />,
        })
      : null;
  const advancedRulePreviewPlan =
    runtimePreviewStyle && selectedWorkbenchTarget
      ? buildStylingPreviewIsolationPlan({
          styleJson: runtimePreviewStyle,
          target: {
            label: selectedWorkbenchTarget.label,
            sourceId: selectedWorkbenchTarget.sourceId,
            sourceLayer: selectedWorkbenchTarget.sourceLayer,
            geometryKind: selectedWorkbenchTarget.geometry,
          },
          mode: advancedRulesPreviewMode,
          sampleSet: advancedRulesPreviewSampleSet,
          background: advancedRulesPreviewBackground,
          sampleFeatures: advancedRulePreviewSampleFeatures,
        })
      : null;
  const advancedRulePreviewStyle =
    runtimePreviewStyle && advancedRulePreviewPlan
      ? applyStylingPreviewIsolationPlan(runtimePreviewStyle, advancedRulePreviewPlan)
      : runtimePreviewStyle;
  const advancedRuleInspectableLayerIds = useMemo(() => {
    const availableLayerIds = collectInspectableLayerIds(advancedRulePreviewStyle);
    if (!advancedRulePreviewPlan || advancedRulePreviewPlan.mode === 'context_preview' || advancedRulePreviewPlan.focusLayerIds.length === 0) {
      return availableLayerIds;
    }
    const focusLayerIds = new Set(advancedRulePreviewPlan.focusLayerIds);
    return availableLayerIds.filter(layerId => focusLayerIds.has(layerId));
  }, [advancedRulePreviewPlan, advancedRulePreviewStyle]);
  const handleOpenAdvancedRulesWorkspace = useCallback(() => {
    setAdvancedRulesPreviewView(previewView ?? defaultViewState);
    if (advancedRulePreviewSampleFeatures.length > 0 && advancedRulesPreviewSampleSet === 'viewport_sample') {
      setAdvancedRulesPreviewSampleSet('pinned_examples');
    }
    setAdvancedRulesWorkspaceOpen(true);
  }, [
    advancedRulePreviewSampleFeatures.length,
    advancedRulesPreviewSampleSet,
    defaultViewState,
    previewView,
  ]);
  const advancedRuleEditorPanel = advancedRuleEditorPanelProps ? (
    <MapWorkbenchAdvancedRuleEditorPanel {...advancedRuleEditorPanelProps} />
  ) : null;
  const advancedRuleWorkspaceSummary = advancedRuleEditorPanelProps ? (
    <div className="flex h-full min-h-0 flex-col" data-workbench-advanced-rules-panel>
      {advancedRuleEditorPanel}
    </div>
  ) : null;
  const desktopPaneStyleTargetStates = workbenchTargets.map(target => ({
    target,
    styleState: resolveTargetStyleState(target),
  }));
  const desktopPaneStyleLayerTargets = desktopPaneStyleTargetStates.map(({ target, styleState }) => ({
    id: target.key,
    label: target.label,
    bindingLabel: `${target.sourceId}:${target.sourceLayer}`,
    summary: styleState.summaryText,
    presentationCount: styleState.renderStackController.entries.length,
    rendered: target.rendered,
    selected: selectedWorkbenchTarget?.key === target.key,
  }));
  const desktopPaneStylePresentationTemplates =
    selectedWorkbenchStyleState?.renderStackController.template_options.map(option => ({
      id: option.id,
      label: option.label,
      disabled: false,
    })) ?? [];
  const desktopPaneStylePresentationRecords =
    selectedWorkbenchTarget && selectedWorkbenchStyleState
      ? selectedWorkbenchStyleState.renderStackController.entries.map(entry => ({
          targetKey: selectedWorkbenchTarget.key,
          profileId: entry.profile_id,
          item: {
            id: entry.id,
            label: entry.is_base ? selectedWorkbenchTarget.label : entry.label,
            role: entry.stack_role as any,
            roleLabel: entry.role_label,
            summary: entry.is_base
              ? `${selectedWorkbenchTarget.sourceId}:${selectedWorkbenchTarget.sourceLayer}`
              : entry.filter_summary ?? 'No extra filter applied',
            swatchColor: entry.swatch_color ?? null,
            enabled: entry.enabled,
            selected: selectedWorkbenchStyleState.renderStackController.selected_entry_id === entry.id,
            removable: !entry.is_base,
          },
        }))
      : [];
  const desktopPaneStylePresentations = desktopPaneStylePresentationRecords.map(record => record.item);
  const desktopPaneStylePresentationLookup = new Map(
    desktopPaneStylePresentationRecords.map(record => [
      record.item.id,
      { targetKey: record.targetKey, profileId: record.profileId, entryId: record.item.id },
    ]),
  );
  const desktopAuthoringPaneAvailableFields =
    selectedWorkbenchTarget?.fields.map(field => ({
      name: field.id,
      type: field.value_type,
    })) ?? [];
  const activeStyleEditorAvailableFields =
    activeStyleEditorTarget?.fields.map(field => ({
      name: field.id,
      type: field.value_type,
    })) ?? [];
  const desktopPaneStyleEditor =
    activeStyleEditorLayerExperience &&
    activeStyleEditorState &&
    Boolean(editingStyleEntryId) &&
    activeStyleEditorState.renderStackController.selected_entry_editor
      ? {
          appearance: activeStyleEditorLayerExperience.appearance,
          availableFields: activeStyleEditorAvailableFields,
          editorDescriptor: activeStyleEditorState.renderStackController.selected_entry_editor,
          iconPicker: desktopPaneIconPicker,
          glyphBundle: {
            selectedBundleId: selectedGlyphSet?.id ?? null,
            bundleOptions: glyphSets
              .filter((entry): entry is MapWorkbenchGlyphSet & { id: string } => Boolean(entry.id))
              .map(entry => ({
                value: entry.id,
                label: entry.name || entry.id,
              })),
            onSelectBundle: glyphId => {
              const glyph = glyphSets.find(entry => entry.id === glyphId);
              if (glyph) {
                handleSelectGlyphSet(glyph);
              }
            },
            activeBundleLabel: selectedGlyphSet?.name ?? null,
            activeBundleDescription: selectedGlyphSet?.description ?? null,
          },
          rulesModeBody:
            activeStyleEditorState.renderStackController.selected_entry?.profile_kind === 'rules'
              ? advancedRuleWorkspaceSummary
              : null,
          onBack: () => {
            setEditingStyleEntryId(null);
            setEditingStyleOwner(null);
          },
          onUpdate: (patch: Partial<LayerExperience['appearance']>) =>
            handleDesktopPaneLayerUpdateForTarget(activeStyleEditorTarget, activeStyleEditorLayerExperience, activeStyleEditorState, prev => ({
              ...prev,
              appearance: { ...prev.appearance, ...patch },
            })),
        }
      : null;
  const desktopPaneStylePane: MapStyleAuthoringPaneProps | null =
    selectedWorkbenchTarget && selectedWorkbenchStyleState
      ? {
          targets: desktopPaneStyleLayerTargets,
          presentationTemplates: desktopPaneStylePresentationTemplates,
          presentations: desktopPaneStylePresentations,
          currentTargetId: selectedWorkbenchTarget.key,
          currentTargetTitle: selectedWorkbenchTarget.label,
          currentTargetDescription: selectedWorkbenchStyleState.summaryText,
          onSelectTarget: targetId => {
            setSelectedStyleTargetKey(targetId);
            setEditingStyleEntryId(null);
            setEditingStyleOwner(null);
          },
          onToggleTargetRendered: (targetId, nextRendered) => {
            const target = workbenchTargets.find(entry => entry.key === targetId);
            if (!target) {
              return;
            }
            handleToggleSourceLayer(target.sourceAlias, target.sourceLayer, nextRendered);
          },
          onAddPresentation: (targetId, templateId) => {
            const target = workbenchTargets.find(entry => entry.key === targetId);
            if (!target) {
              return;
            }
            handleAddStylePresentation(target, templateId as MapWorkbenchRenderStackAddRequestId);
          },
          onSelectPresentation: presentationId => {
            const presentation = desktopPaneStylePresentationLookup.get(presentationId);
            if (!presentation) {
              return;
            }
            setEditingStyleEntryId(presentation.entryId);
            setEditingStyleOwner({
              targetKey: presentation.targetKey,
              profileId: presentation.profileId,
            });
            handleSelectStylePresentation(presentation.targetKey, presentation.profileId);
          },
          onTogglePresentationEnabled: (presentationId, nextEnabled) => {
            const presentation = desktopPaneStylePresentationLookup.get(presentationId);
            if (!presentation) {
              return;
            }
            const target = workbenchTargets.find(entry => entry.key === presentation.targetKey);
            if (!target) {
              return;
            }
            handleToggleStylePresentationEnabled(target, presentationId, nextEnabled);
          },
          onRemovePresentation: presentationId => {
            const presentation = desktopPaneStylePresentationLookup.get(presentationId);
            if (!presentation) {
              return;
            }
            const target = workbenchTargets.find(entry => entry.key === presentation.targetKey);
            if (!target) {
              return;
            }
            handleDeleteStylePresentation(target, presentationId);
          },
          editor: desktopPaneStyleEditor,
        }
      : null;


  const desktopPaneSectionContent = (section: MapWorkbenchDesktopAuthoringPaneSection) => {
    if (section === 'filters' && selectedLayerExperience) {
      return renderTargetScopedSectionShell(
        'Filters',
        <FiltersSection
          filters={selectedLayerExperience.filters}
          onUpdate={patch =>
            handleDesktopPaneLayerUpdate(prev => ({
              ...prev,
              filters: { ...prev.filters, ...patch },
            }))
          }
          availableFields={desktopAuthoringPaneAvailableFields}
          mode={resolvedMode}
        />,
      );
    }
    if (section === 'interactions' && selectedLayerExperience) {
      return renderTargetScopedSectionShell(
        'Interactions',
        <InteractivitySection
          interactivity={selectedLayerExperience.interactivity}
          onCreateItem={handleCreateInteractivityItem}
          onEditItem={handleEditInteractivityItem}
        />,
      );
    }
    if (section === 'data' && selectedLayerExperience) {
      return renderTargetScopedSectionShell(
        'Data Resolvers',
        <DataSection
          enrichments={selectedLayerExperience.enrichments}
          onUpdate={patch =>
            handleDesktopPaneLayerUpdate(prev => ({
              ...prev,
              enrichments: { ...prev.enrichments, ...patch },
            }))
          }
          availableFields={desktopAuthoringPaneAvailableFields}
          availableResolverCatalog={availableResolverCatalog}
          availableResolverConnections={availableResolverConnections}
        />,
      );
    }
    if (section === 'source-info' && selectedLayerExperience) {
      return renderTargetScopedSectionShell(
        'Source Information',
        <SourceInformationSection
          enrichments={selectedLayerExperience.enrichments}
          onUpdate={patch =>
            handleDesktopPaneLayerUpdate(prev => ({
              ...prev,
              enrichments: { ...prev.enrichments, ...patch },
            }))
          }
          availableFields={desktopAuthoringPaneAvailableFields}
        />,
      );
    }
    if (section === 'advanced' && selectedLayerExperience) {
      return renderTargetScopedSectionShell(
        'Advanced',
        <AdvancedSection
          layer={selectedLayerExperience}
          onUpdate={patch =>
            handleDesktopPaneLayerUpdate(prev => ({
              ...prev,
              ...patch,
            }))
          }
          spriteRootSelector={desktopPaneSpriteRootSelector}
        />,
      );
    }
    return null;
  };

  const compactInteractivityEditorModal =
    activeDedicatedInteractivityEditor && selectedLayerExperience && activeInteractivityEditorTitle ? (
      <MapInteractivityModalShell
        open={Boolean(activeDedicatedInteractivityEditor)}
        title={activeInteractivityEditorTitle}
        description="Use the dedicated workspace to author surfaces and interactions while keeping the side-panel inventory in place."
        onOpenChange={handleInteractivityEditorOpenChange}
        className="h-[calc(100vh-2rem)] max-h-[calc(100vh-2rem)] w-[calc(100vw-2rem)] max-w-5xl"
        contentClassName="h-full overflow-hidden p-0"
      >
        {activeDedicatedInteractivityEditor.kind === 'surface' ? (
          <MapSurfaceDefinitionEditor
            mode={activeDedicatedInteractivityEditor.mode}
            value={activeDedicatedInteractivityEditor.draft}
            availableFields={interactivityEditorFieldOptions}
            builtinValues={selectedLayerExperience.enrichments?.builtin_values ?? []}
            availablePipelines={selectedLayerExperience.interactivity?.pipeline_definitions ?? []}
            capabilities={interactivityEditorCapabilities}
            className="h-full rounded-none border-0 shadow-none"
            onChange={next =>
              setActiveInteractivityEditor(current => (current?.kind === 'surface' ? { ...current, draft: next } : current))
            }
            onSave={handleSaveInteractivityEditor}
            onCancel={() => setActiveInteractivityEditor(null)}
          />
        ) : (
          <MapPipelineDefinitionEditor
            mode={activeDedicatedInteractivityEditor.mode}
            value={activeDedicatedInteractivityEditor.draft}
            availableFields={interactivityEditorFieldOptions}
            builtinValues={selectedLayerExperience.enrichments?.builtin_values ?? []}
            resolvers={selectedLayerExperience.enrichments?.resolver_definitions ?? []}
            surfaces={selectedLayerExperience.interactivity?.surface_definitions ?? []}
            availablePipelines={selectedLayerExperience.interactivity?.pipeline_definitions ?? []}
            capabilities={interactivityEditorCapabilities}
            className="h-full rounded-none border-0 shadow-none"
            onChange={next =>
              setActiveInteractivityEditor(current => (current?.kind === 'pipeline' ? { ...current, draft: next } : current))
            }
            onSave={handleSaveInteractivityEditor}
            onCancel={() => setActiveInteractivityEditor(null)}
          />
        )}
      </MapInteractivityModalShell>
    ) : null;
  const desktopAuthoringPaneTarget = selectedWorkbenchTarget
    ? {
        key: selectedWorkbenchTarget.key,
        label: selectedWorkbenchTarget.label,
        sourceId: selectedWorkbenchTarget.sourceId,
        sourceLayer: selectedWorkbenchTarget.sourceLayer,
        geometry: selectedWorkbenchTarget.geometry,
        rendered: selectedWorkbenchTarget.rendered,
        layerIndex: selectedWorkbenchTarget.layerIndex,
        fields: selectedWorkbenchTarget.fields,
      }
    : null;

  const advancedRulesWorkspaceModal =
    advancedRuleWorkspaceSummary && advancedRulePreviewCanvasProps && selectedWorkbenchTarget ? (
      <MapInteractivityModalShell
        open={advancedRulesWorkspaceOpen}
        title={(
          <div className="flex items-center gap-2 text-[10px] font-bold uppercase tracking-[0.2em] text-muted-foreground/60">
            <span>Workbench</span>
            <ChevronRight className="h-3 w-3" />
            <span className="text-foreground">{selectedWorkbenchTarget.label}</span>
            <ChevronRight className="h-3 w-3" />
            <span className="text-primary">Advanced Rules</span>
          </div>
        )}
        onOpenChange={setAdvancedRulesWorkspaceOpen}
        className="h-[calc(100vh-2rem)] max-h-[calc(100vh-2rem)] w-[calc(100vw-2rem)] max-w-[calc(100vw-2rem)]"
        contentClassName="h-full overflow-hidden p-0"
      >
        <div className="grid h-full min-h-0 grid-cols-1 md:flex" data-workbench-advanced-rules-modal>
          <div 
            className={cn(
              "min-h-0 overflow-y-auto border-r border-border bg-background transition-all duration-300 ease-in-out",
              advancedRulesLayout === 'standard' ? "md:w-[30rem]" : 
              advancedRulesLayout === 'editor_focus' ? "md:w-[50rem]" : 
              "md:w-[24rem]"
            )}
          >
            {advancedRuleWorkspaceSummary}
          </div>
          <div className="flex min-h-0 flex-1 flex-col bg-muted/10">
            <header className="flex h-12 shrink-0 items-center justify-between border-b border-border bg-background/50 px-4 backdrop-blur-sm">
              <div className="flex items-center gap-4">
                <div className="flex items-center gap-1 rounded-lg border border-border bg-background p-1 shadow-sm">
                  <Button
                    variant={advancedRulesLayout === 'editor_focus' ? 'secondary' : 'ghost'}
                    size="sm"
                    className="h-7 px-2 text-[10px] font-bold uppercase tracking-wider"
                    onClick={() => setAdvancedRulesLayout('editor_focus')}
                  >
                    Editor Focus
                  </Button>
                  <Button
                    variant={advancedRulesLayout === 'standard' ? 'secondary' : 'ghost'}
                    size="sm"
                    className="h-7 px-2 text-[10px] font-bold uppercase tracking-wider"
                    onClick={() => setAdvancedRulesLayout('standard')}
                  >
                    Standard
                  </Button>
                  <Button
                    variant={advancedRulesLayout === 'map_focus' ? 'secondary' : 'ghost'}
                    size="sm"
                    className="h-7 px-2 text-[10px] font-bold uppercase tracking-wider"
                    onClick={() => setAdvancedRulesLayout('map_focus')}
                  >
                    Map Focus
                  </Button>
                </div>
              </div>

              <div className="flex items-center gap-2">
                <Badge variant="muted" className="h-6 px-2 font-mono text-[10px]">
                  {advancedRuleCount} rule{advancedRuleCount === 1 ? '' : 's'}
                </Badge>
                {advancedRulePreviewCanvasProps.sampleFeatures?.length ? (
                  <Badge variant="muted" className="h-6 px-2 font-mono text-[10px]">
                    {advancedRulePreviewCanvasProps.sampleFeatures.length} sample
                    {advancedRulePreviewCanvasProps.sampleFeatures.length === 1 ? '' : 's'}
                  </Badge>
                ) : null}
              </div>
            </header>

            <div className="flex-1 min-h-0 p-4">
              <MapPreviewFrame
                className="h-full"
                title={advancedRulePreviewCanvasProps.title}
                description={advancedRulePreviewCanvasProps.description}
                helperText={advancedRulePreviewCanvasProps.helperText}
                status={advancedRulePreviewCanvasProps.status}
                loading={previewStatus === 'loading'}
                errorMessage={previewStatus === 'error' ? (editorError ?? 'Failed to render preview.') : null}
                emptyMessage={advancedRulePreviewCanvasProps.emptyMessage}
                toolbar={(
                  <div className="flex w-full flex-wrap items-center gap-2">
                    <Select value={advancedRulesPreviewMode} onValueChange={value => setAdvancedRulesPreviewMode(value as MapStylingPreviewMode)}>
                      <SelectTrigger className="h-8 w-[12rem] bg-background">
                        <SelectValue placeholder="Preview mode" />
                      </SelectTrigger>
                      <SelectContent>
                        {advancedRulePreviewCanvasProps.modeOptions.map(option => (
                          <SelectItem key={option.value} value={option.value}>
                            {option.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <Select
                      value={advancedRulesPreviewSampleSet}
                      onValueChange={value => setAdvancedRulesPreviewSampleSet(value as MapStylingPreviewSampleSet)}
                    >
                      <SelectTrigger className="h-8 w-[12rem] bg-background">
                        <SelectValue placeholder="Sample set" />
                      </SelectTrigger>
                      <SelectContent>
                        {advancedRulePreviewCanvasProps.sampleSetOptions.map(option => (
                          <SelectItem key={option.value} value={option.value}>
                            {option.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <Select
                      value={advancedRulesPreviewBackground}
                      onValueChange={value => setAdvancedRulesPreviewBackground(value as MapStylingPreviewBackground)}
                    >
                      <SelectTrigger className="h-8 w-[10rem] bg-background">
                        <SelectValue placeholder="Background" />
                      </SelectTrigger>
                      <SelectContent>
                        {advancedRulePreviewCanvasProps.backgroundOptions.map(option => (
                          <SelectItem key={option.value} value={option.value}>
                            {option.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                )}
                viewport={(
                  <div className="h-full min-h-[24rem]">
                    <MapWorkbenchRuntimePreview
                      styleJson={advancedRulePreviewStyle}
                      mapExperience={mapExperience}
                      themeColorPalette={themeColorPalette}
                      viewState={advancedRulesPreviewView}
                      status={previewStatus}
                      defaultViewState={previewView ?? defaultViewState}
                      inspectableLayerIds={advancedRuleInspectableLayerIds}
                      runtimeOverlayGeoJSON={null}
                      activeToolId="select"
                      drawnGeometry={null}
                      onViewStateChange={setAdvancedRulesPreviewView}
                      onHoverFeatureChange={handlePreviewHoverFeatureChange}
                      onPinnedFeatureChange={(feature, trigger) => {
                        setPinnedFeature(feature);
                        setPinnedFeatureTrigger(feature ? trigger : null);
                      }}
                    />
                  </div>
                )}
                footer={advancedRulePreviewCanvasProps.sampleFeatures?.length ? (
                  <div className="flex flex-wrap items-center justify-end gap-2">
                    {advancedRulePreviewCanvasProps.sampleFeatures.map(feature => (
                      <Button
                        key={feature.id}
                        type="button"
                        size="sm"
                        variant={feature.selected ? 'secondary' : 'outline'}
                        onClick={() => advancedRulePreviewCanvasProps.onSampleFeatureSelect?.(feature.id)}
                      >
                        {feature.label}
                      </Button>
                    ))}
                  </div>
                ) : undefined}
              />
            </div>
          </div>
        </div>
      </MapInteractivityModalShell>
    ) : null;
  const advancedRuleStyleModal = (() => {
    if (!activeRuleIdForStyleEditing || !selectedRuleLayer) {
      return null;
    }
    const rule = selectedRuleLayer.rules.find(r => r.id === activeRuleIdForStyleEditing);
    if (!rule) {
      return null;
    }
    const selectedRuleStackRole =
      selectedWorkbenchStyleState?.renderStackController.selected_entry?.stack_role ?? 'base';
    const selectedRuleRepresentation = coerceRepresentationForGeometry(
      selectedWorkbenchTarget?.geometry ?? 'unknown',
      (selectedWorkbenchStyleState?.renderStackController.selected_entry?.representation
        ?? selectedRulesProfile?.representation
        ?? selectedWorkbenchStyleState?.activeProfile?.representation
        ?? previewKindToRepresentation(
          selectedWorkbenchStyleState?.stylePreview.kind
            ?? buildDefaultStylePreview(selectedWorkbenchTarget?.geometry ?? 'unknown', selectedWorkbenchTarget?.layerIndex ?? 0).kind,
        )) as MapExperienceStyleRepresentation,
    );
    const forcedRuleSymbolContentMode =
      selectedRuleStackRole === 'overlay_label'
        ? 'glyph'
        : selectedRuleRepresentation === 'symbol' &&
            (selectedRuleStackRole === 'base' || selectedRuleStackRole === 'overlay_symbol')
          ? 'sprite'
          : null;

    return (
      <MapWorkbenchStyleEditorModal
        open={Boolean(activeRuleIdForStyleEditing)}
        onOpenChange={open => !open && setActiveRuleIdForStyleEditing(null)}
        rule={{
          id: rule.id,
          label: rule.label,
          enabled: rule.enabled,
          color: rule.color,
          logic: ensureAdvancedRuleGroup(rule.logic),
          representation: null,
          paint: rule.paint ?? null,
          layout: rule.layout ?? null,
        }}
        defaultRepresentation={selectedRuleRepresentation}
        representationOptions={getRepresentationOptionsForGeometry(selectedWorkbenchTarget?.geometry ?? 'unknown')}
        allowRepresentationSelection={false}
        iconPicker={selectedRuleStackRole === 'overlay_label' ? null : desktopPaneIconPicker}
        forcedSymbolContentMode={forcedRuleSymbolContentMode}
        onUpdate={patch => {
          const nextRule = {
            id: rule.id,
            label: rule.label,
            enabled: rule.enabled,
            color: rule.color,
            logic: ensureAdvancedRuleGroup(rule.logic),
            representation: null,
            paint: rule.paint ?? null,
            layout: rule.layout ?? null,
            ...patch,
            representation: null,
          };
          const resolvedRepresentation = selectedRuleRepresentation;
          const sanitizedPaint = sanitizePaintForRepresentation(
            resolvedRepresentation,
            nextRule.paint ?? null,
            {
              iconColorEnabled: isSymbolIconTintable(
                activeSpriteIconSet,
                readLayoutIconImage(nextRule.layout ?? null),
              ),
            },
          );
          const sanitizedLayout = sanitizeLayoutForRepresentation(
            resolvedRepresentation,
            nextRule.layout ?? null,
          );
          handleAdvancedRuleChange(activeRuleIdForStyleEditing, {
            ...nextRule,
            representation: null,
            paint: sanitizedPaint,
            layout: sanitizedLayout,
            color: normalizeAdvancedRuleColor(
              readPaintPrimaryColor(
                resolvedRepresentation,
                sanitizedPaint,
                nextRule.color,
              ),
            ),
          });
        }}
      />
    );
  })();
  const desktopAuthoringPaneSections = isAuthorMode
    ? DESKTOP_AUTHORING_PANE_SECTIONS
    : (['filters'] as MapWorkbenchDesktopAuthoringPaneSection[]);
  const desktopAuthoringPaneEnabled = Boolean(renderDesktopAuthoringPane) && !rightPaneCollapsed;
  const desktopAuthoringPaneTargetOptions = workbenchTargets.map(target => ({
    key: target.key,
    label: target.label,
    sourceId: target.sourceId,
    sourceLayer: target.sourceLayer,
    geometry: target.geometry,
    rendered: target.rendered,
    layerIndex: target.layerIndex,
    fields: target.fields,
  }));
  const rightPaneCollapseButton = (
    <div className="flex items-start gap-3">
      <button
        type="button"
        onClick={() => setRightPaneCollapsed(true)}
        aria-label="Collapse right sidebar"
        className="rounded-full p-2 text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
      >
        <ChevronRight className="h-4 w-4" />
      </button>
    </div>
  );

  const fullSourcesPane = (
    <MapSourcesPane
      activeTab={sourcesPaneTab}
      catalogSources={sourceCatalogItems}
      mountedGroups={sourceLayerManagerGroups}
      spriteCatalog={availableSpriteCatalogItems}
      attachedSprites={attachedSpriteCatalog}
      glyphOptions={glyphOptions}
      selectedGlyphId={selectedGlyphSet?.id ?? null}
      searchValue={sourceSearch}
      scopeValue={sourceFilter}
      scopeOptions={sourceCatalogScopeOptions}
      loading={sourcesLoading}
      errorMessage={sourcesError}
      spriteLoading={spriteSetsLoading}
      spriteErrorMessage={spriteSetsError}
      fontLoading={glyphSetsLoading}
      fontErrorMessage={glyphSetsError}
      emptyCatalogMessage="No sources found matching your criteria."
      emptyMountedMessage="No sources are mounted in this workspace yet."
      emptySpriteCatalogMessage="No sprite sheets are available for this scope."
      emptyAttachedSpritesMessage="No sprite sheets are attached to this workbench yet."
      emptyFontsMessage="No font bundles are available for this scope."
      onTabChange={setSourcesPaneTab}
      onSearchChange={setSourceSearch}
      onScopeChange={(value: 'all' | MapWorkbenchShareScope) => setSourceFilter(value)}
      onAddSource={(sourceId: string) => {
        const source = filteredSources.find(entry => entry.id === sourceId);
        if (!source) {
          return;
        }
        handleAddSource(source);
      }}
      onAddSprite={(spriteSetId: string) => {
        const sprite = spriteSets.find(entry => entry.id === spriteSetId);
        if (!sprite) {
          return;
        }
        handleSelectSpriteSet(sprite);
      }}
      onSelectSprite={spriteId => updateActiveSpriteId(spriteId)}
      onRemoveSprite={handleRemoveAttachedSprite}
      onMoveSprite={handleMoveAttachedSprite}
      onSelectGlyph={(glyphId: string) => {
        const glyph = glyphSets.find(entry => entry.id === glyphId);
        if (!glyph) {
          return;
        }
        handleSelectGlyphSet(glyph);
      }}
      onClearGlyph={handleClearGlyphSet}
      onSelectLayer={handleSelectLayerManagerTarget}
      onRemoveSource={handleRemoveMountedSource}
      onToggleLayer={handleToggleSourceLayer}
      headerAction={rightPaneCollapseButton}
      className="h-full w-full border-l-0 shadow-none"
    />
  );

  const embeddedSourcesPane = (
    <MapSourcesPane
      activeTab={sourcesPaneTab}
      catalogSources={sourceCatalogItems}
      mountedGroups={sourceLayerManagerGroups}
      spriteCatalog={availableSpriteCatalogItems}
      attachedSprites={attachedSpriteCatalog}
      glyphOptions={glyphOptions}
      selectedGlyphId={selectedGlyphSet?.id ?? null}
      searchValue={sourceSearch}
      scopeValue={sourceFilter}
      scopeOptions={sourceCatalogScopeOptions}
      loading={sourcesLoading}
      errorMessage={sourcesError}
      spriteLoading={spriteSetsLoading}
      spriteErrorMessage={spriteSetsError}
      fontLoading={glyphSetsLoading}
      fontErrorMessage={glyphSetsError}
      emptyCatalogMessage="No sources found matching your criteria."
      emptyMountedMessage="No sources are mounted in this workspace yet."
      emptySpriteCatalogMessage="No sprite sheets are available for this scope."
      emptyAttachedSpritesMessage="No sprite sheets are attached to this workbench yet."
      emptyFontsMessage="No font bundles are available for this scope."
      onTabChange={setSourcesPaneTab}
      onSearchChange={setSourceSearch}
      onScopeChange={(value: 'all' | MapWorkbenchShareScope) => setSourceFilter(value)}
      onAddSource={(sourceId: string) => {
        const source = filteredSources.find(entry => entry.id === sourceId);
        if (!source) {
          return;
        }
        handleAddSource(source);
      }}
      onAddSprite={(spriteSetId: string) => {
        const sprite = spriteSets.find(entry => entry.id === spriteSetId);
        if (!sprite) {
          return;
        }
        handleSelectSpriteSet(sprite);
      }}
      onSelectSprite={spriteId => updateActiveSpriteId(spriteId)}
      onRemoveSprite={handleRemoveAttachedSprite}
      onMoveSprite={handleMoveAttachedSprite}
      onSelectGlyph={(glyphId: string) => {
        const glyph = glyphSets.find(entry => entry.id === glyphId);
        if (!glyph) {
          return;
        }
        handleSelectGlyphSet(glyph);
      }}
      onClearGlyph={handleClearGlyphSet}
      onSelectLayer={handleSelectLayerManagerTarget}
      onRemoveSource={handleRemoveMountedSource}
      onToggleLayer={handleToggleSourceLayer}
      embedded
      className="h-full w-full border-l-0 shadow-none"
    />
  );

  const rightPaneBody = styleSheetTab === 'sources' ? (
    fullSourcesPane
  ) : styleSheetTab === 'legend' ? (
    <MapLegendPane
      sections={legendPaneSections}
      emptyMessage="No active legends yet."
      className="h-full w-full border-l-0 shadow-none"
    />
  ) : styleSheetTab === 'presets' ? (
    <MapPresetPane
      mode={resolvedMode}
      presets={presetPanePresets}
      draftLabel={presetDraftLabel}
      saveCurrentLabel="Save current view"
      onDraftLabelChange={setPresetDraftLabel}
      onSaveCurrent={canEditPresets ? handleSavePreset : undefined}
      onApplyPreset={(presetId: string) => {
        const preset = mapExperience.presets.find(entry => entry.id === presetId);
        if (preset) {
          handleApplyPreset(preset);
        }
      }}
      onDeletePreset={canEditPresets ? handleDeletePreset : undefined}
      className="h-full w-full border-l-0 shadow-none"
    />
  ) : (
    <MapDiagnosticsPane
      generatedJson={generatedJsonText}
      summaryItems={diagnosticsSummaryItems}
      onCopyJson={() => void handleCopyJson()}
      className="h-full w-full border-l-0 shadow-none"
    />
  );
  const runtimeSurfaceSectionContent = resolveRuntimeSurfaceSectionContent({
    feature: actionSurfaceFeature,
    runtimeFeature: activeRuntimeSurfaceState?.runtimeFeature ?? null,
    surface: actionSurface,
    renderRuntimeSurfaceSection,
  });
  const runtimeSurfaceTabContent = resolveRuntimeSurfaceTabContent({
    feature: actionSurfaceFeature,
    runtimeFeature: activeRuntimeSurfaceState?.runtimeFeature ?? null,
    surface: actionSurface,
    renderRuntimeSurfaceSection,
  });
  const dockedSurfacePane =
    actionSurface?.surface.surface_kind === 'side_panel' ? (
      runtimeSurfaceTabContent.length > 0 ? (
        <MapTabbedSidePanelSurface
          title={actionSurface.title || actionSurface.surface.label || 'Feature Surface'}
          subtitle={actionSurface.subtitle}
          description={actionSurface.surface.label ?? undefined}
          badges={actionSurface.badges}
          summary={actionSurface.summary}
          headerMode={runtimeSurfacePanelProps?.headerMode}
          hideDescription={runtimeSurfacePanelProps?.hideDescription}
          hideSummary={runtimeSurfacePanelProps?.hideSummary}
          tabs={runtimeSurfaceTabContent}
          defaultTabId={runtimeSurfacePanelProps?.defaultTabId}
          actions={actionSurfaceItems}
          onAction={(actionId: string) => {
            if (!actionSurfaceFeature) {
              return;
            }
            const action = actionSurface.actions.find(entry => entry.pipeline.pipeline_id === actionId);
            if (!action) {
              return;
            }
            void handleFeaturePopupAction(actionSurface, action, actionSurfaceFeature);
          }}
          onInspect={
            runtimeSurfacePanelProps?.hideInspectAction
              ? undefined
              : actionSurfaceFeature
                ? () => openInspectionForFeature(actionSurfaceFeature)
                : undefined
          }
          onClear={() => {
            clearPinnedFeatureSelection();
            setActionSurface(null);
            setActionSurfaceFeature(null);
            setActionPanelSource(null);
          }}
          className="h-full w-full border-l-0 shadow-none"
        />
      ) : (
        <MapSidePanelSurface
          title={actionSurface.title || actionSurface.surface.label || 'Feature Surface'}
          subtitle={actionSurface.subtitle}
          description={actionSurface.surface.label ?? undefined}
          badges={actionSurface.badges}
          summary={actionSurface.summary}
          headerMode={runtimeSurfacePanelProps?.headerMode}
          hideDescription={runtimeSurfacePanelProps?.hideDescription}
          hideSummary={runtimeSurfacePanelProps?.hideSummary}
          content={runtimeSurfaceSectionContent.length > 0 ? runtimeSurfaceSectionContent : undefined}
          actions={actionSurfaceItems}
          onAction={(actionId: string) => {
            if (!actionSurfaceFeature) {
              return;
            }
            const action = actionSurface.actions.find(entry => entry.pipeline.pipeline_id === actionId);
            if (!action) {
              return;
            }
            void handleFeaturePopupAction(actionSurface, action, actionSurfaceFeature);
          }}
          onInspect={
            runtimeSurfacePanelProps?.hideInspectAction
              ? undefined
              : actionSurfaceFeature
                ? () => openInspectionForFeature(actionSurfaceFeature)
                : undefined
          }
          onClear={() => {
            clearPinnedFeatureSelection();
            setActionSurface(null);
            setActionSurfaceFeature(null);
            setActionPanelSource(null);
          }}
          className="h-full w-full border-l-0 shadow-none"
        />
      )
    ) : null;
  const suppressIdleViewerRightPane = viewerHideIdleRightPane && isViewMode && !dockedSurfacePane;
  const effectiveRightPaneCollapsed = rightPaneCollapsed || suppressIdleViewerRightPane;
  const visibleRightPaneBody = suppressIdleViewerRightPane ? null : rightPaneBody;

  useEffect(() => {
    if (suppressIdleViewerRightPane) {
      setRightPaneCollapsed(true);
    }
  }, [suppressIdleViewerRightPane]);

  const desktopPaneRenderSection = (section: MapWorkbenchDesktopAuthoringPaneSection) => {
    if (section !== 'sources') {
      return desktopPaneSectionContent(section);
    }
    if (section === 'sources') {
      return embeddedSourcesPane;
    }
    return undefined;
  };

  const desktopAuthoringPane = desktopAuthoringPaneEnabled && renderDesktopAuthoringPane
    ? renderDesktopAuthoringPane({
        mode: resolvedMode,
        activeSection: desktopPaneSection,
        availableFields: desktopAuthoringPaneAvailableFields,
        stylePane: desktopPaneStylePane,
        spriteRootSelector: desktopPaneSpriteRootSelector,
        footerMessage: rightPaneFooterMessage,
        layerExperience: selectedLayerExperience,
        onApply: handleApplyPreview,
        onSave: handlePrimarySaveAction,
        onSectionChange: handleDesktopPaneSectionChange,
        onSelectTarget: handleSelectWorkbenchTarget,
        onUpdateLayer: handleDesktopPaneLayerUpdate,
        renderSection: desktopPaneRenderSection,
        sections: desktopAuthoringPaneSections,
        target: desktopAuthoringPaneTarget,
        targetOptions: desktopAuthoringPaneTargetOptions,
        applyDisabled: previewStatus === 'loading',
        applyLabel: previewStatus === 'loading' ? 'Refreshing…' : previewStatus === 'error' ? 'Retry preview' : 'Refresh preview',
        canApply: canApplyChanges,
        canSave: canSaveCurrent || canSaveCopy,
        saveDisabled: saving || (canSaveCurrent && !hasUnsavedChanges),
        saveLabel: saving ? 'Saving…' : primarySaveActionLabel,
      })
    : null;
  const visibleDesktopAuthoringPane = suppressIdleViewerRightPane ? null : desktopAuthoringPane;

  if (styleId && styleLoading && !style) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-8 w-1/3" />
        <Skeleton className="h-80 w-full" />
      </div>
    );
  }

  if (styleId && styleError) {
    return buildInlineError(styleError);
  }

  if (styleId && !style) {
    return buildInlineError('Style not found.');
  }

  const rightPaneUsesMobileOverlay = isMobileWorkbench && !effectiveRightPaneCollapsed;
  const hideWorkbenchToolbar = resolvedMode === 'view' && (isMobileWorkbench || hideViewModeToolbar);

  return (
    <div
      className={cn(
        'flex min-h-0 flex-col overflow-hidden bg-background',
        rootClassName ?? 'h-full'
      )}
      data-workbench-root
    >
      {!hideWorkbenchToolbar ? (
        <div
          className="flex h-10 shrink-0 items-center border-b border-border bg-background/95 px-2 backdrop-blur supports-[backdrop-filter]:bg-background/80"
          data-workbench-toolbar
        >
          <div className="min-w-0 flex-1">
            <MapWorkspaceCommandBar
              mode={resolvedMode}
              primaryActions={commandBarPrimaryActions}
              secondaryActions={commandBarSecondaryActions}
              statusBadges={[
                ...(isCustomMap ? [{ id: 'asset', label: 'Custom Map', tone: 'muted' as const }] : []),
                ...commandBarStatusBadges,
              ]}
              onAction={(actionId: string) => {
                switch (actionId) {
                  case 'apply':
                    handleApplyPreview();
                    return;
                  case 'save':
                    handlePrimarySaveAction();
                    return;
                  case 'save-as':
                    handleOpenSaveSheet();
                    return;
                  default:
                    return;
                }
              }}
              className="h-10 border-b-0 bg-transparent px-0 shadow-none"
            />
          </div>

          <div className="ml-2 hidden shrink-0 items-center gap-1 md:flex">
            <DropdownMenu modal={false}>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="icon" className="h-8 w-8" aria-label="Workbench more actions">
                  <MoreHorizontal className="h-4 w-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                {canUseAssets ? <DropdownMenuItem onClick={() => setAssetsSheetOpen(true)}>Style assets</DropdownMenuItem> : null}
                {pinnedFeature ? <DropdownMenuItem onClick={() => setInspectionSheetOpen(true)}>Pinned inspection</DropdownMenuItem> : null}
                {canUseAi ? <DropdownMenuItem onClick={() => setAiPanelOpen(true)}>AI helper</DropdownMenuItem> : null}
                {canUseDiagnostics ? (
                  <>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem onClick={() => openWorkbenchTab('diagnostics')}>Generated Style JSON</DropdownMenuItem>
                    <DropdownMenuItem onClick={() => void handleCopyJson()}>Copy generated JSON</DropdownMenuItem>
                  </>
                ) : null}
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={() => navigate(collectionPath)}>
                  {isCustomMap ? 'Back to custom maps' : 'Back to styles'}
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
      ) : null}

      {ownership.notice ? (
        <div className="border-b border-border bg-muted/20 px-3 py-2 text-sm text-muted-foreground">
          {ownership.notice}
        </div>
      ) : null}

      <div className="relative flex min-h-0 flex-1 overflow-hidden">
        <div
          ref={centerPaneRef}
          className={cn(
            'relative min-h-0 min-w-0 flex-1',
            !rightPaneUsesMobileOverlay && 'border-r border-border'
          )}
          data-workbench-center-pane
        >
          <div className="absolute inset-0" data-workbench-map>
            <MapWorkbenchRuntimePreview
              styleJson={runtimePreviewStyle}
              mapExperience={mapExperience}
              themeColorPalette={themeColorPalette}
              viewState={previewView}
              status={previewStatus}
              defaultViewState={defaultViewState}
              inspectableLayerIds={inspectableLayerIds}
              runtimeFeatureStateTargets={runtimeFeatureStateTargets}
              hydrateRuntimeFeatureState={hydrateRuntimeFeatureState}
              runtimeOverlayGeoJSON={runtimeOverlayGeoJSON}
              runtimeOverlayStyle={runtimeOverlayStyle}
              runtimeOverlayInteractive={runtimeOverlayInteractive}
              activeToolId={activeToolId}
              drawnGeometry={drawnGeometry}
              showFilterControl
              filterControlActive={viewerFiltersSheetOpen}
              filterControlBadge={viewerFilterSections.length > 0 ? String(viewerFilterSections.length) : null}
              showLegendControl={viewerLegendSections.length > 0}
              legendControlActive={viewerLegendSheetOpen}
              legendControlBadge={null}
              hideFloatingControls={rightPaneUsesMobileOverlay}
              onOpenFilters={handleToggleViewerFiltersSheet}
              onOpenLegend={handleToggleViewerLegendSheet}
              resolveSpiderifyChooserItem={resolveSpiderifyChooserItem}
              isRuntimeOverlayFeatureInteractive={isRuntimeOverlayFeatureInteractive}
              onRuntimeOverlayFeatureHover={onRuntimeOverlayFeatureHover}
              onRuntimeOverlayFeatureClick={onRuntimeOverlayFeatureClick}
              onViewStateChange={handlePreviewViewChange}
              onHoverFeatureChange={handlePreviewHoverFeatureChange}
              onPinnedFeatureChange={(feature, trigger) => {
                setPinnedFeature(feature);
                setPinnedFeatureTrigger(feature ? trigger : null);
                onPinnedFeatureChange?.(feature, trigger);
              }}
              onToolRuntimeEvent={handleToolRuntimeEvent}
            />
          </div>
          {showToolRail && toolRailItems.length > 0 && !rightPaneUsesMobileOverlay ? (
            <div className="absolute left-3 top-3 z-20">
              <MapToolRail items={toolRailItems} onAction={handleToolRailAction} />
            </div>
          ) : null}
          {(!isMobileWorkbench || effectiveRightPaneCollapsed) && !suppressIdleViewerRightPane ? (
            <div className="absolute right-3 top-3 z-20 rounded-lg border border-[var(--border)] bg-[var(--card)]/92 p-1 shadow-lg backdrop-blur-sm">
              <button
                type="button"
                className={cn(
                  'relative flex h-8 w-8 items-center justify-center rounded-md transition-colors',
                  'text-[var(--muted-foreground)] hover:bg-[var(--accent)] hover:text-[var(--accent-foreground)]',
                  'focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-[var(--ring)]',
                )}
                onClick={() => setRightPaneCollapsed(current => !current)}
                aria-label={effectiveRightPaneCollapsed ? 'Expand right sidebar' : 'Collapse right sidebar'}
              >
                {effectiveRightPaneCollapsed ? <ChevronLeft className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
              </button>
            </div>
          ) : null}
          {saveError ? (
            <div className="absolute left-3 top-14 z-20 w-[min(34rem,calc(100%-1.5rem))]">
              {buildInlineError(saveError)}
            </div>
          ) : null}
          {editorError ? (
            <div className="absolute left-3 top-14 z-20 w-[min(34rem,calc(100%-1.5rem))]">
              {buildInlineError(editorError)}
            </div>
          ) : null}
          {popupFeature ? (
            <div
              className={cn(
                'absolute z-20',
                popupPinned ? 'pointer-events-auto' : 'pointer-events-none'
              )}
              style={popupAnchorStyle}
            >
              <MapHoverPopupSurface
                title={popupIdentity?.title ?? 'Visible feature'}
                subtitle={popupIdentity?.subtitle ?? undefined}
                badges={activeFeaturePopup?.badges ?? []}
                summary={activeFeaturePopup?.summary ?? []}
                actions={popupPinned ? popupActionItems : []}
                pinned={popupPinned}
                onAction={(actionId: string) => {
                  if (!activeFeaturePopup) {
                    return;
                  }
                  const action = activeFeaturePopup.actions.find(entry => entry.pipeline.pipeline_id === actionId);
                  if (!action) {
                    return;
                  }
                  void handleFeaturePopupAction(activeFeaturePopup, action, popupFeature);
                }}
                onInspect={popupPinned ? () => openInspectionForFeature(popupFeature) : undefined}
                onClear={
                  popupPinned
                    ? clearPinnedFeatureSelection
                    : undefined
                }
              />
            </div>
          ) : null}
          {floatingSurfaceFeature && activeRuntimeSurface?.surface.surface_kind === 'floating_summary_card' ? (
            <div
              className={cn('absolute z-20', floatingSurfacePinned ? 'pointer-events-auto' : 'pointer-events-none')}
              style={floatingSurfaceAnchorStyle}
            >
              <MapFloatingSummaryCardSurface
                title={
                  activeRuntimeSurface.title ||
                  floatingSurfaceFeature.sourceLayer ||
                  floatingSurfaceFeature.layerId ||
                  'Visible feature'
                }
                subtitle={
                  activeRuntimeSurface.subtitle ||
                  `${floatingSurfaceFeature.geometryType ?? 'Unknown geometry'}${
                    floatingSurfaceFeature.id != null ? ` · ${String(floatingSurfaceFeature.id)}` : ''
                  }`
                }
                badges={activeRuntimeSurface.badges}
                summary={activeRuntimeSurface.summary}
                actions={floatingSurfaceActionItems}
                onAction={(actionId: string) => {
                  const action = activeRuntimeSurface.actions.find(entry => entry.pipeline.pipeline_id === actionId);
                  if (!action) {
                    return;
                  }
                  void handleFeaturePopupAction(activeRuntimeSurface, action, floatingSurfaceFeature);
                }}
                onInspect={floatingSurfacePinned ? () => openInspectionForFeature(floatingSurfaceFeature) : undefined}
                onClear={
                  floatingSurfacePinned
                    ? clearPinnedFeatureSelection
                    : undefined
                }
              />
            </div>
          ) : null}
        </div>

        {isMobileWorkbench ? (
          <div
            className={cn(
              'absolute inset-0 z-30 flex min-h-0 flex-col bg-background shadow-2xl transition-transform duration-300 ease-in-out',
              'relative',
                  effectiveRightPaneCollapsed ? 'pointer-events-none translate-x-full' : 'translate-x-0'
                )}
            data-workbench-right-pane
          >
            <div className="pointer-events-auto absolute right-3 top-3 z-40">
              <button
                type="button"
                onClick={() => setRightPaneCollapsed(true)}
                aria-label="Collapse right sidebar"
                className="rounded-full border border-border bg-background/95 p-2 text-muted-foreground shadow-sm backdrop-blur-sm transition-colors hover:bg-muted hover:text-foreground"
              >
                <ChevronRight className="h-4 w-4" />
              </button>
            </div>
            <aside className="min-h-0 flex-1 overflow-hidden pt-11">
              {!effectiveRightPaneCollapsed ? (
                dockedSurfacePane ? (
                  dockedSurfacePane
                ) : visibleDesktopAuthoringPane ? (
                  visibleDesktopAuthoringPane
                ) : visibleRightPaneBody ? (
                  visibleRightPaneBody
                ) : null
              ) : null}
            </aside>
          </div>
        ) : (
          <div
            className={cn(
              'group relative flex min-h-0 shrink-0 border-l border-border bg-background transition-[width] duration-300 ease-in-out',
              effectiveRightPaneCollapsed ? 'w-0 border-l-0' : 'max-w-[calc(100vw-2.5rem)]'
            )}
            style={{ width: effectiveRightPaneCollapsed ? 0 : `${rightPaneWidth}px` }}
            data-workbench-right-pane
          >
            {!effectiveRightPaneCollapsed && (
              <div
                className="absolute -left-1 top-0 z-30 h-full w-2 cursor-col-resize hover:bg-primary/20"
                onMouseDown={handleResizeStart}
              />
            )}
            <aside className="h-full w-full overflow-hidden">
              {!effectiveRightPaneCollapsed ? (
                dockedSurfacePane ? (
                  dockedSurfacePane
                ) : visibleDesktopAuthoringPane ? (
                  visibleDesktopAuthoringPane
                ) : visibleRightPaneBody ? (
                  visibleRightPaneBody
                ) : null
              ) : null}
            </aside>
          </div>
        )}
      </div>

      {compactInteractivityEditorModal}
      {advancedRulesWorkspaceModal}
      {advancedRuleStyleModal}

      <Sheet open={viewerFiltersSheetOpen} onOpenChange={setViewerFiltersSheetOpen}>
        <SheetContent side="right" size="sm" className="p-0">
          <MapViewerFiltersSheetBody
            title="Filters"
            description="Adjust authored runtime filters without opening the advanced editor."
            sections={viewerFilterSections}
            emptyMessage="No viewer filters are available for this prepared map."
            onEnabledChange={handleViewerFilterEnabledChange}
            onValueChange={handleViewerFilterValueChange}
            onResetFilter={handleViewerFilterReset}
            onResetAll={handleViewerResetAllFilters}
          />
        </SheetContent>
      </Sheet>

      <Sheet open={viewerLegendSheetOpen} onOpenChange={setViewerLegendSheetOpen}>
        <SheetContent side="right" size="sm" className="p-0">
          <MapViewerLegendSheetBody
            title="Legend"
            description="Read the active rule styling without opening the full workbench."
            sections={viewerLegendSections}
            emptyMessage="No legend items are available for this prepared map."
          />
        </SheetContent>
      </Sheet>

      <Sheet open={assetsSheetOpen} onOpenChange={setAssetsSheetOpen}>
        <SheetContent side="right" size="lg">
          <SheetHeader>
            <SheetTitle>Style Assets</SheetTitle>
            <SheetDescription>Review mounted sources, fixed basemap runtime assets, and overlay asset metadata.</SheetDescription>
          </SheetHeader>

          <div className="mt-6 min-h-0">
            <MapAssetDependenciesSheet
              assetKind={isCustomMap ? 'custom-map' : 'style'}
              basemap={{
                themeLabel: basemapThemeLabel,
                spriteUrl: 'Not used for overlay icon authoring',
                glyphTemplateUrl: '/api/v1/system/basemaps/fonts/{fontstack}/{range}.pbf',
              }}
              mountedSources={assetDependencyMountedSources}
              spriteOptions={spriteOptions}
              glyphOptions={glyphOptions}
              onRemoveMountedSource={handleRemoveMountedSource}
              onSelectSprite={(spriteId: string) => {
                const sprite = spriteSets.find(entry => entry.id === spriteId);
                if (sprite) {
                  handleSelectSpriteSet(sprite);
                }
              }}
              onClearSprite={handleClearSpriteSet}
              onSelectGlyph={(glyphId: string) => {
                const glyph = glyphSets.find(entry => entry.id === glyphId);
                if (glyph) {
                  handleSelectGlyphSet(glyph);
                }
              }}
              onClearGlyph={handleClearGlyphSet}
            />
            {spriteSetsError ? <div className="mt-4">{buildInlineError(spriteSetsError)}</div> : null}
            {glyphSetsError ? <div className="mt-4">{buildInlineError(glyphSetsError)}</div> : null}
            {spriteSetsLoading || glyphSetsLoading ? (
              <div className="mt-4 space-y-2">
                <Skeleton className="h-10 w-full" />
                <Skeleton className="h-10 w-full" />
              </div>
            ) : null}
          </div>
        </SheetContent>
      </Sheet>

      {ai ? (
        <Sheet open={aiPanelOpen} onOpenChange={setAiPanelOpen}>
          <SheetContent side="right" size="lg">
            <SheetHeader>
              <SheetTitle>AI style helper</SheetTitle>
              <SheetDescription>Generate JSON Patch edits and auto-apply them to the preview.</SheetDescription>
            </SheetHeader>
            <div className="mt-6 space-y-4">
              {ai.loading ? (
                <div className="space-y-2">
                  <Skeleton className="h-10 w-full" />
                  <Skeleton className="h-10 w-full" />
                </div>
              ) : null}

              {ai.error ? buildInlineError(ai.error) : null}

              {!ai.loading && !activeAiKeySet ? (
                <div className="rounded-lg border border-border bg-muted/40 p-4 text-sm text-muted-foreground">
                  <div className="font-medium text-foreground">API key required</div>
                  <div className="mt-1">Add a provider key in Settings to enable AI styling.</div>
                  <Button variant="link" className="mt-2 px-0" onClick={ai.openSettings}>
                    Open Settings
                  </Button>
                </div>
              ) : null}

              <div className="space-y-2">
                <Label htmlFor="ai-provider">Provider</Label>
                <Select value={aiProvider} onValueChange={value => setAiProvider(value as MapWorkbenchAiProvider)}>
                  <SelectTrigger id="ai-provider">
                    <SelectValue placeholder="Select provider" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="openai">OpenAI</SelectItem>
                    <SelectItem value="anthropic">Anthropic</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label htmlFor="ai-prompt">What should the style change?</Label>
                <Textarea
                  id="ai-prompt"
                  value={aiPrompt}
                  onChange={event => setAiPrompt(event.target.value)}
                  placeholder="Example: Make land parcels light gray with bold boundaries and highlight roads in orange."
                  className="min-h-[120px]"
                />
              </div>

              {aiError ? buildInlineError(aiError) : null}

              <div className="flex flex-wrap items-center gap-2">
                <Button onClick={handleGenerateAiPatch} disabled={aiGenerating || !activeAiKeySet || !aiPrompt.trim()}>
                  {aiGenerating ? 'Generating…' : 'Generate patch'}
                </Button>
                <Badge variant="muted">Auto-apply</Badge>
              </div>

              <div className="space-y-2">
                <Label htmlFor="ai-patch">Latest patch</Label>
                <Textarea
                  id="ai-patch"
                  value={aiPatchText ?? ''}
                  readOnly
                  placeholder="Patch output will appear here."
                  className="min-h-[200px] font-mono text-xs"
                />
              </div>
            </div>
          </SheetContent>
        </Sheet>
      ) : null}

      <Sheet open={inspectionSheetOpen} onOpenChange={open => {
        setInspectionSheetOpen(open);
        if (!open) {
          setPinnedFeature(null);
          setPinnedFeatureTrigger(null);
        }
      }}>
        <SheetContent side="right" size="lg">
          <div className="mt-2 h-full">
            {pinnedFeature ? (
              <MapFeatureInspectionSheet
                title={activeFeaturePopup?.title ?? pinnedFeature.sourceLayer ?? pinnedFeature.layerId ?? 'Visible feature'}
                subtitle={activeFeaturePopup?.subtitle ?? 'Only currently rendered visible features can be inspected.'}
                metadata={{
                  source: pinnedFeature.source ?? null,
                  sourceLayer: pinnedFeature.sourceLayer ?? null,
                  styleLayer: pinnedFeature.layerId ?? null,
                  featureId: pinnedFeature.id ?? null,
                  geometryType: pinnedFeature.geometryType ?? null,
                }}
                summary={activeFeaturePopup?.summary ?? []}
                badges={activeFeaturePopup?.badges ?? []}
                actions={popupActionItems}
                properties={inspectionPropertyItems}
                rawJson={JSON.stringify(pinnedFeature.rawJson, null, 2)}
                onAction={(actionId: string) => {
                  if (!activeFeaturePopup) {
                    return;
                  }
                  const action = activeFeaturePopup.actions.find(entry => entry.pipeline.pipeline_id === actionId);
                  if (!action) {
                    return;
                  }
                  void handleFeaturePopupAction(activeFeaturePopup, action, pinnedFeature);
                }}
                onClear={() => {
                  clearPinnedFeatureSelection();
                }}
              />
            ) : (
              <div className="rounded-md border border-dashed border-border/60 px-3 py-6 text-sm text-muted-foreground">
                {isAuthorMode
                  ? 'Click or open the context menu on a visible rendered feature to inspect it here.'
                  : 'Hover a visible rendered feature for a quick preview, then click or open the context menu to pin it here.'}
              </div>
            )}
          </div>
        </SheetContent>
      </Sheet>

      <Sheet
        open={Boolean(actionPanelOpen && actionSurface?.surface.surface_kind === 'bottom_sheet')}
        onOpenChange={open => {
          setActionPanelOpen(open);
          if (!open) {
            clearPinnedFeatureSelection();
            setActionSurface(null);
            setActionSurfaceFeature(null);
            setActionPanelSource(null);
          }
        }}
      >
        <SheetContent
          side="bottom"
          size="full"
          className="overflow-hidden p-0"
        >
          <div className="h-full">
            {actionSurface?.surface.surface_kind === 'bottom_sheet' ? (
              <MapBottomSheetSurface
                title={actionSurface.title || actionSurface.surface.label || 'Feature Surface'}
                subtitle={actionSurface.subtitle}
                description={actionSurface.surface.label ?? undefined}
                badges={actionSurface.badges}
                summary={actionSurface.summary}
                content={runtimeSurfaceSectionContent.length > 0 ? runtimeSurfaceSectionContent : undefined}
                actions={actionSurfaceItems}
                onAction={(actionId: string) => {
                  if (!actionSurfaceFeature) {
                    return;
                  }
                  const action = actionSurface.actions.find(entry => entry.pipeline.pipeline_id === actionId);
                  if (!action) {
                    return;
                  }
                  void handleFeaturePopupAction(actionSurface, action, actionSurfaceFeature);
                }}
                onInspect={actionSurfaceFeature ? () => openInspectionForFeature(actionSurfaceFeature) : undefined}
                onClear={() => {
                  setActionPanelOpen(false);
                  clearPinnedFeatureSelection();
                  setActionSurface(null);
                  setActionSurfaceFeature(null);
                  setActionPanelSource(null);
                }}
              />
            ) : null}
          </div>
        </SheetContent>
      </Sheet>

      <Sheet open={saveSheetOpen} onOpenChange={setSaveSheetOpen}>
        <SheetContent side="right" size="lg">
          <div className="mt-2 h-full">
            <MapSaveSheet
              assetKind={isCustomMap ? 'custom-map' : 'style'}
              title={isCustomMap ? 'Save as custom map' : 'Save as style'}
              description={saveForm.description}
              name={saveForm.name}
              summary={
                isCustomMap
                  ? 'Store this composition with its dependencies, rules, and saved viewport.'
                  : 'Store this JSON as a reusable MapLibre style.'
              }
              details={`Mounted sources: ${mountedSourceRefs.length} · Saved views: ${mapExperience.presets.length}`}
              shareScope={saveForm.shareScope}
              shareScopeOptions={shareScopeOptions}
              submitting={creating}
              errorMessage={saveFormError}
              onNameChange={(value: string) => setSaveForm(prev => ({ ...prev, name: value }))}
              onDescriptionChange={(value: string) => setSaveForm(prev => ({ ...prev, description: value }))}
              onShareScopeChange={(value: MapWorkbenchShareScope) => setSaveForm(prev => ({ ...prev, shareScope: value }))}
              onCancel={() => setSaveSheetOpen(false)}
              onSubmit={handleSaveAsStyle}
            />
          </div>
        </SheetContent>
      </Sheet>
    </div>
  );
}
